import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";

// ===================================================
// CONFIG
// ===================================================
var MAX_CHARS = 24000;

// -- GEMINI (PRIMARY) -----------------------------
var GM = "gemini-2.5-flash";
var GB_URL = "https://generativelanguage.googleapis.com/v1beta/models/" + GM + ":generateContent?key=";
var KEYS = [
  process.env.REACT_APP_KEY1,
  process.env.REACT_APP_KEY2,
  process.env.REACT_APP_KEY3,
  process.env.REACT_APP_KEY4,
].filter(Boolean);
var ki = 0;
var KEY_COOLDOWNS = {};
function nk() { if (KEYS.length > 0) ki = (ki + 1) % KEYS.length; }
function markKeyExhausted(idx) { KEY_COOLDOWNS[idx] = Date.now() + 30000; }
function ck() {
  if (!KEYS.length) return null;
  var now = Date.now();
  for (var a = 0; a < KEYS.length; a++) {
    var idx = (ki + a) % KEYS.length;
    if (KEYS[idx] && KEYS[idx].length > 5 && now > (KEY_COOLDOWNS[idx] || 0)) { ki = idx; return KEYS[idx]; }
  }
  return KEYS[ki] || null;
}

// -- OPENROUTER (FALLBACK) ------------------------
var OR_URL = "https://openrouter.ai/api/v1/chat/completions";
var OR_KEY = process.env.REACT_APP_OR_KEY || "";

// Startup check — placed after OR_KEY so the reference is valid
var AI_PROVIDERS_MISSING = (!KEYS.length && !OR_KEY);
if (typeof console !== "undefined") {
  console.log("[TrustEngine] Gemini keys loaded:", KEYS.length, "| OpenRouter:", OR_KEY ? "YES" : "MISSING");
  if (KEYS.length === 0) console.error("[TrustEngine] \u274C NO GEMINI KEYS \u2014 add REACT_APP_KEY1..KEY4 to Vercel env and REDEPLOY");
  if (!OR_KEY) console.warn("[TrustEngine] \u26A0 No OpenRouter fallback key \u2014 add REACT_APP_OR_KEY");
  if (AI_PROVIDERS_MISSING) console.error("[TrustEngine] \u274C CRITICAL: No AI providers configured. All analysis will fail.");
}

// Best free models ranked by JSON reliability + reasoning quality
var OR_MODELS = [
  "deepseek/deepseek-chat:free",
  "deepseek/deepseek-r1:free",
  "meta-llama/llama-3.3-70b:free",
  "meta-llama/llama-3.1-8b-instruct:free",
  "mistralai/mistral-small-3.1-24b:free",
  "mistralai/mistral-7b-instruct:free",
  "qwen/qwen3-235b-a22b:free",
  "qwen/qwen3-8b:free",
  "google/gemma-3-27b-it:free",
  "google/gemma-3-12b-it:free",
  "microsoft/phi-4-reasoning:free",
  "nvidia/llama-3.1-nemotron-70b-instruct:free",
];

// -- SUPABASE -------------------------------------
var SB = "https://bkpmilloalpdlbvwxwuu.supabase.co";
var SK = "sb_publishable_SUAHSedzgb6iiSFPDaUXWA_mPE6knx9";
var HK = "te_v5_hist";

// -- COLORS ----------------------------------------
var G = "#c9a84c";
var GD = "#c9a84c15";
var GB = "#c9a84c44";
var GG = "#c9a84c88";

// -- AI PROVIDER STATUS (runtime tracking) --------
var AI_STATUS = { provider: "gemini", fallbackCount: 0, lastResponseMs: 0, failCount: 0, switching: false };
var AI_STATUS_LISTENERS = new Set();
function notifyAIStatus() { AI_STATUS_LISTENERS.forEach(function(fn) { try { fn({...AI_STATUS}); } catch(e) {} }); }
function useAIStatus() {
  var s = useState({...AI_STATUS}); var status = s[0]; var setStatus = s[1];
  useEffect(function() {
    var fn = function(s2) { setStatus({...s2}); };
    AI_STATUS_LISTENERS.add(fn);
    return function() { AI_STATUS_LISTENERS.delete(fn); };
  }, []);
  return status;
}

// ===================================================
// OPENROUTER CALL
// ===================================================
async function callOpenRouter(prompt, signal) {
  if (!OR_KEY) throw new Error("No OpenRouter key configured.");
  var lastErr = null;
  for (var i = 0; i < OR_MODELS.length; i++) {
    try {
      var res = await fetch(OR_URL, {
        method: "POST",
        headers: {
          "Authorization": "Bearer " + OR_KEY,
          "Content-Type": "application/json",
          "HTTP-Referer": "https://trust-engine-ashen.vercel.app",
          "X-Title": "Trust Engine v5",
        },
        signal: signal,
        body: JSON.stringify({
          model: OR_MODELS[i],
          messages: [{ role: "user", content: prompt }],
          temperature: 0.2,
          max_tokens: 16384,
        }),
      });
      if (!res.ok) {
        var ej = await res.json().catch(function() { return {}; });
        lastErr = (ej && ej.error && ej.error.message) ? ej.error.message : ("HTTP " + res.status);
        continue; // try next model
      }
      var data = await res.json();
      var text = data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content;
      if (!text) { lastErr = "Empty response from " + OR_MODELS[i]; continue; }
      AI_STATUS.provider = "openrouter:" + OR_MODELS[i];
      AI_STATUS.fallbackCount++;
      return text; // returns raw text (not wrapped in candidates)
    } catch (e) {
      if (e && e.name === "AbortError") throw e;
      lastErr = e.message; continue;
    }
  }
  console.error("[OpenRouter] All models failed. Last error:", lastErr);
  throw new Error("OpenRouter failed: " + (lastErr || "All models unavailable."));
}

// ===================================================
// GEMINI CALL (PRIMARY)
// ===================================================
async function callGeminiDirect(msg, sys, signal) {
  if (KEYS.length === 0) throw new Error("No Gemini API keys configured. Check Vercel env vars REACT_APP_KEY1..KEY4 and redeploy.");
  var tried = 0;
  while (tried < KEYS.length) {
    var key = ck(); if (!key) { tried++; nk(); continue; }
    try {
      var body = sys
        ? { contents: [{ role: "user", parts: [{ text: msg }] }], systemInstruction: { parts: [{ text: sys }] }, generationConfig: { temperature: 0.2, maxOutputTokens: 16384 } }
        : { contents: [{ role: "user", parts: [{ text: msg }] }], generationConfig: { temperature: 0.2, maxOutputTokens: 16384 } };
      var res = await fetch(GB_URL + key, {
        method: "POST", headers: { "Content-Type": "application/json" }, signal,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        var ej = await res.json().catch(function() { return {}; });
        var em = (ej && ej.error && ej.error.message) ? ej.error.message : ("HTTP " + res.status);
        if (res.status === 429 || res.status === 503) { markKeyExhausted(ki); nk(); tried++; continue; }
        if (res.status === 400) {
          // retry without system instruction
          var r2 = await fetch(GB_URL + key, {
            method: "POST", headers: { "Content-Type": "application/json" }, signal,
            body: JSON.stringify({ contents: [{ role: "user", parts: [{ text: (sys ? sys + "\n\n" : "") + msg }] }], generationConfig: { temperature: 0.2, maxOutputTokens: 16384 } }),
          });
          if (r2.ok) return await r2.json();
          tried++; nk(); continue;
        }
        throw new Error(em.length < 150 ? em : "Gemini analysis failed.");
      }
      AI_STATUS.provider = "gemini";
      return await res.json();
    } catch (e) {
      if (e && e.name === "AbortError") throw e;
      tried++; nk();
    }
  }
  console.error("[Gemini] All keys exhausted.");
  throw new Error("All Gemini keys are at their daily limit. Switching to backup AI...");
}

// ===================================================
// MASTER AI CONTROLLER  Gemini  OpenRouter fallback
// Returns: { text: string, provider: string }
// ===================================================
async function runAI(msg, sys, signal) {
  var t0 = Date.now();
  
  // Step 1: Try Gemini (all 4 keys via callGeminiDirect)
  try {
    var gData = await callGeminiDirect(msg, sys, signal);
    var gText = gt(gData);
    if (gText && gText.length > 10) {
      AI_STATUS.provider = "gemini";
      AI_STATUS.lastResponseMs = Date.now() - t0;
      AI_STATUS.switching = false;
      notifyAIStatus();
      return { text: String(gText || ""), provider: "gemini" };
    }
    throw new Error("Gemini returned empty response.");
  } catch (e) {
    if (e && e.name === "AbortError") throw e;
    AI_STATUS.failCount++;
    // Step 2: Fallback to OpenRouter if key available
    if (OR_KEY) {
      AI_STATUS.switching = true; notifyAIStatus();
      try {
        var fullPrompt = sys ? (sys + "\n\n" + msg + "\n\nRETURN JSON ONLY. NO TEXT. NO MARKDOWN. START WITH { END WITH }.") : (msg + "\n\nRETURN JSON ONLY. NO TEXT. NO MARKDOWN. START WITH { END WITH }.");
        var orText = await callOpenRouter(fullPrompt, signal);
        if (orText && orText.length > 10) {
          AI_STATUS.provider = "openrouter";
          AI_STATUS.lastResponseMs = Date.now() - t0;
          AI_STATUS.switching = false;
          notifyAIStatus();
          return { text: String(orText || ""), provider: "openrouter" };
        }
      } catch(e2) {
        if (e2 && e2.name === "AbortError") throw e2;
      }
      AI_STATUS.switching = false; notifyAIStatus();
    }
    return {
      text: JSON.stringify({
        status: "fallback",
        message: KEYS.length === 0 && !OR_KEY ? "No AI providers configured. Add API keys and redeploy." : "AI unavailable — all providers exhausted. Please retry.",
        fallback: true
      }),
      provider: "system"
    };
  }
}

async function gemini(msg, sys, signal) {
  var result = await runAI(msg, sys, signal);
  // Wrap text back into Gemini-style response object for gt() compatibility
  return { candidates: [{ content: { parts: [{ text: result.text }] } }] };
}

function gt(data) { try { return data.candidates[0].content.parts[0].text; } catch (e) { return null; } }

// -- COMPANY INTELLIGENCE SYSTEM ------------------------------
var CO_SYS = "You are a company intelligence analyst specializing in Indian job market fraud detection. Given web search results and a company name, analyze whether this company is legitimate, actually hiring, or potentially fraudulent.\n\nRespond ONLY with valid JSON:\n{\"company_name\":\"Acme Corp\",\"verdict\":\"Legitimate\",\"verdict_color\":\"green\",\"trust_score\":82,\"founded\":\"2010\",\"headquarters\":\"Mumbai, India\",\"industry\":\"IT Services\",\"size\":\"500-1000 employees\",\"website\":\"acme.com\",\"linkedin_presence\":true,\"is_registered\":true,\"cin_number\":\"U72900MH2010PLC123456\",\"glassdoor_rating\":\"3.8/5\",\"actual_hiring\":true,\"hiring_evidence\":[\"Active jobs on LinkedIn\",\"Recent Naukri postings verified\"],\"red_flags\":[],\"positive_signals\":[\"Registered on MCA portal\",\"Active social media\",\"Multiple verified reviews\"],\"news_mentions\":[\"Featured in Economic Times 2024\"],\"summary\":\"2-sentence assessment of this company.\",\"warning\":null,\"recommendation\":\"Safe to apply. Verify offer letter on company official email domain.\"}";

async function searchCompany(companyName, signal) {
  var results = [];
  var queries = [
    companyName + " company India review",
    companyName + " jobs hiring India",
    companyName + " scam fraud India",
  ];
  for (var i = 0; i < queries.length; i++) {
    try {
      var url = "https://api.allorigins.win/get?url=" + encodeURIComponent("https://duckduckgo.com/html/?q=" + encodeURIComponent(queries[i]));
      var res = await fetch(url, { signal: signal || undefined });
      if (!res.ok) continue;
      var data = await res.json();
      var html = data.contents || "";
      var _dp = typeof DOMParser !== "undefined" ? new DOMParser() : null; if (!_dp) continue;
      var doc = _dp.parseFromString(html, "text/html");
      var snippets = [];
      doc.querySelectorAll(".result__snippet, .result__title, .result__url").forEach(function(el) {
        var t = (el.textContent || "").trim();
        if (t.length > 20) snippets.push(t);
      });
      results = results.concat(snippets.slice(0, 5));
    } catch (e) { continue; }
  }
  return results;
}

async function analyzeCompany(companyName, signal) {
  var webResults = await searchCompany(companyName, signal);
  var context = "Company to investigate: " + companyName + "\n\n";
  if (webResults.length > 0) {
    context += "Web search findings:\n" + webResults.slice(0, 15).map(function(r, i) { return (i + 1) + ". " + r; }).join("\n");
  } else {
    context += "No web results found. Base analysis on company name patterns and India job market knowledge.";
  }
  context += "\n\nAnalyze this company thoroughly for an Indian job seeker considering applying.";
  return gemini(context, CO_SYS, signal);
}

// ===================================================
// ULTRA JSON PARSER — production-grade, crash-proof
// Handles all AI output quirks: markdown, trailing
// commas, extra text, newlines, control chars.
// ===================================================
function ultraParseJSON(text) {
  if (!text) return null;
  try {
    // Step 1: Strip markdown code fences
    var c = text
      .replace(/```json[\s]*/gi, "")
      .replace(/```[\s]*/gi, "")
      .trim();

    // Step 2: Strip preamble — find first {
    var fb = c.indexOf("{");
    if (fb > 0) c = c.slice(fb);

    // Step 3: Strip trailing junk — find last }
    var lb = c.lastIndexOf("}");
    if (lb >= 0 && lb < c.length - 1) c = c.slice(0, lb + 1);

    // Step 4: Direct parse (fastest path)
    try { return JSON.parse(c); } catch(e1) {}

    // Step 5: Extract largest JSON block
    var m = c.match(/\{[\s\S]*\}/);
    if (!m) return null;
    var jsonStr = m[0];

    // Step 6: Fix trailing commas before } or ]
    var fixed = jsonStr.replace(/,\s*([}\]])/g, "$1");
    try { return JSON.parse(fixed); } catch(e2) {}

    // Step 7: Fix unescaped control characters
    var cleaned = fixed.replace(/[\x00-\x1F\x7F]/g, function(ch) {
      if (ch === "\n") return "\\n";
      if (ch === "\r") return "\\r";
      if (ch === "\t") return "\\t";
      return "";
    });
    try { return JSON.parse(cleaned); } catch(e3) {}

    // Step 8: Aggressive cleanup — remove single-line comments
    var noComments = cleaned.replace(/\/\/[^\n"]*/g, "");
    try { return JSON.parse(noComments); } catch(e4) {}

    console.error("[ultraParseJSON] All parse attempts failed. Preview:", text.slice(0, 200));
    return null;
  } catch(e) {
    console.error("[ultraParseJSON] Unexpected error:", e.message);
    return null;
  }
}

// Alias — pj now routes through ultraParseJSON
function pj(text) { return ultraParseJSON(text); }

// ===================================================
// STRICT JSON SYSTEM PROMPT — prepended to all AI calls
// Forces clean JSON output from any model
// ===================================================
var STRICT_JSON_SYSTEM = [
  "You are a high-precision AI system integrated into a production application.",
  "CRITICAL OUTPUT RULES — VIOLATING THESE WILL BREAK THE SYSTEM:",
  "1. Return ONLY valid JSON. Nothing else.",
  "2. DO NOT include markdown code fences (no ``` or ```json)",
  "3. DO NOT include any explanation, commentary, or preamble",
  "4. DO NOT include text before or after the JSON object",
  "5. Start your response with { and end with }",
  "6. Use ONLY double quotes \" for all keys and string values",
  "7. NO trailing commas after the last item in objects or arrays",
  "8. NO JavaScript comments inside JSON",
  "9. If a string value contains quotes, escape them: \\\"",
  "10. If uncertain about a value, use null or an empty string \"\"",
  "JSON FAILURE = SYSTEM CRASH. Return best-effort valid JSON always."
].join("\n");

// ===================================================
// runAIStable — production AI controller
// • Injects strict JSON prompt
// • Retries once on parse failure with stronger nudge
// • NEVER returns undefined or throws uncaught
// • Always returns safe fallback object on total failure
// ===================================================
async function runAIStable(prompt, systemPrompt, signal) {
  var feedbackCtx = "";
  var s3Ctx       = "";

  try { feedbackCtx = s3GetFeedbackContext();   } catch(e) {}
  try { s3Ctx       = s3GetPromptContext();      } catch(e) {}

  // Prepend ONLY to prompt (not system) — avoids token overflow on system instructions
  var ctxPrefix      = (feedbackCtx || "") + (s3Ctx || "");
  var enrichedPrompt = ctxPrefix + prompt;

  var effectiveSystem = STRICT_JSON_SYSTEM + (systemPrompt ? ("\n\n" + systemPrompt) : "");
  var currentPrompt   = enrichedPrompt;
  var lastRawText     = null;
  var attempts        = 0;

  while (attempts < 2) {
    try {
      var res = await withTimeout(runAI(currentPrompt, effectiveSystem, signal), 45000);
      var text = (res && res.text) ? res.text : (typeof res === "string" ? res : null);
      lastRawText = text;
      if (typeof text === "string") { text = text.replace(/```json[\s]*/gi, "").replace(/```[\s]*/gi, "").trim(); }
      var parsed = ultraParseJSON(text || "");
      if (parsed && !parsed.error && !parsed.fallback) return parsed;

      currentPrompt = enrichedPrompt +
        "\n\nCRITICAL: YOUR LAST RESPONSE WAS INVALID JSON. THIS BREAKS THE SYSTEM." +
        "\nOUTPUT ONLY VALID JSON NOW." +
        "\n- START WITH {" +
        "\n- END WITH }" +
        "\n- NO TEXT BEFORE OR AFTER" +
        "\n- NO MARKDOWN, NO BACKTICKS" +
        "\n- DOUBLE QUOTES ONLY" +
        "\n- NO TRAILING COMMAS";
      attempts++;
    } catch(e) {
      if (e && e.name === "AbortError") throw e;
      console.error("[runAIStable] attempt", attempts, "error:", e.message);
      if (e && e.message === "AI_TIMEOUT") { attempts++; continue; }
      attempts++;
    }
  }

  console.error("[runAIStable] Both attempts failed. Last raw:", lastRawText && lastRawText.slice(0, 300));
  return { error: true, message: "AI response could not be parsed. Please retry.", fallback: true };
}

// ===================================================
// PATHFINDER INTELLIGENCE ENGINE
// ===================================================
var PATHFINDER_PROMPT = "You are PATHFINDER — a high-precision Decision Intelligence Engine.\nYou operate as: Forensic Hiring Manager, Market Reality Analyst, Execution Strategist, Behavioral Pattern Detector.\nYou DO NOT give generic advice. You DO NOT motivate. You DO NOT assume.\nYou compute decisions based ONLY on resume evidence, market demand, and execution feasibility.\n\nCRITICAL OUTPUT RULE: RETURN ONLY VALID JSON. NO MARKDOWN. NO EXTRA TEXT. NO PREAMBLE. NO EXPLANATION. START YOUR RESPONSE WITH { AND END WITH }.\nIF OUTPUT EXCEEDS TOKEN LIMIT, TRUNCATE free_resources AND paid_resources ARRAYS FIRST. NEVER truncate user_analysis, paths titles, or scores.\n\nCORE OBJECTIVE: Convert the user's resume into EXACTLY 3 executable life paths.\n\nINTELLIGENCE LAYERS (ALL MANDATORY):\n1. SKILL FORENSICS: Real job/project=STRONG. Course/cert=MODERATE. Mention only=WEAK.\n2. MARKET POSITION: low | average | competitive | strong.\n3. MOAT ENGINE: Identify specific unfair advantage from skill combination.\n4. TRUTH ENGINE: Biggest mistake and unrealistic expectation. Be direct.\n5. ANTI-GOAL ENGINE: STOP_DOING list — specific harmful actions to stop now.\n6. PROOF-OF-WORK ENGINE: Never say 'build project'. Define exact deliverable.\n7. RESOURCE ENGINE: Free resources first. Real platforms: YouTube, Coursera, Udemy.\n8. TIMELINE ENGINE: working=part-time. jobless=full-time aggressive.\n9. SALARY ENGINE: Infer region. If unknown=realistic global. Never exaggerate.\n10. DECISION ENGINE: Compare all 3 paths. Risk vs reward, time vs money.\n\nPATH GENERATION (EXACTLY 3):\nPATH 1 PRIMARY — Highest probability based on current skills.\nPATH 2 GROWTH — Higher income, requires upskilling.\nPATH 3 ALTERNATIVE — Different direction using existing skills.\n\nTONE: Affects ONLY truth_analysis, fit_reason, final_advice. Default=mentor.\n\nOUTPUT THIS EXACT JSON SCHEMA (no deviations):\n{\"user_analysis\":{\"profile_summary\":\"\",\"experience_level\":\"beginner | intermediate | advanced\",\"market_position\":\"low | average | competitive | strong\",\"strengths\":[],\"weaknesses\":[],\"unfair_advantage\":\"\",\"stop_doing\":[],\"truth_analysis\":\"\",\"readiness_score\":0},\"paths\":[{\"path_type\":\"primary\",\"title\":\"\",\"fit_reason\":\"\",\"readiness_score\":0,\"market_demand\":\"low | medium | high\",\"salary\":{\"entry\":\"\",\"growth\":\"\"},\"timeline\":{\"entry_level\":\"\",\"growth_level\":\"\"},\"difficulty\":\"low | medium | high\",\"risk\":\"low | medium | high\",\"failure_points\":[],\"skill_gap\":[],\"learning_plan\":{\"topics\":[],\"free_resources\":[{\"type\":\"\",\"name\":\"\",\"link\":\"\"}],\"paid_resources\":[{\"platform\":\"\",\"course\":\"\",\"link\":\"\"}]},\"execution_plan\":[{\"phase\":\"\",\"duration\":\"\",\"actions\":[],\"portfolio_target\":\"\"}],\"outcome\":\"\",\"final_advice\":\"\"}],\"final_recommendation\":{\"recommended_path\":\"primary | growth | alternative\",\"reason\":\"\"},\"decision_matrix\":{\"primary_vs_growth\":\"\",\"primary_vs_alternative\":\"\",\"growth_vs_alternative\":\"\"},\"immediate_next_steps\":[],\"execution_warning\":\"\"}";

// -- SAFE JSON PARSER (all modules) — routes through ultraParseJSON
function safeParseJSON(text) {
  return ultraParseJSON(text);
}

// withTimeout — wraps any promise with a hard timeout
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise(function(_, reject) {
      setTimeout(function() { reject(new Error("AI_TIMEOUT")); }, ms);
    })
  ]);
}

async function runPathfinder(resumeText, goal, situation, constraints, tone, signal, uploadedParts) {
  var safeResume = (resumeText || "")
    .replace(/\s+/g, " ")
    .slice(0, 4000); // 🔥 HARD LIMIT — prevents token overflow
  var input = "RESUME_TEXT:\n" + safeResume + "\n\nGOAL:\n" + (goal || "Not provided") + "\n\nSITUATION:\n" + (situation || "Not provided") + "\n\nCONSTRAINTS:\n" + (constraints || "None") + "\n\nTONE_PREFERENCE:\n" + (tone || "mentor");

  // Attempt 1: direct Gemini multimodal call (supports uploaded file parts)
  var text = null;
  try {
    var parts = [{ text: PATHFINDER_PROMPT + "\n\n" + input }];
    if (uploadedParts && uploadedParts.length > 0) {
      uploadedParts.slice(0, 2).forEach(function(p) { parts.push(p); });
    }
    var key = ck();
    if (key) {
      var body = { contents: [{ role: "user", parts: parts }], generationConfig: { temperature: 0.15, maxOutputTokens: 16384 } };
      var res = await fetch(GB_URL + key, { method: "POST", headers: { "Content-Type": "application/json" }, signal, body: JSON.stringify(body) });
      if (res.ok) {
        var d = await res.json();
        text = gt(d);
      } else if (res.status === 429 || res.status === 503) {
        markKeyExhausted(ki); nk();
      }
    }
  } catch(e) { if (e && e.name === "AbortError") throw e; }

  // Parse attempt 1 (from direct call)
  if (text) {
    var parsed = ultraParseJSON(text);
    if (parsed && !parsed.error && (parsed.paths || parsed.user_analysis)) return parsed;
  }

  // Attempt 2: use runAIStable which handles retry + fallback
  var stableResult = await runAIStable(input, PATHFINDER_PROMPT, signal);
  if (stableResult && !stableResult.error && (stableResult.paths || stableResult.user_analysis)) {
    return stableResult;
  }

  // Both attempts failed — return minimal safe structure so UI never crashes
  return {
    _fallback: true,
    user_analysis: {
      profile_summary: "AI analysis temporarily unavailable. Please retry.",
      experience_level: "intermediate",
      market_position: "average",
      strengths: ["Could not be determined — please retry"],
      weaknesses: ["Could not be determined — please retry"],
      unfair_advantage: "Retry for full analysis.",
      stop_doing: [],
      truth_analysis: "AI was unable to analyze your resume at this time. Please try again.",
      readiness_score: 0
    },
    paths: [
      {
        path_type: "primary", title: "Analysis Unavailable", fit_reason: "Please retry for accurate path analysis.",
        readiness_score: 0, market_demand: "medium",
        salary: { entry: "—", growth: "—" },
        timeline: { entry_level: "—", growth_level: "—" },
        difficulty: "medium", risk: "medium",
        failure_points: [], skill_gap: [],
        learning_plan: { topics: [], free_resources: [], paid_resources: [] },
        execution_plan: [], outcome: "Retry for full analysis.", final_advice: "Please retry."
      }
    ],
    final_recommendation: { recommended_path: "primary", reason: "Retry for accurate recommendation." },
    decision_matrix: { primary_vs_growth: "—", primary_vs_alternative: "—", growth_vs_alternative: "—" },
    immediate_next_steps: ["Retry the analysis with your full resume text."],
    execution_warning: "This is a fallback result. Retry for accurate intelligence."
  };
}


// =======================================================================
// TRUST ENGINE v6  DECISION INTELLIGENCE PLATFORM
// 12-Phase Intelligence Upgrade
// =======================================================================

// -- PHASE 1: UNIVERSAL DECISION SCHEMA ---------------------------------
function normalizeDecision(result, module) {
  if (!result) return null;
  var score = result.risk_score || result.trust_score || result.ats_score || 50;
  var verdict = "SUSPICIOUS";
  var decision = "VERIFY";
  if (score >= 70) { verdict = "HIGH_RISK"; decision = "AVOID"; }
  else if (score <= 30) { verdict = "SAFE"; decision = "PROCEED"; }
  return {
    trust_score: Math.max(0, Math.min(100, score)),
    verdict: verdict,
    confidence: result.confidence ? parseFloat(result.confidence) / 100 : 0.75,
    decision: decision,
    reasoning: result.final_reasoning || result.summary || "",
    module_specific: result,
    module: module,
    timestamp: Date.now()
  };
}

// -- PHASE 2 & 3: MEMORY + PATTERN ENGINE (IndexedDB + localStorage) ----
var MEM_KEY = "te_v6_memory";
var CACHE_KEY = "te_v6_cache";
var PROFILE_KEY = "te_v6_profile";
var PATTERN_KEY = "te_v6_patterns";

function simHash(str) {
  var s = str.toLowerCase().replace(/\s+/g, " ").slice(0, 500);
  var h = 0;
  for (var i = 0; i < s.length; i++) { h = ((h << 5) - h + s.charCodeAt(i)) | 0; }
  return Math.abs(h).toString(36);
}

function memGet(key) {
  try { return JSON.parse(localStorage.getItem(key) || "null"); } catch(e) { return null; }
}
function memSet(key, val) {
  try {
    var s = JSON.stringify(val);
    if (s.length > 3 * 1024 * 1024) return;
    localStorage.setItem(key, s);
  } catch(e) {}
}

// -- MEMORY CLEANUP (triggered every 50 scans) ---------------------------
function cleanupMemory() {
  try {
    // Trim pattern verdict_history to last 30
    var patterns = memGet(PATTERN_KEY) || {};
    if (patterns.verdict_history && patterns.verdict_history.length > 30) {
      patterns.verdict_history = patterns.verdict_history.slice(-30);
      memSet(PATTERN_KEY, patterns);
    }
    // Trim scam_phrases map to top 20 most seen
    if (patterns.scam_phrases && Object.keys(patterns.scam_phrases).length > 20) {
      var sorted = Object.keys(patterns.scam_phrases).sort(function(a,b){return (patterns.scam_phrases[b]||0)-(patterns.scam_phrases[a]||0);}).slice(0,20);
      var trimmed = {};
      sorted.forEach(function(k){trimmed[k]=patterns.scam_phrases[k];});
      patterns.scam_phrases = trimmed;
      memSet(PATTERN_KEY, patterns);
    }
    // Evict expired cache entries (older than 1 hour)
    var cache = memGet(CACHE_KEY) || {};
    var now = Date.now(); var changed = false;
    Object.keys(cache).forEach(function(k){ if(now - (cache[k].ts||0) > 3600000){delete cache[k];changed=true;} });
    if (changed) memSet(CACHE_KEY, cache);
    // Trim trajectory to last 15 sessions
    var traj = memGet(S3_TRAJ_KEY) || [];
    if (traj.length > 15) { memSet(S3_TRAJ_KEY, traj.slice(-15)); }
    // Trim feedback rolling window
    var fb = memGet(S3_FEEDBACK_KEY) || {};
    if (fb.recentFeedback && fb.recentFeedback.length > 30) { fb.recentFeedback = fb.recentFeedback.slice(-30); memSet(S3_FEEDBACK_KEY, fb); }
  } catch(e) {}
}

function learnFromScan(input, result, module) {
  if (!result) return;
  try {
    var patterns = memGet(PATTERN_KEY) || { scam_phrases: {}, domains: {}, salary_flags: {}, verdict_history: [], scan_count: 0 };
    patterns.scan_count = (patterns.scan_count || 0) + 1;
    // Periodic deep cleanup every 50 scans to prevent localStorage bloat
    if (patterns.scan_count % 50 === 0) { try { cleanupMemory(); } catch(ce) {} }
    var inp = (input || "").toLowerCase();
    var scamPhrases = ["registration fee", "upi", "joining fee", "telegram", "whatsapp job", "work from home kit", "ghar baithe", "mlm", "flipkart packing"];
    scamPhrases.forEach(function(p) {
      if (inp.includes(p)) { patterns.scam_phrases[p] = (patterns.scam_phrases[p] || 0) + 1; }
    });
    if (result.detected_entities && result.detected_entities.domain) {
      var d = result.detected_entities.domain;
      if (d) patterns.domains[d] = (patterns.domains[d] || 0) + 1;
    }
    patterns.verdict_history = (Array.isArray(patterns.verdict_history) ? patterns.verdict_history : []).slice(-50);
    patterns.verdict_history.push({ verdict: result.verdict || "Unknown", score: result.risk_score || 0, ts: Date.now() });
    memSet(PATTERN_KEY, patterns);

    var cache = memGet(CACHE_KEY) || {};
    var hash = simHash(input || "");
    cache[hash] = { result: result, module: module, ts: Date.now(), hits: (cache[hash] ? cache[hash].hits + 1 : 1) };
    var cacheKeys = Object.keys(cache);
    if (cacheKeys.length > 100) {
      var oldest = cacheKeys.sort(function(a,b) { return cache[a].ts - cache[b].ts; })[0];
      delete cache[oldest];
    }
    memSet(CACHE_KEY, cache);
  } catch(e) {}
}

function checkCache(input) {
  try {
    var cache = memGet(CACHE_KEY) || {};
    var hash = simHash(input || "");
    var hit = cache[hash];
    if (!hit) return null;
    var age = Date.now() - hit.ts;
    if (age > 3600000) return null;
    return hit;
  } catch(e) { return null; }
}

function getPatternContext(input) {
  try {
    var patterns = memGet(PATTERN_KEY) || {};
    var inp = (input || "").toLowerCase();
    var knownScams = Object.keys(patterns.scam_phrases || {}).filter(function(p) { return inp.includes(p) && patterns.scam_phrases[p] > 1; });
    var context = "";
    if (knownScams.length > 0) context += "PATTERN_MATCH: Previously seen scam phrases detected: " + knownScams.join(", ") + ". ";
    if (patterns.scan_count > 10) context += "SYSTEM_INTELLIGENCE: " + patterns.scan_count + " total scans processed. ";
    return context;
  } catch(e) { return ""; }
}

// -- PHASE 4: SELF-HEALING RULE ENGINE ----------------------------------
var RULE_ENGINE = {
  scamPhrases: ["registration fee", "joining fee", "security deposit", "upi id", "flipkart packing", "telegram group", "mlm", "work from home kit", "ghar baithe paise", "online typing job", "data entry fraud", "payment first"],
  urgencyWords: ["urgent", "limited seats", "apply now only", "last chance", "today only", "hurry"],
  salaryRegex: /(\d[\d,]*)\s*(?:\/|-|per)\s*(?:day|daily)/gi,
  fakePatterns: ["no experience required", "freshers only Rs", "earn lakhs", "guaranteed income"],

  analyze: function(text) {
    var t = (text || "").toLowerCase();
    var score = 20;
    var flags = [];
    var decision = "PROCEED";

    this.scamPhrases.forEach(function(p) { if (t.includes(p)) { score += 25; flags.push("Known scam phrase: " + p); } });
    this.urgencyWords.forEach(function(p) { if (t.includes(p)) { score += 10; flags.push("Urgency tactic: " + p); } });
    this.fakePatterns.forEach(function(p) { if (t.includes(p)) { score += 15; flags.push("Fake pattern: " + p); } });

    var salMatches = Array.from(text.matchAll(this.salaryRegex) || []);
    salMatches.forEach(function(m) {
      var v = parseInt((m[1] || "0").replace(/,/g, ""), 10);
      if (v > 3000) { score += 20; flags.push("Unrealistic daily salary: Rs." + v.toLocaleString("en-IN")); }
    });

    if (!t.match(/apply\s*(at|on|to|via|here)/i) && !t.match(/careers\.(com|in)/i)) score += 5;
    if (t.includes("+91") || t.includes("whatsapp")) score += 10;
    if (t.match(/telegram\.me|t\.me/i)) score += 20;

    score = Math.min(100, score);
    if (score >= 70) decision = "AVOID";
    else if (score >= 40) decision = "VERIFY";

    return {
      risk_score: score,
      verdict: score >= 70 ? "Likely Scam" : score >= 40 ? "Suspicious" : "Likely Legitimate",
      quick_verdict: score >= 70 ? "SCAM" : score >= 40 ? "SUSPICIOUS" : "REAL",
      confidence: "Rule-Based",
      platform_detected: t.includes("whatsapp") ? "WhatsApp" : t.includes("telegram") ? "Telegram" : t.includes("linkedin") ? "LinkedIn" : "Unknown",
      red_flags: flags,
      positive_signals: [],
      final_reasoning: score >= 70 ? "Multiple scam signatures detected by rule engine. AI unavailable." : score >= 40 ? "Some suspicious patterns found. Verify before proceeding." : "No obvious scam patterns detected. AI unavailable for full analysis.",
      user_advice: { action: decision, steps: flags.length > 0 ? ["Do not pay any fees", "Verify company on LinkedIn", "Check MCA portal", "Report to cybercrime.gov.in"] : ["Verify job exists on company official site", "Cross-check salary with Glassdoor", "Apply only via official channels"] },
      manipulation_tags: [],
      manipulation_signals: [],
      india_specific: { detected: flags.length > 0, scam_type: flags.length > 0 ? "Pattern Match" : "None", signals: flags },
      score_breakdown: { financial_risk: Math.min(25, score * 0.25), india_scam_patterns: Math.min(20, score * 0.2), identity_verification: 5, social_manipulation: Math.min(15, score * 0.15), content_authenticity: 10, platform_behavior: 5 },
      hiring_intent: { score: Math.max(0, 50 - score), probability: score >= 70 ? "Very Low" : "Medium", is_ghost_hiring: score >= 60, ghost_signals: score >= 60 ? ["Suspicious contact method"] : [] },
      detected_entities: { company: null, recruiter: null, email: null, domain: null },
      explainability: { why_risky: flags.slice(0, 2).join(". ") || "No immediate flags.", how_they_manipulate: score >= 40 ? "Uses urgency and unrealistic promises." : "N/A", what_they_want: score >= 70 ? "Your money or personal data." : "Unknown." },
      detailed_analysis: { identity: "Rule-based analysis only.", platform_behavior: "Detected via pattern matching.", social_engineering: "Urgency tactics detected.", job_authenticity: "Cannot fully verify without AI.", financial_risk: flags.filter(function(f) { return f.includes("salary") || f.includes("fee"); }).join("; ") || "None flagged.", narrative_psychology: "N/A" },
      ai_generated_jd: { likely: false, confidence: "Low", markers_found: [] },
      salary_intel: { market_rate_mismatch: score >= 60, claimed: "Unknown", market_realistic_range: "Rs.15,000–40,000/month (India avg)", verdict: score >= 60 ? "Verify salary claim" : "Within range" },
      _source: "rule_engine"
    };
  }
};

// -- PHASE 5: CROSS-MODULE INTELLIGENCE ---------------------------------
var CROSS_INTEL = {
  store: function(module, key, data) {
    try {
      var mi = memGet("te_v6_cross") || {};
      if (!mi[module]) mi[module] = {};
      mi[module][key] = { data: data, ts: Date.now() };
      memSet("te_v6_cross", mi);
    } catch(e) {}
  },
  get: function(module, key) {
    try { var mi = memGet("te_v6_cross") || {}; return mi[module] && mi[module][key] ? mi[module][key].data : null; } catch(e) { return null; }
  },
  getCorrelation: function() {
    try {
      var mi = memGet("te_v6_cross") || {};
      var insights = [];
      if (mi.scan && mi.resume) {
        var lastScan = Object.values(mi.scan).sort(function(a,b){return b.ts-a.ts;})[0];
        if (lastScan && lastScan.data && lastScan.data.risk_score > 60) {
          insights.push("⚠ Your last scanned job post was HIGH RISK. Compare carefully with your resume strengths.");
        }
      }
      if (mi.hr && mi.career) {
        insights.push("💡 Cross-module: HR rankings and career path aligned. Check skill overlaps.");
      }
      return insights;
    } catch(e) { return []; }
  }
};

// -- PHASE 6: PERSONALIZATION ENGINE ------------------------------------
function getUserProfile() {
  return memGet(PROFILE_KEY) || { experience_level: "mid", domain: "general", goal: "job_search", region: "india", locale: (typeof navigator !== "undefined" && navigator.language) || "en-IN", setup: false };
}
function saveUserProfile(profile) { memSet(PROFILE_KEY, { ...profile, updated: Date.now() }); }
function getPersonalizedContext(profile) {
  var p = profile || getUserProfile();
  var ctx = "";
  if (p.experience_level === "fresher") ctx += "User is a FRESHER. Focus on entry-level roles, internships, campus placements. ";
  else if (p.experience_level === "senior") ctx += "User is SENIOR PROFESSIONAL (5+ years). Focus on leadership roles and premium companies. ";
  else ctx += "User is MID-LEVEL professional. ";
  if (p.domain && p.domain !== "general") ctx += "Domain: " + p.domain + ". ";
  if (p.goal === "switch") ctx += "Goal: Career switch. Highlight transferable skills. ";
  else if (p.goal === "growth") ctx += "Goal: Vertical growth in current domain. ";
  return ctx;
}

// -- PHASE 7: GLOBAL INTELLIGENCE (Region Detection) ---------------------
var REGION_PATTERNS = {
  "en-IN": { label: "India", currency: "INR", fraudPatterns: ["registration fee", "joining fee", "ghar baithe", "flipkart packing", "telegram group", "mlm scheme", "upi payment"], salaryUnit: "LPA/month", benchmarks: { fresher: "Rs.2.5-4 LPA", mid: "Rs.6-15 LPA", senior: "Rs.20-50 LPA" } },
  "en-US": { label: "USA", currency: "USD", fraudPatterns: ["wire transfer", "gift card payment", "reshipping job", "mystery shopper"], salaryUnit: "USD/year", benchmarks: { fresher: "$45-65K", mid: "$80-130K", senior: "$150-250K" } },
  "en-GB": { label: "UK", currency: "GBP", fraudPatterns: ["cheque overpayment", "advance fee", "admin fee"], salaryUnit: "GBP/year", benchmarks: { fresher: "£22-30K", mid: "£40-70K", senior: "£80-150K" } },
  "default": { label: "Global", currency: "USD", fraudPatterns: ["advance fee", "registration fee", "wire transfer"], salaryUnit: "USD/year", benchmarks: { fresher: "$30-50K", mid: "$60-100K", senior: "$120-200K" } }
};
function getRegionContext() {
  var locale = (typeof navigator !== "undefined" && navigator.language) || "en-IN";
  var r = REGION_PATTERNS[locale] || REGION_PATTERNS[locale.slice(0,2)] || REGION_PATTERNS["default"];
  if (!r) r = REGION_PATTERNS["en-IN"];
  return "REGION: " + r.label + ". Currency: " + r.currency + ". Common local fraud: " + r.fraudPatterns.slice(0,4).join(", ") + ". Salary benchmarks: Fresher=" + r.benchmarks.fresher + ", Mid=" + r.benchmarks.mid + ", Senior=" + r.benchmarks.senior + ".";
}

// -- PHASE 8: SHAREABLE REPORTS ------------------------------------------
function generateShareableId(result) {
  var h = simHash(JSON.stringify({ v: result.verdict, s: result.risk_score, t: Math.floor(Date.now() / 60000) }));
  return "TE-" + h.toUpperCase().slice(0, 8);
}
function generateShareText(result, type) {
  var id = generateShareableId(result);
  var score = result.risk_score || result.trust_score || 0;
  var verdict = result.verdict || "Unknown";
  var emoji = score >= 70 ? "🚨" : score >= 40 ? "⚠️" : "✅";
  var lines = [
    emoji + " Trust Engine v6 — " + (type || "Scan") + " Report",
    "Verdict: " + verdict + " | Risk: " + score + "/100",
    result.final_reasoning ? result.final_reasoning.slice(0, 120) + "..." : "",
    "Report ID: " + id,
    "Verify at: trust-engine-ashen.vercel.app",
    "#TrustEngine #JobFraud #IndiaJobs"
  ];
  return lines.filter(Boolean).join("\n");
}
function shareResult(result, type) {
  var text = generateShareText(result, type);
  if (typeof navigator !== "undefined" && navigator.share) {
    navigator.share({ title: "Trust Engine Report", text: text, url: "https://trust-engine-ashen.vercel.app" }).catch(function() {});
  } else {
    try { navigator.clipboard.writeText(text).catch(function() {
      try { var ta = document.createElement("textarea"); ta.value = text;
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); } catch(e2) {}
    }); } catch(e) {}
  }
}

// -- PHASE 1: WHATSAPP SHARE ---------------------------------
function shareToWhatsApp(text) {
  try {
    var url = "https://wa.me/?text=" + encodeURIComponent(text);
    if (typeof window !== "undefined") window.open(url, "_blank");
  } catch(e) {}
}


// -- PHASE 1: PATHFINDER PDF EXPORT -------------------------
function downloadPathfinderPDF(data) {
  if (!data || !data.paths) return;
  var jspdf = window.jspdf;
  if (!jspdf) {
    // Lazy-load jsPDF if not present
    var s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s.onload = function() { downloadPathfinderPDF(data); };
    document.head.appendChild(s);
    return;
  }
  var jsPDF = jspdf.jsPDF;
  var doc = new jsPDF({ unit: "mm", format: "a4" });
  var W = doc.internal.pageSize.getWidth();
  var margin = 15;
  var y = 20;
  var lineH = 6;

  function safe(v) { try { return (v || "").toString().replace(/[\u{D800}-\u{DFFF}]/gu, ""); } catch(e) { return String(v || ""); } }
  function addLine(text, size, color, indent) {
    doc.setFontSize(size || 11);
    if (color) doc.setTextColor.apply(doc, color); else doc.setTextColor(220, 220, 230);
    var x = margin + (indent || 0);
    var maxW = W - x - margin;
    var lines = doc.splitTextToSize(safe(text), maxW);
    lines.forEach(function(l) {
      if (y > 275) { doc.addPage(); y = 20; }
      doc.text(l, x, y);
      y += lineH;
    });
  }
  function addGap(n) { y += (n || 4); }

  // Header
  doc.setFillColor(2, 6, 23);
  doc.rect(0, 0, W, 30, "F");
  doc.setFontSize(16); doc.setTextColor(0, 229, 160);
  doc.text("Trust Engine OS — Pathfinder Report", margin, 18);
  doc.setFontSize(9); doc.setTextColor(100, 116, 139);
  doc.text("trust-engine-ashen.vercel.app", W - margin, 18, { align: "right" });
  y = 38;

  // User analysis
  if (data.user_analysis) {
    var ua = data.user_analysis;
    addLine("PROFILE ANALYSIS", 8, [100, 116, 139]); addGap(2);
    addLine(ua.profile_summary || "", 11, [200, 210, 230]); addGap(3);
    addLine("Experience: " + (ua.experience_level || "") + "  |  Market: " + (ua.market_position || "") + "  |  Readiness: " + (ua.readiness_score || 0) + "/100", 9, [100, 116, 139]); addGap(4);
    if (ua.unfair_advantage) {
      addLine("UNFAIR ADVANTAGE", 8, [0, 200, 140]); addGap(1);
      addLine(ua.unfair_advantage, 10, [180, 220, 200]); addGap(3);
    }
    if (ua.truth_analysis) {
      addLine("TRUTH ENGINE", 8, [239, 68, 68]); addGap(1);
      addLine(ua.truth_analysis, 10, [200, 180, 180]); addGap(4);
    }
  }

  // 3 Paths
  var pathColors = { primary: [0, 229, 160], growth: [59, 158, 255], alternative: [245, 158, 11] };
  (data.paths || []).forEach(function(p, i) {
    if (y > 240) { doc.addPage(); y = 20; }
    var col = pathColors[p.path_type] || [180, 180, 180];
    doc.setFillColor(col[0], col[1], col[2]); doc.rect(margin, y, 3, 14, "F");
    addLine(["PATH " + (i + 1), (p.path_type || "").toUpperCase(), "—", p.title].join(" "), 13, col, 6);
    addLine("Readiness: " + (p.readiness_score || 0) + "/100  |  Market: " + (p.market_demand || "") + "  |  Risk: " + (p.risk || ""), 9, [100, 116, 139], 6);
    addGap(2);
    addLine(p.fit_reason || "", 10, [180, 190, 210], 6); addGap(3);
    addLine("SALARY:  Entry: " + safe(p.salary && p.salary.entry || "") + "   Growth: " + safe(p.salary && p.salary.growth || ""), 10, [34, 197, 94], 6);
    addLine("TIMELINE: " + safe(p.timeline && p.timeline.entry_level || "") + " to " + safe(p.timeline && p.timeline.growth_level || ""), 10, [100, 116, 139], 6);
    addGap(2);
    if (p.outcome) { addLine("Outcome: " + p.outcome, 10, [200, 210, 230], 6); addGap(2); }
    if (p.final_advice) { addLine("Advice: " + p.final_advice, 10, [160, 170, 190], 6); }
    addGap(6);
  });

  // Decision matrix
  if (data.decision_matrix) {
    if (y > 230) { doc.addPage(); y = 20; }
    addLine("DECISION MATRIX", 8, [100, 116, 139]); addGap(2);
    var dm = data.decision_matrix;
    if (dm.primary_vs_growth) { addLine("Primary vs Growth: " + dm.primary_vs_growth, 10, [180, 190, 210], 4); addGap(3); }
    if (dm.primary_vs_alternative) { addLine("Primary vs Alt: " + dm.primary_vs_alternative, 10, [180, 190, 210], 4); addGap(3); }
    if (dm.growth_vs_alternative) { addLine("Growth vs Alt: " + dm.growth_vs_alternative, 10, [180, 190, 210], 4); addGap(3); }
  }

  // Immediate next steps
  if (data.immediate_next_steps && data.immediate_next_steps.length) {
    if (y > 230) { doc.addPage(); y = 20; }
    addLine("DO THIS TODAY", 8, [0, 229, 160]); addGap(2);
    data.immediate_next_steps.forEach(function(step, i) {
      addLine((i + 1) + ". " + step, 10, [180, 200, 220], 4); addGap(2);
    });
  }
  if (data.execution_warning) {
    addGap(2); addLine("WARNING: " + data.execution_warning, 10, [239, 68, 68], 4);
  }

  // Footer
  var pages = doc.internal.getNumberOfPages();
  for (var pg = 1; pg <= pages; pg++) {
    doc.setPage(pg);
    doc.setFontSize(8); doc.setTextColor(60, 80, 100);
    doc.text("Generated by Trust Engine OS · trust-engine-ashen.vercel.app · Page " + pg + "/" + pages, W / 2, 290, { align: "center" });
  }

  try { doc.save("pathfinder-report.pdf"); } catch(e) { console.error("[PDF] Save failed:", e.message); }
}

// -- PHASE 10: LIVE INTELLIGENCE FEED ------------------------------------
function getLiveStats() {
  try {
    var patterns = memGet(PATTERN_KEY) || {};
    var history = patterns.verdict_history || [];
    var today = history.filter(function(h) { return Date.now() - h.ts < 86400000; });
    var scams = today.filter(function(h) { return h.verdict === "Likely Scam"; }).length;
    var topPhrase = Object.keys(patterns.scam_phrases || {}).sort(function(a,b) { return (patterns.scam_phrases[b]||0) - (patterns.scam_phrases[a]||0); })[0] || "None";
    return { total: patterns.scan_count || 0, today: today.length, scams_today: scams, top_pattern: topPhrase };
  } catch(e) { return { total: 0, today: 0, scams_today: 0, top_pattern: "None" }; }
}

// -- PHASE 11: FAKE RESUME DETECTOR --------------------------------------
function detectFakeResume(text) {
  var t = (text || "").toLowerCase();
  var flags = [];
  var suspicion = 0;

  var aiPhrases = ["results-driven professional", "dynamic team player", "passionate about", "leverage synergies", "spearheaded initiatives", "detail-oriented self-starter"];
  var aiCount = aiPhrases.filter(function(p) { return t.includes(p); }).length;
  if (aiCount >= 3) { flags.push("AI-generated language detected (" + aiCount + " AI phrases found)"); suspicion += 30; }

  var words = t.split(/\s+/).length;
  var uniqueWords = new Set(t.split(/\s+/)).size;
  var diversity = uniqueWords / Math.max(words, 1);
  if (diversity < 0.3 && words > 200) { flags.push("Low vocabulary diversity — possible template reuse"); suspicion += 15; }

  if (!t.match(/\d{4}/)) { flags.push("No years found — vague experience claims"); suspicion += 10; }
  if (!t.match(/\b(at|@|for)\s+[A-Z][a-z]+/)) { flags.push("No specific company names found"); suspicion += 10; }
  if (!t.match(/\d+\s*(%|percent|projects?|teams?|clients?)/i)) { flags.push("No quantified achievements"); suspicion += 15; }

  return { suspicion_score: Math.min(100, suspicion), flags: flags, is_ai_generated: aiCount >= 3, is_inflated: suspicion >= 40 };
}

// -- PHASE 12: RESPONSE CACHE & DEDUP ------------------------------------
var RESP_CACHE = {};
function getCachedResponse(key) {
  var c = RESP_CACHE[key];
  if (!c) return null;
  if (Date.now() - c.ts > 1800000) { delete RESP_CACHE[key]; return null; }
  return c.data;
}
function setCachedResponse(key, data) { RESP_CACHE[key] = { data: data, ts: Date.now() }; }

// =======================================================================
// STAGE 3 — CONVERGENT INTELLIGENCE LAYER
// S3-A: User Model  — unified signal store across all 6 modules
// S3-B: Real Correlation — data-driven insights, not hardcoded strings
// S3-C: Temporal Trajectory — tracks improvement across sessions
// Integration: memGet/memSet, CROSS_INTEL, normalizeDecision, runAIStable
// UI: zero changes
// =======================================================================

var S3_KEY      = "te_s3_usermodel";
var S3_TRAJ_KEY     = "te_s3_trajectory";
var S3_FEEDBACK_KEY = "te_s3_feedback";

// S3-A: USER MODEL -------------------------------------------------------
function s3Get() {
  return memGet(S3_KEY) || {
    resume: null, career: null, hr: null,
    scan: null, pathfinder: null, jd: null,
    ats_score: null, risk_score: null, top_path: null,
    skill_gaps: [], strengths: [], updated: 0
  };
}

// S3 GLOBAL DECISION LISTENERS — notified on every s3Update
var S3_DECISION_LISTENERS = new Set();
function notifyDecisionListeners() {
  S3_DECISION_LISTENERS.forEach(function(fn) { try { fn(s3ComputeGlobalDecision()); } catch(e) {} });
}

function s3Update(module, result) {
  if (!result || !module) return;
  try {
    var m = s3Get();
    if (module === "scan") {
      m.scan = { risk: result.risk_score || 0, verdict: result.quick_verdict || "UNKNOWN", platform: result.platform_detected || "Unknown", ts: Date.now() };
      m.risk_score = result.risk_score || 0;
    } else if (module === "resume") {
      m.resume = { ats: result.ats_score || 0, market: result.market_position || "unknown", ts: Date.now() };
      m.ats_score = result.ats_score || 0;
      m.strengths  = (result.strengths   || []).slice(0, 5);
      m.skill_gaps = (result.skills_missing || result.skill_gaps || []).slice(0, 5);
    } else if (module === "career") {
      var ladder   = result.career_ladder || result.ladder || [];
      var eligible = ladder.filter(function(r) { return r.eligible; }).map(function(r) { return r.title || r.role || ""; });
      m.career = { roles_eligible: eligible.slice(0, 3), top_salary: result.top_salary || null, ts: Date.now() };
    } else if (module === "hr") {
      m.hr = { candidates_ranked: (result.candidates || []).length, top_candidate: result.candidates && result.candidates[0] ? result.candidates[0].name : null, ts: Date.now() };
    } else if (module === "pathfinder") {
      var primary = (result.paths || []).find(function(p) { return p.path_type === "primary"; }) || (result.paths && result.paths[0]);
      m.pathfinder = { recommended: (result.final_recommendation && result.final_recommendation.recommended_path) || "primary", top_path_title: primary ? primary.title : null, readiness: primary ? primary.readiness_score : null, ts: Date.now() };
      m.top_path = primary ? primary.title : null;
    } else if (module === "jd") {
      m.jd = { match_score: result.match_score || null, ats_keywords: (result.ats_keywords || result.key_skills || []).slice(0, 8), ts: Date.now() };
    }
    m.updated = Date.now();
    memSet(S3_KEY, m);
    s3TrajectoryRecord(module, m);
    notifyDecisionListeners();
  } catch(e) {}
}

// =======================================================================
// STAGE 5 — ADAPTIVE + PREDICTIVE INTELLIGENCE LAYER
// =======================================================================

function s5ComputeAdaptiveWeights() {
  try {
    var fb = memGet(S3_FEEDBACK_KEY);
    var BASE = { risk_weight: 0.50, ats_weight: 0.25, readiness_weight: 0.25, jd_weight: 0.10, adapted: false };
    if (!fb || fb.total < 5) return BASE;
    var modAcc = fb.moduleAccuracy || {};
    function getModuleAccuracy(mod) {
      var ma = modAcc[mod];
      if (!ma || ma.total < 3) return 0.5;
      return ma.accurate / ma.total;
    }
    var scanAcc       = getModuleAccuracy("scan");
    var resumeAcc     = getModuleAccuracy("resume");
    var pathfinderAcc = getModuleAccuracy("pathfinder");
    var jdAcc         = getModuleAccuracy("jd");
    function adapt(base, acc) {
      var delta = (acc - 0.5) * 0.30;
      return Math.max(0.05, Math.min(0.80, base + delta));
    }
    return {
      risk_weight:         adapt(0.50, scanAcc),
      ats_weight:          adapt(0.25, resumeAcc),
      readiness_weight:    adapt(0.25, pathfinderAcc),
      jd_weight:           adapt(0.10, jdAcc),
      adapted:             true,
      scan_accuracy:       Math.round(scanAcc       * 100),
      resume_accuracy:     Math.round(resumeAcc     * 100),
      pathfinder_accuracy: Math.round(pathfinderAcc * 100)
    };
  } catch(e) {
    return { risk_weight: 0.50, ats_weight: 0.25, readiness_weight: 0.25, jd_weight: 0.10, adapted: false };
  }
}

function s5PredictOutcome(model) {
  try {
    var m         = model || s3Get();
    var risk      = (m.risk_score != null) ? Number(m.risk_score) : null;
    var ats       = (m.ats_score  != null) ? Number(m.ats_score)  : null;
    var readiness = (m.pathfinder && m.pathfinder.readiness != null) ? Number(m.pathfinder.readiness) : null;
    var jdMatch   = (m.jd && m.jd.match_score != null) ? Number(m.jd.match_score) : null;
    var signalCount = [risk, ats, readiness].filter(function(v) { return v !== null; }).length;
    if (signalCount === 0) {
      return { scam_probability: 50, success_probability: 50, confidence_band: "low", signals_used: 0 };
    }
    var scamBase = risk !== null ? risk : 30;
    if (m.scan && m.scan.platform && (m.scan.platform === "WhatsApp" || m.scan.platform === "Telegram")) {
      scamBase = Math.min(100, scamBase + 8);
    }
    if (ats !== null && ats < 35) scamBase = Math.min(100, scamBase + 5);
    var scamProbability = Math.round(Math.min(100, Math.max(0, scamBase)));
    var successComponents = [];
    if (ats       !== null) successComponents.push(ats);
    if (readiness !== null) successComponents.push(readiness);
    if (jdMatch   !== null) successComponents.push(jdMatch);
    var successBase = successComponents.length > 0
      ? successComponents.reduce(function(a, b) { return a + b; }, 0) / successComponents.length
      : 40;
    if (risk !== null && risk >= 70) successBase = Math.max(5,  successBase - 30);
    else if (risk !== null && risk >= 45) successBase = Math.max(10, successBase - 15);
    try {
      var traj = s3GetTrajectory();
      if (traj) {
        if (traj.ats_trend       === "improving") successBase = Math.min(100, successBase + 5);
        if (traj.readiness_trend === "improving") successBase = Math.min(100, successBase + 5);
        if (traj.ats_trend       === "declining") successBase = Math.max(0,   successBase - 5);
        if (traj.improvement_score != null) {
          var trajBoost = (traj.improvement_score - 50) / 50 * 4;
          successBase = Math.min(100, Math.max(0, successBase + trajBoost));
        }
      }
    } catch(te) {}
    var successProbability = Math.round(Math.min(100, Math.max(0, successBase)));
    var confidenceBand;
    if (signalCount >= 3) {
      var spread = Math.abs(scamProbability - (100 - successProbability));
      confidenceBand = spread > 40 ? "high" : spread > 15 ? "medium" : "low";
    } else if (signalCount === 2) {
      confidenceBand = "medium";
    } else {
      confidenceBand = "low";
    }
    return {
      scam_probability:    scamProbability,
      success_probability: successProbability,
      confidence_band:     confidenceBand,
      signals_used:        signalCount
    };
  } catch(e) {
    return { scam_probability: 50, success_probability: 50, confidence_band: "low", signals_used: 0 };
  }
}

function s5GetLearningAdjustment() {
  try {
    var fb = memGet(S3_FEEDBACK_KEY);
    if (!fb || fb.total < 5) {
      return { confidence_modifier: 0, risk_bias_adjustment: 0, learning_active: false };
    }
    var overallAccuracy = fb.total > 0 ? (fb.accurate / fb.total) : 0.5;
    var inaccuracyRate  = fb.total > 0 ? (fb.inaccurate / fb.total) : 0;
    var confidenceModifier = 0;
    if (overallAccuracy >= 0.75)     confidenceModifier =  Math.round((overallAccuracy - 0.5) * 20);
    else if (inaccuracyRate >= 0.30) confidenceModifier = -Math.round(inaccuracyRate * 25);
    confidenceModifier = Math.max(-15, Math.min(10, confidenceModifier));
    var riskBiasAdjustment = 0;
    var scanMod = fb.moduleAccuracy && fb.moduleAccuracy.scan;
    if (scanMod && scanMod.total >= 3) {
      var scanInaccRate = scanMod.inaccurate / scanMod.total;
      if (scanInaccRate >= 0.40) riskBiasAdjustment = -8;
      else if (scanInaccRate <= 0.10 && (scanMod.accurate / scanMod.total) >= 0.70) riskBiasAdjustment = 5;
    }
    var recent = (fb.recentFeedback || []).slice(-10);
    var recentAccurate = recent.filter(function(r) { return r.rating === "accurate"; }).length;
    var recentAccuracy = recent.length > 0 ? (recentAccurate / recent.length) : overallAccuracy;
    return {
      confidence_modifier:  confidenceModifier,
      risk_bias_adjustment: riskBiasAdjustment,
      overall_accuracy:     Math.round(overallAccuracy * 100),
      recent_accuracy:      Math.round(recentAccuracy  * 100),
      total_feedback:       fb.total,
      learning_active:      true
    };
  } catch(e) {
    return { confidence_modifier: 0, risk_bias_adjustment: 0, learning_active: false };
  }
}

function s3RecordFeedback(module, verdict, userRating) {
  try {
    var fb = memGet(S3_FEEDBACK_KEY) || {
      total: 0, accurate: 0, inaccurate: 0, partial: 0,
      moduleAccuracy: {}, recentFeedback: [],
      rollingWindow: [], confidenceAdjustment: 0
    };
    fb.total      = (fb.total      || 0) + 1;
    fb.accurate   = (fb.accurate   || 0) + (userRating === "accurate"   ? 1 : 0);
    fb.inaccurate = (fb.inaccurate || 0) + (userRating === "inaccurate" ? 1 : 0);
    fb.partial    = (fb.partial    || 0) + (userRating === "partial"    ? 1 : 0);
    if (!fb.moduleAccuracy[module]) {
      fb.moduleAccuracy[module] = { accurate: 0, inaccurate: 0, partial: 0, total: 0, accuracy_score: 50 };
    }
    var ma = fb.moduleAccuracy[module];
    ma.total      += 1;
    ma.accurate   += (userRating === "accurate"   ? 1 : 0);
    ma.inaccurate += (userRating === "inaccurate" ? 1 : 0);
    ma.partial    += (userRating === "partial"    ? 1 : 0);
    ma.accuracy_score = Math.round((ma.accurate / ma.total) * 100);
    fb.recentFeedback = (fb.recentFeedback || []).slice(-19);
    fb.recentFeedback.push({ module: module, verdict: verdict, rating: userRating, ts: Date.now() });
    fb.rollingWindow = (fb.rollingWindow || []).slice(-9);
    fb.rollingWindow.push({ rating: userRating, ts: Date.now() });
    var recentAcc = fb.rollingWindow.filter(function(r) { return r.rating === "accurate"; }).length;
    fb.confidenceAdjustment = Math.round((recentAcc / fb.rollingWindow.length - 0.5) * 20);
    memSet(S3_FEEDBACK_KEY, fb);
    notifyDecisionListeners();
  } catch(e) {}
}

function s3GetFeedbackContext() {
  try {
    var fb = memGet(S3_FEEDBACK_KEY);
    if (!fb || fb.total < 3) return "";
    var accuracy = fb.total > 0 ? Math.round((fb.accurate / fb.total) * 100) : 0;
    var parts = ["FEEDBACK_CONTEXT: User has rated " + fb.total + " verdicts (" + accuracy + "% accurate)."];
    if (fb.inaccurate > 0 && fb.total > 0) {
      var inaccRate = Math.round((fb.inaccurate / fb.total) * 100);
      if (inaccRate > 30) parts.push("HIGH_ERROR_RATE: " + inaccRate + "% rated inaccurate — increase hedging, reduce overconfident verdicts.");
    }
    Object.keys(fb.moduleAccuracy || {}).forEach(function(mod) {
      var ma = fb.moduleAccuracy[mod];
      if (ma && ma.total >= 3) {
        var modAcc = Math.round((ma.accurate / ma.total) * 100);
        if (modAcc < 50)  parts.push("LOW_ACCURACY_MODULE:"  + mod.toUpperCase() + "(" + modAcc + "%) — be more conservative in this module.");
        else if (modAcc >= 80) parts.push("HIGH_ACCURACY_MODULE:" + mod.toUpperCase() + "(" + modAcc + "%) — high user trust in this module.");
      }
    });
    var rollingAccurate = (fb.rollingWindow || []).filter(function(r) { return r.rating === "accurate"; }).length;
    if ((fb.rollingWindow || []).length >= 5) {
      var rollingAcc = Math.round((rollingAccurate / fb.rollingWindow.length) * 100);
      parts.push("RECENT_ACCURACY:" + rollingAcc + "% (last " + fb.rollingWindow.length + " ratings).");
    }
    return parts.join(" ") + "\n\n";
  } catch(e) { return ""; }
}

function s3GetSystemAccuracy() {
  try {
    var fb = memGet(S3_FEEDBACK_KEY);
    if (!fb || fb.total < 3) return null;
    var rollingAcc = null;
    if ((fb.rollingWindow || []).length >= 3) {
      var ra = fb.rollingWindow.filter(function(r) { return r.rating === "accurate"; }).length;
      rollingAcc = Math.round((ra / fb.rollingWindow.length) * 100);
    }
    return {
      total:                fb.total,
      accuracy:             fb.total > 0 ? Math.round((fb.accurate / fb.total) * 100) : 0,
      rolling_accuracy:     rollingAcc,
      moduleAccuracy:       fb.moduleAccuracy || {},
      confidenceAdjustment: fb.confidenceAdjustment || 0
    };
  } catch(e) { return null; }
}

// S3-B: REAL CORRELATION -------------------------------------------------
function s3GetCorrelation() {
  try {
    var m = s3Get();
    var insights = [];

    if (m.resume && m.jd && m.jd.ats_keywords && m.jd.ats_keywords.length > 0 && m.ats_score !== null) {
      var kw = m.jd.ats_keywords.slice(0, 3).join(", ");
      if (m.ats_score < 60) {
        insights.push("\u26a0 Resume ATS is " + m.ats_score + "/100. Last JD needs: " + kw + ". Add these to close the gap.");
      } else {
        insights.push("\u2705 Resume ATS " + m.ats_score + "/100 matches JD keywords: " + kw + ".");
      }
    }

    if (m.scan && m.scan.risk >= 60 && (m.resume || m.pathfinder)) {
      insights.push("\uD83D\uDEA8 High-risk scan (" + m.scan.risk + "/100 on " + m.scan.platform + "). Cross-check Pathfinder paths before applying.");
    }

    if (m.pathfinder && m.career && m.pathfinder.readiness !== null) {
      var r = m.pathfinder.readiness;
      var tp = m.pathfinder.top_path_title || "your primary path";
      if (r < 50) {
        insights.push("\uD83D\uDCCA Pathfinder readiness: " + r + "/100 for \u201c" + tp + "\u201d. Start with Career Engine eligible roles while building toward this.");
      } else if (r >= 75) {
        insights.push("\uD83D\uDE80 Pathfinder readiness " + r + "/100 for \u201c" + tp + "\u201d \u2014 you\u2019re ready. Apply now.");
      }
    }

    if (m.skill_gaps && m.skill_gaps.length > 0 && m.pathfinder && m.pathfinder.top_path_title) {
      insights.push("\uD83C\uDFAF Resume gaps: " + m.skill_gaps.slice(0, 3).join(", ") + " \u2014 blocking path to \u201c" + m.pathfinder.top_path_title + "\u201d.");
    }

    if (m.hr && m.hr.candidates_ranked >= 2) {
      insights.push("\uD83D\uDCCB You ranked " + m.hr.candidates_ranked + " candidates. Top: " + (m.hr.top_candidate || "#1") + ". Use that standard for your own resume.");
    }

    return insights.length > 0 ? insights.slice(0, 3) : CROSS_INTEL.getCorrelation();
  } catch(e) { return CROSS_INTEL.getCorrelation(); }
}

// S3-C: TEMPORAL TRAJECTORY ----------------------------------------------
function s3TrajectoryRecord(module, model) {
  try {
    var traj = memGet(S3_TRAJ_KEY) || [];
    traj.push({ ts: Date.now(), module: module, ats: model.ats_score, risk: model.risk_score, readiness: model.pathfinder ? model.pathfinder.readiness : null, top_path: model.top_path });
    if (traj.length > 20) traj = traj.slice(-20);
    memSet(S3_TRAJ_KEY, traj);
  } catch(e) {}
}

function s3GetTrajectory() {
  try {
    var traj = memGet(S3_TRAJ_KEY) || [];
    if (traj.length < 2) return null;
    var atsP  = traj.filter(function(t) { return t.ats       != null; });
    var riskP = traj.filter(function(t) { return t.risk      != null; });
    var readP = traj.filter(function(t) { return t.readiness != null; });
    function trend(arr, key, higherGood) {
      if (arr.length < 2) return null;
      var d = arr[arr.length-1][key] - arr[0][key];
      return d === 0 ? "stable" : (higherGood ? d > 0 : d < 0) ? "improving" : "declining";
    }
    function computeImprovementScore() {
      var contributions = [];
      if (atsP.length  >= 2) contributions.push(Math.min(50, Math.max(-50, atsP[atsP.length-1].ats       - atsP[0].ats))       + 50);
      if (riskP.length >= 2) contributions.push(Math.min(50, Math.max(-50, riskP[0].risk - riskP[riskP.length-1].risk))        + 50);
      if (readP.length >= 2) contributions.push(Math.min(50, Math.max(-50, readP[readP.length-1].readiness - readP[0].readiness)) + 50);
      if (contributions.length === 0) return 50;
      return Math.round(contributions.reduce(function(a, b) { return a + b; }, 0) / contributions.length);
    }
    function computeDirectionStrength() {
      var improving = 0, declining = 0;
      var atsTr  = trend(atsP,  "ats",       true);
      var riskTr = trend(riskP, "risk",      false);
      var readTr = trend(readP, "readiness", true);
      if (atsTr  === "improving") improving++; if (atsTr  === "declining") declining++;
      if (riskTr === "improving") improving++; if (riskTr === "declining") declining++;
      if (readTr === "improving") improving++; if (readTr === "declining") declining++;
      var total = improving + declining;
      if (total === 0) return "stable";
      var dominance = Math.max(improving, declining) / total;
      if (dominance >= 1.0 && total >= 2) return "strong";
      if (dominance >= 0.67) return "moderate";
      return "weak";
    }
    var improvementScore  = computeImprovementScore();
    var directionStrength = computeDirectionStrength();
    return {
      sessions:           traj.length,
      ats_trend:          trend(atsP,  "ats",       true),
      risk_trend:         trend(riskP, "risk",      false),
      readiness_trend:    trend(readP, "readiness", true),
      latest_path:        traj[traj.length-1].top_path || null,
      improvement_score:  improvementScore,
      direction_strength: directionStrength,
      prompt_context: [
        atsP.length  >= 2 ? "ATS_TREND="       + trend(atsP,"ats",true)   + "(" + atsP[0].ats + "\u2192" + atsP[atsP.length-1].ats + ")" : "",
        riskP.length >= 2 ? "RISK_TREND="      + trend(riskP,"risk",false) + "(last=" + riskP[riskP.length-1].risk + ")" : "",
        readP.length >= 2 ? "READINESS_TREND=" + trend(readP,"readiness",true) : "",
        "IMPROVEMENT_SCORE=" + improvementScore,
        "DIRECTION_STRENGTH=" + directionStrength
      ].filter(Boolean).join(" ")
    };
  } catch(e) { return null; }
}

function s3GetPromptContext() {
  try {
    var m    = s3Get();
    var traj = s3GetTrajectory();
    var pred = s5PredictOutcome(m);
    var parts = [];
    if (m.updated > 0) {
      if (m.ats_score  != null) parts.push("USER_ATS:"    + m.ats_score);
      if (m.risk_score != null) parts.push("LAST_RISK:"   + m.risk_score);
      if (m.top_path)           parts.push("TARGET_PATH:" + m.top_path);
      if (m.skill_gaps  && m.skill_gaps.length)  parts.push("GAPS:"      + m.skill_gaps.slice(0,3).join(","));
      if (m.strengths   && m.strengths.length)   parts.push("STRENGTHS:" + m.strengths.slice(0,3).join(","));
      if (m.pathfinder  && m.pathfinder.readiness != null) parts.push("READINESS:" + m.pathfinder.readiness);
      if (m.jd && m.jd.match_score != null) parts.push("JD_MATCH:" + m.jd.match_score + "%");
      if (m.career && m.career.roles_eligible && m.career.roles_eligible.length > 0)
        parts.push("ELIGIBLE_ROLES:" + m.career.roles_eligible.slice(0,2).join(","));
      if (m.ats_score != null && m.ats_score < 50)   parts.push("ATS_STATUS:CRITICAL_LOW");
      else if (m.ats_score != null && m.ats_score >= 75) parts.push("ATS_STATUS:STRONG");
      if (m.risk_score != null && m.risk_score >= 70) parts.push("RISK_STATUS:HIGH_DANGER");
      else if (m.risk_score != null && m.risk_score <= 25) parts.push("RISK_STATUS:LOW_RISK");
      if (m.pathfinder && m.pathfinder.readiness != null && m.pathfinder.readiness >= 70) parts.push("READINESS_STATUS:READY_TO_APPLY");
      else if (m.pathfinder && m.pathfinder.readiness != null && m.pathfinder.readiness < 40) parts.push("READINESS_STATUS:NOT_READY");
    }
    if (traj && traj.prompt_context) parts.push(traj.prompt_context);
    if (pred && pred.signals_used > 0) {
      parts.push("SCAM_PROB:"       + pred.scam_probability + "%");
      parts.push("SUCCESS_PROB:"    + pred.success_probability + "%");
      parts.push("PRED_CONFIDENCE:" + pred.confidence_band.toUpperCase());
    }
    var learning = s5GetLearningAdjustment();
    if (learning.learning_active) {
      parts.push("SYSTEM_ACCURACY:" + learning.recent_accuracy + "%");
      if (learning.confidence_modifier !== 0)
        parts.push("CONF_MODIFIER:" + (learning.confidence_modifier > 0 ? "+" : "") + learning.confidence_modifier);
      if (learning.risk_bias_adjustment !== 0)
        parts.push("RISK_BIAS_ADJUST:" + (learning.risk_bias_adjustment > 0 ? "+" : "") + learning.risk_bias_adjustment);
    }
    return parts.length > 0
      ? "STAGE5_CONTEXT [Use this to personalize and calibrate your analysis. Reference specific values in reasoning]: " + parts.join(" | ") + "\n\n"
      : "";
  } catch(e) { return ""; }
}


// =======================================================================
// STAGE 5 — GLOBAL DECISION ENGINE (adaptive weights + prediction + learning)
// =======================================================================
function s3ComputeGlobalDecision() {
  try {
    var m    = s3Get();
    var traj = s3GetTrajectory();
    var risk      = (m.risk_score != null) ? Number(m.risk_score) : null;
    var ats       = (m.ats_score  != null) ? Number(m.ats_score)  : null;
    var readiness = (m.pathfinder && m.pathfinder.readiness != null) ? Number(m.pathfinder.readiness) : null;
    var jdMatch   = (m.jd && m.jd.match_score != null) ? Number(m.jd.match_score) : null;
    var hasData = (risk !== null || ats !== null || readiness !== null);
    if (!hasData) {
      return {
        final_decision:          "INSUFFICIENT_DATA",
        confidence_score:        0,
        reasoning:               "No module data yet. Run at least one analysis to generate your intelligence decision.",
        dominant_factors:        [],
        next_recommended_action: "Start with Scan to check a job post, or Resume to analyze your profile.",
        scam_probability:        null,
        success_probability:     null,
        decision_strength:       "insufficient"
      };
    }
    var weights  = s5ComputeAdaptiveWeights();
    var learning = s5GetLearningAdjustment();
    var pred     = s5PredictOutcome(m);
    var riskW = weights.risk_weight;
    var atsW  = weights.ats_weight;
    var readW = weights.readiness_weight;
    var weightSum = 0, concernSum = 0, dominants = [], signals = [];
    if (risk !== null) {
      var adjustedRisk = Math.min(100, Math.max(0, risk + (learning.risk_bias_adjustment || 0)));
      var riskContrib  = adjustedRisk * riskW;
      concernSum += riskContrib; weightSum += riskW * 100;
      signals.push({ key: "risk_score", value: risk, adjusted: adjustedRisk, contribution: riskContrib });
      if      (adjustedRisk >= 80) dominants.push("CRITICAL fraud risk (" + risk + "/100) — confirmed scam signals present");
      else if (adjustedRisk >= 65) dominants.push("HIGH risk (" + risk + "/100) — multiple suspicious patterns detected");
      else if (adjustedRisk >= 40) dominants.push("MODERATE risk (" + risk + "/100) — verify before proceeding");
      else                         dominants.push("LOW risk (" + risk + "/100) — job appears legitimate");
    }
    if (ats !== null) {
      var atsContrib = (100 - ats) * atsW;
      concernSum += atsContrib; weightSum += atsW * 100;
      signals.push({ key: "ats_score", value: ats, contribution: atsContrib });
      if      (ats < 40)  dominants.push("CRITICAL ATS score (" + ats + "/100) — resume auto-rejected by most systems");
      else if (ats < 60)  dominants.push("LOW ATS score (" + ats + "/100) — resume needs optimization before applying");
      else if (ats >= 80) dominants.push("STRONG ATS score (" + ats + "/100) — resume passes automated screening");
    }
    if (readiness !== null) {
      var readContrib = (100 - readiness) * readW;
      concernSum += readContrib; weightSum += readW * 100;
      signals.push({ key: "readiness", value: readiness, contribution: readContrib });
      if      (readiness >= 75) dominants.push("HIGH readiness (" + readiness + "/100) for target path — ready to apply competitively");
      else if (readiness >= 50) dominants.push("MODERATE readiness (" + readiness + "/100) — close 1-2 gaps before bulk applying");
      else                      dominants.push("LOW readiness (" + readiness + "/100) — significant skill gaps before target role");
    }
    var normConcern = weightSum > 0 ? (concernSum / weightSum) * 100 : 50;
    if (jdMatch !== null) {
      if      (jdMatch >= 80) { normConcern = Math.max(0,   normConcern - 10); dominants.push("STRONG JD match (" + jdMatch + "%) — role fit confirmed"); }
      else if (jdMatch >= 60) { normConcern = Math.max(0,   normConcern - 4); }
      else if (jdMatch <  40) { normConcern = Math.min(100, normConcern + 10); dominants.push("WEAK JD match (" + jdMatch + "%) — significant role misalignment"); }
    }
    if (traj) {
      if (traj.ats_trend  === "improving" && traj.sessions >= 3) normConcern = Math.max(0, normConcern - 5);
      if (traj.risk_trend === "declining" && traj.sessions >= 3) normConcern = Math.max(0, normConcern - 5);
      if (traj.improvement_score != null) {
        var trajMod = (traj.improvement_score - 50) / 50 * 5;
        normConcern = Math.min(100, Math.max(0, normConcern - trajMod));
      }
    }
    var finalDecision;
    if      (normConcern >= 62) finalDecision = "AVOID";
    else if (normConcern >= 35) finalDecision = "VERIFY";
    else                        finalDecision = "APPLY";
    if (risk !== null && risk >= 78) finalDecision = "AVOID";
    if (risk === null && readiness !== null && readiness >= 72 && (ats === null || ats >= 60)) finalDecision = "APPLY";
    var modulesUsed = signals.length;
    var baseConf    = Math.round(35 + (modulesUsed / 4) * 45);
    var clarity     = Math.abs(normConcern - 50) / 50;
    var confidence  = Math.min(96, Math.round(baseConf + clarity * 20 + (learning.confidence_modifier || 0)));
    if (modulesUsed === 1) confidence = Math.min(confidence, 65);
    confidence = Math.max(0, Math.min(99, confidence));
    var decisionStrength;
    if      (confidence >= 80 && modulesUsed >= 3) decisionStrength = "strong";
    else if (confidence >= 60 && modulesUsed >= 2) decisionStrength = "moderate";
    else if (confidence >= 40)                      decisionStrength = "weak";
    else                                            decisionStrength = "insufficient";
    var decisionLabel = finalDecision === "APPLY" ? "safe to apply"
      : finalDecision === "VERIFY" ? "requires verification before applying"
      : "high risk — do not engage";
    var reasonParts = ["Overall assessment: " + decisionLabel + " (concern index " + Math.round(normConcern) + "/100 across " + modulesUsed + " signal" + (modulesUsed !== 1 ? "s" : "") + ")."];
    if (risk      !== null) reasonParts.push("Job risk: " + risk + "/100.");
    if (ats       !== null) reasonParts.push("Resume ATS: " + ats + "/100.");
    if (readiness !== null) reasonParts.push("Path readiness: " + readiness + "/100.");
    if (jdMatch   !== null) reasonParts.push("JD match: " + jdMatch + "%.");
    if (traj && traj.sessions >= 2) {
      var trends = [];
      if (traj.ats_trend)       trends.push("ATS " + traj.ats_trend);
      if (traj.risk_trend)      trends.push("risk " + traj.risk_trend);
      if (traj.readiness_trend) trends.push("readiness " + traj.readiness_trend);
      if (trends.length > 0) reasonParts.push("Trajectory (" + traj.sessions + " sessions): " + trends.join(", ") + ".");
      if (traj.improvement_score != null) reasonParts.push("Improvement score: " + traj.improvement_score + "/100 (" + (traj.direction_strength || "stable") + " direction).");
    }
    if (learning.learning_active) {
      reasonParts.push("System accuracy: " + (learning.recent_accuracy || learning.overall_accuracy) + "% across " + learning.total_feedback + " user ratings.");
    }
    if (pred && pred.signals_used > 0) {
      reasonParts.push("Predicted scam probability: " + pred.scam_probability + "%. Job success probability: " + pred.success_probability + "%.");
    }
    var nextAction;
    if (finalDecision === "AVOID") {
      nextAction = "Do not apply to this job. Report to cybercrime.gov.in if a fee was requested. Use Pathfinder to find legitimate opportunities matching your profile.";
    } else if (finalDecision === "VERIFY") {
      if (ats !== null && ats < 60) nextAction = "Before applying: (1) Optimize resume to 70+ ATS, (2) Verify company on LinkedIn + MCA portal, (3) Confirm role exists on official company site.";
      else nextAction = "Verify company legitimacy on LinkedIn and MCA India portal before submitting application. Check email domain matches official company website.";
    } else {
      if (readiness !== null && readiness >= 70) nextAction = "Apply now — you're ready. Customize resume to JD keywords, prepare 3 behavioral answers, and apply directly via company site.";
      else nextAction = "Apply while continuing to close identified skill gaps. Don't wait for perfect readiness — start with your strongest matching role.";
    }
    if (dominants.length === 0) dominants.push("Decision computed from " + modulesUsed + " module signal" + (modulesUsed !== 1 ? "s" : "") + ".");
    return {
      final_decision:          finalDecision,
      confidence_score:        confidence,
      reasoning:               reasonParts.join(" "),
      dominant_factors:        dominants.slice(0, 4),
      next_recommended_action: nextAction,
      concern_index:           Math.round(normConcern),
      signals_used:            modulesUsed,
      scam_probability:        pred ? pred.scam_probability    : null,
      success_probability:     pred ? pred.success_probability : null,
      prediction_confidence:   pred ? pred.confidence_band     : null,
      decision_strength:       decisionStrength,
      adaptive_weights_used:   weights.adapted || false,
      learning_active:         learning.learning_active || false,
      improvement_score:       traj ? traj.improvement_score  : null,
      direction_strength:      traj ? traj.direction_strength : null
    };
  } catch(e) {
    return {
      final_decision:          "VERIFY",
      confidence_score:        0,
      reasoning:               "Decision engine encountered an error: " + (e.message || "unknown"),
      dominant_factors:        ["Engine fault — defaulting to VERIFY for safety"],
      next_recommended_action: "Retry your analysis to generate an accurate decision.",
      scam_probability:        null,
      success_probability:     null,
      decision_strength:       "insufficient"
    };
  }
}

// -- INTELLIGENCE STATUS HOOK ---------------------------------------------
function useIntelligence() {
  var s1 = useState(getLiveStats()); var liveStats = s1[0]; var setLiveStats = s1[1];
  var s2 = useState(getUserProfile()); var userProfile = s2[0]; var setUserProfile = s2[1];
  var s3 = useState(s3GetCorrelation()); var crossInsights = s3[0]; var setCrossInsights = s3[1];
  var s4 = useState(s3ComputeGlobalDecision()); var globalDecision = s4[0]; var setGlobalDecision = s4[1];

  // Subscribe to decision updates fired by s3Update
  useEffect(function() {
    S3_DECISION_LISTENERS.add(setGlobalDecision);
    return function() { S3_DECISION_LISTENERS.delete(setGlobalDecision); };
  }, []);

  var refreshStats = useCallback(function() {
    setLiveStats(getLiveStats());
    setCrossInsights(s3GetCorrelation());
    setGlobalDecision(s3ComputeGlobalDecision());
  }, []);

  var updateProfile = useCallback(function(updates) {
    var newProfile = { ...userProfile, ...updates };
    saveUserProfile(newProfile);
    setUserProfile(newProfile);
  }, [userProfile]);

  return { liveStats: liveStats, userProfile: userProfile, crossInsights: crossInsights, globalDecision: globalDecision, refreshStats: refreshStats, updateProfile: updateProfile };
}

// DESIGN TOKENS
// ===================================================
var DS = {
  bg0:"#010812", bg1:"#050E1E", bg2:"#081628", bg3:"#0D1F38", bg4:"#121E2E",
  border0:"rgba(255,255,255,0.04)", border1:"rgba(255,255,255,0.08)", border2:"rgba(255,255,255,0.14)",
  t1:"#F0F6FF", t2:"#8BA3C0", t3:"#4A5F7A", t4:"#243040",
  red:"#F43F5E", amber:"#F59E0B", green:"#10B981", blue:"#3B82F6",
  purple:"#8B5CF6", cyan:"#06B6D4", teal:"#00D4AA",
  gold:"#c9a84c", goldDim:"rgba(201,168,76,0.12)", goldBorder:"rgba(201,168,76,0.25)",
  shadow0:"0 1px 3px rgba(0,0,0,0.4)",
  shadow1:"0 4px 16px rgba(0,0,0,0.5)",
  shadow2:"0 8px 32px rgba(0,0,0,0.6)",
  shadow3:"0 16px 64px rgba(0,0,0,0.7)",
  r0:"6px", r1:"10px", r2:"14px", r3:"18px", r4:"24px",
  sp1:"8px", sp2:"16px", sp3:"24px", sp4:"32px", sp5:"40px", sp6:"48px",
  fs1:"48px", fs2:"28px", fs3:"16px", fs4:"13px", fs5:"10px",
};
var DS_VERDICT = {
  scam:  {bg:"rgba(244,63,94,0.08)",  border:"rgba(244,63,94,0.22)",  text:"#F43F5E", glow:"rgba(244,63,94,0.15)"},
  susp:  {bg:"rgba(245,158,11,0.08)", border:"rgba(245,158,11,0.22)", text:"#F59E0B", glow:"rgba(245,158,11,0.12)"},
  safe:  {bg:"rgba(16,185,129,0.08)", border:"rgba(16,185,129,0.22)", text:"#10B981", glow:"rgba(16,185,129,0.12)"},
}

// ===================================================
// SVG ICON SYSTEM — replaces all structural emojis
// ===================================================
function Icon({ name, size, color, style: extraStyle }) {
  var sz = size || 20;
  var col = color || "currentColor";
  var base = { width: sz, height: sz, display: "inline-block", flexShrink: 0, verticalAlign: "middle" };
  var s = Object.assign({}, base, extraStyle || {});
  var icons = {
    shield: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 2L3 5v5c0 4.418 3.134 7.442 7 8 3.866-.558 7-3.582 7-8V5L10 2z"/>
      </svg>
    ),
    scan: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="10" r="3"/>
        <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.93 4.93l1.41 1.41M13.66 13.66l1.41 1.41M4.93 15.07l1.41-1.41M13.66 6.34l1.41-1.41"/>
      </svg>
    ),
    resume: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="12" height="16" rx="2"/>
        <path d="M8 7h4M7 10h6M7 13h4"/>
      </svg>
    ),
    path: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="5" cy="15" r="2"/><circle cx="15" cy="5" r="2"/><circle cx="15" cy="15" r="2"/>
        <path d="M7 15h6M15 7v6M7 13l6-6"/>
      </svg>
    ),
    career: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 17l4-4 3 3 4-5 3-3"/>
        <circle cx="17" cy="8" r="1.5"/>
      </svg>
    ),
    hr: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="7" cy="7" r="3"/><circle cx="14" cy="7" r="2.5"/>
        <path d="M1 17c0-3 2.686-5 6-5s6 2 6 5M13 12c2 .5 4 2 4 5"/>
      </svg>
    ),
    jd: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="2" width="14" height="16" rx="2"/>
        <path d="M7 6h6M7 9h6M7 12h4"/><circle cx="14" cy="14" r="2.5"/><path d="M16 16l2 2"/>
      </svg>
    ),
    check: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 10l5 5 7-8"/>
      </svg>
    ),
    alert: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 2L2 17h16L10 2z"/><path d="M10 8v4M10 14.5v.5"/>
      </svg>
    ),
    warning: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="10" r="8"/><path d="M10 6v5M10 14.5v.5"/>
      </svg>
    ),
    chevron: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 8l3 3 3-3"/>
      </svg>
    ),
    share: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="15" cy="5" r="2"/><circle cx="5" cy="10" r="2"/><circle cx="15" cy="15" r="2"/>
        <path d="M7 9l6-3M7 11l6 3"/>
      </svg>
    ),
    x: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round">
        <path d="M5 5l10 10M15 5L5 15"/>
      </svg>
    ),
    clock: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="10" r="8"/><path d="M10 6v4l3 3"/>
      </svg>
    ),
    live: (
      <svg style={s} viewBox="0 0 20 20" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="10" r="3"/>
        <path d="M5.5 5.5a6.5 6.5 0 019 0M14.5 14.5a6.5 6.5 0 01-9 0"/>
        <path d="M3 3a10 10 0 0114 0M17 17a10 10 0 01-14 0"/>
      </svg>
    ),
  };
  return icons[name] || icons.shield;
}

function getVS(result) {
  if (!result) return DS_VERDICT.susp;
  var s = result.risk_score || 0;
  var v = (result.verdict || "").toLowerCase();
  if (s >= 70 || v.includes("scam")) return DS_VERDICT.scam;
  if (s <= 30 || v.includes("legit")) return DS_VERDICT.safe;
  return DS_VERDICT.susp;
}
function riskColor(score) {
  if (score >= 70) return DS.red;
  if (score >= 40) return DS.amber;
  return DS.green;
}
function scoreColor(v) {
  if (v >= 80) return DS.green;
  if (v >= 60) return DS.amber;
  if (v >= 40) return DS.amber;
  return DS.red;
}

// ===================================================
// HOOKS
// ===================================================
function useCountUp(target, duration) {
  var sv = useState(0); var val = sv[0]; var setVal = sv[1];
  useEffect(function() {
    var end = isFinite(target) ? (target || 0) : 0;
    if (end === 0) { setVal(0); return; }
    var startTime = null;
    var raf = requestAnimationFrame(function step(ts) {
      if (!startTime) startTime = ts;
      var p = Math.min((ts - startTime) / (duration || 800), 1);
      setVal(Math.round((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) raf = requestAnimationFrame(step);
    });
    return function() { cancelAnimationFrame(raf); };
  }, [target]);
  return val;
}

// ===================================================
// BASE UI COMPONENTS
// ===================================================
function Pill({ text, color }) {
  return (
    <span style={{ fontSize:10, fontWeight:700, color:color||DS.t2, background:(color||DS.t2)+"12", border:"1px solid "+(color||DS.t2)+"28", padding:"3px 10px", borderRadius:100, fontFamily:"'JetBrains Mono',monospace", letterSpacing:0.8, display:"inline-flex", alignItems:"center", whiteSpace:"nowrap", textTransform:"uppercase" }}>
      {text}
    </span>
  );
}

function Flag({ text, type }) {
  var c = type==="red" ? {fg:DS.red,bg:"rgba(244,63,94,0.05)",bd:"rgba(244,63,94,0.14)"} : type==="green" ? {fg:DS.green,bg:"rgba(16,185,129,0.05)",bd:"rgba(16,185,129,0.14)"} : {fg:DS.amber,bg:"rgba(245,158,11,0.05)",bd:"rgba(245,158,11,0.14)"};
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"11px 14px 11px 12px", background:c.bg, border:"1px solid "+c.bd, borderLeft:"3px solid "+c.fg, borderRadius:"0 8px 8px 0", marginBottom:6 }}>
      <span style={{ fontSize:13, color:DS.t2, lineHeight:1.7, letterSpacing:0.1 }}>{text}</span>
    </div>
  );
}

function ABar({ value, color, delay }) {
  var sv = useState(0); var w = sv[0]; var setW = sv[1];
  useEffect(function() { var t = setTimeout(function(){ setW(value||0); }, (delay||0)+120); return function(){ clearTimeout(t); }; }, [value, delay]);
  return (
    <div style={{ height:5, background:"rgba(255,255,255,0.04)", borderRadius:4, overflow:"hidden", flex:1, position:"relative" }}>
      <div style={{ height:"100%", width:w+"%", background:"linear-gradient(90deg,"+color+"99,"+color+")", borderRadius:4, transition:"width 1.3s cubic-bezier(0.4,0,0.2,1)", boxShadow:"0 0 8px "+color+"44", position:"relative" }}>
        <div style={{ position:"absolute",right:0,top:0,bottom:0,width:24,background:"linear-gradient(90deg,transparent,"+color+"55)",borderRadius:4 }}/>
      </div>
    </div>
  );
}

function ScoreRing({ value, color, size }) {
  var sz = size||60; var stroke = sz>55?7:5;
  var r = (sz/2)-stroke; var circ = 2*Math.PI*r;
  var animated = useCountUp(isFinite(value)?(value||0):0, 900);
  var d = (animated/100)*circ;
  var c = color || scoreColor(animated);
  return (
    <div style={{ position:"relative", width:sz, height:sz, flexShrink:0 }}>
      <svg width={sz} height={sz} style={{ transform:"rotate(-90deg)" }}>
        <circle cx={sz/2} cy={sz/2} r={r} fill="none" stroke={DS.bg3} strokeWidth={stroke}/>
        <circle cx={sz/2} cy={sz/2} r={r} fill="none" stroke={c} strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={circ-d} strokeLinecap="round" style={{ transition:"stroke-dashoffset 0.05s linear", filter:"drop-shadow(0 0 4px "+c+")" }}/>
      </svg>
      <div style={{ position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center" }}>
        <span style={{ fontSize:sz>55?13:10, fontWeight:900, color:c, fontFamily:"'JetBrains Mono',monospace" }}>{animated}</span>
      </div>
    </div>
  );
}

function RiskGauge({ score, size }) {
  var sz = size||160; var stroke = 14; var r = (sz/2)-stroke-4;
  var cx = sz/2; var cy = sz/2+8;
  var animated = useCountUp(isFinite(score)?(score||0):0, 1000);
  var color = riskColor(animated);
  function polar(deg, rad) { var rd = ((deg-90)*Math.PI)/180; return {x:cx+rad*Math.cos(rd),y:cy+rad*Math.sin(rd)}; }
  function arc(s, e, rad) { var sp=polar(s,rad), ep=polar(e,rad), lg=(e-s)>180?1:0; return "M "+sp.x+" "+sp.y+" A "+rad+" "+rad+" 0 "+lg+" 1 "+ep.x+" "+ep.y; }
  var ts=-90, te=90, fe=ts+(animated/100)*180;
  return (
    <div style={{ position:"relative", width:sz, height:sz/2+28, flexShrink:0, margin:"0 auto" }}>
      <svg width={sz} height={sz/2+20} style={{ overflow:"visible" }}>
        <path d={arc(ts,te,r)} fill="none" stroke={DS.bg3} strokeWidth={stroke} strokeLinecap="round"/>
        <path d={arc(ts,ts+54,r)} fill="none" stroke={DS.green+"25"} strokeWidth={stroke-2} strokeLinecap="round"/>
        <path d={arc(ts+54,ts+108,r)} fill="none" stroke={DS.amber+"25"} strokeWidth={stroke-2} strokeLinecap="round"/>
        <path d={arc(ts+108,te,r)} fill="none" stroke={DS.red+"25"} strokeWidth={stroke-2} strokeLinecap="round"/>
        {animated>0.5&&<path d={arc(ts,Math.min(fe,te-0.1),r)} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" style={{ filter:"drop-shadow(0 0 10px "+color+"88)" }}/>}
      </svg>
      <div style={{ position:"absolute", bottom:0, left:0, right:0, textAlign:"center" }}>
        <div style={{ fontSize:sz>140?40:26, fontWeight:900, color:color, fontFamily:"'JetBrains Mono',monospace", lineHeight:1, letterSpacing:"-2px" }}>{animated}</div>
        <div style={{ fontSize:11, color:DS.t3, fontFamily:"'JetBrains Mono',monospace", letterSpacing:2, marginTop:2 }}>/ 100</div>
      </div>
    </div>
  );
}

// ===================================================
// VERDICT THEATER — Cinematic phase-based reveal system
// phases: idle → counting → locked → revealed
// ===================================================
function VerdictTheater({ result, onShare }) {
  var s1 = useState("idle"); var phase = s1[0]; var setPhase = s1[1];
  var s2 = useState(false); var shared = s2[0]; var setShared = s2[1];

  var score = result ? (result.risk_score || 0) : 0;
  var animatedScore = useCountUp(phase !== "idle" ? score : 0, 950);

  useEffect(function() {
    if (!result) return;
    setPhase("idle");
    var t1 = setTimeout(function() { setPhase("counting"); }, 100);
    var t2 = setTimeout(function() { setPhase("locked"); }, 1100);
    var t3 = setTimeout(function() { setPhase("revealed"); }, 1550);
    return function() { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [result]);

  if (!result) return null;
  var isScam = score >= 70;
  var isSusp = score >= 40 && score < 70;
  var color   = isScam ? DS.red   : isSusp ? DS.amber : DS.green;
  var bg      = isScam ? "rgba(244,63,94,0.08)"   : isSusp ? "rgba(245,158,11,0.07)"  : "rgba(16,185,129,0.07)";
  var border  = isScam ? "rgba(244,63,94,0.32)"   : isSusp ? "rgba(245,158,11,0.32)"  : "rgba(16,185,129,0.32)";
  var glow    = isScam ? "rgba(244,63,94,0.24)"   : isSusp ? "rgba(245,158,11,0.20)"  : "rgba(16,185,129,0.20)";
  var label   = isScam ? "DO NOT ENGAGE"          : isSusp ? "VERIFY FIRST"           : "SAFE TO PROCEED";
  var icon    = isScam ? "alert"                  : isSusp ? "warning"                : "check";
  var reason  = (result.final_reasoning || "").slice(0, 130);

  var mounted      = phase !== "idle";
  var scoreVis     = phase === "counting" || phase === "locked" || phase === "revealed";
  var labelVis     = phase === "locked" || phase === "revealed";
  var bodyVis      = phase === "revealed";
  var locked       = phase === "locked";

  return (
    <div className="te-verdict-theater" style={{
      opacity:    mounted ? 1 : 0,
      transform:  mounted ? "translateY(0) scale(1)" : "translateY(-14px) scale(0.97)",
      transition: "opacity 0.55s var(--ease-out), transform 0.55s var(--ease-out)",
      background: bg,
      border:     "1px solid " + border,
      borderRadius: 20,
      overflow:   "hidden",
      marginBottom: 24,
      boxShadow:  "0 0 80px " + glow + ", 0 12px 48px rgba(0,0,0,0.5)",
      position:   "relative",
    }}>
      {/* Accent bar */}
      <div style={{ height: 3, background: "linear-gradient(90deg," + color + "00 0%," + color + " 40%," + color + " 60%," + color + "00 100%)" }}/>

      <div style={{ padding: "32px 32px 28px", display: "flex", alignItems: "center", gap: 32, flexWrap: "wrap" }}>

        {/* Score orb + number */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <div style={{
            width: 80, height: 80, borderRadius: "50%",
            background: color + "12",
            border: "1.5px solid " + color + (locked ? "80" : "42"),
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 " + (locked ? "56px " : "32px ") + color + (locked ? "48" : "28") + ", inset 0 0 32px " + color + "08",
            opacity:    mounted ? 1 : 0,
            transform:  locked ? "scale(1.07)" : "scale(1)",
            transition: "opacity 0.4s ease 0.1s, transform 0.35s var(--ease-spring), box-shadow 0.35s ease, border-color 0.25s ease",
          }}>
            <Icon name={icon} size={34} color={color}/>
          </div>
          <div style={{
            opacity:    scoreVis ? 1 : 0,
            transform:  scoreVis ? "translateY(0) scale(1)" : "translateY(12px) scale(0.88)",
            transition: "all 0.5s var(--ease-out)",
            textAlign:  "center",
          }}>
            <div style={{
              fontSize:    52,
              fontWeight:  900,
              color:       color,
              fontFamily:  "'JetBrains Mono',monospace",
              lineHeight:  1,
              letterSpacing: "-3px",
              transform:   locked ? "scale(1.05)" : "scale(1)",
              transition:  "transform 0.28s var(--ease-spring)",
              textShadow:  "0 0 28px " + color + "44",
            }}>{animatedScore}</div>
            <div style={{ fontSize: 10, color: color, opacity: 0.5, fontFamily: "'JetBrains Mono',monospace", letterSpacing: "0.28em", marginTop: 5 }}>/ 100 RISK</div>
          </div>
        </div>

        {/* Verdict text */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 10, color: DS.t4, letterSpacing: "0.3em", fontFamily: "'JetBrains Mono',monospace", marginBottom: 12, fontWeight: 600 }}>TRUST ENGINE VERDICT</div>
          <div style={{
            fontSize:       "clamp(24px,3.2vw,34px)",
            fontWeight:     900,
            color:          color,
            letterSpacing:  "-0.5px",
            lineHeight:     1.05,
            marginBottom:   14,
            opacity:        labelVis ? 1 : 0,
            transform:      labelVis ? "scaleX(1) translateY(0)" : "scaleX(0.91) translateY(8px)",
            transformOrigin:"left center",
            transition:     "all 0.45s var(--ease-out)",
            textShadow:     labelVis ? "0 0 36px " + color + "30" : "none",
          }}>{label}</div>
          {reason && (
            <div style={{
              fontSize:   13,
              color:      DS.t2,
              lineHeight: 1.8,
              maxWidth:   520,
              opacity:    bodyVis ? 1 : 0,
              transform:  bodyVis ? "translateY(0)" : "translateY(8px)",
              transition: "all 0.45s var(--ease-out)",
            }}>{reason}{result.final_reasoning && result.final_reasoning.length > 130 ? "\u2026" : ""}</div>
          )}
        </div>

        {/* Share button */}
        <div style={{ opacity: bodyVis ? 1 : 0, transition: "opacity 0.4s ease", flexShrink: 0 }}>
          <button
            onClick={function() { shareResult(result, "Scan"); setShared(true); setTimeout(function() { setShared(false); }, 2200); if (onShare) onShare(); }}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "10px 18px",
              background: shared ? color + "18" : "rgba(255,255,255,0.03)",
              border:     "1px solid " + (shared ? color + "44" : "rgba(255,255,255,0.07)"),
              borderRadius: 10,
              color:  shared ? color : DS.t3,
              fontSize: 11, fontWeight: 700, cursor: "pointer",
              fontFamily: "'JetBrains Mono',monospace", letterSpacing: "0.12em",
              transition: "all 0.22s var(--ease-out)",
            }}
            onMouseEnter={function(e) { e.currentTarget.style.transform = "scale(1.04)"; e.currentTarget.style.borderColor = color + "44"; e.currentTarget.style.color = color; }}
            onMouseLeave={function(e) { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.borderColor = shared ? color + "44" : "rgba(255,255,255,0.07)"; e.currentTarget.style.color = shared ? color : DS.t3; }}>
            <Icon name={shared ? "check" : "share"} size={13} color={shared ? color : DS.t3}/>
            {shared ? "COPIED" : "SHARE"}
          </button>
        </div>
      </div>
      <div style={{ height: 1, background: "linear-gradient(90deg,transparent," + color + "28,transparent)" }}/>
    </div>
  );
}

// -- DECISION BANNER COMPONENT --------------------------------------------
function DecisionBanner({ result, onShare }) {
  var s = useState(false); var show = s[0]; var setShow = s[1];
  var s2 = useState(false); var shared = s2[0]; var setShared = s2[1];
  useEffect(function() { var t = setTimeout(function() { setShow(true); }, 80); return function() { clearTimeout(t); }; }, []);

  if (!result) return null;
  var score = result.risk_score || 0;
  var isScam = score >= 70;
  var isSusp = score >= 40 && score < 70;
  var color = isScam ? "#EF4444" : isSusp ? "#F59E0B" : "#22C55E";
  var bg = isScam ? "rgba(239,68,68,0.08)" : isSusp ? "rgba(245,158,11,0.08)" : "rgba(34,197,94,0.08)";
  var icon = isScam ? "🚨" : isSusp ? "⚠️" : "✅";
  var label = isScam ? "DO NOT ENGAGE" : isSusp ? "VERIFY FIRST" : "SAFE TO PROCEED";
  var decisionColor = isScam ? "#FF6B6B" : isSusp ? "#FCD34D" : "#6EE7B7";

  return (
    <div style={{ opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(-12px)", transition: "all 0.5s cubic-bezier(.4,0,.2,1)", background: bg, border: "2px solid " + color + "55", borderRadius: 14, padding: "18px 20px", marginBottom: 16, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg," + color + "00," + color + "," + color + "00)" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ fontSize: 36, lineHeight: 1 }}>{icon}</div>
          <div>
            <div style={{ fontSize: 9, color: "#475569", letterSpacing: 3, fontFamily: "JetBrains Mono,monospace", marginBottom: 3 }}>INSTANT DECISION</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: decisionColor, letterSpacing: 0.5, lineHeight: 1.1 }}>{label}</div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 4, maxWidth: 400, lineHeight: 1.5 }}>{(result.final_reasoning || "").slice(0, 100)}{result.final_reasoning && result.final_reasoning.length > 100 ? "..." : ""}</div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 40, fontWeight: 900, color: color, fontFamily: "JetBrains Mono,monospace", lineHeight: 1 }}>{score}</div>
          <div style={{ fontSize: 9, color: "#475569", letterSpacing: 2 }}>RISK / 100</div>
          <button onClick={function() { shareResult(result, "Scan"); setShared(true); setTimeout(function() { setShared(false); }, 2000); if (onShare) onShare(); }}
            style={{ fontSize: 9, padding: "4px 10px", background: shared ? color + "22" : "transparent", border: "1px solid " + color + "44", borderRadius: 5, color: shared ? color : "#475569", cursor: "pointer", fontFamily: "JetBrains Mono,monospace", letterSpacing: 1, transition: "all 0.2s" }}>
            {shared ? "✓ COPIED" : "📤 SHARE"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -- CROSS-MODULE INSIGHT BANNER -------------------------------------------
function CrossIntelBanner({ insights }) {
  if (!insights || insights.length === 0) return null;
  return (
    <div style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
      <div style={{ fontSize: 9, color: "#8B5CF6", letterSpacing: 2, fontFamily: "JetBrains Mono,monospace", marginBottom: 6, fontWeight: 700 }}>CROSS-MODULE INTELLIGENCE</div>
      {insights.map(function(ins, i) { return <div key={i} style={{ fontSize: 11, color: "#A78BFA", lineHeight: 1.6 }}>{ins}</div>; })}
    </div>
  );
}

// -- USER PROFILE SETUP WIDGET ---------------------------------------------
function ProfileSetup({ profile, onSave }) {
  var s1 = useState(profile.experience_level || "mid"); var exp = s1[0]; var setExp = s1[1];
  var s2 = useState(profile.domain || "general"); var domain = s2[0]; var setDomain = s2[1];
  var s3 = useState(profile.goal || "job_search"); var goal = s3[0]; var setGoal = s3[1];
  return (
    <div style={{ background: "#050E1E", border: "1px solid #1E293B", borderRadius: 10, padding: "16px 18px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "#8B5CF6", letterSpacing: 2, fontFamily: "JetBrains Mono,monospace", marginBottom: 12, fontWeight: 700 }}>⚡ PERSONALIZE YOUR EXPERIENCE</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 9, color: "#475569", marginBottom: 5, fontFamily: "JetBrains Mono,monospace" }}>EXPERIENCE</div>
          <select value={exp} onChange={function(e) { setExp(e.target.value); }} style={{ width: "100%", background: "#020617", color: "#E2E8F0", border: "1px solid #1E293B", borderRadius: 5, padding: "6px 8px", fontSize: 11, fontFamily: "JetBrains Mono,monospace" }}>
            <option value="fresher">Fresher</option>
            <option value="mid">Mid-Level</option>
            <option value="senior">Senior</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: 9, color: "#475569", marginBottom: 5, fontFamily: "JetBrains Mono,monospace" }}>DOMAIN</div>
          <select value={domain} onChange={function(e) { setDomain(e.target.value); }} style={{ width: "100%", background: "#020617", color: "#E2E8F0", border: "1px solid #1E293B", borderRadius: 5, padding: "6px 8px", fontSize: 11, fontFamily: "JetBrains Mono,monospace" }}>
            <option value="general">General</option>
            <option value="tech">Tech / IT</option>
            <option value="finance">Finance</option>
            <option value="marketing">Marketing</option>
            <option value="hr">HR</option>
            <option value="sales">Sales</option>
            <option value="design">Design</option>
            <option value="healthcare">Healthcare</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: 9, color: "#475569", marginBottom: 5, fontFamily: "JetBrains Mono,monospace" }}>GOAL</div>
          <select value={goal} onChange={function(e) { setGoal(e.target.value); }} style={{ width: "100%", background: "#020617", color: "#E2E8F0", border: "1px solid #1E293B", borderRadius: 5, padding: "6px 8px", fontSize: 11, fontFamily: "JetBrains Mono,monospace" }}>
            <option value="job_search">Find a Job</option>
            <option value="switch">Career Switch</option>
            <option value="growth">Get Promoted</option>
            <option value="freelance">Go Freelance</option>
          </select>
        </div>
      </div>
      <button onClick={function() { onSave({ experience_level: exp, domain: domain, goal: goal, setup: true }); }}
        style={{ width: "100%", padding: "9px", background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.3)", borderRadius: 6, color: "#A78BFA", fontSize: 10, letterSpacing: 2, cursor: "pointer", fontFamily: "JetBrains Mono,monospace", fontWeight: 700 }}>
        SAVE PROFILE — PERSONALIZE INTELLIGENCE
      </button>
    </div>
  );
}

function isURL(t) { var s = t.trim(); return s.startsWith("http://") || s.startsWith("https://"); }
function rp(url) { var t = url.toLowerCase(); if (t.includes("linkedin.com")) return "LinkedIn"; if (t.includes("instagram.com")) return "Instagram"; if (t.includes("x.com") || t.includes("twitter.com")) return "X"; if (t.includes("facebook.com")) return "Facebook"; return null; }
async function scrape(url, signal) {
  var proxies = ["https://corsproxy.io/?" + encodeURIComponent(url), "https://api.allorigins.win/get?url=" + encodeURIComponent(url)];
  for (var i = 0; i < proxies.length; i++) {
    try { var px = proxies[i], r = await fetch(px, { signal }); if (!r.ok) continue; var html = px.includes("allorigins") ? (await r.json()).contents : await r.text(); if (!html) continue; var _dp1 = typeof DOMParser !== "undefined" ? new DOMParser() : null; if (!_dp1) continue; var doc = _dp1.parseFromString(html, "text/html"); doc.querySelectorAll("script,style,nav,footer,header,aside").forEach(function(el) { el.remove(); }); var text = (doc.body && doc.body.textContent ? doc.body.textContent : "").replace(/\s+/g, " ").trim(); if (text.length > 50) return text.slice(0, MAX_CHARS); }
    catch (e) { continue; }
  }
  return null;
}
function dp(t) { var x = t.toLowerCase(); if (x.includes("linkedin")) return "LinkedIn"; if (x.includes("whatsapp") || x.includes("+91")) return "WhatsApp"; if (x.includes("naukri") || x.includes("indeed")) return "Job Portal"; if (x.includes("telegram")) return "Telegram"; return "Unknown"; }
function sc2(t) { var a = []; Array.from(t.matchAll(/(\d[\d,]*)\s*(?:\/|-|per)\s*(?:day|daily)/gi)).forEach(function(m) { var v = parseInt(m[1].replace(/,/g, ""), 10); if (v > 2000) a.push("Daily claim Rs." + v.toLocaleString("en-IN") + " unrealistic."); }); return a; }
var SP = ["ghar baithe paise kamao", "registration fee", "security deposit", "upi id", "flipkart packing", "telegram group", "mlm", "joining fee", "work from home kit"];
function spd(t) { var x = t.toLowerCase(); return SP.filter(function(p) { return x.includes(p); }); }
function ajd(t) { var x = t.toLowerCase(), s = 0; [{ p: "fast-paced environment", w: 2 }, { p: "go-getter", w: 2 }, { p: "hit the ground running", w: 3 }].forEach(function(m) { if (x.includes(m.p)) s += m.w; }); if (!/python|sql|react|aws|figma|jira/i.test(t)) s += 3; return s; }

async function sbSave(r) {
  if (!r || r.risk_score < 40) return;
  try { await fetch(SB + "/rest/v1/scans", { method: "POST", headers: { "Content-Type": "application/json", "apikey": SK, "Authorization": "Bearer " + SK, "Prefer": "return=minimal" }, body: JSON.stringify({ verdict: r.verdict || "Unknown", risk_score: r.risk_score || 0, quick_verdict: r.quick_verdict || "SUSPICIOUS", platform: r.platform_detected || "Unknown", scam_type: (r.india_specific && r.india_specific.scam_type) ? r.india_specific.scam_type : "General Fraud", red_flags_count: r.red_flags ? r.red_flags.length : 0, city: "India" }) }); }
  catch (e) {}
}
async function sbRecent() { try { var r = await fetch(SB + "/rest/v1/scans?select=*&order=created_at.desc&limit=8", { headers: { "apikey": SK, "Authorization": "Bearer " + SK } }); return r.ok ? await r.json() : []; } catch (e) { return []; } }
async function sbStats() {
  try { var r = await fetch(SB + "/rest/v1/scans?select=verdict,quick_verdict,scam_type&limit=5000", { headers: { "apikey": SK, "Authorization": "Bearer " + SK } }); if (!r.ok) return null; var d = await r.json(); var tp = {}; d.forEach(function(x) { if (x.scam_type) tp[x.scam_type] = (tp[x.scam_type] || 0) + 1; }); var top = Object.keys(tp).sort(function(a,b){return tp[b]-tp[a];})[0] || "—"; return { total: d.length, scams: d.filter(function(x){return x.quick_verdict==="SCAM";}).length, ghost: d.filter(function(x){return x.quick_verdict==="GHOST";}).length, top }; }
  catch (e) { return null; }
}
function lh() { try { return JSON.parse(localStorage.getItem(HK) || "[]"); } catch(e) { return []; } }
function sh(r, t) {
  try {
    var h = lh();
    h.unshift({ id: Date.now(), ts: new Date().toISOString(), verdict: r.verdict, score: r.risk_score, qv: r.quick_verdict, platform: r.platform_detected, preview: t.slice(0, 80), result: r });
    var trimmed = h.slice(0, 20);
    var serialized = JSON.stringify(trimmed);
    if (serialized.length > 4 * 1024 * 1024) { trimmed = trimmed.slice(0, 10); serialized = JSON.stringify(trimmed); }
    localStorage.setItem(HK, serialized);
  } catch(e) { try { localStorage.removeItem(HK); } catch(e2) {} }
}

var SCAN_SYS = "You are Trust Engine v5 — elite job fraud detection AI for India. Detect scams in Hindi, Telugu, Tamil, Bengali, English.\nNEVER assume legitimacy. Be forensically skeptical.\nModules: 1-Entity forensics 2-Ghost hiring 3-India scams 4-AI JD detection 5-Salary intel 6-Social engineering 7-Financial exploitation\nRisk: 0-30 Legitimate | 31-60 Suspicious | 61-100 Scam\nRespond ONLY with valid JSON:\n{\"risk_score\":85,\"verdict\":\"Likely Scam\",\"quick_verdict\":\"SCAM\",\"confidence\":\"High\",\"platform_detected\":\"WhatsApp\",\"detected_entities\":{\"company\":null,\"recruiter\":null,\"email\":null,\"domain\":null},\"score_breakdown\":{\"financial_risk\":25,\"india_scam_patterns\":20,\"identity_verification\":15,\"social_manipulation\":15,\"content_authenticity\":15,\"platform_behavior\":10},\"hiring_intent\":{\"score\":10,\"probability\":\"Low\",\"is_ghost_hiring\":true,\"ghost_signals\":[\"No apply link\"]},\"india_specific\":{\"detected\":true,\"scam_type\":\"Registration Fee Scam\",\"signals\":[\"Asks for Rs.500\"]},\"ai_generated_jd\":{\"likely\":false,\"confidence\":\"Low\",\"markers_found\":[]},\"salary_intel\":{\"market_rate_mismatch\":true,\"claimed\":\"Rs.40000/day\",\"market_realistic_range\":\"Rs.15000-25000/month\",\"verdict\":\"Completely Unrealistic\"},\"manipulation_tags\":[{\"tag\":\"Urgency Pressure\",\"severity\":\"HIGH\"}],\"manipulation_signals\":[\"Limited seats\"],\"red_flags\":[\"Asks for registration fee\"],\"positive_signals\":[],\"explainability\":{\"why_risky\":\"Short explanation\",\"how_they_manipulate\":\"Short explanation\",\"what_they_want\":\"Short explanation\"},\"detailed_analysis\":{\"identity\":\"...\",\"platform_behavior\":\"...\",\"social_engineering\":\"...\",\"job_authenticity\":\"...\",\"financial_risk\":\"...\",\"narrative_psychology\":\"...\"},\"final_reasoning\":\"2 sentence conclusion.\",\"user_advice\":{\"action\":\"Avoid\",\"steps\":[\"Do not pay any fee\",\"Block sender\",\"Report to cybercrime.gov.in\"]}}";

var RESUME_SYS = "You are India's most advanced AI Career Intelligence Engine. Analyze resumes with deep expertise in India's job market.\nBe brutally honest, highly specific, actionable. No generic advice.\nRespond ONLY with valid JSON:\n{\"ats_score\":72,\"interview_readiness\":58,\"overall_grade\":\"B+\",\"candidate_level\":\"Mid-Level\",\"years_experience\":3,\"summary\":\"Precise 2-sentence profile.\",\"top_roles\":[{\"role\":\"Blockchain Researcher\",\"match\":91,\"why\":\"Strong on-chain analysis.\",\"gap\":\"Missing publications.\",\"salary_range\":\"Rs.18-35 LPA\",\"top_companies\":[\"Polygon\",\"CoinDCX\",\"Nansen\"],\"roadmap\":[\"Publish 2 research reports\",\"Build public GitHub\",\"Apply to Messari\"]}],\"interview_readiness_breakdown\":{\"technical_depth\":65,\"communication_clarity\":70,\"portfolio_strength\":45,\"industry_knowledge\":85},\"salary_benchmarks\":{\"current_market_value\":\"Rs.8-14 LPA\",\"target_after_upskilling\":\"Rs.18-30 LPA\",\"negotiation_tip\":\"Specific tip.\",\"in_demand_skills_premium\":[\"Solidity +40%\"]},\"skills_found\":[\"Python\",\"DeFi\"],\"skills_missing\":[{\"skill\":\"Solidity\",\"priority\":\"HIGH\",\"why\":\"Required for Web3 roles.\",\"learn_in\":\"6-8 weeks\"}],\"rewrites\":[{\"section\":\"Experience\",\"original\":\"Worked on blockchain\",\"rewritten\":\"Conducted on-chain analysis of 15+ DeFi protocols\",\"why\":\"Too vague. Add specifics.\"}],\"action_plan\":{\"day_30\":[\"Fix ATS issues\",\"Update LinkedIn\"],\"day_60\":[\"Complete top skill\",\"Build 2 projects\"],\"day_90\":[\"3 active interviews\",\"Negotiate offers\"]},\"ats_issues\":[\"No measurable achievements\"],\"strengths\":[\"Deep domain expertise\"],\"quick_wins\":[\"Add LinkedIn URL\",\"Quantify achievements\"],\"india_insights\":[\"Top 5% of Indian crypto talent.\"]}";

var VCFG = {
  "Likely Legitimate": { color: "#00ff88", bg: "#001a0e", border: "#00ff8833", glow: "#00ff8822", label: "LEGITIMATE" },
  "Suspicious":        { color: "#ffaa00", bg: "#1a0e00", border: "#ffaa0033", glow: "#ffaa0022", label: "SUSPICIOUS" },
  "Likely Scam":       { color: "#ff3366", bg: "#1a0008", border: "#ff336633", glow: "#ff336622", label: "SCAM DETECTED" },
};
var QVC = {
  REAL:       { color: "#00ff88", label: "✓ REAL JOB",      bg: "#001a0e", border: "#00ff8833" },
  GHOST:      { color: "#ffaa00", label: "◎ GHOST HIRING",  bg: "#1a0e00", border: "#ffaa0033" },
  SCAM:       { color: "#ff3366", label: "⚠ SCAM DETECTED", bg: "#1a0008", border: "#ff336633" },
  SUSPICIOUS: { color: "#aa88ff", label: "? SUSPICIOUS",    bg: "#0e0018", border: "#aa88ff33" },
};
var AC = { "Proceed": { color: "#00ff88", label: "✓ SAFE TO PROCEED" }, "Verify Further": { color: "#ffaa00", label: "⚠ VERIFY FIRST" }, "Avoid": { color: "#ff3366", label: "✕ DO NOT ENGAGE" } };
// WM defined below

var HR_RANKING_PROMPT = "You are an elite AI Hiring Decision Engine v2.\n\nYou analyze multiple candidates for ONE job role and rank them from BEST to WORST.\nYou think like a real hiring manager — brutally realistic, not a keyword matcher.\n\nEvaluate:\n- Skill depth vs role requirements\n- Relevant experience quality\n- Project authenticity\n- Role + domain alignment\n- Seniority fit\n- Red flags\n- Relative ranking vs other candidates\n\nSCORING: 90-100 Strong Hire | 75-89 Good Fit | 60-74 Consider | 40-59 Weak | <40 Reject\n\nCRITICAL:\n- Be brutally realistic\n- Compare RELATIVE to each other\n- Do NOT inflate scores\n- Do NOT assume missing experience\n- Extract candidate name from resume if available, else Candidate N\n\nOUTPUT STRICT JSON ONLY. No markdown, no backticks:\n{\"candidates\":[{\"rank\":1,\"name\":\"\",\"fit_score\":0,\"verdict\":\"\",\"strengths\":[],\"weaknesses\":[],\"why_selected\":\"\",\"risk_flags\":[],\"skills_match\":0,\"experience_match\":0,\"domain_fit\":0,\"hiring_risk\":\"Low\",\"hiring_risk_reason\":\"\"}],\"shortlist\":{\"top_percent\":20,\"selected_candidates\":[],\"summary\":\"\"},\"comparison\":{\"why_top_candidate\":\"\",\"key_differentiators\":[],\"ranking_logic\":\"\"},\"hiring_risk_analysis\":[{\"candidate\":\"\",\"risk_level\":\"Low\",\"reason\":\"\"}],\"final_decision\":{\"hire\":\"\",\"reason\":\"\",\"confidence\":\"High\"}}";

// ===================================================
// EXPORT / COPY  SCAN
// ===================================================
function generateReportText(result, inputText) {
  if (!result) return "";
  var lines = [
    "═══════════════════════════════════════",
    "     TRUST ENGINE v5 — SCAN REPORT",
    "═══════════════════════════════════════",
    "Verdict      : " + (result.verdict || "Unknown"),
    "Risk Score   : " + (result.risk_score || 0) + "/100",
    "Confidence   : " + (result.confidence || "—"),
    "Platform     : " + (result.platform_detected || "—"),
    "Quick Tag    : " + (result.quick_verdict || "—"),
    "───────────────────────────────────────",
    "FINAL REASONING:",
    result.final_reasoning || "—",
    "───────────────────────────────────────",
    "RED FLAGS:",
  ];
  if (result.red_flags && result.red_flags.length > 0) {
    result.red_flags.forEach(function(f, i) { lines.push("  " + (i+1) + ". " + f); });
  } else { lines.push("  None detected"); }
  lines.push("───────────────────────────────────────");
  lines.push("RECOMMENDED ACTION: " + (result.user_advice && result.user_advice.action ? result.user_advice.action : "—"));
  if (result.user_advice && result.user_advice.steps) {
    result.user_advice.steps.forEach(function(s, i) { lines.push("  " + (i+1) + ". " + s); });
  }
  lines.push("───────────────────────────────────────");
  lines.push("Scanned by Trust Engine v5");
  lines.push("github.com/realworldgaming/trust-engine");
  lines.push("═══════════════════════════════════════");
  return lines.join("\n");
}
function exportReport(result, inputText) {
  try {
    var text = generateReportText(result, inputText);
    var blob = new Blob([text], { type: "text/plain" });
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a"); a.href = url; a.download = "trust-engine-report.txt"; a.click();
    setTimeout(function() { try { URL.revokeObjectURL(url); } catch(e) {} }, 1000);
  } catch(e) {}
}
function copyReport(result, inputText, setCopied) {
  var text = generateReportText(result, inputText);
  try { navigator.clipboard.writeText(text).then(function() {
    setCopied(true); setTimeout(function() { setCopied(false); }, 2000);
  }).catch(function() {
    try { var ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select();
    document.execCommand("copy"); document.body.removeChild(ta); } catch(e2) {}
    setCopied(true); setTimeout(function() { setCopied(false); }, 2000);
  }); } catch(e) { setCopied(false); }
}

// ===================================================
// STYLE CONFIGS
// ===================================================
var VS = {
  "Likely Legitimate": { color: "#22c55e", bg: "#031a0c", border: "#166534", icon: "✓", glow: "#22c55e18" },
  "Suspicious":        { color: "#f59e0b", bg: "#1a0f00", border: "#92400e", icon: "⚠", glow: "#f59e0b18" },
  "Likely Scam":       { color: "#ef4444", bg: "#1a0303", border: "#991b1b", icon: "✕", glow: "#ef444418" },
};
var QVS = {
  REAL:       { color: "#22c55e", label: "✓ REAL JOB",      bg: "#031a0c", border: "#166534" },
  GHOST:      { color: "#f59e0b", label: "◎ GHOST HIRING",  bg: "#1a0f00", border: "#92400e" },
  SCAM:       { color: "#ef4444", label: "✕ SCAM DETECTED", bg: "#1a0303", border: "#991b1b" },
  SUSPICIOUS: { color: "#a78bfa", label: "⚠ SUSPICIOUS",    bg: "#110a1f", border: "#6d28d9" },
};
var AS = {
  "Proceed":        { color: "#22c55e", label: "✓ Safe to Proceed" },
  "Verify Further": { color: "#f59e0b", label: "⚠ Verify First" },
  "Avoid":          { color: "#ef4444", label: "✕ Do Not Engage" },
};
var TC = {
  HIGH:   { bg: "#3b0000", border: "#991b1b", text: "#fca5a5" },
  MEDIUM: { bg: "#1a1200", border: "#92400e", text: "#fde68a" },
  LOW:    { bg: "#0a1a0a", border: "#166534", text: "#86efac" },
};
var WM = {
  financial_risk:        { label: "Financial Risk",        w: "25%", color: "#ef4444" },
  india_scam_patterns:   { label: "India Scam Patterns",   w: "20%", color: "#f97316" },
  identity_verification: { label: "Identity Verification", w: "15%", color: "#facc15" },
  social_manipulation:   { label: "Social Manipulation",   w: "15%", color: "#a78bfa" },
  content_authenticity:  { label: "Content Authenticity",  w: "15%", color: "#22d3ee" },
  platform_behavior:     { label: "Platform Behavior",     w: "10%", color: "#ec4899" },
};
var HR_STEPS = ["Ingesting candidate profiles...","Extracting skill signatures...","Running competency forensics...","Computing role fit vectors...","Calibrating relative rankings...","Detecting hiring risks...","Building decision matrix...","Compiling final verdict..."];

// -- CAREER ENGINE PROMPT --------------------------------------
var CAREER_ENGINE_PROMPT = [
  "You are TRUST ENGINE — Career Intelligence System v5.",
  "You are NOT a career advisor. You are a forensic evaluator of employability, skills, and market readiness.",
  "Analyze resumes like: Senior Hiring Manager, Technical Interviewer, ATS System, Market Analyst.",
  "PRIMARY OBJECTIVE: Transform resume into candidate classification, employability breakdown, career ladder (Rs5K-1Cr), job eligibility per role, salary realism, growth roadmap, rejection analysis, skills gap plan.",
  "NO motivation. NO fluff. ONLY reality.",
  "DEEP ANALYSIS (MANDATORY):",
  "1. Skill Depth: Beginner/Surface/Practical/Advanced",
  "2. Tool Exposure: Real tools vs theoretical",
  "3. Project Authenticity: Real-world vs tutorial",
  "4. Domain Clarity: Focused vs scattered",
  "5. Experience Credibility: Genuine vs inflated",
  "6. Market Fit: Demand vs oversupply",
  "7. ATS Compatibility: Parsing risks, keyword issues",
  "8. Hiring Risk: Why recruiters will reject",
  "CANDIDATE CLASSIFICATION (ONE ONLY): Beginner / Emerging / Job-Ready / Competitive / Elite. STRICT: Do NOT overrate.",
  "CAREER LADDER (5 LEVELS MANDATORY):",
  "L1 ENTRY Rs5K-15K/mo | L2 FOUNDATION Rs15K-40K/mo | L3 SKILLED Rs40K-1L/mo | L4 ADVANCED Rs1L-10L+/mo | L5 ELITE Rs10L-1Cr+/yr",
  "For each level 3-5 roles. Each role: job_title, eligibility(Yes/Partial/No), confidence_score(0-100), salary_range, reason(resume only), gap, one_line.",
  "STRICT: Yes ONLY if hireable NOW. Freshers: higher roles mostly Partial/No. NEVER inflate salary.",
  "EMPLOYABILITY: ats_score, interview_readiness, technical_depth_score, portfolio_strength, communication_score, market_demand_score (all 0-100). strengths(max 6), weaknesses(max 6), hidden_advantages.",
  "SALARY INTEL: current_value_range, potential_value_range, overvaluation_risk.",
  "GROWTH PATH: target_role, current_gap_level(Low/Medium/High), time_to_reach, difficulty(Easy/Moderate/Hard), steps(6-10 each with action+tools+outcome).",
  "REJECTION ANALYSIS: top_reasons(exactly 3), missing_signals, recruiter_red_flags.",
  "SKILL GAP: each skill has skill_name, priority(HIGH/MEDIUM/LOW), why_needed, time_to_learn, impact_on_salary.",
  "OUTPUT: Valid JSON ONLY. No markdown. No explanation. No text before or after.",
  '{"candidate_profile":{"name":"","level":"","summary":"","domain":"","employability_risk":""},"scores":{"ats_score":0,"interview_readiness":0,"technical_depth":0,"portfolio":0,"communication":0,"market_demand":0},"strengths":[],"weaknesses":[],"hidden_advantages":[],"career_ladder":[{"level_num":1,"level_name":"ENTRY","salary_range":"Rs5K-15K/month","roles":[{"job_title":"","eligibility":"","confidence_score":0,"salary_range":"","reason":"","gap":"","one_line":""}]}],"salary_intelligence":{"current_value_range":"","potential_value_range":"","overvaluation_risk":""},"growth_path":{"target_role":"","current_gap_level":"","time_to_reach":"","difficulty":"","steps":[]},"rejection_analysis":{"top_reasons":[],"missing_signals":[],"recruiter_red_flags":[]},"skills_gap":[]}'
].join("\n");



var SCAN_STEPS = [
  { id: 1, label: "Payload ingested", sub: "Tokenizing input signals...", done: false },
  { id: 2, label: "Matching fraud signatures", sub: "30+ India regional patterns active", done: false },
  { id: 3, label: "Extracting entities", sub: "Company · recruiter · domain forensics", done: false },
  { id: 4, label: "Detecting ghost hiring", sub: "Evaluating hiring intent signals...", done: false },
  { id: 5, label: "Computing salary risk", sub: "Cross-referencing India market data...", done: false },
  { id: 6, label: "Analyzing AI-generated JD", sub: "Detecting synthetic content patterns...", done: false },
  { id: 7, label: "Evaluating manipulation", sub: "Urgency · authority · FOMO tactics...", done: false },
  { id: 8, label: "Generating verdict", sub: "Compiling forensic intelligence report...", done: false },
];

function genRep(r) {
  if (!r) return "";
  var l = ["═══════════════════════════════════", "     TRUST ENGINE v5 — SCAN REPORT", "═══════════════════════════════════", "Verdict    : " + (r.verdict || "Unknown"), "Risk Score : " + (r.risk_score || 0) + "/100", "Platform   : " + (r.platform_detected || "—"), "───────────────────────────────────", "FINAL REASONING:", r.final_reasoning || "—", "───────────────────────────────────", "RED FLAGS:"];
  if (r.red_flags && r.red_flags.length > 0) r.red_flags.forEach(function(f, i) { l.push("  " + (i + 1) + ". " + f); }); else l.push("  None detected");
  l.push("───────────────────────────────────", "ACTION: " + (r.user_advice && r.user_advice.action ? r.user_advice.action : "—"));
  if (r.user_advice && r.user_advice.steps) r.user_advice.steps.forEach(function(s, i) { l.push("  " + (i + 1) + ". " + s); });
  l.push("───────────────────────────────────", "Trust Engine v5 · trust-engine-ashen.vercel.app", "═══════════════════════════════════");
  return l.join("\n");
}
function expR(r) { try { var t = genRep(r), b = new Blob([t], { type: "text/plain" }), u = URL.createObjectURL(b), a = document.createElement("a"); a.href = u; a.download = "trust-engine-report.txt"; a.click(); setTimeout(function(){ try{URL.revokeObjectURL(u);}catch(e){} }, 1000); } catch(e) {} }
function cpR(r, set) { try { if(navigator.vibrate) navigator.vibrate(10); } catch(e){} navigator.clipboard.writeText(genRep(r)).then(function() { set(true); setTimeout(function() { set(false); }, 2000); }).catch(function() { var ta = document.createElement("textarea"); ta.value = genRep(r); document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); set(true); setTimeout(function() { set(false); }, 2000); }); }


// ===================================================

// ====================================================
// UTILITY FUNCTIONS (AI helpers, file extractors)
// ====================================================
async function callGeminiRaw(prompt, signal) {
  try {
    var result = await runAI(prompt, null, signal);
    return (result && result.text) ? result.text : null;
  } catch(e) {
    if (e && e.name === "AbortError") throw e;
    throw new Error("AI call failed: " + (e.message || "Unknown error"));
  }
}

// ===================================================
// GEMINI VISION  extract text from image/PDF/DOCX
// ===================================================
async function extractViaGemini(file) {
  var reader = new FileReader();
  var b64 = await new Promise(function(res, rej) {
    reader.onload = function() {
      try { res((reader.result || "").split(",")[1] || ""); }
      catch(e) { res(""); }
    };
    reader.onerror = function(e) { rej(new Error("FileReader error")); };
    reader.readAsDataURL(file);
  });
  if (!b64) throw new Error("Could not read file as base64.");
  var mtype = (file && file.type) || "application/octet-stream";
  var key = KEYS[ki] || KEYS[0];
  if (!key) throw new Error("No API key available for vision extraction.");
  var response = await fetch(GB_URL + key, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      contents: [{ parts: [
        { inline_data: { mime_type: mtype, data: b64 } },
        { text: "Extract ALL text from this resume document. Output only the raw text, preserve structure. No commentary." }
      ]}]
    })
  });
  if (!response.ok) throw new Error("Gemini vision HTTP " + response.status);
  var data = await response.json().catch(function() { return {}; });
  return (data && data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts && data.candidates[0].content.parts[0]) ? (data.candidates[0].content.parts[0].text || "") : "";
}

// ===================================================
// MULTI-FORMAT TEXT EXTRACTOR
// PDF  pdfjs | DOCX  mammoth | Image  Tesseract OCR | txt  direct
// ===================================================
async function loadScript(src) {
  if (document.querySelector('script[src="' + src + '"]')) return;
  return new Promise(function(res, rej) {
    var s = document.createElement("script");
    s.src = src; s.onload = res; s.onerror = function() { rej(new Error("Failed: " + src)); };
    document.head.appendChild(s);
  });
}

async function extractPDFText(file) {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js");
  var pdfjsLib = window.pdfjsLib;
  pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
  var ab = await file.arrayBuffer();
  var pdf = await pdfjsLib.getDocument({ data: ab }).promise;
  var text = "";
  for (var i = 1; i <= pdf.numPages; i++) {
    var page = await pdf.getPage(i);
    var content = await page.getTextContent();
    text += content.items.map(function(item) { return item.str; }).join(" ") + "\n";
  }
  return text.trim();
}

async function extractDOCXText(file) {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js");
  var ab = await file.arrayBuffer();
  var result = await window.mammoth.extractRawText({ arrayBuffer: ab });
  return result.value.trim();
}

async function extractImageOCR(file, onProgress) {
  if (!window.Tesseract) {
    await loadScript("https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js");
  }
  var res = await window.Tesseract.recognize(file, "eng+hin", {
    logger: function(m) { if (m.status === "recognizing text" && onProgress) onProgress(Math.round(m.progress * 100)); }
  });
  return res.data.text.trim();
}

async function extractResumeText(file, onProgress) {
  var name = file.name.toLowerCase();
  var type = file.type;
  // Plain text
  if (type === "text/plain" || name.endsWith(".txt") || name.endsWith(".md")) {
    return await file.text();
  }
  // PDF  try pdfjs first, fallback to Gemini vision
  if (type === "application/pdf" || name.endsWith(".pdf")) {
    try {
      var txt = await extractPDFText(file);
      if (txt && txt.length > 50) return txt;
    } catch(e) {}
    // fallback
    try { return await extractViaGemini(file); } catch(e) {}
    throw new Error("Could not read PDF.");
  }
  // DOCX  mammoth first, fallback to Gemini
  if (name.endsWith(".docx") || name.endsWith(".doc") || type.includes("word")) {
    try {
      var txt2 = await extractDOCXText(file);
      if (txt2 && txt2.length > 50) return txt2;
    } catch(e) {}
    try { return await extractViaGemini(file); } catch(e) {}
    throw new Error("Could not read DOCX.");
  }
  // Images  Tesseract OCR first, fallback to Gemini vision
  if (type.startsWith("image/")) {
    try {
      var txt3 = await extractImageOCR(file, onProgress);
      if (txt3 && txt3.length > 20) return txt3;
    } catch(e) {}
    try { return await extractViaGemini(file); } catch(e) {}
    throw new Error("Could not extract text from image.");
  }
  throw new Error("Unsupported format: " + (name.split(".").pop() || "unknown"));
}

// ===================================================
// SAFE FILE TEXT EXTRACTOR (crash-proof)
// Handles PDF / Image / Text with full fallbacks
// ===================================================
async function extractTextFromFile(file) {
  try {
    if (!file) return "";
    var type = (file && file.type) ? file.type : "";

    // SAFE PDF
    if (
      type.includes("pdf") &&
      typeof window !== "undefined" &&
      window.pdfjsLib
    ) {
      try {
        var buffer = await file.arrayBuffer();
        var pdf = await window.pdfjsLib.getDocument({ data: buffer }).promise;
        var text = "";
        for (var i = 1; i <= pdf.numPages; i++) {
          var page = await pdf.getPage(i);
          var content = await page.getTextContent();
          text += content.items.map(function(it) { return it.str; }).join(" ") + "\n";
        }
        return text;
      } catch(e) { return ""; }
    }

    // SAFE IMAGE OCR
    if (
      type.includes("image") &&
      typeof window !== "undefined" &&
      window.Tesseract
    ) {
      try {
        var imgResult = await window.Tesseract.recognize(file, "eng");
        return (imgResult && imgResult.data && imgResult.data.text) ? imgResult.data.text : "";
      } catch(e) { return ""; }
    }

    // DEFAULT SAFE TEXT
    try { return await file.text(); } catch(e) { return ""; }

  } catch(e) { return ""; }
}

// SAFE CLIPBOARD PASTE
async function pasteFromClipboard(setter) {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      var text = await navigator.clipboard.readText();
      if (text && setter) setter(text);
    }
  } catch(e) {}
}

// -- UNIFIED RESUME EXTRACTOR (full format support) -------------------
// Handles PDF, DOCX, TXT, JPG/PNG (OCR) with size guard + fallback chain
async function extractResume(file) {
  var MAX_SIZE = 5 * 1024 * 1024;
  if (!file) throw new Error("No file provided.");
  if (file.size > MAX_SIZE) throw new Error("File too large. Max 5MB allowed.");
  var ext = (file.name || "").toLowerCase().split(".").pop();
  var mime = file.type || "";

  // TXT -------------------------------------------------------------------
  if (ext === "txt" || mime === "text/plain") {
    var txt = await file.text();
    if (!txt.trim()) throw new Error("TXT file is empty.");
    return txt.trim().slice(0, 24000);
  }

  // IMAGE (JPG/PNG/WEBP)  — Tesseract OCR --------------------------------
  if (["jpg","jpeg","png","webp"].includes(ext) || mime.startsWith("image/")) {
    if (!window.Tesseract) {
      await new Promise(function(resolve, reject) {
        var s = document.createElement("script");
        s.src = "https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js";
        s.onload = resolve;
        s.onerror = function() { reject(new Error("OCR engine failed to load.")); };
        document.head.appendChild(s);
      });
    }
    var imgResult = await window.Tesseract.recognize(file, "eng+hin", { logger: function(m) {} });
    var imgText = (imgResult && imgResult.data && imgResult.data.text) ? imgResult.data.text : "";
    if (!imgText.trim()) throw new Error("No text found in image. Try a clearer image.");
    return imgText.trim().slice(0, 24000);
  }

  // DOCX — mammoth.js ----------------------------------------------------
  if (ext === "docx" || ext === "doc" || mime.includes("wordprocessingml") || mime.includes("msword")) {
    if (!window.mammoth) {
      await new Promise(function(resolve, reject) {
        var s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js";
        s.onload = resolve;
        s.onerror = function() { reject(new Error("DOCX parser failed to load.")); };
        document.head.appendChild(s);
      });
    }
    var docBuf = await file.arrayBuffer();
    var docResult = await window.mammoth.extractRawText({ arrayBuffer: docBuf });
    var docText = (docResult && docResult.value) ? docResult.value : "";
    if (!docText.trim()) throw new Error("No text found in DOCX file.");
    return docText.trim().slice(0, 24000);
  }

  // PDF — pdfjs (loads library on demand) --------------------------------
  if (!window.pdfjsLib) {
    await new Promise(function(resolve, reject) {
      var s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      s.onload = function() {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        resolve();
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  } else {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
  }
  var buf = await file.arrayBuffer();
  var pdf = await window.pdfjsLib.getDocument({ data: buf }).promise;
  var full = "";
  for (var i = 1; i <= pdf.numPages; i++) {
    var page = await pdf.getPage(i);
    var tc = await page.getTextContent();
    full += tc.items.map(function(x) { return x.str; }).join(" ") + "\n";
  }
  if (!full.trim()) throw new Error("No text found in PDF. Try a text-based or non-scanned PDF.");
  return full.trim().slice(0, 24000);
}

// extractPDF — backward-compat wrapper used throughout the app.
// Despite the name it handles ALL formats (PDF, DOCX, TXT, image).
async function extractPDF(file) {
  return extractResume(file);
}




// ===================================================
// PHASE 3  NEGOTIATION SCRIPT PANEL
// Additive extension. Renders after Pathfinder or Career results.
// Uses: runAI, safeParseJSON  no new dependencies.
// ===================================================
function NegotiationScriptPanel({ role, salaryRange, experienceLevel }) {
  var s0 = useState(null); var script = s0[0]; var setScript = s0[1];
  var s1 = useState(false); var loading = s1[0]; var setLoading = s1[1];
  var s2 = useState(null); var error = s2[0]; var setError = s2[1];
  var s3 = useState(false); var expanded = s3[0]; var setExpanded = s3[1];
  var s4 = useState({}); var copied = s4[0]; var setCopied = s4[1];
  var abRef = useRef(null);

  var NC = "#C6A86B";

  async function generate() {
    if (abRef.current) abRef.current.abort();
    abRef.current = new AbortController();
    setLoading(true); setError(null); setScript(null);
    try {
      var roleStr = (role || "Software Engineer").slice(0, 80);
      var salStr = (salaryRange || "Not specified").slice(0, 80);
      var expStr = (experienceLevel || "mid-level").slice(0, 40);
      var prompt = [
        "You are an elite salary negotiation strategist for India's job market.",
        "Generate a highly specific negotiation script for this candidate.",
        "Role: " + roleStr,
        "Expected Salary: " + salStr,
        "Experience Level: " + expStr,
        "",
        "CRITICAL: Return ONLY valid JSON. No markdown. No preamble. Start with {",
        "JSON schema:",
        '{"opening_line":"Exact word-for-word opening sentence to use on the call","counter_offer":"Specific strategy and exact counter number with reasoning","walk_away":"The minimum acceptable number and what to say when you walk away","key_phrases":["Phrase 1 to use verbatim","Phrase 2 to use verbatim","Phrase 3","Phrase 4"]}'
      ].join("\n");
      var parsed = await runAIStable(prompt, null, abRef.current.signal);
      if (!parsed || parsed.error || !parsed.opening_line) {
        parsed = {
          opening_line: "Thank you for the offer. I'm excited about this opportunity. Based on my research and experience, I was expecting a figure closer to [your target]. Is there flexibility there?",
          counter_offer: "Counter 10-15% above your minimum. State: 'Based on my skills in [X] and market rates, I believe [amount] is appropriate.'",
          walk_away: "Know your floor before the call. If they can't meet it, say: 'I appreciate the offer but I need to respectfully decline. I hope we can work together in the future.'",
          key_phrases: ["I'm very interested in this role and the company.", "Based on my research, the market rate for this role is...", "Is there any flexibility on the base compensation?", "I'd like to take 24 hours to review the full offer."],
          _fallback: true
        };
        setError("AI script unavailable — showing template. Retry for personalized script.");
      }
      setScript(parsed);
    } catch(e) {
      if (e && e.name !== "AbortError") setError((e && e.message) || "Generation failed. Please retry.");
    } finally {
      setLoading(false);
    }
  }

  function copySection(key, text) {
    if (!text) return;
    try {
      navigator.clipboard.writeText(text).catch(function() {
        var ta = document.createElement("textarea"); ta.value = text;
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      });
    } catch(e) {}
    setCopied(function(prev) { var next = Object.assign({}, prev); next[key] = true; return next; });
    setTimeout(function() { setCopied(function(prev) { var next = Object.assign({}, prev); next[key] = false; return next; }); }, 2000);
  }

  var sections = script ? [
    { key: "opening_line", label: "OPENING LINE", icon: "🎙", color: "#22C55E", value: script.opening_line },
    { key: "counter_offer", label: "COUNTER OFFER STRATEGY", icon: "💰", color: "#3B82F6", value: script.counter_offer },
    { key: "walk_away", label: "WALK AWAY NUMBER", icon: "🚪", color: "#EF4444", value: script.walk_away },
  ] : [];

  return (
    <div style={{ marginTop: 20, background: "#060612", border: "1px solid " + NC + "33", borderRadius: 10, overflow: "hidden" }}>
      <button onClick={function() { setExpanded(function(v) { return !v; }); }}
        style={{ width: "100%", padding: "14px 18px", background: "transparent", border: "none", borderBottom: expanded ? "1px solid " + NC + "22" : "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", textAlign: "left" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, background: NC + "15", border: "1px solid " + NC + "33", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>💼</div>
          <div>
            <div style={{ fontSize: 9, letterSpacing: 3, color: NC, fontFamily: "JetBrains Mono,monospace", fontWeight: 700, marginBottom: 2 }}>NEGOTIATION SCRIPT</div>
            <div style={{ fontSize: 11, color: "#475569" }}>Get a word-for-word salary negotiation script</div>
          </div>
        </div>
        <span style={{ color: NC + "88", fontSize: 16, transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>›</span>
      </button>

      {expanded && (
        <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
          {!script && !loading && (
            <div style={{ background: NC + "08", border: "1px solid " + NC + "18", borderRadius: 8, padding: "12px 14px", fontSize: 12, color: "#64748B", lineHeight: 1.7 }}>
              Generates a personalized negotiation script for <span style={{ color: NC, fontWeight: 700 }}>{role || "your target role"}</span>
              {salaryRange ? " targeting " : ""}<span style={{ color: "#22C55E", fontWeight: 700 }}>{salaryRange || ""}</span>.
              Includes the exact opening line, counter strategy, walk-away number, and power phrases.
            </div>
          )}

          {error && (
            <div style={{ background: "#1a0008", border: "1px solid #ff336633", borderRadius: 5, padding: "9px 13px", fontSize: 12, color: "#ff8899", display: "flex", gap: 8 }}>
              <span>⚠</span><span>{error}</span>
            </div>
          )}

          {!script && (
            <button onClick={generate} disabled={loading}
              style={{ padding: "11px 20px", background: loading ? "#0a0a18" : NC + "15", border: "1px solid " + (loading ? "#1a1a2e" : NC + "44"), borderRadius: 7, color: loading ? "#3a3a5a" : NC, fontSize: 12, fontFamily: "JetBrains Mono,monospace", letterSpacing: 2, cursor: loading ? "not-allowed" : "pointer", fontWeight: 700, transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {loading ? (
                <><div style={{ width: 14, height: 14, border: "2px solid #1a1a2e", borderTopColor: NC, borderRadius: "50%", animation: "teSpin 0.7s linear infinite" }} />Generating script...</>
              ) : "💼 Get Negotiation Script"}
            </button>
          )}

          {script && !loading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {sections.map(function(sec) {
                return (
                  <div key={sec.key} style={{ background: "#050E1E", border: "1px solid " + sec.color + "22", borderLeft: "3px solid " + sec.color, borderRadius: 8, padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ fontSize: 13 }}>{sec.icon}</span>
                        <span style={{ fontSize: 8, letterSpacing: 2, color: sec.color, fontFamily: "JetBrains Mono,monospace", fontWeight: 700 }}>{sec.label}</span>
                      </div>
                      <button onClick={function() { copySection(sec.key, sec.value); }}
                        style={{ fontSize: 9, padding: "3px 9px", background: copied[sec.key] ? sec.color + "18" : "transparent", border: "1px solid " + (copied[sec.key] ? sec.color + "44" : "#1a1a2e"), borderRadius: 4, color: copied[sec.key] ? sec.color : "#475569", cursor: "pointer", fontFamily: "JetBrains Mono,monospace", letterSpacing: 1, transition: "all 0.2s" }}>
                        {copied[sec.key] ? "✓ COPIED" : "⎘ COPY"}
                      </button>
                    </div>
                    <div style={{ fontSize: 13, color: "#C8D8E8", lineHeight: 1.75, fontStyle: "italic", background: sec.color + "06", borderRadius: 5, padding: "10px 12px" }}>
                      {sec.value || "—"}
                    </div>
                  </div>
                );
              })}

              {script.key_phrases && script.key_phrases.length > 0 && (
                <div style={{ background: "#050E1E", border: "1px solid " + NC + "22", borderLeft: "3px solid " + NC, borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <span style={{ fontSize: 13 }}>🗣</span>
                      <span style={{ fontSize: 8, letterSpacing: 2, color: NC, fontFamily: "JetBrains Mono,monospace", fontWeight: 700 }}>KEY PHRASES</span>
                    </div>
                    <button onClick={function() { copySection("key_phrases", script.key_phrases.join("\n")); }}
                      style={{ fontSize: 9, padding: "3px 9px", background: copied["key_phrases"] ? NC + "18" : "transparent", border: "1px solid " + (copied["key_phrases"] ? NC + "44" : "#1a1a2e"), borderRadius: 4, color: copied["key_phrases"] ? NC : "#475569", cursor: "pointer", fontFamily: "JetBrains Mono,monospace", letterSpacing: 1, transition: "all 0.2s" }}>
                      {copied["key_phrases"] ? "✓ COPIED" : "⎘ COPY ALL"}
                    </button>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {script.key_phrases.map(function(phrase, i) {
                      return (
                        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", background: NC + "06", borderRadius: 5, border: "1px solid " + NC + "18" }}>
                          <span style={{ color: NC, fontFamily: "JetBrains Mono,monospace", fontSize: 10, flexShrink: 0, paddingTop: 2 }}>{i + 1}.</span>
                          <span style={{ fontSize: 12, color: "#C8D8E8", lineHeight: 1.65, fontStyle: "italic", flex: 1 }}>{phrase}</span>
                          <button onClick={function() { copySection("phrase_" + i, phrase); }}
                            style={{ fontSize: 9, padding: "2px 7px", background: copied["phrase_" + i] ? NC + "18" : "transparent", border: "1px solid " + (copied["phrase_" + i] ? NC + "33" : "#1a1a2e"), borderRadius: 3, color: copied["phrase_" + i] ? NC : "#334155", cursor: "pointer", flexShrink: 0, fontFamily: "JetBrains Mono,monospace", transition: "all 0.2s" }}>
                            {copied["phrase_" + i] ? "✓" : "⎘"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <button onClick={function() { setScript(null); setError(null); }}
                style={{ marginTop: 4, alignSelf: "flex-start", fontSize: 9, padding: "4px 12px", background: "transparent", border: "1px solid #1a1a2e", borderRadius: 5, color: "#334155", cursor: "pointer", fontFamily: "JetBrains Mono,monospace", letterSpacing: 1 }}>
                ↺ REGENERATE
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -- P4 STATS ENGINE -----------------------------------------------
var TE_STATS_KEY = "te_user_stats";
function p4GetStats() {
  try { var s = memGet(TE_STATS_KEY); return s || { scans:0, resumes:0, paths:0, jd:0, lastUsed:null, streak:0, lastDate:null }; }
  catch(e) { return { scans:0, resumes:0, paths:0, jd:0, lastUsed:null, streak:0, lastDate:null }; }
}
function p4TrackUsage(type) {
  try {
    var s = p4GetStats();
    var today = new Date().toDateString();
    if (!s.lastDate) { s.streak = 1; }
    else if (s.lastDate !== today) {
      var yest = new Date(); yest.setDate(yest.getDate() - 1);
      s.streak = (s.lastDate === yest.toDateString()) ? ((s.streak || 0) + 1) : 1;
    }
    s.lastDate = today; s.lastUsed = Date.now();
    if (type === "scan") s.scans = (s.scans || 0) + 1;
    else if (type === "resume") s.resumes = (s.resumes || 0) + 1;
    else if (type === "paths") s.paths = (s.paths || 0) + 1;
    else if (type === "jd") s.jd = (s.jd || 0) + 1;
    try { memSet(TE_STATS_KEY, s); } catch(me) {}
  } catch(e) {}
}

// -- STATS DASHBOARD (home screen) ---------------------------------
function StatsDashboard() {
  var sv = useState(null); var stats = sv[0]; var setStats = sv[1];
  useEffect(function() { try { setStats(p4GetStats()); } catch(e) {} }, []);
  if (!stats) return null;
  var total = (stats.scans||0)+(stats.resumes||0)+(stats.paths||0)+(stats.jd||0);
  if (total === 0) return null;
  return (
    <div style={{ background:"rgba(5,14,30,0.7)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:12,padding:"14px 18px",marginBottom:24,backdropFilter:"blur(10px)" }}>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8 }}>
        <div style={{ display:"flex",alignItems:"center",gap:8 }}>
          <div style={{ width:6,height:6,borderRadius:"50%",background:G,animation:"tePulse 2s infinite",boxShadow:"0 0 6px "+G+"66" }}/>
          <span style={{ fontSize:9,color:DS.t2,letterSpacing:2.5,fontFamily:"JetBrains Mono,monospace" }}>YOUR INTELLIGENCE HISTORY</span>
        </div>
        {(stats.streak||0) > 0 && (
          <div style={{ display:"flex",alignItems:"center",gap:5,background:G+"14",border:"1px solid "+G+"33",borderRadius:100,padding:"3px 10px" }}>
            <span style={{ fontSize:11 }}>🔥</span>
            <span style={{ fontSize:9,color:G,fontFamily:"JetBrains Mono,monospace",fontWeight:700 }}>{stats.streak} DAY STREAK</span>
          </div>
        )}
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(88px,1fr))",gap:8 }}>
        {[
          { label:"SCANS",    value:stats.scans||0,   color:"#EF4444", icon:"🛡" },
          { label:"RESUMES",  value:stats.resumes||0, color:"#3B82F6", icon:"📄" },
          { label:"PATHS",    value:stats.paths||0,   color:"#00E5A0", icon:"🧭" },
          { label:"JD READS", value:stats.jd||0,      color:"#38BDF8", icon:"📋" },
        ].map(function(item){
          return <div key={item.label} style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:8,padding:"10px 6px",textAlign:"center" }}>
            <div style={{ fontSize:16,marginBottom:4 }}>{item.icon}</div>
            <div style={{ fontSize:22,fontWeight:900,color:item.color,fontFamily:"JetBrains Mono,monospace",lineHeight:1 }}>{item.value}</div>
            <div style={{ fontSize:8,color:DS.t3,letterSpacing:1.5,fontFamily:"JetBrains Mono,monospace",marginTop:4 }}>{item.label}</div>
          </div>;
        })}
      </div>
    </div>
  );
}

// -- NEXT ACTION CARD (after every result) -------------------------
function NextActionCard({ moduleType }) {
  var CFGS = {
    scan:      { color:"#EF4444", bg:"rgba(239,68,68,0.05)",   border:"rgba(239,68,68,0.18)",   actions:[{ label:"Scan another opportunity", icon:"🛡", mode:"scan" }, { label:"Analyze your resume", icon:"📄", mode:"resume" }] },
    resume:    { color:"#3B82F6", bg:"rgba(59,130,246,0.05)",  border:"rgba(59,130,246,0.18)",  actions:[{ label:"Improve your score and scan again", icon:"📄", mode:"resume" }, { label:"Explore your career paths", icon:"🧭", mode:"pathfinder" }] },
    pathfinder:{ color:"#00E5A0", bg:"rgba(0,229,160,0.05)",   border:"rgba(0,229,160,0.18)",   actions:[{ label:"Explore another path or refine your goal", icon:"🧭", mode:"pathfinder" }, { label:"Match this job with your resume", icon:"📄", mode:"resume" }] },
    jd:        { color:"#38BDF8", bg:"rgba(56,189,248,0.05)",  border:"rgba(56,189,248,0.18)",  actions:[{ label:"Match this job with your resume", icon:"📄", mode:"resume" }, { label:"Scan this job for threats", icon:"🛡", mode:"scan" }] },
    career:    { color:"#8B5CF6", bg:"rgba(139,92,246,0.05)",  border:"rgba(139,92,246,0.18)",  actions:[{ label:"Scan a job matching your level", icon:"🛡", mode:"scan" }, { label:"Decode a target job description", icon:"📋", mode:"jd" }] },
    hr:        { color:"#F59E0B", bg:"rgba(245,158,11,0.05)",  border:"rgba(245,158,11,0.18)",  actions:[{ label:"Rank a new set of candidates", icon:"🎯", mode:"hr" }, { label:"Verify the company behind the role", icon:"🛡", mode:"scan" }] },
  };
  var cfg = CFGS[moduleType] || CFGS.scan;
  useEffect(function() { try { p4TrackUsage(moduleType); } catch(e) {} }, [moduleType]);
  return (
    <div style={{ marginTop:20,background:cfg.bg,border:"1px solid "+cfg.border,borderRadius:12,padding:"14px 18px",boxShadow:"0 4px 16px rgba(0,0,0,0.2)" }}>
      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
        <div style={{ width:5,height:5,borderRadius:"50%",background:cfg.color,animation:"tePulse 2s infinite",boxShadow:"0 0 6px "+cfg.color }}/>
        <span style={{ fontSize:8,color:cfg.color,letterSpacing:3,fontFamily:"'JetBrains Mono',monospace",fontWeight:700 }}>NEXT ACTION</span>
      </div>
      <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
        {cfg.actions.map(function(act, i){
          return (
            <button key={i} onClick={function(){ try { p4Navigate(act.mode); } catch(ex){} }}
              style={{ display:"flex",alignItems:"center",gap:8,padding:"9px 16px",background:i===0?cfg.color+"14":"transparent",border:"1px solid "+(i===0?cfg.color+"40":"rgba(255,255,255,0.06)"),borderRadius:100,color:i===0?cfg.color:DS.t3,fontSize:12,fontWeight:i===0?700:500,cursor:"pointer",transition:"all 0.2s",flexShrink:0,letterSpacing:0.2 }}
              onMouseEnter={function(e){e.currentTarget.style.background=cfg.color+"20";e.currentTarget.style.borderColor=cfg.color+"55";e.currentTarget.style.color=cfg.color;}}
              onMouseLeave={function(e){e.currentTarget.style.background=i===0?cfg.color+"14":"transparent";e.currentTarget.style.borderColor=i===0?cfg.color+"40":"rgba(255,255,255,0.06)";e.currentTarget.style.color=i===0?cfg.color:DS.t3;}}>
              <span style={{fontSize:12}}>{act.icon}</span>
              <span>{act.label}</span>
              {i===0&&<span style={{ fontSize:13,color:cfg.color+"88" }}>›</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -- HOOK BANNER (rotating insight messages) -----------------------
var P6_HOOKS = [
  "₹3 LPA → ₹20 LPA? Or wrong path?",
  "Before applying — verify the job first",
  "Most people fail because of wrong decisions",
  "1 in 3 job posts in India is fake or misleading",
  "Your resume may be filtered out before a human sees it",
];
function HookBanner() {
  var sv = useState(0); var idx = sv[0]; var setIdx = sv[1];
  var fv = useState(true); var fading = fv[0]; var setFading = fv[1];
  useEffect(function() {
    var t = setInterval(function() {
      setFading(false);
      setTimeout(function() {
        setIdx(function(i) { return (i + 1) % P6_HOOKS.length; });
        setFading(true);
      }, 300);
    }, 3500);
    return function() { clearInterval(t); };
  }, []);
  return (
    <div style={{ background:"rgba(201,168,76,0.04)",border:"1px solid rgba(201,168,76,0.12)",borderRadius:10,padding:"11px 18px",marginBottom:24,display:"flex",alignItems:"center",justifyContent:"center",gap:10 }}>
      <div style={{ width:5,height:5,borderRadius:"50%",background:G,flexShrink:0,animation:"tePulse 2.5s ease infinite" }}/>
      <div style={{ opacity:fading?1:0,transition:"opacity 0.28s ease",fontSize:12,fontWeight:600,color:"#c9a84c",letterSpacing:0.4 }}>
        {P6_HOOKS[idx]}
      </div>
    </div>
  );
}

// -- SOCIAL PROOF BAR -------------------------------------------
function SocialProofBar() {
  var sv = useState(null); var count = sv[0]; var setCount = sv[1];
  useEffect(function() {
    sbStats().then(function(s) {
      if (s && s.total > 0) {
        var target = s.total;
        var step = Math.ceil(target / 60); var current = 0;
        var t = setInterval(function() {
          current += step;
          if (current >= target) { current = target; clearInterval(t); }
          setCount(current);
        }, 16);
      } else {
        setCount(null);
      }
    }).catch(function() { setCount(null); });
  }, []);
  if (!count) return null;
  return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:10 }}>
      <div style={{ display:"flex",gap:0 }}>
        {["#22C55E","#3B82F6","#F59E0B","#8B5CF6","#EF4444"].map(function(c,i){
          return <div key={i} style={{ width:20,height:20,borderRadius:"50%",background:"linear-gradient(135deg,"+c+"44,"+c+"22)",border:"1.5px solid "+c+"55",marginLeft:i>0?-7:0,boxShadow:"0 2px 6px "+c+"20" }}/>;
        })}
      </div>
      <span style={{ fontSize:11,color:DS.t3,letterSpacing:0.3 }}>
        Trusted by <span style={{ color:DS.t2,fontWeight:700 }}>{count.toLocaleString("en-IN")}+</span> job seekers
      </span>
    </div>
  );
}

// -- P6 STATS WIDGET (home screen) --------------------------------
function P6StatsWidget() {
  var sv = useState(p6GetActivity()); var stats = sv[0]; var setStats = sv[1];
  useEffect(function() { setStats(p6GetActivity()); }, []);
  var total = (stats.scanCount||0) + (stats.resumeCount||0) + (stats.pathCount||0);
  if (total === 0) return null;
  return (
    <div style={{ background:"rgba(5,14,30,0.7)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:12,padding:"14px 18px",marginBottom:20,backdropFilter:"blur(10px)" }}>
      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
        <div style={{ width:6,height:6,borderRadius:"50%",background:"#22C55E",animation:"tePulse 2s infinite",boxShadow:"0 0 6px #22C55E66" }}/>
        <span style={{ fontSize:9,color:DS.t3,letterSpacing:2.5,fontFamily:"JetBrains Mono,monospace" }}>YOUR GROWTH ACTIVITY</span>
        <span style={{ marginLeft:"auto",fontSize:9,color:DS.t3,fontFamily:"JetBrains Mono,monospace" }}>Last: {p6LastActiveLabel(stats.lastScan)}</span>
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8 }}>
        {[
          { label:"SCANS",   value:stats.scanCount||0,   color:"#EF4444", icon:"🛡" },
          { label:"RESUMES", value:stats.resumeCount||0, color:"#3B82F6", icon:"📄" },
          { label:"PATHS",   value:stats.pathCount||0,   color:"#00E5A0", icon:"🧭" },
        ].map(function(item){
          return <div key={item.label} style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:8,padding:"10px 6px",textAlign:"center" }}>
            <div style={{ fontSize:16,marginBottom:4 }}>{item.icon}</div>
            <div style={{ fontSize:22,fontWeight:900,color:item.color,fontFamily:"JetBrains Mono,monospace",lineHeight:1 }}>{item.value}</div>
            <div style={{ fontSize:8,color:DS.t3,letterSpacing:1.5,fontFamily:"JetBrains Mono,monospace",marginTop:4 }}>{item.label}</div>
          </div>;
        })}
      </div>
    </div>
  );
}

// -- P6 SHARE RESULT BLOCK (after every result) ------------------
function P6ShareResultBlock({ result, type }) {
  if (!result) return null;
  var cv = useState(false); var copied = cv[0]; var setCopied = cv[1];
  var score = result.risk_score || result.trust_score || result.ats_score || 0;
  var verdict = result.verdict || "Unknown";
  var text = "";
  if (type === "scan") text = "⚠️ This job scored " + score + "/100 (" + verdict + "). I checked it using Trust Engine. Don't apply blindly.";
  else if (type === "resume") text = "My resume scored " + score + "/100. Found major gaps using Trust Engine.";
  else if (type === "pathfinder") text = "I discovered 3 real career paths based on my resume using Trust Engine. This changes everything.";
  else text = "Just analyzed using Trust Engine — India's job intelligence platform.";
  text += "\n\nTry it: https://trust-engine-ashen.vercel.app";
  var showTrigger = (type === "scan" && score >= 60) || (type === "resume" && score < 60);
  var triggerMsg = type === "scan" ? "⚠️ This looks risky — share to warn others" : "Your resume needs improvement — share and get feedback";
  function doWhatsApp() {
    try { if (navigator.vibrate) navigator.vibrate(10); } catch(e) {}
    window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
  }
  function doCopy() {
    try { if (navigator.vibrate) navigator.vibrate(10); } catch(e) {}
    try { navigator.clipboard.writeText(text).catch(function() { var ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); }); } catch(e) {}
    setCopied(true); setTimeout(function() { setCopied(false); }, 2000);
  }
  return (
    <div style={{ marginTop:20,background:"rgba(5,14,30,0.8)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"16px 18px" }}>
      {showTrigger && <div style={{ background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:8,padding:"9px 13px",marginBottom:12,fontSize:12,color:"#EF4444",fontWeight:600 }}>{triggerMsg}</div>}
      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
        <div style={{ width:5,height:5,borderRadius:"50%",background:"#25D366",animation:"tePulse 2s infinite" }}/>
        <span style={{ fontSize:9,color:"#25D366",letterSpacing:3,fontFamily:"JetBrains Mono,monospace",fontWeight:700 }}>SHARE RESULT</span>
      </div>
      <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
        <button onClick={doWhatsApp} style={{ flex:2,minWidth:140,display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"11px 16px",background:"rgba(37,211,102,0.12)",border:"1px solid rgba(37,211,102,0.35)",borderRadius:9,color:"#25D366",fontSize:12,fontWeight:700,cursor:"pointer",transition:"all 0.18s ease" }}
          onMouseEnter={function(e){e.currentTarget.style.background="rgba(37,211,102,0.2)";}}
          onMouseLeave={function(e){e.currentTarget.style.background="rgba(37,211,102,0.12)";}}>
          💬 Share on WhatsApp
        </button>
        <button onClick={doCopy} style={{ flex:1,minWidth:100,display:"flex",alignItems:"center",justifyContent:"center",gap:7,padding:"11px 14px",background:copied?"rgba(34,197,94,0.12)":"rgba(255,255,255,0.04)",border:"1px solid "+(copied?"rgba(34,197,94,0.35)":"rgba(255,255,255,0.1)"),borderRadius:9,color:copied?"#22C55E":"#94A3B8",fontSize:12,fontWeight:700,cursor:"pointer",transition:"all 0.18s ease" }}>
          {copied ? "✓ Copied!" : "⎘ Copy"}
        </button>
      </div>
    </div>
  );
}

// -- P6 NEXT ACTION BLOCK -----------------------------------------
function P6NextActionBlock({ type }) {
  var CFGS = {
    scan:      { color:"#EF4444", bg:"rgba(239,68,68,0.05)",  border:"rgba(239,68,68,0.18)",  hint:"New scams appear daily — check again tomorrow", actions:[{l:"Scan Another Job",mode:"scan"},{l:"Check Company Profile",mode:"scan"}] },
    resume:    { color:"#3B82F6", bg:"rgba(59,130,246,0.05)", border:"rgba(59,130,246,0.18)", hint:"Improve your score by +15 points → rescan",     actions:[{l:"Improve & Rescan",mode:"resume"},{l:"Generate Cover Letter",mode:"resume"}] },
    pathfinder:{ color:"#00E5A0", bg:"rgba(0,229,160,0.05)",  border:"rgba(0,229,160,0.18)",  hint:"Your next step is Phase 1 — start now",          actions:[{l:"Explore Another Path",mode:"pathfinder"},{l:"Adjust Your Goal",mode:"pathfinder"}] },
  };
  var cfg = CFGS[type] || CFGS.scan;
  return (
    <div style={{ marginTop:16,background:cfg.bg,border:"1px solid "+cfg.border,borderRadius:12,padding:"16px 18px" }}>
      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}>
        <div style={{ width:5,height:5,borderRadius:"50%",background:cfg.color,animation:"tePulse 2s infinite" }}/>
        <span style={{ fontSize:9,color:cfg.color,letterSpacing:3,fontFamily:"JetBrains Mono,monospace",fontWeight:700 }}>NEXT STEP</span>
      </div>
      <div style={{ fontSize:11,color:cfg.color,fontFamily:"JetBrains Mono,monospace",marginBottom:12,opacity:0.8 }}>{cfg.hint}</div>
      <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
        {cfg.actions.map(function(act,i){
          return (
            <button key={i} onClick={function(){ try { p4Navigate(act.mode); } catch(e) {} }}
              style={{ flex:1,minWidth:130,padding:"10px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid "+cfg.border,borderRadius:9,color:cfg.color,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"JetBrains Mono,monospace",letterSpacing:0.5,transition:"all 0.18s ease",textAlign:"center" }}
              onMouseEnter={function(e){e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 6px 20px "+cfg.color+"22";}}
              onMouseLeave={function(e){e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="none";}}>
              {act.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -- GLOBAL DECISION PANEL (cross-module intelligence) -------------
function GlobalDecisionPanel() {
  var sv = useState(function() { try { return s3ComputeGlobalDecision(); } catch(e) { return null; } });
  var decision = sv[0]; var setDecision = sv[1];
  var sa = useState(false); var animate = sa[0]; var setAnimate = sa[1];
  useEffect(function() {
    var fn = function(d) { setDecision(d); setAnimate(false); setTimeout(function() { setAnimate(true); }, 30); };
    S3_DECISION_LISTENERS.add(fn);
    setTimeout(function() { setAnimate(true); }, 100);
    return function() { S3_DECISION_LISTENERS.delete(fn); };
  }, []);
  if (!decision || decision.final_decision === "INSUFFICIENT_DATA") return null;
  var DC = {
    APPLY:  { color:"#22C55E", bg:"rgba(34,197,94,0.05)",   border:"rgba(34,197,94,0.18)",   glow:"rgba(34,197,94,0.12)",   icon:"✓", label:"SAFE TO APPLY" },
    VERIFY: { color:"#F59E0B", bg:"rgba(245,158,11,0.05)",  border:"rgba(245,158,11,0.18)",  glow:"rgba(245,158,11,0.10)",  icon:"⚠", label:"VERIFY FIRST" },
    AVOID:  { color:"#EF4444", bg:"rgba(239,68,68,0.05)",   border:"rgba(239,68,68,0.18)",   glow:"rgba(239,68,68,0.10)",   icon:"✕", label:"DO NOT ENGAGE" }
  };
  var cfg = DC[decision.final_decision] || DC.VERIFY;
  var sColors = { strong:"#22C55E", moderate:"#F59E0B", weak:"#94A3B8", insufficient:"#334155", stable:"#64748B" };
  var sColor = sColors[decision.decision_strength] || "#475569";
  var scamPct  = decision.scam_probability    != null ? Math.min(100, Math.max(0, decision.scam_probability))    : null;
  var succPct  = decision.success_probability != null ? Math.min(100, Math.max(0, decision.success_probability)) : null;
  var impScore = decision.improvement_score   != null ? decision.improvement_score : null;
  var hasPred  = (scamPct !== null || succPct !== null);
  var confColor = decision.confidence_score >= 75 ? "#22C55E" : decision.confidence_score >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div style={{ background:"#030912",border:"1px solid "+cfg.border,borderRadius:14,marginBottom:16,overflow:"hidden",boxShadow:"0 0 40px "+cfg.glow,animation:animate?"teFadeIn 0.4s ease":"none" }}>
      <div style={{ height:3,background:"linear-gradient(90deg,"+cfg.color+"00,"+cfg.color+","+cfg.color+"00)" }}/>
      <div style={{ padding:"16px 18px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
        <div style={{ display:"flex",alignItems:"center",gap:14 }}>
          <div style={{ width:44,height:44,borderRadius:12,background:cfg.color+"18",border:"1px solid "+cfg.color+"30",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:900,color:cfg.color,flexShrink:0,boxShadow:"0 0 20px "+cfg.color+"22" }}>{cfg.icon}</div>
          <div>
            <div style={{ display:"flex",alignItems:"center",gap:7,marginBottom:4 }}>
              <div style={{ fontSize:7,color:DS.t3,letterSpacing:3,fontFamily:"JetBrains Mono,monospace",fontWeight:700 }}>STAGE 5 · INTELLIGENCE DECISION</div>
              {decision.adaptive_weights_used && <span style={{ fontSize:7,color:"#C084FC",fontFamily:"JetBrains Mono,monospace",letterSpacing:1,background:"rgba(192,132,252,0.1)",padding:"1px 6px",borderRadius:3,border:"1px solid rgba(192,132,252,0.2)" }}>⚡ ADAPTIVE</span>}
            </div>
            <div style={{ fontSize:17,fontWeight:900,color:cfg.color,letterSpacing:0.3,lineHeight:1 }}>{cfg.label}</div>
          </div>
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:10,flexShrink:0 }}>
          {decision.decision_strength && decision.decision_strength !== "insufficient" && (
            <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:3 }}>
              <div style={{ width:6,height:6,borderRadius:"50%",background:sColor,boxShadow:"0 0 8px "+sColor }}/>
              <span style={{ fontSize:7,color:sColor,fontFamily:"JetBrains Mono,monospace",letterSpacing:1.5,textTransform:"uppercase" }}>{decision.decision_strength}</span>
            </div>
          )}
          <div style={{ textAlign:"center",padding:"8px 14px",background:"rgba(255,255,255,0.02)",borderRadius:10,border:"1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ fontSize:28,fontWeight:900,color:confColor,fontFamily:"JetBrains Mono,monospace",lineHeight:1 }}>{decision.confidence_score}<span style={{ fontSize:13,fontWeight:700 }}>%</span></div>
            <div style={{ fontSize:7,color:"#334155",letterSpacing:2,fontFamily:"JetBrains Mono,monospace",marginTop:3 }}>CONFIDENCE</div>
          </div>
        </div>
      </div>
      {decision.reasoning && <div style={{ padding:"12px 18px",borderBottom:"1px solid rgba(255,255,255,0.03)",fontSize:11,color:"#64748B",lineHeight:1.75 }}>{decision.reasoning}</div>}
      {(scamPct!==null||succPct!==null)&&<div style={{ padding:"8px 18px",borderBottom:"1px solid rgba(255,255,255,0.02)",fontSize:10,color:"#334155",fontFamily:"JetBrains Mono,monospace",letterSpacing:1,display:"flex",gap:16 }}>{scamPct!==null&&<span>SCAM RISK <span style={{color:"#EF4444",fontWeight:700}}>{scamPct}%</span></span>}{succPct!==null&&<span>JOB SUCCESS <span style={{color:"#22C55E",fontWeight:700}}>{succPct}%</span></span>}{scamPct!==null&&succPct!==null&&<span>COMBINED <span style={{color:"#F59E0B",fontWeight:700}}>{Math.round((succPct+Math.max(0,100-scamPct))/2)}%</span></span>}</div>}
      {hasPred && (
        <div style={{ padding:"14px 18px",display:"flex",gap:10,flexWrap:"wrap",borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
          {scamPct !== null && <div style={{ flex:1,minWidth:110 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6 }}><span style={{ fontSize:9,color:"#EF4444",fontFamily:"JetBrains Mono,monospace",letterSpacing:1.5 }}>SCAM PROB</span><span style={{ fontSize:16,fontWeight:900,color:"#EF4444",fontFamily:"JetBrains Mono,monospace" }}>{scamPct}%</span></div>
            <div style={{ height:4,borderRadius:4,background:"rgba(239,68,68,0.1)",overflow:"hidden" }}><div style={{ height:"100%",width:(animate?scamPct:0)+"%",background:"linear-gradient(90deg,#EF4444aa,#EF4444)",borderRadius:4,transition:"width 1s cubic-bezier(.4,0,.2,1)" }}/></div>
          </div>}
          {succPct !== null && <div style={{ flex:1,minWidth:110 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6 }}><span style={{ fontSize:9,color:"#22C55E",fontFamily:"JetBrains Mono,monospace",letterSpacing:1.5 }}>SUCCESS PROB</span><span style={{ fontSize:16,fontWeight:900,color:"#22C55E",fontFamily:"JetBrains Mono,monospace" }}>{succPct}%</span></div>
            <div style={{ height:4,borderRadius:4,background:"rgba(34,197,94,0.1)",overflow:"hidden" }}><div style={{ height:"100%",width:(animate?succPct:0)+"%",background:"linear-gradient(90deg,#22C55Eaa,#22C55E)",borderRadius:4,transition:"width 1.1s cubic-bezier(.4,0,.2,1) 0.1s" }}/></div>
          </div>}
          {impScore !== null && <div style={{ flex:1,minWidth:110 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6 }}><span style={{ fontSize:9,color:"#3B82F6",fontFamily:"JetBrains Mono,monospace",letterSpacing:1.5 }}>IMPROVEMENT</span><span style={{ fontSize:16,fontWeight:900,color:"#3B82F6",fontFamily:"JetBrains Mono,monospace" }}>{impScore}</span></div>
            <div style={{ height:4,borderRadius:4,background:"rgba(59,130,246,0.1)",overflow:"hidden" }}><div style={{ height:"100%",width:(animate?impScore:0)+"%",background:"linear-gradient(90deg,#3B82F6aa,#3B82F6)",borderRadius:4,transition:"width 1.2s cubic-bezier(.4,0,.2,1) 0.2s" }}/></div>
          </div>}
        </div>
      )}
      {decision.next_recommended_action && (
        <div style={{ padding:"12px 18px",borderBottom:"1px solid rgba(255,255,255,0.03)",display:"flex",gap:10,alignItems:"flex-start" }}>
          <div style={{ width:20,height:20,borderRadius:6,background:cfg.color+"18",border:"1px solid "+cfg.color+"28",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:cfg.color,flexShrink:0,marginTop:1 }}>→</div>
          <div style={{ fontSize:11,color:"#94A3B8",lineHeight:1.65 }}><span style={{ color:cfg.color,fontWeight:700 }}>Next: </span>{decision.next_recommended_action}</div>
        </div>
      )}
      {decision.dominant_factors && decision.dominant_factors.length > 0 && (
        <div style={{ padding:"10px 18px",display:"flex",flexWrap:"wrap",gap:6 }}>
          {decision.dominant_factors.slice(0,3).map(function(f,i){
            return <div key={i} style={{ fontSize:9.5,color:"#475569",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:5,padding:"4px 10px",fontFamily:"JetBrains Mono,monospace",letterSpacing:0.3,lineHeight:1.5 }}><span style={{ color:cfg.color+"66",marginRight:5 }}>▸</span>{f}</div>;
          })}
        </div>
      )}
    </div>
  );
}

function p4Navigate(mode) {
  try { window.dispatchEvent(new CustomEvent("te_p4_navigate", { detail: { mode: mode } })); } catch(e) {}
}

// -- ONBOARDING OVERLAY ------------------------------------------
var P6_ACTIVITY_KEY = "te_user_activity";
function p6TrackActivity(type) {
  try {
    var _raw = null; try { _raw = localStorage.getItem(P6_ACTIVITY_KEY); } catch(le) {}
    var d = (function(){ try { return JSON.parse(_raw || "{}"); } catch(e) { return {}; } })();
    if (type === "scan")       d.scanCount    = (d.scanCount    || 0) + 1;
    if (type === "resume")     d.resumeCount  = (d.resumeCount  || 0) + 1;
    if (type === "pathfinder") d.pathCount    = (d.pathCount    || 0) + 1;
    d.lastScan = Date.now();
    try { if (typeof localStorage !== "undefined") localStorage.setItem(P6_ACTIVITY_KEY, JSON.stringify(d)); } catch(le) {}
  } catch(e) {}
}
function p6GetActivity() {
  try { var _r = null; try { _r = localStorage.getItem(P6_ACTIVITY_KEY); } catch(le) {} return JSON.parse(_r || "{}"); } catch(e) { return {}; }
}
function p6LastActiveLabel(ts) {
  if (!ts) return "Never";
  var diff = Date.now() - ts;
  var mins = Math.floor(diff / 60000);
  if (mins < 2)   return "Just now";
  if (mins < 60)  return mins + " min ago";
  var hrs = Math.floor(mins / 60);
  if (hrs < 24)   return hrs + " hr ago";
  var days = Math.floor(hrs / 24);
  return days + " day" + (days > 1 ? "s" : "") + " ago";
}

// -- P6 SYSTEM 6: SOCIAL PROOF BAR -------------------------------

// ── ThinkingStep — individual step with typewriter reveal on active ──
function ThinkingStep({ label, done, active, color, idx }) {
  var c = color;
  var sv = useState(""); var displayed = sv[0]; var setDisplayed = sv[1];
  useEffect(function() {
    if (!active) { setDisplayed(label || ""); return; }
    setDisplayed("");
    var i = 0; var full = label || "";
    var interval = setInterval(function() {
      i += 3;
      setDisplayed(full.slice(0, i));
      if (i >= full.length) clearInterval(interval);
    }, 16);
    return function() { clearInterval(interval); };
  }, [label, active]);
  return (
    <div className={active ? "te-step-in" : ""}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 14px",
        background: done ? c + "0A" : active ? DS.bg0 : "transparent",
        borderRadius: 10,
        borderLeft: active ? "2px solid " + c : done ? "2px solid " + c + "40" : "2px solid transparent",
        opacity: idx > -1 ? 1 : 0.2,
        transition: "all 0.3s var(--ease-inout)",
      }}>
      <div style={{
        width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
        background: done ? c + "18" : active ? DS.bg3 : "transparent",
        border: "1.5px solid " + (done ? c + "60" : active ? c : DS.border0),
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.25s ease",
      }}>
        {done ? (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ animation: "teCheckDraw 0.3s ease" }}>
            <path d="M2 5l2.5 2.5 3.5-4" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="20" strokeDashoffset="0"/>
          </svg>
        ) : active ? (
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: c, animation: "tePulse 0.8s ease infinite" }}/>
        ) : null}
      </div>
      <span style={{ fontSize: 12, color: done ? c + "88" : active ? DS.t1 : DS.t4, fontWeight: active ? 600 : 400, flex: 1, transition: "color 0.3s" }}>
        {active ? displayed : label}
        {active && displayed.length < (label || "").length && (
          <span style={{ display: "inline-block", width: 2, height: 12, background: c, marginLeft: 2, animation: "tePulse 0.6s ease infinite", verticalAlign: "middle" }}/>
        )}
      </span>
      {active && (
        <div style={{ width: 32, height: 2, background: DS.bg3, borderRadius: 2, overflow: "hidden", flexShrink: 0 }}>
          <div style={{ height: "100%", width: "65%", background: "linear-gradient(90deg,transparent," + c + ",transparent)", animation: "teShimmer 1.4s ease infinite" }}/>
        </div>
      )}
    </div>
  );
}

function Thinking({ steps, currentStep, progress, color, title, fullscreen }) {
  var c = color||DS.blue;
  if (fullscreen) {
    return (
      <div style={{ position:"fixed",inset:0,zIndex:500,background:"rgba(1,8,18,0.97)",backdropFilter:"blur(20px)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px",animation:"teFadeIn 0.3s ease" }}>
        <div style={{ width:"100%",maxWidth:480 }}>
          <div style={{ textAlign:"center",marginBottom:32 }}>
            <div className="te-gold-pulse" style={{ width:72,height:72,borderRadius:"50%",background:c+"12",border:"1.5px solid "+c+"40",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",boxShadow:"0 0 48px "+c+"22" }}>
              <Icon name="shield" size={30} color={c}/>
            </div>
            <div style={{ fontSize:16,fontWeight:700,color:c,marginBottom:8,letterSpacing:"-0.2px" }}>{title||"Analysing..."}</div>
            <div style={{ fontSize:40,fontWeight:900,color:c,fontFamily:"'JetBrains Mono',monospace",lineHeight:1,letterSpacing:"-2px" }}>{Math.round(progress)}<span style={{ fontSize:18,fontWeight:500,opacity:0.5 }}>%</span></div>
          </div>
          <div style={{ height:4,background:DS.bg3,borderRadius:2,marginBottom:28,overflow:"hidden" }}>
            <div style={{ height:"100%",width:progress+"%",background:"linear-gradient(90deg,"+c+","+c+"cc)",transition:"width 0.5s ease",boxShadow:"0 0 10px "+c }}/>
          </div>
          <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
            {(steps||[]).map(function(step,i){
              var done=i<currentStep, active=i===currentStep;
              var lbl = typeof step==="string"?step:(step.label||step.sub||step);
              return <ThinkingStep key={i} label={lbl} done={done} active={active} color={c} idx={i>currentStep+2?-1:i}/>;
            })}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ background:DS.bg1, border:"1px solid "+c+"28", borderRadius:16, padding:"24px", marginBottom:20, overflow:"hidden" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background:c, animation:"tePulse 1s ease infinite", boxShadow:"0 0 8px "+c }}/>
          <span style={{ fontSize:13, fontWeight:700, color:c, letterSpacing:"-0.2px" }}>{title||"Analysing..."}</span>
        </div>
        <span style={{ fontSize:12, color:c, fontFamily:"'JetBrains Mono',monospace", fontWeight:700, opacity:0.8 }}>{Math.round(progress)}%</span>
      </div>
      <div style={{ height:2, background:DS.bg3, borderRadius:2, marginBottom:20, overflow:"hidden" }}>
        <div style={{ height:"100%", width:progress+"%", background:"linear-gradient(90deg,"+c+"88,"+c+")", transition:"width 0.5s ease", boxShadow:"0 0 10px "+c+"66" }}/>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
        {(steps||[]).map(function(step,i) {
          var done=i<currentStep, active=i===currentStep;
          var lbl = typeof step==="string"?step:(step.label||step.sub||step);
          return <ThinkingStep key={i} label={lbl} done={done} active={active} color={c} idx={i>currentStep+1?-1:i}/>;
        })}
      </div>
    </div>
  );
}

function AIStatusBar() {
  var aiStatus = useAIStatus();
  if (AI_PROVIDERS_MISSING) {
    return (
      <div style={{ position:"fixed",top:0,left:0,right:0,zIndex:9999,background:"#7f1d1d",borderBottom:"2px solid #ef4444",padding:"10px 24px",display:"flex",alignItems:"center",gap:12,justifyContent:"center" }}>
        <span style={{ fontSize:16 }}>⚠️</span>
        <span style={{ fontSize:13,color:"#fecaca",fontWeight:600 }}>No AI keys configured — add REACT_APP_KEY1 and/or REACT_APP_OR_KEY to your environment and redeploy.</span>
      </div>
    );
  }
  if (!aiStatus.switching && aiStatus.provider==="gemini" && aiStatus.failCount===0) return null;
  var isOR = aiStatus.provider && aiStatus.provider.startsWith("openrouter");
  var isSw = aiStatus.switching;
  var col = isSw?DS.amber:isOR?DS.purple:DS.green;
  return (
    <div style={{ position:"fixed",bottom:24,right:24,zIndex:9999,background:DS.bg1,border:"1px solid "+col+"44",borderRadius:10,padding:"9px 16px",display:"flex",alignItems:"center",gap:10,boxShadow:"0 8px 32px rgba(0,0,0,0.4)",animation:"teUp 0.3s ease" }}>
      <div style={{ width:7,height:7,borderRadius:"50%",background:col,animation:isSw?"tePulse 1s ease infinite":"none" }}/>
      <span style={{ fontSize:12,color:col,fontFamily:"'JetBrains Mono',monospace",letterSpacing:0.3 }}>{isSw?"Switching AI engine...":isOR?"Backup AI active":"Gemini active"}</span>
    </div>
  );
}

function LiveIntelTicker() {
  var stats = getLiveStats();
  var items = ["🛡 "+(stats.total||0)+" total scans","🚨 "+(stats.scams_today||0)+" scams today","⚡ Top: "+(stats.top_pattern||"Registration fee"),"🇮🇳 India Intelligence · Live"];
  var si = useState(0); var idx = si[0]; var setIdx = si[1];
  useEffect(function() { var t = setInterval(function(){ setIdx(function(i){return (i+1)%items.length;}); }, 3200); return function(){clearInterval(t);}; }, []);
  return (
    <div style={{ overflow:"hidden",maxWidth:220,height:18 }}>
      <div key={idx} style={{ fontSize:10,color:DS.t3,fontFamily:"'JetBrains Mono',monospace",letterSpacing:0.3,animation:"teFadeIn 0.4s ease",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
        {items[idx]}
      </div>
    </div>
  );
}

// ===================================================
// VERDICT FEEDBACK
// ===================================================
function VerdictFeedback({ module, verdict }) {
  var sv = useState(null); var sel = sv[0]; var setSel = sv[1];
  if (sel) {
    var c = sel==="accurate"?DS.green:sel==="inaccurate"?DS.red:DS.amber;
    var msg = sel==="accurate"?"Thanks — this improves our accuracy for everyone":sel==="inaccurate"?"Got it — we'll recalibrate":"Partial match noted";
    return <div style={{ display:"flex",alignItems:"center",gap:10,padding:"14px 0",marginTop:16 }}><div style={{ width:8,height:8,borderRadius:"50%",background:c }}/><span style={{ fontSize:12,color:c }}>{msg}</span></div>;
  }
  return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,padding:"12px 16px",borderRadius:10,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",marginTop:20,flexWrap:"wrap" }}>
      <span style={{ fontSize:11,color:DS.t3,letterSpacing:0.3 }}>Was this verdict accurate?</span>
      <div style={{ display:"flex",gap:6 }}>
        {[{l:"✓ Yes",r:"accurate",c:DS.green},{l:"Partial",r:"partial",c:DS.amber},{l:"✕ No",r:"inaccurate",c:DS.red}].map(function(btn){
          return (
            <button key={btn.r} onClick={function(){setSel(btn.r);try{s3RecordFeedback(module||"unknown",verdict||"unknown",btn.r);}catch(e){}}}
              className="te-btn-ghost"
              style={{ fontSize:11,padding:"6px 13px",background:"transparent",border:"1px solid "+btn.c+"25",borderRadius:100,color:btn.c+"66",cursor:"pointer",fontWeight:600,transition:"all 0.18s",letterSpacing:0.3 }}
              onMouseEnter={function(e){e.currentTarget.style.background=btn.c+"14";e.currentTarget.style.color=btn.c;e.currentTarget.style.borderColor=btn.c+"55";}}
              onMouseLeave={function(e){e.currentTarget.style.background="transparent";e.currentTarget.style.color=btn.c+"66";e.currentTarget.style.borderColor=btn.c+"25";}}>
              {btn.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}



// ===================================================
// PHASE 3 — MARKET DIFFERENTIATION FEATURES
// ===================================================

// ── AUDIT KEY ────────────────────────────────────────
var AUDIT_KEY = "te_v6_audit";

// ── 1. TRUST SCORE CERTIFICATE ───────────────────────
function TrustCertificate({ result }) {
  if (!result || result.risk_score >= 40) return null;
  var sv = useState(false); var show = sv[0]; var setShow = sv[1];
  var dv = useState(false); var downloaded = dv[0]; var setDownloaded = dv[1];
  var qe = useState(false); var qrError = qe[0]; var setQrError = qe[1];
  var canvasRef = useRef(null);
  var certId = useMemo(function() { try { return generateShareableId(result); } catch(e) { return "TE-" + Date.now().toString(36).toUpperCase().slice(-8); } }, [result]);
  var ts = new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
  var score = result.risk_score || 0;
  var verdict = result.verdict || "Likely Legitimate";
  var certUrl = "https://trust-engine-ashen.vercel.app?cert=" + certId;

  useEffect(function() {
    var t = setTimeout(function() { setShow(true); }, 200);
    return function() { clearTimeout(t); };
  }, []);

  // Load QR lib lazily with full error handling
  useEffect(function() {
    if (!show) return;
    var cancelled = false;
    function doRender() {
      try {
        var el = document.getElementById("cert-qr-" + certId);
        if (!el || cancelled) return;
        el.innerHTML = "";
        if (!window.QRCode) { if (!cancelled) setQrError(true); return; }
        new window.QRCode(el, { text: certUrl, width: 72, height: 72, colorDark: "#10B981", colorLight: "#050E1E" });
      } catch(e) { if (!cancelled) setQrError(true); }
    }
    if (typeof window !== "undefined" && !window.QRCode) {
      var s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js";
      s.onload = function() { setTimeout(doRender, 80); };
      s.onerror = function() { if (!cancelled) setQrError(true); };
      document.head.appendChild(s);
    } else {
      setTimeout(doRender, 80);
    }
    return function() { cancelled = true; };
  }, [show]);

  function downloadCert() {
    try {
      var canvas = document.createElement("canvas");
      canvas.width = 700; canvas.height = 380;
      var ctx = canvas.getContext("2d");
      // Background
      ctx.fillStyle = "#010812";
      ctx.fillRect(0, 0, 700, 380);
      // Border
      ctx.strokeStyle = "#10B981";
      ctx.lineWidth = 2;
      ctx.strokeRect(12, 12, 676, 356);
      // Top accent
      var grad = ctx.createLinearGradient(0, 0, 700, 0);
      grad.addColorStop(0, "transparent");
      grad.addColorStop(0.5, "#10B981");
      grad.addColorStop(1, "transparent");
      ctx.strokeStyle = grad;
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(12, 12); ctx.lineTo(688, 12); ctx.stroke();
      // Title
      ctx.fillStyle = "#10B981";
      ctx.font = "bold 11px monospace";
      ctx.letterSpacing = "3px";
      ctx.fillText("TRUST ENGINE OS — VERIFIED CERTIFICATE", 40, 55);
      // Score
      ctx.fillStyle = "#10B981";
      ctx.font = "bold 72px monospace";
      ctx.fillText(score, 40, 155);
      ctx.fillStyle = "#4A5F7A";
      ctx.font = "11px monospace";
      ctx.fillText("/ 100  RISK SCORE", 40, 178);
      // Verdict
      ctx.fillStyle = "#F0F6FF";
      ctx.font = "bold 24px sans-serif";
      ctx.fillText(verdict, 40, 230);
      // ID + Date
      ctx.fillStyle = "#4A5F7A";
      ctx.font = "11px monospace";
      ctx.fillText("Certificate ID: " + certId, 40, 300);
      ctx.fillText("Verified: " + ts, 40, 322);
      ctx.fillText("trust-engine-ashen.vercel.app", 40, 344);
      // Download
      var link = document.createElement("a");
      link.download = "trust-certificate-" + certId + ".png";
      link.href = canvas.toDataURL("image/png");
      link.click();
      setDownloaded(true);
      setTimeout(function() { setDownloaded(false); }, 2000);
    } catch(e) { console.error("[Cert]", e); }
  }

  function shareCert() {
    var text = "✅ I verified this job post using Trust Engine.\n\nResult: " + verdict + " | Risk: " + score + "/100\nCertificate ID: " + certId + "\nVerify at: trust-engine-ashen.vercel.app?cert=" + certId + "\n\n#TrustEngine #JobVerified #SafeApply";
    if (navigator.share) {
      navigator.share({ title: "Trust Certificate", text: text }).catch(function() {});
    } else {
      try { navigator.clipboard.writeText(text); } catch(e) {}
    }
  }

  return (
    <div style={{ opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(12px)", transition: "all 0.5s cubic-bezier(0.22,1,0.36,1)", background: DS.green + "06", border: "2px solid " + DS.green + "30", borderRadius: 18, overflow: "hidden", marginBottom: 20 }}>
      <div style={{ height: 3, background: "linear-gradient(90deg," + DS.green + "00," + DS.green + "," + DS.green + "00)" }} />
      <div style={{ padding: "20px 22px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 9, color: DS.green, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, marginBottom: 10 }}>✓ VERIFIED CERTIFICATE</div>
            <div style={{ fontSize: 32, fontWeight: 900, color: DS.green, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1, marginBottom: 4 }}>{score}<span style={{ fontSize: 14, color: DS.t3, fontWeight: 400 }}>/100</span></div>
            <div style={{ fontSize: 16, fontWeight: 700, color: DS.t1, marginBottom: 8 }}>{verdict}</div>
            <div style={{ fontSize: 11, color: DS.t3, fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>ID: {certId}</div>
            <div style={{ fontSize: 11, color: DS.t3, fontFamily: "'JetBrains Mono',monospace", marginBottom: 16 }}>Verified: {ts}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={shareCert} style={{ padding: "9px 16px", background: DS.green + "15", border: "1px solid " + DS.green + "40", borderRadius: 9, color: DS.green, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
                📤 Share Certificate
              </button>
              <button onClick={downloadCert} style={{ padding: "9px 16px", background: downloaded ? DS.green + "20" : DS.bg2, border: "1px solid " + DS.border1, borderRadius: 9, color: downloaded ? DS.green : DS.t2, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}>
                {downloaded ? "✓ Downloaded" : "⬇ Download PNG"}
              </button>
            </div>
          </div>
          <div style={{ flexShrink: 0, background: DS.bg0, border: "1px solid " + DS.green + "25", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            {qrError ? (
              <div style={{ width: 72, height: 72, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
                <div style={{ fontSize: 18 }}>🔗</div>
                <div style={{ fontSize: 8, color: DS.green, textAlign: "center", lineHeight: 1.4, wordBreak: "break-all" }}>{certUrl.slice(0, 36)}…</div>
              </div>
            ) : (
              <div id={"cert-qr-" + certId} style={{ width: 72, height: 72 }} />
            )}
            <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", textAlign: "center" }}>SCAN TO VERIFY</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 2. JOB OFFER NEGOTIATION ASSISTANT ───────────────
var OFFER_NEGO_SYS = "You are India's top salary negotiation expert. Analyze this job offer letter for an Indian professional.\nRespond ONLY with valid JSON:\n{\"salary_verdict\":\"Fair/Below Market/Above Market\",\"market_rate\":\"₹X-Y LPA\",\"your_offered\":\"₹X LPA\",\"gap_percentage\":0,\"negotiable_components\":[{\"component\":\"Base Salary\",\"current\":\"₹X\",\"target\":\"₹Y\",\"tip\":\"How to ask\"}],\"red_flags\":[{\"clause\":\"...\",\"why\":\"...\",\"severity\":\"HIGH/MEDIUM/LOW\"}],\"positive_terms\":[],\"negotiation_script\":{\"opening_line\":\"...\",\"counter_offer\":\"...\",\"walk_away_number\":\"₹X LPA\",\"key_phrases\":[\"...\",\"...\"]},\"overall_advice\":\"2 sentence summary.\",\"negotiate\":true,\"confidence_score\":0}";

function OfferNegotiationAssistant() {
  var s0 = useState(""); var offerText = s0[0]; var setOfferText = s0[1];
  var s1 = useState(null); var result = s1[0]; var setResult = s1[1];
  var s2 = useState(false); var loading = s2[0]; var setLoading = s2[1];
  var s3 = useState(null); var error = s3[0]; var setError = s3[1];
  var s4 = useState(false); var open = s4[0]; var setOpen = s4[1];
  var s5 = useState({}); var copied = s5[0]; var setCopied = s5[1];
  var abRef = useRef(null);
  var NC = "#F59E0B";

  useEffect(function() { return function() { if (abRef.current) abRef.current.abort(); }; }, []);

  async function analyze() {
    if (!offerText.trim() || offerText.trim().length < 30) { setError("Paste your offer letter (min 30 chars)."); return; }
    if (abRef.current) abRef.current.abort();
    abRef.current = new AbortController();
    setLoading(true); setError(null); setResult(null);
    try {
      var parsed = await runAIStable(offerText.slice(0, 6000), OFFER_NEGO_SYS, abRef.current.signal);
      if (!parsed || parsed.error || parsed.fallback) {
        parsed = {
          salary_verdict: "Analysis Unavailable", market_rate: "—", your_offered: "—", gap_percentage: 0,
          negotiable_components: [], red_flags: [], positive_terms: [],
          negotiation_script: { opening_line: "I'm excited about this offer. Before I sign, I'd like to discuss the compensation package.", counter_offer: "Based on my research and experience, I was expecting something closer to [target range].", walk_away_number: "—", key_phrases: ["I'm very interested in this role", "Can we revisit the base compensation?"] },
          overall_advice: "AI unavailable. Review offer manually — check salary against Glassdoor and AmbitionBox for your role.", negotiate: true, confidence_score: 0, _fallback: true
        };
        setError("AI unavailable — showing template guidance. Retry for personalised analysis.");
      }
      setResult(parsed);
    } catch(e) { if (e && e.name !== "AbortError") setError((e && e.message) || "Analysis failed."); }
    finally { setLoading(false); }
  }

  function copyText(key, text) {
    try { navigator.clipboard.writeText(text || "").catch(function() {}); } catch(e) {}
    setCopied(function(p) { var n = Object.assign({}, p); n[key] = true; return n; });
    setTimeout(function() { setCopied(function(p) { var n = Object.assign({}, p); n[key] = false; return n; }); }, 2000);
  }

  var sevColor = function(s) { return s === "HIGH" ? DS.red : s === "MEDIUM" ? DS.amber : DS.t3; };

  return (
    <div style={{ background: DS.bg1, border: "1px solid " + NC + "25", borderRadius: 16, overflow: "hidden", marginBottom: 20 }}>
      <button onClick={function() { setOpen(function(v) { return !v; }); }}
        style={{ width: "100%", padding: "16px 20px", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, background: NC + "15", border: "1px solid " + NC + "30", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>💼</div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 9, color: NC, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>OFFER NEGOTIATION ASSISTANT</div>
            <div style={{ fontSize: 12, color: DS.t3, marginTop: 2 }}>Paste your offer letter — get exact negotiation scripts</div>
          </div>
        </div>
        <span style={{ color: NC + "80", fontSize: 18, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>›</span>
      </button>
      {open && (
        <div style={{ padding: "0 20px 20px", borderTop: "1px solid " + NC + "15" }}>
          <textarea value={offerText} onChange={function(e) { setOfferText(e.target.value); setError(null); }}
            placeholder={"Paste your full offer letter here...\n\nInclude: salary, designation, joining date, clauses, benefits.\nWe'll tell you what's negotiable, what's a red flag, and exactly what to say."}
            rows={6} style={{ width: "100%", marginTop: 16, background: DS.bg0, border: "1px solid " + DS.border1, borderRadius: 12, padding: "12px 14px", color: DS.t1, fontSize: 13, lineHeight: 1.8, resize: "vertical", outline: "none", caretColor: NC, boxSizing: "border-box" }} />
          {error && <div style={{ background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.2)", borderRadius: 8, padding: "9px 14px", marginTop: 10, fontSize: 13, color: DS.red }}>⚠ {error}</div>}
          <button onClick={analyze} disabled={loading}
            style={{ width: "100%", marginTop: 12, padding: "13px", background: loading ? "transparent" : NC + "15", border: "1px solid " + (loading ? NC + "20" : NC + "44"), borderRadius: 12, color: loading ? NC + "55" : NC, fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            {loading ? <><div style={{ width: 14, height: 14, border: "2px solid " + NC + "30", borderTopColor: NC, borderRadius: "50%", animation: "teSpin 0.7s linear infinite" }} />Analysing offer...</> : "💼 Analyse My Offer"}
          </button>
          {result && !loading && (
            <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 14, animation: "teFadeIn 0.3s ease" }}>
              {/* Verdict */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                {[
                  { label: "VERDICT", value: result.salary_verdict, color: result.salary_verdict === "Above Market" ? DS.green : result.salary_verdict === "Below Market" ? DS.red : DS.amber },
                  { label: "MARKET RATE", value: result.market_rate, color: DS.blue },
                  { label: "GAP", value: (result.gap_percentage > 0 ? "+" : "") + result.gap_percentage + "%", color: result.gap_percentage >= 0 ? DS.green : DS.red },
                ].map(function(item) {
                  return (
                    <div key={item.label} style={{ background: DS.bg0, border: "1px solid " + item.color + "22", borderRadius: 12, padding: "12px 14px", textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 6 }}>{item.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: item.color, fontFamily: "'JetBrains Mono',monospace" }}>{item.value}</div>
                    </div>
                  );
                })}
              </div>
              {/* Red flags */}
              {result.red_flags && result.red_flags.length > 0 && (
                <div style={{ background: "rgba(244,63,94,0.05)", border: "1px solid rgba(244,63,94,0.18)", borderRadius: 12, padding: "14px 16px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: DS.red, marginBottom: 10 }}>🚩 Red Flags in This Offer</div>
                  {result.red_flags.map(function(f, i) {
                    var c = sevColor(f.severity);
                    return (
                      <div key={i} style={{ display: "flex", gap: 10, padding: "9px 12px", background: c + "06", border: "1px solid " + c + "18", borderRadius: 8, marginBottom: i < result.red_flags.length - 1 ? 8 : 0 }}>
                        <span style={{ fontSize: 9, color: c, fontFamily: "'JetBrains Mono',monospace", background: c + "15", padding: "1px 6px", borderRadius: 4, flexShrink: 0, height: 16 }}>{f.severity}</span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: DS.t1, marginBottom: 2 }}>{f.clause}</div>
                          <div style={{ fontSize: 11, color: DS.t3, lineHeight: 1.5 }}>{f.why}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Negotiable components */}
              {result.negotiable_components && result.negotiable_components.length > 0 && (
                <div style={{ background: DS.green + "05", border: "1px solid " + DS.green + "20", borderRadius: 12, padding: "14px 16px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: DS.green, marginBottom: 10 }}>💡 What You Can Negotiate</div>
                  {result.negotiable_components.map(function(comp, i) {
                    return (
                      <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "center", padding: "8px 10px", background: DS.bg0, borderRadius: 8, marginBottom: i < result.negotiable_components.length - 1 ? 8 : 0 }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: DS.t1 }}>{comp.component}</div>
                          <div style={{ fontSize: 11, color: DS.red }}>Current: {comp.current}</div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: DS.green }}>Target: {comp.target}</div>
                        <div style={{ fontSize: 11, color: DS.t3, maxWidth: 140, lineHeight: 1.4 }}>{comp.tip}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Negotiation script */}
              {result.negotiation_script && (
                <div style={{ background: NC + "06", border: "1px solid " + NC + "22", borderRadius: 12, padding: "14px 16px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: NC, marginBottom: 12 }}>🗣 Your Negotiation Script</div>
                  {[
                    { key: "opening", label: "OPENING LINE", value: result.negotiation_script.opening_line },
                    { key: "counter", label: "COUNTER OFFER", value: result.negotiation_script.counter_offer },
                    { key: "walkaway", label: "WALK-AWAY NUMBER", value: result.negotiation_script.walk_away_number },
                  ].map(function(item) {
                    return (
                      <div key={item.key} style={{ background: DS.bg0, border: "1px solid " + NC + "18", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                          <span style={{ fontSize: 8, color: NC, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2, fontWeight: 700 }}>{item.label}</span>
                          <button onClick={function() { copyText(item.key, item.value); }}
                            style={{ fontSize: 9, padding: "2px 8px", background: copied[item.key] ? NC + "18" : "transparent", border: "1px solid " + (copied[item.key] ? NC + "44" : DS.border0), borderRadius: 4, color: copied[item.key] ? NC : DS.t4, cursor: "pointer" }}>
                            {copied[item.key] ? "✓" : "Copy"}
                          </button>
                        </div>
                        <div style={{ fontSize: 12, color: DS.t1, lineHeight: 1.65, fontStyle: "italic" }}>{item.value}</div>
                      </div>
                    );
                  })}
                  {result.negotiation_script.key_phrases && result.negotiation_script.key_phrases.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ fontSize: 9, color: NC, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2, marginBottom: 8 }}>KEY PHRASES</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {result.negotiation_script.key_phrases.map(function(phrase, i) {
                          return (
                            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "7px 10px", background: NC + "06", borderRadius: 6 }}>
                              <span style={{ color: NC, fontFamily: "'JetBrains Mono',monospace", fontSize: 10, flexShrink: 0 }}>{i + 1}.</span>
                              <span style={{ fontSize: 12, color: DS.t2, fontStyle: "italic", lineHeight: 1.6 }}>{phrase}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Overall advice */}
              {result.overall_advice && (
                <div style={{ fontSize: 13, color: DS.t2, lineHeight: 1.75, padding: "12px 14px", background: DS.bg0, borderLeft: "3px solid " + NC, borderRadius: "0 8px 8px 0" }}>
                  💡 {result.overall_advice}
                </div>
              )}
              <button onClick={function() { setResult(null); setOfferText(""); setError(null); }}
                style={{ alignSelf: "flex-start", fontSize: 12, padding: "7px 14px", background: "transparent", border: "1px solid " + DS.border0, borderRadius: 8, color: DS.t3, cursor: "pointer" }}>
                ← Analyse another offer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 3. CAREER OBITUARY (enhanced standalone) ─────────
function CareerObituarySection({ result }) {
  if (!result) return null;
  var s0 = useState(false); var open = s0[0]; var setOpen = s0[1];
  var s1 = useState(null); var obituary = s1[0]; var setObituary = s1[1];
  var s2 = useState(false); var loading = s2[0]; var setLoading = s2[1];
  var abRef = useRef(null);
  var C = DS.purple;

  var OBT_SYS = "You are a brutally honest career advisor. Given this career profile, predict what happens if they change NOTHING for 3 years.\nRespond ONLY with valid JSON:\n{\"title\":\"One line future state title\",\"salary_in_3y\":\"₹X LPA\",\"role_in_3y\":\"Job title in 3 years if no change\",\"market_relevance\":0,\"stagnation_signals\":[\"...\"],\"missed_opportunities\":[\"...\"],\"point_of_no_return\":\"When this becomes irreversible\",\"survival_probability\":0,\"verdict\":\"one sentence brutal truth\",\"one_action\":\"The single most important thing to do this week\"}";

  async function generate() {
    if (loading) return;
    if (abRef.current) abRef.current.abort();
    abRef.current = new AbortController();
    setLoading(true);
    try {
      var profile = JSON.stringify({
        level: result.candidate_profile && result.candidate_profile.level,
        domain: result.candidate_profile && result.candidate_profile.domain,
        skills_gap: (result.skills_gap || []).slice(0, 5).map(function(s) { return s.skill_name; }),
        rejection_reasons: result.rejection_analysis && result.rejection_analysis.top_reasons,
        ats: result.scores && result.scores.ats_score,
      });
      var parsed = await runAIStable("Career profile: " + profile, OBT_SYS, abRef.current.signal);
      if (!parsed || parsed.error) {
        parsed = {
          title: "Stagnation confirmed in " + (result.growth_path && result.growth_path.time_to_reach || "2-3 years"),
          salary_in_3y: result.salary_intelligence && result.salary_intelligence.current_value_range || "Similar to today",
          role_in_3y: result.candidate_profile && result.candidate_profile.level || "Same level",
          market_relevance: 35, stagnation_signals: ["No upskilling detected", "Skill gaps widening"],
          missed_opportunities: ["Senior role window closes", "Emerging tech adoption gap"],
          point_of_no_return: "12-18 months", survival_probability: 40,
          verdict: "Without deliberate action, you are optimizing for comfort over growth.",
          one_action: "Start the top-priority skill gap this week — even 30 minutes/day compounds.",
        };
      }
      setObituary(parsed);
    } catch(e) { } finally { setLoading(false); }
  }

  var svPct = obituary && obituary.survival_probability != null ? obituary.survival_probability : null;
  var svColor = svPct != null ? (svPct >= 60 ? DS.amber : svPct >= 40 ? DS.amber : DS.red) : DS.red;

  return (
    <div style={{ background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.18)", borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
      <button onClick={function() { setOpen(function(v) { return !v; }); if (!obituary && !loading) {} }}
        style={{ width: "100%", padding: "14px 18px", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>⚰️</span>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 9, color: DS.red, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>CAREER OBITUARY</div>
            <div style={{ fontSize: 12, color: DS.t3, marginTop: 2 }}>If you change nothing for 3 years — here's what happens</div>
          </div>
        </div>
        <span style={{ color: DS.t4, fontSize: 16, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>›</span>
      </button>
      {open && (
        <div style={{ padding: "0 18px 18px", borderTop: "1px solid rgba(239,68,68,0.12)", animation: "teFadeIn 0.2s ease" }}>
          {!obituary && !loading && (
            <div style={{ paddingTop: 14 }}>
              <div style={{ fontSize: 13, color: DS.t3, lineHeight: 1.7, marginBottom: 14 }}>
                This uses your career profile to compute your exact trajectory if you make zero changes. It's designed to be uncomfortable.
              </div>
              <button onClick={generate}
                style={{ padding: "11px 20px", background: DS.red + "10", border: "1px solid " + DS.red + "30", borderRadius: 10, color: DS.red, fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
                ⚰️ Show My Career Obituary
              </button>
            </div>
          )}
          {loading && (
            <div style={{ padding: "20px 0", display: "flex", alignItems: "center", gap: 12, color: DS.red }}>
              <div style={{ width: 14, height: 14, border: "2px solid " + DS.red + "30", borderTopColor: DS.red, borderRadius: "50%", animation: "teSpin 0.7s linear infinite" }} />
              <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace" }}>Computing your trajectory...</span>
            </div>
          )}
          {obituary && !loading && (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ background: DS.red + "08", border: "1px solid " + DS.red + "25", borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: DS.red, marginBottom: 8 }}>{obituary.title}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div style={{ background: DS.bg0, borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 10, color: DS.t4, marginBottom: 4 }}>SALARY IN 3 YEARS</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: DS.red }}>{obituary.salary_in_3y}</div>
                  </div>
                  <div style={{ background: DS.bg0, borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 10, color: DS.t4, marginBottom: 4 }}>ROLE IN 3 YEARS</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: DS.t2 }}>{obituary.role_in_3y}</div>
                  </div>
                </div>
              </div>
              {svPct != null && (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ fontSize: 11, color: DS.t3, flexShrink: 0 }}>Career survival probability</div>
                  <div style={{ flex: 1, height: 6, background: DS.bg3, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: svPct + "%", background: svColor, borderRadius: 3, transition: "width 1s cubic-bezier(0.22,1,0.36,1)" }} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 900, color: svColor, fontFamily: "'JetBrains Mono',monospace", flexShrink: 0 }}>{svPct}%</div>
                </div>
              )}
              {obituary.stagnation_signals && obituary.stagnation_signals.length > 0 && (
                <div style={{ background: DS.bg0, border: "1px solid " + DS.border0, borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, color: DS.red, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 8 }}>STAGNATION SIGNALS</div>
                  {obituary.stagnation_signals.map(function(s, i) {
                    return <div key={i} style={{ fontSize: 12, color: DS.t2, marginBottom: 5, display: "flex", gap: 8 }}><span style={{ color: DS.red }}>→</span>{s}</div>;
                  })}
                </div>
              )}
              {obituary.missed_opportunities && obituary.missed_opportunities.length > 0 && (
                <div style={{ background: DS.bg0, border: "1px solid " + DS.border0, borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, color: DS.amber, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 8 }}>MISSED OPPORTUNITIES</div>
                  {obituary.missed_opportunities.map(function(s, i) {
                    return <div key={i} style={{ fontSize: 12, color: DS.t2, marginBottom: 5, display: "flex", gap: 8 }}><span style={{ color: DS.amber }}>⚠</span>{s}</div>;
                  })}
                </div>
              )}
              <div style={{ borderLeft: "3px solid " + DS.red + "60", paddingLeft: 14 }}>
                <div style={{ fontSize: 13, color: "#fca5a5", lineHeight: 1.75, fontStyle: "italic", marginBottom: 8 }}>{obituary.verdict}</div>
                {obituary.point_of_no_return && <div style={{ fontSize: 11, color: DS.t3 }}>Point of no return: <span style={{ color: DS.red, fontWeight: 700 }}>{obituary.point_of_no_return}</span></div>}
              </div>
              {obituary.one_action && (
                <div style={{ background: DS.green + "08", border: "1px solid " + DS.green + "25", borderRadius: 10, padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>⚡</span>
                  <div>
                    <div style={{ fontSize: 10, color: DS.green, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 4 }}>DO THIS THIS WEEK</div>
                    <div style={{ fontSize: 13, color: DS.t1, fontWeight: 600, lineHeight: 1.6 }}>{obituary.one_action}</div>
                  </div>
                </div>
              )}
              <button onClick={function() { setObituary(null); }}
                style={{ alignSelf: "flex-start", fontSize: 11, padding: "6px 12px", background: "transparent", border: "1px solid " + DS.border0, borderRadius: 7, color: DS.t4, cursor: "pointer" }}>
                ↺ Regenerate
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 4. SCAM NETWORK MAP ───────────────────────────────
function ScamNetworkMap({ result }) {
  if (!result || result.risk_score < 40) return null;
  var s0 = useState(false); var open = s0[0]; var setOpen = s0[1];
  var canvasRef = useRef(null);

  var nodes = useMemo(function() {
    var ns = [];
    var ent = result.detected_entities || {};
    if (ent.company) ns.push({ id: "company", label: ent.company.slice(0, 20), type: "company", x: 0, y: 0 });
    if (ent.email) ns.push({ id: "email", label: ent.email.slice(0, 24), type: "contact", x: 0, y: 0 });
    if (ent.domain) ns.push({ id: "domain", label: ent.domain.slice(0, 20), type: "domain", x: 0, y: 0 });
    if (ent.recruiter) ns.push({ id: "recruiter", label: ent.recruiter.slice(0, 20), type: "person", x: 0, y: 0 });
    // Add pattern nodes from red flags
    (result.red_flags || []).slice(0, 3).forEach(function(f, i) {
      ns.push({ id: "flag_" + i, label: f.slice(0, 22), type: "flag", x: 0, y: 0 });
    });
    if (result.india_specific && result.india_specific.scam_type) {
      ns.push({ id: "scam_type", label: result.india_specific.scam_type.slice(0, 22), type: "scam", x: 0, y: 0 });
    }
    // Layout: center = highest risk node, radiate outward
    var cx = 180, cy = 130, r = 95;
    ns.forEach(function(n, i) {
      if (i === 0) { n.x = cx; n.y = cy; }
      else {
        var angle = ((i - 1) / Math.max(ns.length - 1, 1)) * 2 * Math.PI;
        n.x = cx + r * Math.cos(angle);
        n.y = cy + r * Math.sin(angle);
      }
    });
    return ns;
  }, [result]);

  var edges = useMemo(function() {
    if (!nodes.length) return [];
    var es = [];
    for (var i = 1; i < nodes.length; i++) {
      es.push({ from: nodes[0].id, to: nodes[i].id });
    }
    return es;
  }, [nodes]);

  useEffect(function() {
    if (!open || !canvasRef.current || !nodes.length) return;
    var canvas = canvasRef.current;
    var ctx = canvas.getContext("2d");
    var W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    var typeColor = { company: DS.red, contact: DS.amber, domain: DS.cyan, person: DS.purple, flag: "#F97316", scam: DS.red };
    var typeIcon = { company: "🏢", contact: "📧", domain: "🌐", person: "👤", flag: "🚩", scam: "⚠" };
    // Draw edges
    edges.forEach(function(e) {
      var from = nodes.find(function(n) { return n.id === e.from; });
      var to = nodes.find(function(n) { return n.id === e.to; });
      if (!from || !to) return;
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.strokeStyle = DS.red + "30";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    });
    // Draw nodes
    nodes.forEach(function(n) {
      var c = typeColor[n.type] || DS.t3;
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.id === nodes[0].id ? 22 : 16, 0, 2 * Math.PI);
      ctx.fillStyle = c + "18";
      ctx.fill();
      ctx.strokeStyle = c + "60";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = c;
      ctx.font = (n.id === nodes[0].id ? "12" : "10") + "px 'Sora',sans-serif";
      ctx.textAlign = "center";
      var labelY = n.id === nodes[0].id ? n.y + 36 : n.y + 28;
      var label = n.label;
      if (label.length > 14) label = label.slice(0, 12) + "..";
      ctx.fillText(label, n.x, labelY);
    });
  }, [open, nodes, edges]);

  if (nodes.length < 2) return null;

  return (
    <div style={{ background: DS.bg1, border: "1px solid rgba(244,63,94,0.18)", borderRadius: 14, overflow: "hidden", marginBottom: 14 }}>
      <button onClick={function() { setOpen(function(v) { return !v; }); }}
        style={{ width: "100%", padding: "13px 16px", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14 }}>🕸</span>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 9, color: DS.red, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>SCAM NETWORK MAP</div>
            <div style={{ fontSize: 11, color: DS.t3, marginTop: 2 }}>{nodes.length} entities · {edges.length} connections detected</div>
          </div>
        </div>
        <span style={{ color: DS.t4, fontSize: 16, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>›</span>
      </button>
      {open && (
        <div style={{ padding: "0 16px 16px", borderTop: "1px solid rgba(244,63,94,0.1)", animation: "teFadeIn 0.2s ease" }}>
          <canvas ref={canvasRef} width={360} height={260}
            style={{ width: "100%", maxWidth: 360, height: "auto", display: "block", margin: "12px auto 0" }} />
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
            {[["🏢 Company", DS.red], ["📧 Contact", DS.amber], ["🌐 Domain", DS.cyan], ["👤 Person", DS.purple], ["🚩 Flag", "#F97316"]].map(function(item) {
              return <span key={item[0]} style={{ fontSize: 10, color: item[1], background: item[1] + "12", border: "1px solid " + item[1] + "25", padding: "2px 8px", borderRadius: 5, fontFamily: "'JetBrains Mono',monospace" }}>{item[0]}</span>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── 5. INTERVIEW DEBRIEF ASSISTANT ───────────────────
var DEBRIEF_SYS = "You are an expert interview coach for the Indian job market. Analyze this interview experience.\nRespond ONLY with valid JSON:\n{\"performance_score\":0,\"performance_label\":\"Strong/Average/Weak\",\"selection_probability\":0,\"what_went_well\":[],\"red_flags_noticed\":[{\"signal\":\"...\",\"what_it_means\":\"...\"}],\"interviewer_read\":{\"enthusiasm\":\"High/Medium/Low\",\"technical_bar\":\"High/Medium/Low\",\"culture_fit\":\"Strong/Average/Weak\",\"overall_vibe\":\"Positive/Neutral/Negative\"},\"next_step_prediction\":\"...\",\"follow_up_advice\":\"...\",\"follow_up_template\":\"...\",\"should_move_on\":false,\"key_learning\":\"...\"}";

function InterviewDebriefAssistant() {
  var s0 = useState(""); var text = s0[0]; var setText = s0[1];
  var s1 = useState(null); var result = s1[0]; var setResult = s1[1];
  var s2 = useState(false); var loading = s2[0]; var setLoading = s2[1];
  var s3 = useState(null); var error = s3[0]; var setError = s3[1];
  var s4 = useState(false); var copied = s4[0]; var setCopied = s4[1];
  var abRef = useRef(null);
  var IC = "#06B6D4";

  useEffect(function() { return function() { if (abRef.current) abRef.current.abort(); }; }, []);

  async function analyze() {
    if (!text.trim() || text.trim().length < 30) { setError("Describe your interview (min 30 chars)."); return; }
    if (abRef.current) abRef.current.abort();
    abRef.current = new AbortController();
    setLoading(true); setError(null); setResult(null);
    try {
      var parsed = await runAIStable(text.slice(0, 4000), DEBRIEF_SYS, abRef.current.signal);
      if (!parsed || parsed.error || parsed.fallback) {
        parsed = {
          performance_score: 60, performance_label: "Average",
          selection_probability: 45,
          what_went_well: ["Showed up prepared", "Answered core questions"],
          red_flags_noticed: [{ signal: "AI unavailable for detailed signal analysis", what_it_means: "Retry for personalized read." }],
          interviewer_read: { enthusiasm: "Medium", technical_bar: "Medium", culture_fit: "Average", overall_vibe: "Neutral" },
          next_step_prediction: "Expect a response within 5-7 business days. No news after 10 days usually means rejection.",
          follow_up_advice: "Send a thank-you email within 24 hours. Keep it under 100 words — concise and confident.",
          follow_up_template: "Hi [Name], thank you for the time today. I left energized about the [role] — especially [specific topic discussed]. Looking forward to next steps.",
          should_move_on: false,
          key_learning: "Keep applying in parallel regardless of how this one felt.",
        };
        setError("AI unavailable — showing general guidance. Retry for personalised debrief.");
      }
      setResult(parsed);
    } catch(e) { if (e && e.name !== "AbortError") setError((e && e.message) || "Analysis failed."); }
    finally { setLoading(false); }
  }

  function copyFollowUp() {
    if (!result || !result.follow_up_template) return;
    try { navigator.clipboard.writeText(result.follow_up_template).catch(function() {}); } catch(e) {}
    setCopied(true); setTimeout(function() { setCopied(false); }, 2000);
  }

  var probColor = function(p) { return p >= 65 ? DS.green : p >= 40 ? DS.amber : DS.red; };

  return (
    <div style={{ background: DS.bg1, border: "1px solid " + IC + "25", borderRadius: 16, overflow: "hidden", marginBottom: 20 }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid " + IC + "15" }}>
        <div style={{ fontSize: 9, color: IC, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, marginBottom: 3 }}>INTERVIEW DEBRIEF ASSISTANT</div>
        <div style={{ fontSize: 12, color: DS.t3 }}>Describe your interview — AI tells you how you performed and what happens next</div>
      </div>
      <div style={{ padding: "16px 20px" }}>
        <textarea value={text} onChange={function(e) { setText(e.target.value); setError(null); }}
          placeholder={"Describe your interview experience...\n\nE.g: I had a 45-min interview at Swiggy for SDE-2. The interviewer seemed rushed. They asked 2 DSA questions and one system design. I solved one fully, the other partially. They said 'we'll be in touch'. The call ended abruptly."}
          rows={6} style={{ width: "100%", background: DS.bg0, border: "1px solid " + DS.border1, borderRadius: 12, padding: "12px 14px", color: DS.t1, fontSize: 13, lineHeight: 1.8, resize: "vertical", outline: "none", caretColor: IC, boxSizing: "border-box" }} />
        {error && <div style={{ background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.2)", borderRadius: 8, padding: "9px 14px", marginTop: 10, fontSize: 13, color: DS.red }}>⚠ {error}</div>}
        <button onClick={analyze} disabled={loading}
          style={{ width: "100%", marginTop: 12, padding: "13px", background: loading ? "transparent" : IC + "12", border: "1px solid " + (loading ? IC + "20" : IC + "44"), borderRadius: 12, color: loading ? IC + "55" : IC, fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
          {loading ? <><div style={{ width: 14, height: 14, border: "2px solid " + IC + "30", borderTopColor: IC, borderRadius: "50%", animation: "teSpin 0.7s linear infinite" }} />Analysing your interview...</> : "🎯 Debrief My Interview"}
        </button>
        {result && !loading && (
          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 14, animation: "teFadeIn 0.3s ease" }}>
            {/* Score row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              {[
                { label: "PERFORMANCE", value: result.performance_score + "/100", color: probColor(result.performance_score) },
                { label: "SELECTION PROB.", value: result.selection_probability + "%", color: probColor(result.selection_probability) },
                { label: "LABEL", value: result.performance_label, color: IC },
              ].map(function(item) {
                return (
                  <div key={item.label} style={{ background: DS.bg0, border: "1px solid " + item.color + "22", borderRadius: 12, padding: "12px 14px", textAlign: "center" }}>
                    <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 6 }}>{item.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: item.color, fontFamily: "'JetBrains Mono',monospace" }}>{item.value}</div>
                  </div>
                );
              })}
            </div>
            {/* Interviewer read */}
            {result.interviewer_read && (
              <div style={{ background: DS.bg0, border: "1px solid " + DS.border0, borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ fontSize: 10, color: IC, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2, marginBottom: 10 }}>INTERVIEWER READ</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 8 }}>
                  {Object.entries(result.interviewer_read).map(function(en) {
                    var k = en[0]; var v = en[1];
                    var vc = v === "High" || v === "Strong" || v === "Positive" ? DS.green : v === "Low" || v === "Weak" || v === "Negative" ? DS.red : DS.amber;
                    return (
                      <div key={k} style={{ background: vc + "08", border: "1px solid " + vc + "20", borderRadius: 8, padding: "8px 10px" }}>
                        <div style={{ fontSize: 9, color: DS.t4, textTransform: "capitalize", marginBottom: 4 }}>{k.replace(/_/g, " ")}</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: vc }}>{v}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* What went well */}
            {result.what_went_well && result.what_went_well.length > 0 && (
              <div style={{ background: DS.green + "05", border: "1px solid " + DS.green + "18", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, color: DS.green, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 8 }}>WHAT WENT WELL</div>
                {result.what_went_well.map(function(s, i) { return <div key={i} style={{ fontSize: 12, color: DS.t2, marginBottom: 5, display: "flex", gap: 8 }}><span style={{ color: DS.green }}>✓</span>{s}</div>; })}
              </div>
            )}
            {/* Red flags noticed */}
            {result.red_flags_noticed && result.red_flags_noticed.length > 0 && (
              <div style={{ background: DS.amber + "05", border: "1px solid " + DS.amber + "18", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, color: DS.amber, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 8 }}>SIGNALS INTERVIEWER MAY HAVE NOTICED</div>
                {result.red_flags_noticed.map(function(f, i) {
                  return (
                    <div key={i} style={{ marginBottom: i < result.red_flags_noticed.length - 1 ? 8 : 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: DS.amber, marginBottom: 2 }}>{f.signal}</div>
                      <div style={{ fontSize: 11, color: DS.t3 }}>{f.what_it_means}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Prediction + advice */}
            {result.next_step_prediction && (
              <div style={{ fontSize: 13, color: DS.t2, lineHeight: 1.7, padding: "12px 14px", background: DS.bg0, borderLeft: "3px solid " + IC, borderRadius: "0 8px 8px 0" }}>
                <div style={{ fontSize: 9, color: IC, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2, marginBottom: 6 }}>NEXT STEP PREDICTION</div>
                {result.next_step_prediction}
              </div>
            )}
            {/* Follow-up template */}
            {result.follow_up_template && (
              <div style={{ background: IC + "06", border: "1px solid " + IC + "22", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 9, color: IC, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2, fontWeight: 700 }}>FOLLOW-UP EMAIL TEMPLATE</div>
                  <button onClick={copyFollowUp} style={{ fontSize: 9, padding: "3px 10px", background: copied ? IC + "18" : "transparent", border: "1px solid " + (copied ? IC + "44" : DS.border0), borderRadius: 4, color: copied ? IC : DS.t4, cursor: "pointer" }}>
                    {copied ? "✓ Copied" : "Copy"}
                  </button>
                </div>
                <div style={{ fontSize: 12, color: DS.t1, lineHeight: 1.7, fontStyle: "italic" }}>{result.follow_up_template}</div>
              </div>
            )}
            {result.should_move_on && (
              <div style={{ background: DS.amber + "08", border: "1px solid " + DS.amber + "25", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: DS.amber, fontWeight: 600 }}>
                ⚡ Our read: start applying elsewhere in parallel — don't wait on this one.
              </div>
            )}
            {result.key_learning && (
              <div style={{ fontSize: 12, color: DS.t3, lineHeight: 1.6 }}>💡 Key learning: {result.key_learning}</div>
            )}
            <button onClick={function() { setResult(null); setText(""); setError(null); }}
              style={{ alignSelf: "flex-start", fontSize: 12, padding: "7px 14px", background: "transparent", border: "1px solid " + DS.border0, borderRadius: 8, color: DS.t3, cursor: "pointer" }}>
              ← New debrief
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── 6. OFFER COMPARISON ENGINE ────────────────────────
var OFFER_CMP_SYS = "You are India's best offer comparison advisor. Compare these two job offers for an Indian professional.\nRespond ONLY with valid JSON:\n{\"winner\":\"A/B/Tie\",\"winner_reason\":\"One sentence.\",\"confidence\":\"High/Medium/Low\",\"comparison\":{\"compensation\":{\"a_score\":0,\"b_score\":0,\"a_detail\":\"...\",\"b_detail\":\"...\",\"winner\":\"A/B\"},\"growth\":{\"a_score\":0,\"b_score\":0,\"a_detail\":\"...\",\"b_detail\":\"...\",\"winner\":\"A/B\"},\"stability\":{\"a_score\":0,\"b_score\":0,\"a_detail\":\"...\",\"b_detail\":\"...\",\"winner\":\"A/B\"},\"culture\":{\"a_score\":0,\"b_score\":0,\"a_detail\":\"...\",\"b_detail\":\"...\",\"winner\":\"A/B\"},\"risk\":{\"a_score\":0,\"b_score\":0,\"a_detail\":\"...\",\"b_detail\":\"...\",\"winner\":\"A/B\"}},\"red_flags_a\":[],\"red_flags_b\":[],\"negotiation_leverage\":\"A/B/Both\",\"final_advice\":\"2-3 sentence actionable recommendation.\"}";

function OfferComparisonEngine() {
  var s0 = useState(""); var offerA = s0[0]; var setOfferA = s0[1];
  var s1 = useState(""); var offerB = s1[0]; var setOfferB = s1[1];
  var s2 = useState(null); var result = s2[0]; var setResult = s2[1];
  var s3 = useState(false); var loading = s3[0]; var setLoading = s3[1];
  var s4 = useState(null); var error = s4[0]; var setError = s4[1];
  var abRef = useRef(null);
  var OC = "#8B5CF6";

  useEffect(function() { return function() { if (abRef.current) abRef.current.abort(); }; }, []);

  async function compare() {
    if (!offerA.trim() || offerA.trim().length < 20) { setError("Paste Offer A (min 20 chars)."); return; }
    if (!offerB.trim() || offerB.trim().length < 20) { setError("Paste Offer B (min 20 chars)."); return; }
    if (abRef.current) abRef.current.abort();
    abRef.current = new AbortController();
    setLoading(true); setError(null); setResult(null);
    try {
      var prompt = "OFFER A:\n" + offerA.slice(0, 3000) + "\n\nOFFER B:\n" + offerB.slice(0, 3000);
      var parsed = await runAIStable(prompt, OFFER_CMP_SYS, abRef.current.signal);
      if (!parsed || parsed.error || parsed.fallback) {
        parsed = {
          winner: "Tie", winner_reason: "AI unavailable — manual comparison required.", confidence: "Low",
          comparison: {
            compensation: { a_score: 50, b_score: 50, a_detail: "Review salary details", b_detail: "Review salary details", winner: "Tie" },
            growth: { a_score: 50, b_score: 50, a_detail: "Evaluate role scope", b_detail: "Evaluate role scope", winner: "Tie" },
            stability: { a_score: 50, b_score: 50, a_detail: "Research company", b_detail: "Research company", winner: "Tie" },
            culture: { a_score: 50, b_score: 50, a_detail: "Check reviews", b_detail: "Check reviews", winner: "Tie" },
            risk: { a_score: 50, b_score: 50, a_detail: "Check notice period", b_detail: "Check notice period", winner: "Tie" },
          },
          red_flags_a: [], red_flags_b: [],
          negotiation_leverage: "Both",
          final_advice: "Retry for a personalised comparison. In general: choose the offer with better growth trajectory over short-term salary if you are early-to-mid career.",
          _fallback: true,
        };
        setError("AI unavailable — showing template. Retry for personalised analysis.");
      }
      setResult(parsed);
    } catch(e) { if (e && e.name !== "AbortError") setError((e && e.message) || "Analysis failed."); }
    finally { setLoading(false); }
  }

  var winColor = function(w) { return w === "A" ? DS.blue : w === "B" ? DS.teal : DS.amber; };
  var scoreBar = function(score, color) {
    return (
      <div style={{ height: 4, background: DS.bg3, borderRadius: 2, overflow: "hidden", marginTop: 4 }}>
        <div style={{ height: "100%", width: score + "%", background: color, borderRadius: 2 }} />
      </div>
    );
  };

  var DIMS = [
    { key: "compensation", label: "💰 Compensation", icon: "💰" },
    { key: "growth", label: "📈 Growth", icon: "📈" },
    { key: "stability", label: "🏛 Stability", icon: "🏛" },
    { key: "culture", label: "🌱 Culture", icon: "🌱" },
    { key: "risk", label: "⚠ Risk", icon: "⚠" },
  ];

  return (
    <div style={{ background: DS.bg1, border: "1px solid " + OC + "25", borderRadius: 16, overflow: "hidden", marginBottom: 20 }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid " + OC + "15" }}>
        <div style={{ fontSize: 9, color: OC, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, marginBottom: 3 }}>OFFER COMPARISON ENGINE</div>
        <div style={{ fontSize: 12, color: DS.t3 }}>Paste two offers — AI picks the winner with full reasoning</div>
      </div>
      <div style={{ padding: "16px 20px" }}>
        {!result && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              {[{ label: "OFFER A", val: offerA, set: setOfferA, color: DS.blue }, { label: "OFFER B", val: offerB, set: setOfferB, color: DS.teal }].map(function(item) {
                return (
                  <div key={item.label}>
                    <div style={{ fontSize: 10, color: item.color, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2, marginBottom: 6 }}>{item.label}</div>
                    <textarea value={item.val} onChange={function(e) { item.set(e.target.value); setError(null); }}
                      placeholder={"Paste offer details...\nSalary, company, role, benefits"}
                      rows={6} style={{ width: "100%", background: DS.bg0, border: "1px solid " + item.color + "25", borderRadius: 10, padding: "10px 12px", color: DS.t1, fontSize: 12, lineHeight: 1.7, resize: "vertical", outline: "none", caretColor: item.color, boxSizing: "border-box" }} />
                  </div>
                );
              })}
            </div>
            {error && <div style={{ background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.2)", borderRadius: 8, padding: "9px 14px", marginBottom: 10, fontSize: 13, color: DS.red }}>⚠ {error}</div>}
            <button onClick={compare} disabled={loading}
              style={{ width: "100%", padding: "13px", background: loading ? "transparent" : OC + "12", border: "1px solid " + (loading ? OC + "20" : OC + "44"), borderRadius: 12, color: loading ? OC + "55" : OC, fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              {loading ? <><div style={{ width: 14, height: 14, border: "2px solid " + OC + "30", borderTopColor: OC, borderRadius: "50%", animation: "teSpin 0.7s linear infinite" }} />Comparing offers...</> : "⚖ Compare My Offers"}
            </button>
          </>
        )}
        {result && !loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, animation: "teFadeIn 0.3s ease" }}>
            {/* Winner banner */}
            <div style={{ background: winColor(result.winner) + "10", border: "2px solid " + winColor(result.winner) + "35", borderRadius: 14, padding: "16px 18px", textAlign: "center" }}>
              <div style={{ fontSize: 9, color: winColor(result.winner), letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }}>VERDICT — {result.confidence} CONFIDENCE</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: winColor(result.winner) }}>
                {result.winner === "Tie" ? "It's a Tie" : "Offer " + result.winner + " Wins"}
              </div>
              <div style={{ fontSize: 13, color: DS.t2, marginTop: 6 }}>{result.winner_reason}</div>
            </div>
            {/* Dimension table */}
            <div style={{ background: DS.bg0, border: "1px solid " + DS.border0, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", borderBottom: "1px solid " + DS.border0 }}>
                <div style={{ padding: "8px 12px", fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace" }}>DIMENSION</div>
                <div style={{ padding: "8px 12px", fontSize: 9, color: DS.blue, fontFamily: "'JetBrains Mono',monospace", borderLeft: "1px solid " + DS.border0 }}>OFFER A</div>
                <div style={{ padding: "8px 12px", fontSize: 9, color: DS.teal, fontFamily: "'JetBrains Mono',monospace", borderLeft: "1px solid " + DS.border0 }}>OFFER B</div>
              </div>
              {DIMS.map(function(dim) {
                var d = result.comparison && result.comparison[dim.key];
                if (!d) return null;
                var winA = d.winner === "A";
                var winB = d.winner === "B";
                return (
                  <div key={dim.key} style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", borderTop: "1px solid " + DS.border0 }}>
                    <div style={{ padding: "10px 12px", fontSize: 12, color: DS.t2, fontWeight: 600 }}>{dim.label}</div>
                    <div style={{ padding: "10px 12px", borderLeft: "1px solid " + DS.border0, background: winA ? DS.blue + "06" : "transparent" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 12, fontWeight: winA ? 700 : 400, color: winA ? DS.blue : DS.t2 }}>{d.a_detail}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: DS.blue, fontFamily: "'JetBrains Mono',monospace", flexShrink: 0, marginLeft: 6 }}>{d.a_score}</span>
                      </div>
                      {scoreBar(d.a_score, DS.blue)}
                    </div>
                    <div style={{ padding: "10px 12px", borderLeft: "1px solid " + DS.border0, background: winB ? DS.teal + "06" : "transparent" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 12, fontWeight: winB ? 700 : 400, color: winB ? DS.teal : DS.t2 }}>{d.b_detail}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: DS.teal, fontFamily: "'JetBrains Mono',monospace", flexShrink: 0, marginLeft: 6 }}>{d.b_score}</span>
                      </div>
                      {scoreBar(d.b_score, DS.teal)}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Red flags */}
            {(result.red_flags_a && result.red_flags_a.length > 0) || (result.red_flags_b && result.red_flags_b.length > 0) ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[{ label: "OFFER A RED FLAGS", flags: result.red_flags_a, color: DS.blue }, { label: "OFFER B RED FLAGS", flags: result.red_flags_b, color: DS.teal }].map(function(item) {
                  if (!item.flags || item.flags.length === 0) return <div key={item.label} />;
                  return (
                    <div key={item.label} style={{ background: DS.red + "05", border: "1px solid " + DS.red + "18", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: 9, color: DS.red, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 6 }}>{item.label}</div>
                      {item.flags.map(function(f, i) { return <div key={i} style={{ fontSize: 11, color: DS.t2, marginBottom: 4, display: "flex", gap: 6 }}><span style={{ color: DS.red }}>→</span>{f}</div>; })}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {/* Final advice */}
            {result.final_advice && (
              <div style={{ fontSize: 13, color: DS.t2, lineHeight: 1.75, padding: "12px 14px", background: DS.bg0, borderLeft: "3px solid " + OC, borderRadius: "0 8px 8px 0" }}>
                💡 {result.final_advice}
              </div>
            )}
            <button onClick={function() { setResult(null); setOfferA(""); setOfferB(""); setError(null); }}
              style={{ alignSelf: "flex-start", fontSize: 12, padding: "7px 14px", background: "transparent", border: "1px solid " + DS.border0, borderRadius: 8, color: DS.t3, cursor: "pointer" }}>
              ← Compare different offers
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── 7. MONTHLY CAREER AUDIT ───────────────────────────
var AUDIT_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function getLastAuditData() {
  try { return memGet(AUDIT_KEY) || null; } catch(e) { return null; }
}
function saveAuditData(d) {
  try { memSet(AUDIT_KEY, Object.assign({}, d, { ts: Date.now() })); } catch(e) {}
}
function isDueForAudit() {
  var d = getLastAuditData();
  if (!d || !d.ts) return false;
  return Date.now() - d.ts > AUDIT_INTERVAL_MS;
}

function MonthlyCareerAudit() {
  var auditData = getLastAuditData();
  var due = isDueForAudit();
  var s0 = useState(false); var open = s0[0]; var setOpen = s0[1];
  var s1 = useState(auditData); var audit = s1[0]; var setAudit = s1[1];
  var s2 = useState(false); var loading = s2[0]; var setLoading = s2[1];
  var abRef = useRef(null);
  var AC = G; // gold accent

  // Gather data from memory
  var scanHist = useMemo(function() { try { return JSON.parse(typeof localStorage !== "undefined" ? (localStorage.getItem(HK) || "[]") : "[]"); } catch(e) { return []; } }, []);
  var atsHist = useMemo(function() { try { return JSON.parse(typeof localStorage !== "undefined" ? (localStorage.getItem("te_v5_ats_hist") || "[]") : "[]"); } catch(e) { return []; } }, []);
  var s3model = useMemo(function() { try { return memGet("te_s3_usermodel") || {}; } catch(e) { return {}; } }, []);

  var hasData = scanHist.length > 0 || atsHist.length > 0 || s3model.scan || s3model.resume;
  if (!hasData) return null;

  async function generateAudit() {
    if (loading) return;
    if (abRef.current) abRef.current.abort();
    abRef.current = new AbortController();
    setLoading(true);

    var AUDIT_SYS = "You are a career intelligence system. Generate a monthly career health audit.\nRespond ONLY with valid JSON:\n{\"health_score\":0,\"trend\":\"Improving/Stable/Declining\",\"headline\":\"One line summary.\",\"wins\":[\"...\"],\"concerns\":[\"...\"],\"next_30_days\":[\"...\",\"...\",\"...\"],\"risk_alert\":null,\"momentum\":\"High/Medium/Low\"}";

    var ctx = {
      scans: scanHist.length,
      scams_avoided: scanHist.filter(function(h) { return h.score >= 70; }).length,
      ats_scores: atsHist.slice(0, 5).map(function(h) { return h.score; }),
      ats_trend: atsHist.length > 1 ? (atsHist[0].score - atsHist[1].score > 0 ? "improving" : atsHist[0].score - atsHist[1].score < 0 ? "declining" : "stable") : "n/a",
      has_resume: !!(s3model.resume),
      has_paths: !!(s3model.pathfinder),
      last_activity: scanHist[0] && scanHist[0].ts ? new Date(scanHist[0].ts).toDateString() : "unknown",
    };

    try {
      var parsed = await runAIStable("Career data this month: " + JSON.stringify(ctx), AUDIT_SYS, abRef.current.signal);
      if (!parsed || parsed.error || parsed.fallback) {
        var atsAvg = atsHist.length > 0 ? Math.round(atsHist.reduce(function(a, h) { return a + h.score; }, 0) / atsHist.length) : 0;
        parsed = {
          health_score: atsAvg || 55,
          trend: ctx.ats_trend === "improving" ? "Improving" : ctx.ats_trend === "declining" ? "Declining" : "Stable",
          headline: "You've been active this month. Keep the momentum going.",
          wins: [
            ctx.scans > 0 ? "Checked " + ctx.scans + " job post" + (ctx.scans > 1 ? "s" : "") + " for fraud" : null,
            ctx.scams_avoided > 0 ? "Avoided " + ctx.scams_avoided + " high-risk posting" + (ctx.scams_avoided > 1 ? "s" : "") : null,
            ctx.has_resume ? "Resume analyzed and in progress" : null,
          ].filter(Boolean),
          concerns: [
            !ctx.has_paths ? "Career paths not yet explored — try Pathfinder" : null,
            atsHist.length > 0 && atsHist[0].score < 60 ? "ATS score below 60 — needs improvement" : null,
          ].filter(Boolean),
          next_30_days: ["Run Pathfinder for your next career move", "Improve ATS score by 10 points", "Scan job offers before applying"],
          risk_alert: null,
          momentum: ctx.scans > 3 ? "High" : ctx.scans > 1 ? "Medium" : "Low",
        };
      }
      parsed.generated_at = new Date().toLocaleDateString("en-IN");
      setAudit(parsed);
      saveAuditData(parsed);
    } catch(e) {}
    finally { setLoading(false); }
  }

  // Auto-generate on first open if due
  var handleOpen = function() {
    setOpen(function(v) {
      if (!v && !audit && !loading) { setTimeout(generateAudit, 100); }
      return !v;
    });
  };

  var trendColor = audit && audit.trend === "Improving" ? DS.green : audit && audit.trend === "Declining" ? DS.red : DS.amber;
  var momColor = audit && audit.momentum === "High" ? DS.green : audit && audit.momentum === "Low" ? DS.red : DS.amber;

  return (
    <div style={{ background: DS.bg1, border: "1px solid " + AC + "25", borderRadius: 14, overflow: "hidden", marginBottom: 20 }}>
      <button onClick={handleOpen}
        style={{ width: "100%", padding: "14px 18px", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, background: AC + "15", border: "1px solid " + AC + "30", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>📊</div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 9, color: AC, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
              MONTHLY CAREER AUDIT
              {due && <span style={{ marginLeft: 8, background: DS.red + "15", color: DS.red, padding: "1px 7px", borderRadius: 4, fontSize: 8 }}>DUE</span>}
            </div>
            <div style={{ fontSize: 11, color: DS.t3, marginTop: 2 }}>
              {audit ? "Last audit: " + (audit.generated_at || "this session") : "Your 30-day career health summary"}
            </div>
          </div>
        </div>
        <span style={{ color: AC + "80", fontSize: 16, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>›</span>
      </button>
      {open && (
        <div style={{ padding: "0 18px 18px", borderTop: "1px solid " + AC + "15" }}>
          {loading && (
            <div style={{ padding: "20px 0", display: "flex", alignItems: "center", gap: 12, color: AC }}>
              <div style={{ width: 14, height: 14, border: "2px solid " + AC + "30", borderTopColor: AC, borderRadius: "50%", animation: "teSpin 0.7s linear infinite" }} />
              <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace" }}>Generating your career audit...</span>
            </div>
          )}
          {audit && !loading && (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <div style={{ background: DS.bg0, border: "1px solid " + AC + "18", borderRadius: 12, padding: "12px", textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>HEALTH SCORE</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: AC, fontFamily: "'JetBrains Mono',monospace" }}>{audit.health_score}</div>
                </div>
                <div style={{ background: DS.bg0, border: "1px solid " + trendColor + "18", borderRadius: 12, padding: "12px", textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>TREND</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: trendColor }}>{audit.trend}</div>
                </div>
                <div style={{ background: DS.bg0, border: "1px solid " + momColor + "18", borderRadius: 12, padding: "12px", textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>MOMENTUM</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: momColor }}>{audit.momentum}</div>
                </div>
              </div>
              {audit.headline && (
                <div style={{ fontSize: 13, color: DS.t1, fontWeight: 600, lineHeight: 1.6, padding: "10px 14px", background: AC + "08", borderRadius: 10, border: "1px solid " + AC + "18" }}>
                  {audit.headline}
                </div>
              )}
              {audit.wins && audit.wins.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: DS.green, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 8 }}>THIS MONTH'S WINS</div>
                  {audit.wins.map(function(w, i) { return <div key={i} style={{ fontSize: 12, color: DS.t2, marginBottom: 6, display: "flex", gap: 8 }}><span style={{ color: DS.green }}>✓</span>{w}</div>; })}
                </div>
              )}
              {audit.concerns && audit.concerns.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: DS.amber, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 8 }}>CONCERNS</div>
                  {audit.concerns.map(function(c, i) { return <div key={i} style={{ fontSize: 12, color: DS.t2, marginBottom: 6, display: "flex", gap: 8 }}><span style={{ color: DS.amber }}>⚠</span>{c}</div>; })}
                </div>
              )}
              {audit.next_30_days && audit.next_30_days.length > 0 && (
                <div style={{ background: DS.bg0, border: "1px solid " + DS.border0, borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, color: AC, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, marginBottom: 8 }}>NEXT 30 DAYS</div>
                  {audit.next_30_days.map(function(a, i) {
                    return <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "flex-start" }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: AC + "15", border: "1px solid " + AC + "25", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: AC, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                      <span style={{ fontSize: 12, color: DS.t2, lineHeight: 1.6 }}>{a}</span>
                    </div>;
                  })}
                </div>
              )}
              {audit.risk_alert && (
                <div style={{ background: DS.red + "08", border: "1px solid " + DS.red + "22", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: DS.red }}>
                  🚨 {audit.risk_alert}
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={generateAudit} disabled={loading}
                  style={{ fontSize: 11, padding: "6px 14px", background: AC + "10", border: "1px solid " + AC + "25", borderRadius: 8, color: AC, cursor: "pointer", fontWeight: 600 }}>
                  ↺ Refresh Audit
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
// ===================================================
// END PHASE 3 COMPONENTS
// ===================================================

// ===================================================
// PHASE 2 — NEW COMPONENTS
// ===================================================

function ManipulationFingerprint({ result }) {
  if (!result || !result.manipulation_tags || result.manipulation_tags.length === 0) return null;
  var TACTIC_DESC = {
    "Urgency Pressure": { icon: "⏰", desc: "Forces fast decisions — real jobs never expire in hours" },
    "FOMO": { icon: "😰", desc: "Fear of missing out — 'limited seats' is a scam signal" },
    "Authority Impersonation": { icon: "🎭", desc: "Fake brand/company to borrow trust" },
    "False Social Proof": { icon: "👥", desc: "Fake testimonials, inflated numbers" },
    "Financial Bait": { icon: "💸", desc: "Unrealistic salary to hook desperate applicants" },
    "Vagueness Shield": { icon: "🌫", desc: "Intentionally vague to avoid accountability" },
  };
  var sv = useState(false); var show = sv[0]; var setShow = sv[1];
  useEffect(function() { var t = setTimeout(function() { setShow(true); }, 120); return function() { clearTimeout(t); }; }, []);
  return (
    <div style={{ opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(10px)", transition: "all 0.5s cubic-bezier(0.22,1,0.36,1)", background: "rgba(139,92,246,0.04)", border: "1px solid rgba(139,92,246,0.18)", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 14 }}>🧠</span>
        <span style={{ fontSize: 9, color: DS.purple, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>MANIPULATION FINGERPRINT</span>
        <span style={{ marginLeft: "auto", fontSize: 10, color: DS.t3, fontFamily: "'JetBrains Mono',monospace" }}>{result.manipulation_tags.length} tactics detected</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 8 }}>
        {result.manipulation_tags.map(function(t, i) {
          var sev = t.severity === "HIGH" ? DS.red : t.severity === "MEDIUM" ? DS.amber : DS.t3;
          var info = TACTIC_DESC[t.tag] || { icon: "⚠", desc: "Psychological manipulation tactic" };
          return (
            <div key={i} style={{ background: sev + "08", border: "1px solid " + sev + "25", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 13 }}>{info.icon}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: sev }}>{t.tag}</span>
                <span style={{ marginLeft: "auto", fontSize: 8, color: sev, fontFamily: "'JetBrains Mono',monospace", background: sev + "15", padding: "1px 6px", borderRadius: 4 }}>{t.severity}</span>
              </div>
              <div style={{ fontSize: 11, color: DS.t3, lineHeight: 1.5 }}>{info.desc}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimeBombIndicator({ result }) {
  if (!result) return null;
  var urgencyTags = (result.manipulation_tags || []).filter(function(t) {
    return t.tag === "Urgency Pressure" || t.tag === "FOMO";
  });
  var urgencySignals = (result.manipulation_signals || []).filter(function(s) {
    return /urgent|hurry|today|tonight|immediately|24 hours|last chance|limited|closing|deadline/i.test(s);
  });
  if (urgencyTags.length === 0 && urgencySignals.length === 0) return null;
  var sv = useState(false); var pulse = sv[0]; var setPulse = sv[1];
  useEffect(function() {
    var t = setInterval(function() { setPulse(function(p) { return !p; }); }, 900);
    return function() { clearInterval(t); };
  }, []);
  return (
    <div style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 14, padding: "14px 18px", marginBottom: 16, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg,#ef4444,#f97316,#ef4444)", opacity: pulse ? 1 : 0.3, transition: "opacity 0.9s" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 18, opacity: pulse ? 1 : 0.6, transition: "opacity 0.9s" }}>💣</span>
        <span style={{ fontSize: 11, fontWeight: 800, color: DS.red, letterSpacing: 1.5, fontFamily: "'JetBrains Mono',monospace" }}>TIME BOMB DETECTED</span>
      </div>
      <div style={{ fontSize: 13, color: "#fca5a5", lineHeight: 1.65, marginBottom: 8 }}>
        This post is <strong style={{ color: DS.red }}>engineered to make you decide immediately</strong>. Legitimate employers never expire job offers in hours or impose artificial urgency.
      </div>
      {urgencySignals.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {urgencySignals.slice(0, 4).map(function(s, i) {
            return <span key={i} style={{ fontSize: 11, color: DS.red, background: DS.red + "12", border: "1px solid " + DS.red + "25", padding: "3px 10px", borderRadius: 6, fontFamily: "'JetBrains Mono',monospace" }}>"{s}"</span>;
          })}
        </div>
      )}
    </div>
  );
}

function SafeApplyChecklist({ result }) {
  if (!result || result.risk_score >= 70) return null;
  var sv = useState({}); var checked = sv[0]; var setChecked = sv[1];
  var company = result.detected_entities && result.detected_entities.company;
  var domain = result.detected_entities && result.detected_entities.domain;
  var items = [
    company ? "Verify " + company + " on MCA India portal (mcaservices.mca.gov.in)" : "Verify company registration on MCA India portal",
    domain ? "Confirm the official company site is " + domain + " — not a lookalike domain" : "Confirm official company website before applying",
    "Search '" + (company || "this company") + " reviews' on Glassdoor and AmbitionBox",
    "Apply only via the company's official website — never via WhatsApp or Telegram link",
    result.salary_intel && result.salary_intel.market_rate_mismatch
      ? "Salary looks unusual — cross-check on LinkedIn Salary Insights before negotiating"
      : "Cross-check the salary range on LinkedIn Salary Insights",
    "If asked for any deposit, fee, or document charges — it is a scam. Stop immediately.",
  ];
  var doneCount = Object.values(checked).filter(Boolean).length;
  return (
    <div style={{ background: "rgba(34,197,94,0.04)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14 }}>✅</span>
          <span style={{ fontSize: 9, color: DS.green, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>SAFE APPLY CHECKLIST</span>
        </div>
        <span style={{ fontSize: 11, color: doneCount === items.length ? DS.green : DS.t3, fontFamily: "'JetBrains Mono',monospace" }}>{doneCount}/{items.length} complete</span>
      </div>
      {doneCount === items.length && (
        <div style={{ background: DS.green + "12", border: "1px solid " + DS.green + "30", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12, color: DS.green, fontWeight: 600 }}>
          ✓ All checks complete — you're ready to apply safely
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map(function(item, i) {
          var done = !!checked[i];
          return (
            <div key={i} onClick={function() { setChecked(function(prev) { var n = Object.assign({}, prev); n[i] = !prev[i]; return n; }); }}
              style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: done ? DS.green + "08" : DS.bg0, border: "1px solid " + (done ? DS.green + "25" : DS.border0), borderRadius: 10, cursor: "pointer", transition: "all 0.2s" }}>
              <div style={{ width: 18, height: 18, borderRadius: 5, border: "1.5px solid " + (done ? DS.green : DS.border1), background: done ? DS.green : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, transition: "all 0.2s" }}>
                {done && <span style={{ fontSize: 10, color: "#fff", fontWeight: 900 }}>✓</span>}
              </div>
              <span style={{ fontSize: 12, color: done ? DS.t2 : DS.t1, lineHeight: 1.6, textDecoration: done ? "line-through" : "none", opacity: done ? 0.6 : 1, transition: "all 0.2s" }}>{item}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ScamDNAMatch({ result }) {
  if (!result || result.risk_score < 40) return null;
  var KNOWN_SCAM_DNA = [
    { pattern: "Registration Fee + Remote Work", signals: ["registration fee", "joining fee", "work from home", "ghar baithe"], count: 14823, year: "2019", type: "Classic Fee Scam" },
    { pattern: "Packing / Assembly Job Scam", signals: ["flipkart packing", "amazon packing", "assembly work", "kit"], count: 9201, year: "2021", type: "Home Labour Scam" },
    { pattern: "Telegram Group Hiring Scam", signals: ["telegram", "join group", "channel link"], count: 22447, year: "2022", type: "Social Platform Scam" },
    { pattern: "MLM / Network Marketing", signals: ["mlm", "network marketing", "referral income", "team building"], count: 7832, year: "2020", type: "MLM Pyramid" },
    { pattern: "Data Entry Work From Home", signals: ["data entry", "work from home", "unlimited income", "part time"], count: 18902, year: "2018", type: "Ghost Work Scam" },
    { pattern: "UPI/Advance Payment Trap", signals: ["upi", "advance", "security deposit", "refundable"], count: 31200, year: "2023", type: "Payment Fraud" },
  ];
  var text = (JSON.stringify(result) + " " + (result.final_reasoning || "")).toLowerCase();
  var matches = KNOWN_SCAM_DNA.filter(function(dna) {
    return dna.signals.filter(function(s) { return text.includes(s); }).length >= 1;
  });
  if (matches.length === 0) return null;
  return (
    <div style={{ background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 14 }}>🧬</span>
        <span style={{ fontSize: 9, color: DS.red, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>SCAM DNA MATCH</span>
        <span style={{ marginLeft: "auto", fontSize: 10, color: DS.red, fontFamily: "'JetBrains Mono',monospace" }}>{matches.length} pattern{matches.length > 1 ? "s" : ""} matched</span>
      </div>
      <div style={{ fontSize: 12, color: DS.t3, marginBottom: 12, lineHeight: 1.6 }}>
        This job matches confirmed fraud patterns in our registry.
      </div>
      {matches.slice(0, 3).map(function(m, i) {
        return (
          <div key={i} style={{ background: DS.bg0, border: "1px solid rgba(239,68,68,0.15)", borderRadius: 10, padding: "10px 14px", marginBottom: i < matches.length - 1 ? 8 : 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: DS.red, marginBottom: 3 }}>{m.pattern}</div>
                <div style={{ fontSize: 11, color: DS.t3 }}>Type: {m.type} · First seen: {m.year}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: DS.red, fontFamily: "'JetBrains Mono',monospace" }}>{m.count.toLocaleString("en-IN")}</div>
                <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace" }}>CONFIRMED CASES</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ResumeMarketMirror({ result }) {
  if (!result || !result.salary_benchmarks) return null;
  var sv = useState(false); var show = sv[0]; var setShow = sv[1];
  useEffect(function() { var t = setTimeout(function() { setShow(true); }, 200); return function() { clearTimeout(t); }; }, []);
  var rows = [
    {
      label: "Your Current Value",
      you: result.salary_benchmarks.current_market_value || "—",
      market: result.salary_benchmarks.target_after_upskilling || "—",
      verdict: "Gap",
      verdictColor: DS.red,
    },
    {
      label: "Most In-Demand Skill",
      you: result.skills_found && result.skills_found.length > 0 ? result.skills_found[0] : "Not detected",
      market: result.skills_missing && result.skills_missing.length > 0 ? result.skills_missing[0].skill : "Up to date",
      verdict: result.skills_missing && result.skills_missing.length > 0 ? "Missing" : "Strong",
      verdictColor: result.skills_missing && result.skills_missing.length > 0 ? DS.amber : DS.green,
    },
    {
      label: "Profile Positioning",
      you: result.candidate_level || "—",
      market: result.top_roles && result.top_roles[0] ? result.top_roles[0].role : "—",
      verdict: result.top_roles && result.top_roles[0] && result.top_roles[0].match > 80 ? "Aligned" : "Adjust",
      verdictColor: result.top_roles && result.top_roles[0] && result.top_roles[0].match > 80 ? DS.green : DS.amber,
    },
  ];
  return (
    <div style={{ opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(8px)", transition: "all 0.4s ease", background: DS.bg1, border: "1px solid " + DS.border0, borderRadius: 14, overflow: "hidden", marginBottom: 20 }}>
      <div style={{ padding: "12px 18px", borderBottom: "1px solid " + DS.border0, background: "rgba(59,130,246,0.04)" }}>
        <div style={{ fontSize: 9, color: DS.blue, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>RESUME VS MARKET MIRROR</div>
        <div style={{ fontSize: 11, color: DS.t3, marginTop: 2 }}>Your resume says X. The market currently values Y.</div>
      </div>
      <div style={{ padding: "14px 18px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0 }}>
          {["Your Resume", "Market Demands", "Gap"].map(function(h) {
            return <div key={h} style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, padding: "4px 8px", textAlign: "center" }}>{h}</div>;
          })}
        </div>
        {rows.map(function(row, i) {
          return (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0, borderTop: "1px solid " + DS.border0, marginTop: 4, paddingTop: 10, paddingBottom: 10 }}>
              <div style={{ padding: "0 8px" }}>
                <div style={{ fontSize: 10, color: DS.t4, marginBottom: 3 }}>{row.label}</div>
                <div style={{ fontSize: 12, color: DS.t2, fontWeight: 600, lineHeight: 1.4 }}>{row.you}</div>
              </div>
              <div style={{ padding: "0 8px", borderLeft: "1px solid " + DS.border0 }}>
                <div style={{ fontSize: 10, color: DS.t4, marginBottom: 3 }}>Current demand</div>
                <div style={{ fontSize: 12, color: DS.blue, fontWeight: 600, lineHeight: 1.4 }}>{row.market}</div>
              </div>
              <div style={{ padding: "0 8px", borderLeft: "1px solid " + DS.border0, textAlign: "center" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: row.verdictColor, marginTop: 12, fontFamily: "'JetBrains Mono',monospace" }}>{row.verdict}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ResumeLieDetectorPanel({ resumeText }) {
  if (!resumeText || resumeText.length < 50) return null;
  var sv = useState(false); var expanded = sv[0]; var setExpanded = sv[1];
  var fakeData = useMemo(function() {
    try { return detectFakeResume(resumeText); } catch(e) { return null; }
  }, [resumeText]);
  if (!fakeData) return null;
  var score = fakeData.suspicion_score || 0;
  var color = score >= 60 ? DS.red : score >= 30 ? DS.amber : DS.green;
  var label = score >= 60 ? "HIGH RISK — Likely AI-Generated" : score >= 30 ? "MODERATE — Some Inflation Detected" : "CLEAN — Authentic-Looking";
  return (
    <div style={{ background: color + "06", border: "1px solid " + color + "25", borderRadius: 14, overflow: "hidden", marginBottom: 20 }}>
      <button onClick={function() { setExpanded(function(v) { return !v; }); }}
        style={{ width: "100%", padding: "14px 18px", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>🔬</span>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 9, color: color, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>AUTHENTICITY SCANNER</div>
            <div style={{ fontSize: 12, color: color, fontWeight: 600, marginTop: 2 }}>{label}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: color, fontFamily: "'JetBrains Mono',monospace" }}>{score}</div>
          <span style={{ color: DS.t4, fontSize: 16, transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>›</span>
        </div>
      </button>
      {expanded && (
        <div style={{ padding: "0 18px 16px", borderTop: "1px solid " + color + "15", animation: "teFadeIn 0.2s ease" }}>
          {score < 30 && (
            <div style={{ padding: "10px 12px", background: DS.green + "08", borderRadius: 8, fontSize: 12, color: DS.green, marginTop: 12 }}>
              ✓ No major authenticity issues detected. Resume reads as genuine.
            </div>
          )}
          {fakeData.flags && fakeData.flags.length > 0 && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {fakeData.flags.map(function(f, i) {
                return (
                  <div key={i} style={{ display: "flex", gap: 10, padding: "10px 12px", background: color + "06", border: "1px solid " + color + "15", borderRadius: 10 }}>
                    <span style={{ color: color, flexShrink: 0, fontSize: 13 }}>⚠</span>
                    <span style={{ fontSize: 12, color: DS.t2, lineHeight: 1.6 }}>{f}</span>
                  </div>
                );
              })}
            </div>
          )}
          {score >= 30 && (
            <div style={{ marginTop: 12, padding: "10px 12px", background: DS.bg0, borderRadius: 8, fontSize: 12, color: DS.t3, lineHeight: 1.6 }}>
              💡 Fix: Replace AI phrases with specific project outcomes, real company names, and quantified results.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SalaryNegotiationAnchor({ result }) {
  if (!result || !result.salary_benchmarks) return null;
  var current = result.salary_benchmarks.current_market_value || "";
  var target = result.salary_benchmarks.target_after_upskilling || "";
  var tip = result.salary_benchmarks.negotiation_tip || "";
  var premiums = result.salary_benchmarks.in_demand_skills_premium || [];
  return (
    <div style={{ background: "rgba(34,197,94,0.04)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 14 }}>💰</span>
        <span style={{ fontSize: 9, color: DS.green, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>NEGOTIATION ANCHOR</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div style={{ background: DS.red + "08", border: "1px solid " + DS.red + "20", borderRadius: 10, padding: "12px" }}>
          <div style={{ fontSize: 10, color: DS.t3, marginBottom: 4 }}>YOUR FLOOR (don't accept less)</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: DS.red }}>{current}</div>
        </div>
        <div style={{ background: DS.green + "08", border: "1px solid " + DS.green + "20", borderRadius: 10, padding: "12px" }}>
          <div style={{ fontSize: 10, color: DS.t3, marginBottom: 4 }}>YOUR TARGET (after upskilling)</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: DS.green }}>{target}</div>
        </div>
      </div>
      {tip && (
        <div style={{ background: DS.bg0, borderLeft: "3px solid " + DS.green, borderRadius: "0 8px 8px 0", padding: "10px 14px", fontSize: 12, color: DS.t2, lineHeight: 1.65, marginBottom: premiums.length > 0 ? 10 : 0 }}>
          💡 {tip}
        </div>
      )}
      {premiums.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, color: DS.t3, marginBottom: 6, fontFamily: "'JetBrains Mono',monospace" }}>SKILL SALARY PREMIUMS</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {premiums.map(function(p, i) { return <Pill key={i} text={p} color={DS.green} />; })}
          </div>
        </div>
      )}
    </div>
  );
}

function CareerRiskRadar({ result }) {
  if (!result) return null;
  var sv = useState(false); var show = sv[0]; var setShow = sv[1];
  useEffect(function() { var t = setTimeout(function() { setShow(true); }, 300); return function() { clearTimeout(t); }; }, []);
  var axes = [
    { label: "Role Security",      value: result.role_security  != null ? result.role_security  : 65 },
    { label: "Salary Trajectory",  value: result.salary_traj    != null ? result.salary_traj    : 60 },
    { label: "Market Demand",      value: result.market_demand  != null ? result.market_demand  : 70 },
    { label: "Skill Shelf-life",   value: result.skill_shelf    != null ? result.skill_shelf    : 55 },
    { label: "Domain Growth",      value: result.domain_growth  != null ? result.domain_growth  : 65 },
  ];
  return (
    <div style={{ opacity: show ? 1 : 0, transition: "opacity 0.4s", background: DS.bg1, border: "1px solid " + DS.border0, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 14 }}>📡</span>
        <span style={{ fontSize: 9, color: DS.purple, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>CAREER RISK RADAR</span>
      </div>
      {axes.map(function(a, idx) {
        var c = a.value >= 70 ? DS.green : a.value >= 40 ? DS.amber : DS.red;
        return (
          <div key={a.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: DS.t2, width: 110, flexShrink: 0 }}>{a.label}</div>
            <div style={{ flex: 1, height: 6, background: DS.bg3, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: (show ? a.value : 0) + "%", background: c, borderRadius: 3, transition: "width 1s cubic-bezier(0.22,1,0.36,1) " + (idx * 0.08) + "s", boxShadow: "0 0 6px " + c + "66" }} />
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono',monospace", width: 30, textAlign: "right" }}>{a.value}</div>
          </div>
        );
      })}
    </div>
  );
}

function CareerObituary({ result }) {
  if (!result) return null;
  var sv = useState(false); var open = sv[0]; var setOpen = sv[1];
  var danger = result.danger_zones || result.career_danger || null;
  var stagnation = result.stagnation_risk || null;
  if (!danger && !stagnation) return null;
  return (
    <div style={{ background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.18)", borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
      <button onClick={function() { setOpen(function(v) { return !v; }); }}
        style={{ width: "100%", padding: "14px 18px", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>⚰️</span>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 9, color: DS.red, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>CAREER OBITUARY</div>
            <div style={{ fontSize: 12, color: DS.t3, marginTop: 2 }}>If you do nothing for 3 years, here's what happens</div>
          </div>
        </div>
        <span style={{ color: DS.t4, fontSize: 16, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>›</span>
      </button>
      {open && (
        <div style={{ padding: "0 18px 16px", borderTop: "1px solid rgba(239,68,68,0.12)", animation: "teFadeIn 0.2s ease" }}>
          {danger && (
            <div style={{ marginTop: 12, fontSize: 13, color: "#fca5a5", lineHeight: 1.75, borderLeft: "3px solid " + DS.red + "50", paddingLeft: 14 }}>
              {typeof danger === "string" ? danger : (Array.isArray(danger) ? danger.join(" ") : "Career stagnation risk detected.")}
            </div>
          )}
          {stagnation && (
            <div style={{ marginTop: 10, fontSize: 13, color: DS.t2, lineHeight: 1.7 }}>
              Stagnation risk: <span style={{ color: DS.red, fontWeight: 700 }}>{stagnation}</span>
            </div>
          )}
          <div style={{ marginTop: 12, padding: "10px 12px", background: DS.green + "08", border: "1px solid " + DS.green + "20", borderRadius: 8, fontSize: 12, color: DS.green, fontWeight: 600 }}>
            → Use Pathfinder now to map your next move before this becomes reality.
          </div>
        </div>
      )}
    </div>
  );
}

function JDFitVerdict({ result }) {
  if (!result || !result.fit_verdict) return null;
  var JC = DS.cyan;
  var fitMap = {
    "Strong Fit":   { color: DS.green, icon: "✓", bg: DS.green + "06" },
    "Moderate Fit": { color: DS.amber, icon: "~", bg: DS.amber + "06" },
    "Weak Fit":     { color: DS.red,   icon: "✕", bg: DS.red   + "06" },
  };
  var cfg = fitMap[result.fit_verdict] || fitMap["Moderate Fit"];
  return (
    <div style={{ background: cfg.bg, border: "1px solid " + cfg.color + "30", borderRadius: 14, padding: "16px 18px", marginBottom: 14, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: cfg.color + "15", border: "1px solid " + cfg.color + "30", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 900, color: cfg.color, flexShrink: 0 }}>
        {cfg.icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 9, color: cfg.color, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, marginBottom: 3 }}>FIT VERDICT</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: cfg.color, marginBottom: 4 }}>{result.fit_verdict}</div>
        {result.fit_reason && <div style={{ fontSize: 13, color: DS.t2, lineHeight: 1.6 }}>{result.fit_reason}</div>}
      </div>
      {result.apply_verdict && (
        <div style={{ flexShrink: 0 }}>
          <Pill text={result.apply_verdict === "Apply" ? "✓ Apply Now" : result.apply_verdict === "Customize" ? "⚠ Customize First" : "✕ Skip"} color={result.apply_verdict === "Apply" ? DS.green : result.apply_verdict === "Customize" ? DS.amber : DS.red} />
        </div>
      )}
    </div>
  );
}

function JDRealVsPosted({ result }) {
  if (!result || !result.real_vs_posted || result.real_vs_posted.length === 0) return null;
  var JC = DS.cyan;
  return (
    <div style={{ background: DS.bg1, border: "1px solid " + JC + "20", borderRadius: 14, overflow: "hidden", marginBottom: 14 }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid " + JC + "15", background: JC + "04", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14 }}>🔭</span>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: JC }}>Real Job vs Posted Job</div>
          <div style={{ fontSize: 11, color: DS.t3 }}>Corporate speak decoded into plain English</div>
        </div>
      </div>
      <div style={{ padding: "14px 16px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, marginBottom: 8 }}>
          <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, padding: "0 6px 8px" }}>WHAT THEY WROTE</div>
          <div style={{ fontSize: 9, color: JC, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, padding: "0 6px 8px", borderLeft: "1px solid " + DS.border0 }}>WHAT IT ACTUALLY MEANS</div>
        </div>
        {result.real_vs_posted.map(function(row, i) {
          return (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: "1px solid " + DS.border0, paddingTop: 8, paddingBottom: 8, gap: 0 }}>
              <div style={{ fontSize: 12, color: DS.t3, lineHeight: 1.6, padding: "0 8px 0 6px", fontStyle: "italic" }}>"{row.posted}"</div>
              <div style={{ fontSize: 12, color: DS.t1, lineHeight: 1.6, padding: "0 6px 0 8px", borderLeft: "1px solid " + DS.border0, fontWeight: 500 }}>{row.reality}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function JDGhostJobDetector({ result }) {
  if (!result) return null;
  var prob = result.ghost_job_probability || 0;
  if (prob < 20) return null;
  var color = prob >= 60 ? DS.amber : DS.t3;
  var signals = result.ghost_signals || [];
  return (
    <div style={{ background: DS.amber + "06", border: "1px solid " + DS.amber + "22", borderRadius: 14, padding: "14px 16px", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 14 }}>👻</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: DS.amber, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>GHOST JOB DETECTOR</div>
        </div>
        <div style={{ fontSize: 18, fontWeight: 900, color: color, fontFamily: "'JetBrains Mono',monospace" }}>{prob}%</div>
      </div>
      <div style={{ fontSize: 12, color: DS.t2, lineHeight: 1.65, marginBottom: signals.length > 0 ? 10 : 0 }}>
        {prob >= 60
          ? "High probability this is a ghost posting — a role that isn't actually open. Companies post these to build candidate pipelines or satisfy compliance requirements."
          : "Some signals suggest this might not be an active opening. Verify before spending time on your application."}
      </div>
      {signals.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {signals.map(function(s, i) { return <Pill key={i} text={s} color={DS.amber} />; })}
        </div>
      )}
    </div>
  );
}

function JDCultureToxicityScanner({ result }) {
  if (!result) return null;
  var toxicFlags = result.toxicity_flags || [];
  var cultureSignals = result.culture_signals || [];
  if (toxicFlags.length === 0 && cultureSignals.length === 0) return null;
  var DECODE_MAP = {
    "fast-paced": "High pressure, constant firefighting",
    "wear many hats": "Understaffed — you'll do 3 jobs for 1 salary",
    "self-starter": "No onboarding, no support",
    "dynamic environment": "Chaos with no processes",
    "startup culture": "Expect unpaid overtime",
    "hit the ground running": "No training period",
    "go-getter": "They'll push you to work nights/weekends",
    "rockstar": "Toxic 'hustle culture' workplace",
  };
  return (
    <div style={{ background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.18)", borderRadius: 14, padding: "14px 16px", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 14 }}>🚨</span>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: DS.red }}>Culture Toxicity Signals</div>
          <div style={{ fontSize: 11, color: DS.t3 }}>What these phrases really signal about this workplace</div>
        </div>
      </div>
      {toxicFlags.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: cultureSignals.length > 0 ? 10 : 0 }}>
          {toxicFlags.map(function(flag, i) {
            var decoded = DECODE_MAP[flag.toLowerCase()] || "Possible red flag — research company reviews";
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, background: DS.bg0, borderRadius: 8, overflow: "hidden" }}>
                <div style={{ padding: "8px 10px", fontSize: 12, color: DS.t3, fontStyle: "italic" }}>"{flag}"</div>
                <div style={{ padding: "8px 10px", fontSize: 12, color: DS.red, borderLeft: "1px solid " + DS.border0 }}>{decoded}</div>
              </div>
            );
          })}
        </div>
      )}
      {cultureSignals.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {cultureSignals.map(function(s, i) { return <Pill key={i} text={s} color={DS.red} />; })}
        </div>
      )}
    </div>
  );
}

function PathfinderWeek1Plan({ result }) {
  if (!result || !result.immediate_next_steps || result.immediate_next_steps.length === 0) return null;
  var sv = useState({}); var checked = sv[0]; var setChecked = sv[1];
  var PC2 = DS.teal;
  var DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  var steps = result.immediate_next_steps.slice(0, 7);
  return (
    <div style={{ background: PC2 + "05", border: "1px solid " + PC2 + "25", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 14 }}>📅</span>
        <div>
          <div style={{ fontSize: 9, color: PC2, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>WEEK 1 ACTION PLAN</div>
          <div style={{ fontSize: 11, color: DS.t3, marginTop: 2 }}>One action per day — start tomorrow</div>
        </div>
        <div style={{ marginLeft: "auto", fontSize: 11, color: PC2, fontFamily: "'JetBrains Mono',monospace" }}>
          {Object.values(checked).filter(Boolean).length}/{steps.length} done
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {steps.map(function(step, i) {
          var done = !!checked[i];
          return (
            <div key={i} onClick={function() { setChecked(function(prev) { var n = Object.assign({}, prev); n[i] = !prev[i]; return n; }); }}
              style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: done ? PC2 + "10" : DS.bg0, border: "1px solid " + (done ? PC2 + "30" : DS.border0), borderRadius: 10, cursor: "pointer", transition: "all 0.2s" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: done ? PC2 : DS.t4, fontFamily: "'JetBrains Mono',monospace", width: 28, flexShrink: 0, paddingTop: 2 }}>{DAYS[i]}</div>
              <div style={{ flex: 1, fontSize: 13, color: done ? DS.t3 : DS.t1, lineHeight: 1.6, textDecoration: done ? "line-through" : "none", opacity: done ? 0.6 : 1, transition: "all 0.2s" }}>{step}</div>
              <div style={{ width: 18, height: 18, borderRadius: "50%", border: "1.5px solid " + (done ? PC2 : DS.border1), background: done ? PC2 : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, transition: "all 0.2s" }}>
                {done && <span style={{ fontSize: 9, color: "#fff", fontWeight: 900 }}>✓</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PathCollisionDetector({ paths }) {
  if (!paths || paths.length < 2) return null;
  var PC2 = DS.teal;
  var scores = paths.map(function(p) { return p.readiness_score || 0; });
  var bestIdx = scores.indexOf(Math.max.apply(null, scores));
  var fastest = paths.slice().sort(function(a, b) {
    var ta = (a.timeline && a.timeline.entry_level) ? parseInt(a.timeline.entry_level) || 12 : 12;
    var tb = (b.timeline && b.timeline.entry_level) ? parseInt(b.timeline.entry_level) || 12 : 12;
    return ta - tb;
  })[0];
  var fastest_type = fastest ? fastest.path_type : null;
  return (
    <div style={{ background: PC2 + "05", border: "1px solid " + PC2 + "22", borderRadius: 14, padding: "14px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 14 }}>⚡</span>
        <div style={{ fontSize: 9, color: PC2, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>PATH COLLISION DETECTOR</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {paths[bestIdx] && (
          <div style={{ display: "flex", gap: 12, padding: "10px 12px", background: DS.green + "06", border: "1px solid " + DS.green + "20", borderRadius: 10 }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>🎯</span>
            <div style={{ fontSize: 12, color: DS.t2, lineHeight: 1.6 }}>
              <strong style={{ color: DS.green }}>{(paths[bestIdx].path_type || "").toUpperCase()} PATH</strong> has your highest readiness at {scores[bestIdx]}/100. Best for immediate action.
            </div>
          </div>
        )}
        {fastest && fastest_type !== (paths[bestIdx] && paths[bestIdx].path_type) && (
          <div style={{ display: "flex", gap: 12, padding: "10px 12px", background: DS.amber + "06", border: "1px solid " + DS.amber + "20", borderRadius: 10 }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>🏃</span>
            <div style={{ fontSize: 12, color: DS.t2, lineHeight: 1.6 }}>
              <strong style={{ color: DS.amber }}>{(fastest_type || "").toUpperCase()} PATH</strong> is the fastest to entry-level: {fastest.timeline && fastest.timeline.entry_level}. Less readiness, but faster results.
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 12, padding: "10px 12px", background: PC2 + "06", border: "1px solid " + PC2 + "20", borderRadius: 10 }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>🔀</span>
          <div style={{ fontSize: 12, color: DS.t2, lineHeight: 1.6 }}>
            All paths share overlapping skills. Start with your highest-readiness path to build momentum that transfers to the others.
          </div>
        </div>
      </div>
    </div>
  );
}

function HRPersonaSimulator({ ranking }) {
  if (!ranking || !ranking.candidates || ranking.candidates.length === 0) return null;
  var sv = useState("standard"); var persona = sv[0]; var setPersona = sv[1];
  var PERSONAS = {
    standard:   { label: "Standard HR",         desc: "Balanced: skills + culture + experience",         mul: { skills: 1,   culture: 1,   experience: 1   }, color: DS.t2     },
    technical:  { label: "Technical Filter",     desc: "Heavily weights technical depth",                 mul: { skills: 1.4, culture: 0.6, experience: 1   }, color: DS.blue   },
    startup:    { label: "Startup HR",           desc: "Values potential + hustle over credentials",      mul: { skills: 0.9, culture: 1.4, experience: 0.7 }, color: DS.amber  },
    aggressive: { label: "Aggressive Recruiter", desc: "Optimizes for quick placement — strong signals",  mul: { skills: 1.1, culture: 0.8, experience: 1.3 }, color: DS.red    },
  };
  var pCfg = PERSONAS[persona];
  var adjusted = ranking.candidates.map(function(c) {
    var m = pCfg.mul;
    var adj = Math.round(
      ((c.skills_match || 50) * m.skills * 0.4) +
      ((c.domain_fit   || 50) * m.culture * 0.3) +
      ((c.experience_match || 50) * m.experience * 0.3)
    );
    return Object.assign({}, c, { persona_score: Math.min(100, Math.max(0, adj)) });
  }).sort(function(a, b) { return b.persona_score - a.persona_score; });
  return (
    <div style={{ background: DS.bg1, border: "1px solid " + DS.purple + "25", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 14 }}>🎭</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: DS.purple, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>HR PERSONA SIMULATOR</div>
          <div style={{ fontSize: 11, color: DS.t3, marginTop: 2 }}>How different recruiters would rank these candidates</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {Object.entries(PERSONAS).map(function(en) {
          var k = en[0]; var p = en[1];
          var active = persona === k;
          return (
            <button key={k} onClick={function() { setPersona(k); }}
              style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid " + (active ? p.color + "50" : DS.border0), background: active ? p.color + "12" : "transparent", color: active ? p.color : DS.t3, fontSize: 11, fontWeight: active ? 700 : 400, cursor: "pointer", transition: "all 0.2s" }}>
              {p.label}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: DS.t3, marginBottom: 10, padding: "8px 10px", background: DS.bg0, borderRadius: 8 }}>{pCfg.desc}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {adjusted.slice(0, 5).map(function(c, i) {
          var c2 = c.persona_score >= 75 ? DS.green : c.persona_score >= 55 ? DS.amber : DS.red;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: DS.bg0, border: "1px solid " + DS.border0, borderRadius: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", width: 20 }}>#{i + 1}</div>
              <div style={{ flex: 1, fontSize: 13, color: DS.t1, fontWeight: i === 0 ? 700 : 400 }}>{c.name}</div>
              <div style={{ fontSize: 14, fontWeight: 900, color: c2, fontFamily: "'JetBrains Mono',monospace" }}>{c.persona_score}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===================================================
// END PHASE 2 COMPONENTS
// ===================================================

// ===================================================
// VERDICT HERO CARD
// ===================================================
function VerdictHero({ result, onReset }) {
  var sv = useState(false); var show = sv[0]; var setShow = sv[1];
  var sr = useState(0); var revealStep = sr[0]; var setRevealStep = sr[1];
  var se = useState(false); var deepOpen = se[0]; var setDeepOpen = se[1];
  useEffect(function(){
    var t = setTimeout(function(){ setShow(true); }, 60);
    return function(){ clearTimeout(t); };
  }, []);
  useEffect(function(){
    if (!show) return;
    var timers = [
      setTimeout(function(){ setRevealStep(1); }, 0),
      setTimeout(function(){ setRevealStep(2); }, 150),
      setTimeout(function(){ setRevealStep(3); }, 300),
      setTimeout(function(){ setRevealStep(4); }, 450),
    ];
    return function(){ timers.forEach(clearTimeout); };
  }, [show]);
  if (!result) return null;
  var vs = getVS(result);
  var score = result.risk_score||0;
  var qv = result.quick_verdict||"";
  var qvMap = {SCAM:{label:"SCAM DETECTED",color:DS.red},GHOST:{label:"GHOST HIRING",color:DS.amber},REAL:{label:"REAL JOB",color:DS.green},SUSPICIOUS:{label:"SUSPICIOUS",color:DS.purple}};
  var qvs = qvMap[qv]||qvMap["SUSPICIOUS"];
  var headline = score>=70?"This job is likely a scam":score>=40?"This job looks suspicious":"This job looks legitimate";
  var sub = score>=70?"Do not apply. Do not pay any fees.":score>=40?"Verify carefully before applying.":"Still verify the company before applying.";
  var reasons = [];
  if(result.red_flags&&result.red_flags.length>0) reasons.push(result.red_flags[0]);
  if(result.manipulation_signals&&result.manipulation_signals.length>0) reasons.push(result.manipulation_signals[0]);
  if(result.final_reasoning) reasons.push(result.final_reasoning.slice(0,90)+(result.final_reasoning.length>90?"...":""));
  reasons = reasons.slice(0,3);
  var advice = result.user_advice&&result.user_advice.steps?result.user_advice.steps:[];
  var primaryAction = score>=70
    ? {label:"🚨 Report to CyberCrime", fn:function(){window.open("https://cybercrime.gov.in","_blank");}, color:DS.red, bg:"rgba(239,68,68,0.15)", border:"rgba(239,68,68,0.4)"}
    : score>=40
    ? {label:"⚠ Verify This Company", fn:function(){shareResult(result,"Scan");}, color:DS.amber, bg:"rgba(245,158,11,0.12)", border:"rgba(245,158,11,0.35)"}
    : {label:"✓ Apply Safely — Share Verification", fn:function(){shareToWhatsApp(generateShareText(result,"Scan"));}, color:DS.green, bg:"rgba(16,185,129,0.12)", border:"rgba(16,185,129,0.35)"};

  return (
    <div style={{ opacity:show?1:0,transform:show?"translateY(0)":"translateY(16px)",transition:"all 0.5s cubic-bezier(0.22,1,0.36,1)",marginBottom:20 }}>
      <div style={{ background:vs.bg,border:"1px solid "+vs.border,borderRadius:20,overflow:"hidden",boxShadow:"0 0 60px "+vs.glow+",0 24px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ height:3,background:"linear-gradient(90deg,transparent,"+vs.text+",transparent)" }}/>

        {/* LAYER 1 — VERDICT (instant) */}
        {revealStep>=1&&(
          <div style={{ padding:"22px 24px 16px",animation:"teFadeIn 0.3s ease" }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12 }}>
              <div style={{ flex:1,minWidth:180 }}>
                <div style={{ fontSize:9,color:qvs.color,fontFamily:"'JetBrains Mono',monospace",letterSpacing:3,fontWeight:700,marginBottom:6 }}>{qvs.label}</div>
                <div style={{ fontSize:"clamp(22px,4vw,28px)",fontWeight:900,color:vs.text,letterSpacing:"-0.5px",lineHeight:1.1,marginBottom:4 }}>{headline}</div>
                <div style={{ fontSize:14,color:DS.t2,fontWeight:500 }}>{sub}</div>
              </div>
              <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:4,flexShrink:0 }}>
                <div style={{ fontSize:"clamp(36px,6vw,48px)",fontWeight:900,color:vs.text,fontFamily:"'JetBrains Mono',monospace",lineHeight:1 }}>{score}</div>
                <div style={{ fontSize:10,color:DS.t4,fontFamily:"'JetBrains Mono',monospace",letterSpacing:2 }}>/ 100</div>
              </div>
            </div>
          </div>
        )}

        {/* LAYER 2 — 3 REASONS (150ms) */}
        {revealStep>=2&&reasons.length>0&&(
          <div style={{ padding:"0 24px 16px",animation:"teFadeIn 0.3s ease" }}>
            <div style={{ display:"flex",flexDirection:"column",gap:7 }}>
              {reasons.map(function(r,i){
                return (
                  <div key={i} style={{ display:"flex",gap:10,alignItems:"flex-start",padding:"9px 12px",background:"rgba(0,0,0,0.2)",borderRadius:10,border:"1px solid "+DS.border0 }}>
                    <div style={{ width:6,height:6,borderRadius:"50%",background:vs.text,flexShrink:0,marginTop:5 }}/>
                    <span style={{ fontSize:13,color:DS.t2,lineHeight:1.55 }}>{r}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* LAYER 3 — PRIMARY ACTION (300ms) */}
        {revealStep>=3&&(
          <div style={{ padding:"0 24px 16px",animation:"teFadeIn 0.3s ease" }}>
            <button onClick={primaryAction.fn}
              style={{ width:"100%",minHeight:48,padding:"14px",background:primaryAction.bg,border:"1px solid "+primaryAction.border,borderRadius:12,color:primaryAction.color,fontSize:15,fontWeight:800,cursor:"pointer",transition:"all 0.2s",letterSpacing:0.3,boxShadow:"0 4px 20px "+primaryAction.border }}>
              {primaryAction.label}
            </button>
          </div>
        )}

        {/* LAYER 4 — DEEP ANALYSIS (450ms, collapsed) */}
        {revealStep>=4&&(
          <div style={{ animation:"teFadeIn 0.3s ease" }}>
            <button onClick={function(){ setDeepOpen(function(v){return !v;}); }}
              style={{ width:"100%",padding:"12px 24px",background:"transparent",border:"none",borderTop:"1px solid "+DS.border0,display:"flex",alignItems:"center",justifyContent:"center",gap:8,cursor:"pointer",color:DS.t3,fontSize:12,fontWeight:500,transition:"color 0.2s" }}
              onMouseEnter={function(e){e.currentTarget.style.color=DS.t1;}}
              onMouseLeave={function(e){e.currentTarget.style.color=DS.t3;}}>
              <span style={{ transform:deepOpen?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.2s",display:"inline-block",fontSize:14 }}>›</span>
              {deepOpen?"Hide full analysis":"Show full analysis"}
            </button>
            {deepOpen&&(
              <div style={{ padding:"0 24px 20px",animation:"teFadeIn 0.25s ease" }}>
                {/* Pills */}
                <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginBottom:14 }}>
                  {result.platform_detected&&<Pill text={"📡 "+result.platform_detected} color={DS.cyan}/>}
                  {result.red_flags&&result.red_flags.length>0&&<Pill text={result.red_flags.length+" red flags"} color={DS.red}/>}
                  {result.hiring_intent&&result.hiring_intent.is_ghost_hiring&&<Pill text="👻 Ghost hiring" color={DS.amber}/>}
                  {result.india_specific&&result.india_specific.detected&&<Pill text={"🇮🇳 "+result.india_specific.scam_type} color={DS.amber}/>}
                  {result.confidence&&<Pill text={"Confidence: "+result.confidence} color={DS.t3}/>}
                </div>
                {/* Full reasoning */}
                {result.final_reasoning&&(
                  <div style={{ fontSize:13,color:DS.t2,lineHeight:1.75,borderLeft:"3px solid "+vs.text+"50",paddingLeft:14,marginBottom:14 }}>{result.final_reasoning}</div>
                )}
                {/* Action steps */}
                {advice.length>0&&(
                  <div style={{ background:"rgba(0,0,0,0.2)",border:"1px solid "+DS.border0,borderRadius:12,padding:"14px 16px",marginBottom:14 }}>
                    <div style={{ fontSize:11,fontWeight:700,color:DS.t1,marginBottom:10 }}>All recommended steps</div>
                    {advice.map(function(step,i){
                      return (
                        <div key={i} style={{ display:"flex",gap:12,alignItems:"flex-start",marginBottom:i<advice.length-1?8:0 }}>
                          <div style={{ width:20,height:20,borderRadius:"50%",background:vs.text+"18",border:"1px solid "+vs.text+"30",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:vs.text,flexShrink:0 }}>{i+1}</div>
                          <div style={{ fontSize:12,color:DS.t2,lineHeight:1.6,paddingTop:2 }}>{step}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <PredictionEngine result={result}/>
                <ManipulationFingerprint result={result}/>
                <TimeBombIndicator result={result}/>
                <ScamDNAMatch result={result}/>
                {/* Secondary actions */}
                <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginTop:4 }}>
                  <button onClick={function(){shareResult(result,"Scan");}}
                    style={{ flex:1,minWidth:100,display:"flex",alignItems:"center",justifyContent:"center",gap:7,padding:"10px 14px",background:DS.bg2,border:"1px solid "+DS.border1,borderRadius:10,color:DS.t2,fontSize:12,fontWeight:600,cursor:"pointer" }}>
                    📤 Share
                  </button>
                  {onReset&&<button onClick={onReset} style={{ padding:"10px 16px",background:"transparent",border:"1px solid "+DS.border0,borderRadius:10,color:DS.t3,fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap" }}>← New scan</button>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ===================================================
// SCAN DETAIL (tabs)
// ===================================================
function ScanDetail({ result, tab, setTab, copied, setCopied, coData, coLoading, coError, setCoData, setCoLoading, setCoError, coName, setCoName }) {
  if (!result) return null;
  var vs = getVS(result);
  var WM2 = {financial_risk:{label:"Financial Risk",color:DS.red},india_scam_patterns:{label:"India Scam Patterns",color:"#F97316"},identity_verification:{label:"Identity Verification",color:DS.amber},social_manipulation:{label:"Social Manipulation",color:DS.purple},content_authenticity:{label:"Content Authenticity",color:DS.cyan},platform_behavior:{label:"Platform Behavior",color:"#EC4899"}};
  var flagCount=(result.red_flags?result.red_flags.length:0)+(result.manipulation_signals?result.manipulation_signals.length:0);
  var TABS=[{id:"flags",l:"Red Flags ("+(flagCount)+")"},{id:"overview",l:"Analysis"},{id:"company",l:"Company"},{id:"deep",l:"Deep Intel"},{id:"advice",l:"Action Plan"}];

  var runCoCheck = async function(name) {
    if(!name||name.length<2) return;
    setCoName(name);setCoLoading(true);setCoData(null);setCoError(null);
    var coAbort = new AbortController();
    var coTimer = setTimeout(function(){try{coAbort.abort();}catch(e){}},30000);
    try{
      var webR=await searchCompany(name,coAbort.signal);
      var coCtx="Company: "+name+"\n\n"+(webR.length>0?"Web findings:\n"+webR.slice(0,15).map(function(r,i){return (i+1)+". "+r;}).join("\n"):"No web results. Use name patterns.")+"\n\nAnalyze for Indian job seeker.";
      var p=await runAIStable(coCtx,CO_SYS,coAbort.signal);
      if(!p||p.error||p.fallback){p={company_name:name,verdict:"Insufficient Data",verdict_color:"yellow",trust_score:50,founded:"Unknown",headquarters:"Unknown",industry:"Unknown",size:"Unknown",website:null,linkedin_presence:false,is_registered:false,cin_number:null,glassdoor_rating:null,actual_hiring:false,hiring_evidence:[],red_flags:["Could not retrieve company data — verify manually"],positive_signals:[],news_mentions:[],summary:"AI could not analyze. Please verify manually.",warning:"Manual verification required.",recommendation:"Check MCA portal and LinkedIn.",_fallback:true};setCoError("Limited data. Retry for full analysis.");}
      setCoData(p);
    }catch(e){
      setCoData({company_name:name,verdict:"Check Failed",verdict_color:"yellow",trust_score:50,red_flags:["Analysis failed — verify manually"],positive_signals:[],summary:"Could not analyze.",warning:"Manual verification required.",recommendation:"Search on LinkedIn and MCA portal.",_fallback:true});
      if(e&&e.name!=="AbortError")setCoError((e&&e.message)||"Company check failed.");
    }finally{clearTimeout(coTimer);setCoLoading(false);}
  };

  var txRef = useRef(null);
  var handleTouchStart = useCallback(function(e){ try{ txRef.current = e.touches[0].clientX; }catch(ex){} }, []);
  var handleTouchEnd = useCallback(function(e){
    try{
      if(txRef.current===null) return;
      var dx = e.changedTouches[0].clientX - txRef.current;
      txRef.current = null;
      if(Math.abs(dx) < 40) return;
      var ids = TABS.map(function(t){ return t.id; });
      var ci = ids.indexOf(tab);
      if(dx < 0 && ci < ids.length-1) setTab(ids[ci+1]);
      else if(dx > 0 && ci > 0) setTab(ids[ci-1]);
    }catch(ex){}
  }, [tab, TABS, setTab]);

  return (
    <div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:16,overflow:"hidden",marginBottom:20 }}>
      <div style={{ display:"flex",borderBottom:"1px solid "+DS.border0,overflowX:"auto",background:"rgba(0,0,0,0.2)" }}>
        {TABS.map(function(t){
          var active=tab===t.id;
          return <button key={t.id} onClick={function(){setTab(t.id);}} style={{ padding:"13px 16px",background:"transparent",border:"none",borderBottom:"2px solid "+(active?vs.text:"transparent"),color:active?vs.text:DS.t3,fontSize:12,fontWeight:active?700:500,cursor:"pointer",whiteSpace:"nowrap",transition:"all 0.2s" }}>{t.l}</button>;
        })}
        <div style={{ marginLeft:"auto",display:"flex",alignItems:"center",gap:6,padding:"0 14px",flexShrink:0 }}>
          <button onClick={function(){cpR(result,setCopied);}} style={{ fontSize:11,color:copied?DS.green:DS.t3,background:copied?"rgba(16,185,129,0.1)":"transparent",border:"1px solid "+(copied?DS.green+"30":DS.border0),padding:"5px 12px",borderRadius:8,cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",transition:"all 0.2s" }}>{copied?"✓ Copied":"Copy"}</button>
          <button onClick={function(){expR(result);}} style={{ fontSize:11,color:DS.t3,background:"transparent",border:"1px solid "+DS.border0,padding:"5px 12px",borderRadius:8,cursor:"pointer",fontFamily:"'JetBrains Mono',monospace" }}>Export</button>
        </div>
      </div>
      <div style={{ padding:"22px" }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {tab==="flags"&&(
          <div style={{ animation:"teFadeIn 0.25s ease" }}>
            <SafeApplyChecklist result={result}/>
            {result.red_flags&&result.red_flags.length>0&&(<div style={{ marginBottom:18 }}><div style={{ fontSize:12,fontWeight:700,color:DS.red,marginBottom:10,display:"flex",alignItems:"center",gap:8 }}><span style={{ width:8,height:8,borderRadius:"50%",background:DS.red,display:"inline-block" }}/>Red Flags ({result.red_flags.length})</div>{result.red_flags.map(function(f,i){return <Flag key={i} text={f} type="red"/>;})}</div>)}
            {result.manipulation_signals&&result.manipulation_signals.length>0&&(<div style={{ marginBottom:18 }}><div style={{ fontSize:12,fontWeight:700,color:DS.amber,marginBottom:10,display:"flex",alignItems:"center",gap:8 }}><span style={{ width:8,height:8,borderRadius:"50%",background:DS.amber,display:"inline-block" }}/>Manipulation Tactics ({result.manipulation_signals.length})</div>{result.manipulation_signals.map(function(f,i){return <Flag key={i} text={f} type="yellow"/>;})}</div>)}
            {result.manipulation_tags&&result.manipulation_tags.length>0&&(<div style={{ marginBottom:18 }}><div style={{ fontSize:12,fontWeight:700,color:DS.t2,marginBottom:10 }}>Tactic Types</div><div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>{result.manipulation_tags.map(function(t,i){var c2=t.severity==="HIGH"?DS.red:t.severity==="MEDIUM"?DS.amber:DS.t3;return <span key={i} style={{ fontSize:11,color:c2,background:c2+"12",border:"1px solid "+c2+"28",padding:"4px 10px",borderRadius:6,fontFamily:"'JetBrains Mono',monospace" }}>{t.tag}</span>;})}</div></div>)}
            {result.positive_signals&&result.positive_signals.length>0&&(<div><div style={{ fontSize:12,fontWeight:700,color:DS.green,marginBottom:10,display:"flex",alignItems:"center",gap:8 }}><span style={{ width:8,height:8,borderRadius:"50%",background:DS.green,display:"inline-block" }}/>Positive Signals ({result.positive_signals.length})</div>{result.positive_signals.map(function(f,i){return <Flag key={i} text={f} type="green"/>;})}</div>)}
            {flagCount===0&&<div style={{ textAlign:"center",padding:"32px 0",color:DS.t3 }}><div style={{ fontSize:32,marginBottom:10 }}>✓</div><div style={{ fontSize:14 }}>No significant red flags detected.</div></div>}
          </div>
        )}
        {tab==="overview"&&(
          <div style={{ animation:"teFadeIn 0.25s ease" }}>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:14,marginBottom:16 }}>
              <div style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12,padding:"16px 18px" }}>
                <div style={{ fontSize:12,fontWeight:700,color:DS.t2,marginBottom:14 }}>Risk Breakdown</div>
                {result.score_breakdown&&Object.entries(result.score_breakdown).map(function(en,i){var k=en[0],v=en[1],m=WM2[k];if(!m)return null;return(<div key={k} style={{ marginBottom:10 }}><div style={{ display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:5 }}><span style={{ color:DS.t2 }}>{m.label}</span><span style={{ color:m.color,fontWeight:700,fontFamily:"'JetBrains Mono',monospace" }}>{v}</span></div><ABar value={v} color={m.color} delay={i*60}/></div>);})}
              </div>
              <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
                {result.india_specific&&result.india_specific.detected&&(<div style={{ background:"rgba(249,115,22,0.06)",border:"1px solid rgba(249,115,22,0.2)",borderRadius:12,padding:"14px 16px" }}><div style={{ fontSize:12,fontWeight:700,color:"#F97316",marginBottom:8 }}>🇮🇳 India Scam Pattern</div><div style={{ fontSize:14,fontWeight:700,color:"#FED7AA",marginBottom:8 }}>{result.india_specific.scam_type||"Localized Threat"}</div>{(result.india_specific.signals||[]).map(function(s,i){return <div key={i} style={{ fontSize:12,color:"#FED7AA",marginBottom:4,display:"flex",gap:8 }}><span style={{ color:"#F97316" }}>›</span>{s}</div>;})}</div>)}
                <div style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12,padding:"14px 16px" }}>
                  <div style={{ fontSize:12,fontWeight:700,color:DS.t2,marginBottom:10 }}>Detected Entities</div>
                  {[["Company",result.detected_entities&&result.detected_entities.company],["Recruiter",result.detected_entities&&result.detected_entities.recruiter],["Email",result.detected_entities&&result.detected_entities.email],["Domain",result.detected_entities&&result.detected_entities.domain]].map(function(pair){return(<div key={pair[0]} style={{ display:"flex",justifyContent:"space-between",paddingBottom:8,marginBottom:8,borderBottom:"1px solid "+DS.border0 }}><span style={{ fontSize:12,color:DS.t3 }}>{pair[0]}</span><span style={{ fontSize:12,color:pair[1]?DS.t1:DS.t4,fontWeight:pair[1]?600:400 }}>{pair[1]||"—"}</span></div>);})}
                </div>
              </div>
            </div>
            {result.salary_intel&&(<div style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12,padding:"14px 18px",marginBottom:12 }}><div style={{ fontSize:12,fontWeight:700,color:DS.t2,marginBottom:12 }}>Salary Reality Check</div><div style={{ display:"flex",gap:16,flexWrap:"wrap",alignItems:"center" }}><div><div style={{ fontSize:11,color:DS.t3,marginBottom:4 }}>Claimed</div><div style={{ fontSize:15,fontWeight:700,color:result.salary_intel.market_rate_mismatch?DS.red:DS.green }}>{result.salary_intel.claimed||"—"}</div></div><div style={{ width:1,background:DS.border0 }}/><div><div style={{ fontSize:11,color:DS.t3,marginBottom:4 }}>Market Range</div><div style={{ fontSize:15,fontWeight:700,color:DS.t1 }}>{result.salary_intel.market_realistic_range||"—"}</div></div>{result.salary_intel.verdict&&<Pill text={result.salary_intel.verdict} color={result.salary_intel.market_rate_mismatch?DS.red:DS.green}/>}</div></div>)}
            {result.hiring_intent&&(<div style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12,padding:"14px 18px" }}><div style={{ fontSize:12,fontWeight:700,color:DS.t2,marginBottom:10 }}>Hiring Intent</div><div style={{ display:"flex",gap:16,flexWrap:"wrap",alignItems:"center" }}><div><div style={{ fontSize:11,color:DS.t3,marginBottom:4 }}>Intent Score</div><div style={{ fontSize:22,fontWeight:900,color:DS.blue,fontFamily:"'JetBrains Mono',monospace" }}>{result.hiring_intent.score||0}</div></div><div><div style={{ fontSize:11,color:DS.t3,marginBottom:4 }}>Probability</div><div style={{ fontSize:14,fontWeight:700,color:DS.t1 }}>{result.hiring_intent.probability||"—"}</div></div><Pill text={result.hiring_intent.is_ghost_hiring?"Ghost hiring detected":"No ghost signals"} color={result.hiring_intent.is_ghost_hiring?DS.amber:DS.green}/></div></div>)}
          </div>
        )}
        {tab==="company"&&(
          <div style={{ animation:"teFadeIn 0.25s ease" }}>
            <div style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12,padding:"16px",marginBottom:16 }}>
              <div style={{ fontSize:13,fontWeight:600,color:DS.t1,marginBottom:12 }}>Check company legitimacy</div>
              <div style={{ display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:(result.detected_entities&&result.detected_entities.company&&!coData&&!coLoading)?12:0 }}>
                <input defaultValue={result.detected_entities&&result.detected_entities.company||""} id="co-input" placeholder="Enter company name..." style={{ flex:1,minWidth:160,padding:"10px 14px",fontSize:13,color:DS.t1,background:DS.bg1,border:"1px solid "+DS.border1,borderRadius:10,outline:"none" }}/>
                <button onClick={function(){var el=document.getElementById("co-input");if(el)runCoCheck(el.value.trim());}} style={{ background:DS.cyan+"15",border:"1px solid "+DS.cyan+"30",color:DS.cyan,padding:"10px 20px",borderRadius:10,fontSize:12,fontWeight:700,cursor:"pointer" }}>Search</button>
              </div>
              {result.detected_entities&&result.detected_entities.company&&!coData&&!coLoading&&(<div style={{ display:"flex",gap:8,alignItems:"center" }}><span style={{ fontSize:12,color:DS.t3 }}>Detected:</span><button onClick={function(){runCoCheck(result.detected_entities.company);}} style={{ fontSize:12,color:DS.cyan,background:DS.cyan+"10",border:"1px solid "+DS.cyan+"25",padding:"4px 12px",borderRadius:8,cursor:"pointer" }}>▸ {result.detected_entities.company}</button></div>)}
            </div>
            {coLoading&&(<div style={{ textAlign:"center",padding:"36px",background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12 }}><div style={{ width:32,height:32,border:"3px solid "+DS.border1,borderTopColor:DS.cyan,borderRadius:"50%",display:"inline-block",animation:"teSpin 0.8s linear infinite",marginBottom:12 }}/><div style={{ fontSize:13,color:DS.cyan }}>Analysing {(coName||"")}...</div></div>)}
            {coError&&!coLoading&&<div style={{ background:"rgba(244,63,94,0.07)",border:"1px solid rgba(244,63,94,0.2)",borderRadius:10,padding:"10px 14px",fontSize:13,color:DS.red,marginBottom:12 }}>⚠ {coError}</div>}
            {coData&&!coLoading&&(function(){
              var tc=coData.trust_score>=70?DS.green:coData.trust_score>=40?DS.amber:DS.red;
              return (
                <div style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12,overflow:"hidden" }}>
                  <div style={{ padding:"16px 18px",borderBottom:"1px solid "+DS.border0,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap" }}>
                    <div style={{ fontSize:17,fontWeight:800,color:DS.t1,flex:1 }}>{coData.company_name}</div>
                    <div style={{ fontSize:26,fontWeight:900,color:tc,fontFamily:"'JetBrains Mono',monospace" }}>{coData.trust_score}</div>
                    <Pill text={coData.verdict||"Unknown"} color={tc}/>
                  </div>
                  <div style={{ padding:"16px 18px" }}>
                    {coData.summary&&<div style={{ fontSize:13,color:DS.t2,lineHeight:1.75,marginBottom:14 }}>{coData.summary}</div>}
                    {coData.recommendation&&<div style={{ fontSize:13,color:DS.t1,padding:"12px 14px",background:tc+"10",border:"1px solid "+tc+"25",borderRadius:10,marginBottom:14 }}>💡 {coData.recommendation}</div>}
                    {coData.red_flags&&coData.red_flags.length>0&&(<div style={{ marginBottom:12 }}><div style={{ fontSize:12,fontWeight:700,color:DS.red,marginBottom:8 }}>Company Red Flags</div>{coData.red_flags.map(function(f,i){return <Flag key={i} text={f} type="red"/>;})}</div>)}
                    {coData.positive_signals&&coData.positive_signals.length>0&&(<div><div style={{ fontSize:12,fontWeight:700,color:DS.green,marginBottom:8 }}>Positive Signals</div>{coData.positive_signals.map(function(f,i){return <Flag key={i} text={f} type="green"/>;})}</div>)}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        {tab==="deep"&&(
          <div style={{ animation:"teFadeIn 0.25s ease",display:"flex",flexDirection:"column",gap:12 }}>
            {result.explainability&&([["Why it's risky",result.explainability.why_risky],["How they manipulate",result.explainability.how_they_manipulate],["What they want",result.explainability.what_they_want]].map(function(item,i){return item[1]&&item[1]!=="N/A"?(<div key={i} style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12,padding:"14px 16px" }}><div style={{ fontSize:11,color:DS.t3,marginBottom:6 }}>{item[0]}</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.7 }}>{item[1]}</div></div>):null;}))}
            {result.ai_generated_jd&&result.ai_generated_jd.likely&&(<div style={{ background:"rgba(139,92,246,0.07)",border:"1px solid rgba(139,92,246,0.2)",borderRadius:12,padding:"14px 16px" }}><div style={{ fontSize:12,fontWeight:700,color:DS.purple,marginBottom:8 }}>🤖 AI-Generated JD Detected</div><div style={{ fontSize:13,color:DS.t2 }}>This job description was likely written by AI, not a real hiring manager — a common tactic to make scam posts look professional.</div></div>)}
            {result.detailed_analysis&&Object.entries(result.detailed_analysis).map(function(en,i){var val=en[1];if(!val||val==="...") return null;return(<div key={i} style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:10,padding:"12px 14px" }}><div style={{ fontSize:10,color:DS.t3,textTransform:"capitalize",marginBottom:5 }}>{en[0].replace(/_/g," ")}</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.7 }}>{val}</div></div>);})}
          </div>
        )}
        {tab==="advice"&&(
          <div style={{ animation:"teFadeIn 0.25s ease" }}>
            {result.user_advice&&result.user_advice.steps&&(
              <div>
                <div style={{ fontSize:16,fontWeight:800,color:DS.t1,marginBottom:20 }}>Your action plan</div>
                {result.user_advice.steps.map(function(step,i){
                  return (<div key={i} style={{ display:"flex",gap:14,alignItems:"flex-start",marginBottom:14 }}><div style={{ width:28,height:28,borderRadius:"50%",background:DS.bg3,border:"1px solid "+DS.border1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:DS.t1,flexShrink:0,fontFamily:"'JetBrains Mono',monospace" }}>{i+1}</div><div style={{ fontSize:14,color:DS.t1,lineHeight:1.7,paddingTop:3 }}>{step}</div></div>);
                })}
                <div style={{ marginTop:20,background:"rgba(6,182,212,0.07)",border:"1px solid rgba(6,182,212,0.2)",borderRadius:12,padding:"14px 16px" }}>
                  <div style={{ fontSize:12,fontWeight:700,color:DS.cyan,marginBottom:8 }}>Report this scam</div>
                  <a href="https://cybercrime.gov.in" target="_blank" rel="noreferrer" style={{ fontSize:13,color:DS.cyan,textDecoration:"none" }}>cybercrime.gov.in →</a>
                </div>
              </div>
            )}
            <VerdictFeedback module="scan" verdict={result.quick_verdict||"SUSPICIOUS"}/>
          </div>
        )}
      </div>
    </div>
  );
}

// ===================================================
// SALARY CHART
// ===================================================
function SalaryChart({ current, market, target }) {
  var vals=[current,market,target].map(function(v){return Number(v)||0;});
  var max=Math.max.apply(null,vals)||1;
  var bars=[{label:"Current",value:vals[0],color:DS.red},{label:"Market",value:vals[1],color:DS.blue},{label:"Target",value:vals[2],color:DS.green}];
  function fmt(v){if(v>=100000) return "₹"+(v/100000).toFixed(1)+"L";if(v>=1000) return "₹"+(v/1000).toFixed(0)+"K";return "₹"+v;}
  var AB2=function(p){var sv2=useState(0);var w=sv2[0];var setW=sv2[1];useEffect(function(){var t=setTimeout(function(){setW((p.value/max)*100);},(p.delay||0)+200);return function(){clearTimeout(t);};},[p.value]);return <div style={{ height:5,background:DS.bg3,borderRadius:3,overflow:"hidden",flex:1 }}><div style={{ height:"100%",width:w+"%",background:p.color,borderRadius:3,transition:"width 0.9s cubic-bezier(.4,0,.2,1)" }}/></div>;};
  return (
    <div style={{ marginTop:14,padding:"16px 18px",background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12 }}>
      <div style={{ fontSize:12,fontWeight:700,color:DS.t2,marginBottom:14 }}>Salary Comparison</div>
      {bars.map(function(b,i){return(<div key={b.label} style={{ marginBottom:i<bars.length-1?12:0 }}><div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:6 }}><span style={{ fontSize:12,color:DS.t2,minWidth:52 }}>{b.label}</span><AB2 value={b.value} color={b.color} delay={i*100}/><span style={{ fontSize:13,fontWeight:700,color:b.color,fontFamily:"'JetBrains Mono',monospace",minWidth:52,textAlign:"right" }}>{fmt(b.value)}</span></div></div>);})}
    </div>
  );
}

// ===================================================
// ATS HISTORY CHART
// ===================================================
var TE_HIST_KEY="te_v5_ats_hist";
function readATSHist(){try{return JSON.parse(localStorage.getItem(TE_HIST_KEY)||"[]");}catch(e){return [];}}

function ATSHistoryChart({ atsScore }) {
  useEffect(function(){
    if(!atsScore||typeof atsScore!=="number") return;
    var h=readATSHist();h.unshift({score:atsScore,ts:Date.now()});if(h.length>8)h=h.slice(0,8);
    try{localStorage.setItem(TE_HIST_KEY,JSON.stringify(h));}catch(e){}
  },[atsScore]);
  var hist=readATSHist();if(hist.length===0) return null;
  var max=Math.max.apply(null,hist.map(function(h){return h.score;}))||100;
  function fmtDate(ts){try{var d=new Date(ts);return d.getDate()+"/"+(d.getMonth()+1)+"/"+String(d.getFullYear()).slice(2);}catch(e){return "—";}}
  return (
    <div style={{ marginBottom:20,background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,padding:"18px" }}>
      <div style={{ fontSize:12,fontWeight:700,color:DS.t2,marginBottom:16 }}>ATS Score History</div>
      {hist.slice(0,8).map(function(h,i){var pct=Math.round((h.score/max)*100);var c=scoreColor(h.score);return(<div key={i} style={{ display:"flex",alignItems:"center",gap:12,marginBottom:10 }}><div style={{ fontSize:11,color:DS.t3,fontFamily:"'JetBrains Mono',monospace",width:50,textAlign:"right" }}>{fmtDate(h.ts)}</div><div style={{ flex:1,height:5,background:DS.bg3,borderRadius:3,overflow:"hidden" }}><div style={{ height:"100%",width:pct+"%",background:c,borderRadius:3,transition:"width 0.7s cubic-bezier(.4,0,.2,1)" }}/></div><div style={{ fontSize:12,fontWeight:700,color:c,fontFamily:"'JetBrains Mono',monospace",width:28 }}>{h.score}</div></div>);})}
      {hist.length>1&&(function(){var delta=hist[0].score-hist[1].score;if(delta===0)return null;return <div style={{ marginTop:10,fontSize:12,color:delta>0?DS.green:DS.red,display:"flex",alignItems:"center",gap:6 }}>{delta>0?"↑":"↓"} {Math.abs(delta)} points vs last scan</div>;})()} 
    </div>
  );
}

// ===================================================
// INTERVIEW GENERATOR
// ===================================================
function InterviewGenerator({ callFn, resumeText, detectedRole }) {
  var s0=useState(null);var questions=s0[0];var setQ=s0[1];
  var s1=useState(false);var loading=s1[0];var setL=s1[1];
  var s2=useState(null);var err=s2[0];var setE=s2[1];
  var s3=useState({});var open=s3[0];var setO=s3[1];
  var abRef=useRef(null);
  useEffect(function(){return function(){if(abRef.current)abRef.current.abort();};},[]);
  var ISYS="You are an expert technical interviewer. Generate role-specific interview questions WITH model answers. Based on the resume and detected role, produce EXACTLY this JSON (no markdown, no extra text): {\"technical\":[{\"q\":\"question\",\"a\":\"model answer\"}],\"behavioral\":[{\"q\":\"question\",\"a\":\"model answer\"}]}";
  async function generate(){
    if(!callFn) return;
    var text=(typeof resumeText==="string"?resumeText:"")||"";
    if(text.trim().length<20){setE("Resume text not available. Please re-upload.");return;}
    if(abRef.current) abRef.current.abort();
    abRef.current=new AbortController();
    setL(true);setE(null);setQ(null);setO({});
    try{
      var role=(detectedRole||"").slice(0,80)||"Unknown";
      var prompt="Resume:\n"+text.slice(0,3000)+"\n\nDetected Role: "+role+"\n\nGenerate 5 technical + 5 behavioral interview questions WITH detailed model answers.";
      var parsed=await runAIStable(prompt,ISYS,abRef.current.signal);
      if(!parsed||parsed.error||(!parsed.technical&&!parsed.behavioral)){parsed={_fallback:true,technical:[{q:"Describe your most technically challenging project.",a:"Explain the problem, your approach, technologies used, and the outcome."},{q:"How do you handle bugs in production?",a:"Describe your debugging process, monitoring, and rollback strategy."},{q:"What is your experience with version control?",a:"Discuss Git workflow, branching strategies, and code review practices."},{q:"How do you ensure code quality?",a:"Mention testing (unit/integration), code reviews, linting, and documentation."},{q:"Walk me through your development workflow.",a:"Describe planning, implementation, testing, and deployment steps."}],behavioral:[{q:"Tell me about a time you missed a deadline.",a:"Use the STAR method: Situation, Task, Action, Result. Be honest and show learning."},{q:"Describe a conflict with a coworker and how you resolved it.",a:"Focus on communication, empathy, and reaching a constructive resolution."},{q:"What is your greatest professional achievement?",a:"Choose a specific, quantifiable achievement relevant to the role."},{q:"Why do you want to leave your current job?",a:"Stay positive. Focus on growth opportunities, not negatives about current employer."},{q:"Where do you see yourself in 5 years?",a:"Align your answer with the company's growth trajectory and the role."}]};setE("Using template questions — retry for personalised prep.");}
      setQ(parsed);
    }catch(e){if(e&&e.name!=="AbortError")setE((e&&e.message)||"Generation failed.");}finally{setL(false);}
  }
  function toggle(key){setO(function(prev){var n=Object.assign({},prev);n[key]=!prev[key];return n;});}
  return (
    <div style={{ marginBottom:20,background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:16,overflow:"hidden" }}>
      <div style={{ padding:"16px 20px",borderBottom:"1px solid "+DS.border0,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10 }}>
        <div><div style={{ fontSize:14,fontWeight:700,color:DS.t1,marginBottom:3 }}>Interview Prep</div><div style={{ fontSize:12,color:DS.t2 }}>Role-specific questions with model answers</div></div>
        {!questions&&<button onClick={generate} disabled={loading} style={{ padding:"10px 18px",background:loading?"transparent":DS.purple+"15",border:"1px solid "+(loading?DS.border0:DS.purple+"40"),borderRadius:10,color:loading?DS.t3:DS.purple,fontSize:13,fontWeight:700,cursor:loading?"not-allowed":"pointer",transition:"all 0.2s" }}>{loading?"Generating...":"Generate Questions"}</button>}
        {questions&&<button onClick={function(){setQ(null);setO({});setE(null);}} style={{ padding:"8px 14px",background:"transparent",border:"1px solid "+DS.border0,borderRadius:8,color:DS.t3,fontSize:12,cursor:"pointer" }}>Regenerate</button>}
      </div>
      {err&&<div style={{ padding:"10px 20px",background:"rgba(244,63,94,0.07)",borderBottom:"1px solid rgba(244,63,94,0.18)",fontSize:13,color:DS.red }}>⚠ {err}</div>}
      {loading&&<div style={{ padding:"24px",textAlign:"center" }}><div style={{ width:28,height:28,border:"2px solid "+DS.border1,borderTopColor:DS.purple,borderRadius:"50%",display:"inline-block",animation:"teSpin 0.8s linear infinite",marginBottom:8 }}/><div style={{ fontSize:12,color:DS.purple }}>Generating personalised questions...</div></div>}
      {questions&&!loading&&(function(){
        var secs=[{key:"technical",label:"Technical",color:DS.cyan,items:questions.technical||[]},{key:"behavioral",label:"Behavioural",color:DS.purple,items:questions.behavioral||[]}];
        return (<div style={{ padding:"16px 20px",display:"flex",flexDirection:"column",gap:14 }}>{secs.map(function(sec){if(!sec.items.length)return null;return(<div key={sec.key}><div style={{ fontSize:12,fontWeight:700,color:sec.color,marginBottom:10 }}>{sec.label}</div>{sec.items.map(function(item,idx){var ik=sec.key+"_"+idx;var isOpen=!!open[ik];return(<div key={idx} style={{ background:DS.bg0,border:"1px solid "+(isOpen?sec.color+"40":DS.border0),borderRadius:12,overflow:"hidden",marginBottom:8,transition:"border-color 0.2s" }}><button onClick={function(){toggle(ik);}} style={{ width:"100%",padding:"12px 16px",background:"transparent",border:"none",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12,cursor:"pointer",textAlign:"left" }}><div style={{ fontSize:13,color:DS.t1,lineHeight:1.6,flex:1 }}><span style={{ color:sec.color,fontFamily:"'JetBrains Mono',monospace",fontSize:10,marginRight:8 }}>Q{idx+1}</span>{item.q||""}</div><span style={{ color:sec.color,fontSize:16,flexShrink:0,marginTop:1,transform:isOpen?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.2s",display:"inline-block" }}>›</span></button>{isOpen&&(<div style={{ padding:"0 16px 14px",borderTop:"1px solid "+DS.border0 }}><div style={{ fontSize:10,color:sec.color,fontFamily:"'JetBrains Mono',monospace",fontWeight:700,margin:"12px 0 8px" }}>MODEL ANSWER</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.75,background:DS.bg1,border:"1px solid "+sec.color+"18",borderRadius:8,padding:"12px 14px" }}>{item.a||""}</div></div>)}</div>);})}</div>);})}</div>);
      })()}
    </div>
  );
}

// ===================================================
// COVER LETTER GENERATOR
// ===================================================
function CoverLetterGenerator({ callFn, resumeText }) {
  var s0=useState("");var jd=s0[0];var setJd=s0[1];
  var s1=useState("");var letter=s1[0];var setLetter=s1[1];
  var s2=useState(false);var loading=s2[0];var setLoading=s2[1];
  var s3=useState(null);var err=s3[0];var setErr=s3[1];
  var s4=useState(false);var copied=s4[0];var setCopied=s4[1];
  var abRef=useRef(null);
  async function generate(){
    if(!callFn)return;if(!jd.trim()||jd.trim().length<30){setErr("Please paste a job description (at least 30 characters).");return;}
    var text=(typeof resumeText==="string"?resumeText:"")||"";
    if(text.trim().length<20){setErr("Resume text not available. Please re-upload.");return;}
    if(abRef.current)abRef.current.abort();abRef.current=new AbortController();
    setLoading(true);setErr(null);setLetter("");
    try{
      var sysp="You are a professional cover letter writer for the Indian job market. Write in exactly 3 paragraphs. P1: Connect key strength to role. P2: 2-3 specific achievements relevant to JD. P3: Confident closing. Max 280 words. Output ONLY the letter text, no subject line, no extra commentary.";
      var prompt="RESUME:\n"+text.slice(0,2500)+"\n\nJOB DESCRIPTION:\n"+jd.slice(0,1500)+"\n\nWrite the cover letter now:";
      var res=await runAIStable(prompt,sysp,abRef.current.signal);
      var raw="";
      if(res&&typeof res==="object"&&!res.error&&!res.fallback){
        // runAIStable returned parsed JSON when it shouldn't for free-text — try callFn fallback
        raw=JSON.stringify(res);
      }
      if(!raw||raw.length<50){
        try{var r2=await callFn(prompt,abRef.current.signal);raw=(typeof r2==="string"?r2:(r2&&r2.candidates&&r2.candidates[0]&&r2.candidates[0].content&&r2.candidates[0].content.parts&&r2.candidates[0].content.parts[0]&&r2.candidates[0].content.parts[0].text)||"");}catch(fe){}
      }
      raw=(raw||"").trim();
      if(!raw||raw.length<50){raw="Dear Hiring Manager,\n\nI am writing to express my strong interest in this position. With my background directly aligned with your requirements, I am confident I can contribute meaningfully from day one.\n\nThroughout my career, I have demonstrated consistent results with the key technologies and responsibilities outlined in your job description. I am eager to apply this expertise at your organization.\n\nI would welcome the opportunity to discuss how my background aligns with your needs. Thank you for your consideration.\n\nSincerely,\n[Your Name]";setErr("AI unavailable — showing template. Retry for personalised version.");}
      setLetter(raw);
    }catch(e){if(e&&e.name!=="AbortError")setErr((e&&e.message)||"Generation failed.");}finally{setLoading(false);}
  }
  function copyLetter(){if(!letter)return;navigator.clipboard.writeText(letter).catch(function(){try{var ta=document.createElement("textarea");ta.value=letter;document.body.appendChild(ta);ta.select();document.execCommand("copy");document.body.removeChild(ta);}catch(e){}});setCopied(true);setTimeout(function(){setCopied(false);},2000);}
  return (
    <div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:16,overflow:"hidden" }}>
      <div style={{ padding:"16px 20px",borderBottom:"1px solid "+DS.border0 }}><div style={{ fontSize:14,fontWeight:700,color:DS.t1,marginBottom:3 }}>Cover Letter Generator</div><div style={{ fontSize:12,color:DS.t2 }}>AI-written, personalised to the job</div></div>
      <div style={{ padding:"16px 20px",display:"flex",flexDirection:"column",gap:12 }}>
        <textarea value={jd} onChange={function(e){setJd(e.target.value);setErr(null);}} placeholder="Paste the job description here..." rows={5} style={{ width:"100%",background:DS.bg0,border:"1px solid "+DS.border1,borderRadius:10,padding:"12px 14px",color:DS.t1,fontSize:13,lineHeight:1.75,resize:"vertical",outline:"none" }}/>
        {err&&<div style={{ background:"rgba(244,63,94,0.08)",border:"1px solid rgba(244,63,94,0.2)",borderRadius:8,padding:"10px 14px",fontSize:13,color:DS.red }}>⚠ {err}</div>}
        <button onClick={generate} disabled={loading} style={{ padding:"12px 20px",background:loading?"transparent":DS.green+"15",border:"1px solid "+(loading?DS.border0:DS.green+"40"),borderRadius:10,color:loading?DS.t3:DS.green,fontSize:13,fontWeight:700,cursor:loading?"not-allowed":"pointer",transition:"all 0.2s",display:"flex",alignItems:"center",justifyContent:"center",gap:8 }}>
          {loading?<><div style={{ width:14,height:14,border:"2px solid "+DS.border1,borderTopColor:DS.green,borderRadius:"50%",animation:"teSpin 0.7s linear infinite" }}/>Writing...</>:"✍ Generate Cover Letter"}
        </button>
        {letter&&!loading&&(
          <div style={{ animation:"teFadeIn 0.3s ease" }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:8 }}>
              <div style={{ fontSize:12,fontWeight:700,color:DS.green }}>Cover letter ready</div>
              <div style={{ display:"flex",gap:8 }}>
                <button onClick={copyLetter} style={{ padding:"7px 14px",background:copied?DS.green+"15":"transparent",border:"1px solid "+(copied?DS.green+"40":DS.border1),borderRadius:8,color:copied?DS.green:DS.t2,fontSize:12,fontWeight:600,cursor:"pointer",transition:"all 0.2s" }}>{copied?"✓ Copied":"Copy"}</button>
                <button onClick={function(){setLetter("");setJd("");}} style={{ padding:"7px 14px",background:"transparent",border:"1px solid "+DS.border0,borderRadius:8,color:DS.t3,fontSize:12,cursor:"pointer" }}>Redo</button>
              </div>
            </div>
            <div style={{ background:DS.bg0,border:"1px solid "+DS.green+"20",borderRadius:12,padding:"18px 20px" }}>
              {letter.split("\n\n").map(function(para,i){if(!para.trim())return null;return <p key={i} style={{ fontSize:13,color:DS.t2,lineHeight:1.85,margin:"0 0 16px 0" }}>{para}</p>;})}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ===================================================
// ATS OPTIMIZER — Phase 1 (Deterministic) + Phase 2 (AI Gap Analysis)
// ===================================================

function atsExtractJDKeywords(jdResult) {
  try {
    var pool = [];
    var push = function(arr) {
      if (Array.isArray(arr)) arr.forEach(function(x) { if (typeof x === "string" && x.trim()) pool.push(x.trim()); });
    };
    push(jdResult.skills_needed);
    push(jdResult.keyword_suggestions);
    var seen = {}; var out = [];
    pool.forEach(function(kw) { var lo = kw.toLowerCase(); if (!seen[lo]) { seen[lo] = true; out.push(kw); } });
    return out.slice(0, 40);
  } catch(e) { return []; }
}

function atsMatchKeywords(keywords, resumeText) {
  try {
    if (!keywords || !keywords.length || !resumeText) return { matched: [], missing: [], score: 0 };
    var rlo = resumeText.toLowerCase();
    var matched = []; var missing = [];
    keywords.forEach(function(kw) {
      var klo = kw.toLowerCase();
      var found = rlo.includes(klo);
      if (!found) found = rlo.includes(klo.replace(/[.\-+#]/g, ""));
      if (!found) { var fw = klo.split(/\s+/)[0]; if (fw && fw.length >= 4) found = rlo.includes(fw); }
      if (found) matched.push(kw); else missing.push(kw);
    });
    var score = keywords.length > 0 ? Math.round((matched.length / keywords.length) * 100) : 0;
    return { matched: matched, missing: missing, score: score };
  } catch(e) { return { matched: [], missing: [], score: 0 }; }
}

function atsCheckSections(resumeText) {
  try {
    var rlo = resumeText.toLowerCase(); var flags = [];
    var secs = [
      { name: "Work Experience", p: ["experience","work history","employment","professional experience"] },
      { name: "Education",       p: ["education","qualification","degree","academic"] },
      { name: "Skills",          p: ["skills","technical skills","competencies","expertise"] },
      { name: "Summary / Objective", p: ["summary","objective","profile","about me","overview"] },
    ];
    var miss = [];
    secs.forEach(function(s) { if (!s.p.some(function(x) { return rlo.includes(x); })) miss.push(s.name); });
    if (miss.length > 0) flags.push("Missing sections detected: " + miss.join(", "));
    if (!/\d+[%xX]|\d+\s*(lakh|crore|k|million|users|clients|projects|years)/i.test(resumeText))
      flags.push("No quantified achievements found — add numbers and % to experience bullets");
    if (!/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/i.test(resumeText))
      flags.push("Email address not detected — verify contact info is ATS-parseable");
    if (resumeText.trim().length < 400)
      flags.push("Resume text is very short — possible extraction issue, try a different file format");
    return flags;
  } catch(e) { return []; }
}

async function atsRunPhase2(jdText, resumeText, matchResult, signal) {
  var SYS = "You are an elite resume strategist for the Indian job market. Analyze the match between this resume and job description.\nReturn ONLY valid JSON — no markdown, no text before or after the JSON object.\n{\"gap_severity\":\"Low\",\"semantic_score\":65,\"top_gaps\":[{\"skill\":\"\",\"severity\":\"HIGH\",\"why_needed\":\"\",\"how_to_add\":\"\"}],\"semantic_matches\":[{\"resume_term\":\"\",\"jd_term\":\"\",\"confidence\":\"High\"}],\"recruiter_risk_signals\":[{\"signal\":\"\",\"impact\":\"High\",\"fix\":\"\"}],\"quick_wins\":[],\"seniority_match\":\"Match\",\"seniority_reason\":\"\",\"overall_verdict\":\"Moderate\",\"verdict_reason\":\"\"}\nsemantic_score is 0-100 integer representing how well the resume semantically covers the JD's intent beyond exact keyword matching.";
  var prompt = "JOB DESCRIPTION:\n" + (jdText || "").slice(0, 1500) +
    "\n\nRESUME:\n" + (resumeText || "").slice(0, 2500) +
    "\n\nKEYWORDS MATCHED: " + ((matchResult && matchResult.matched) || []).join(", ") +
    "\n\nKEYWORDS MISSING: " + ((matchResult && matchResult.missing) || []).join(", ") +
    "\n\nProvide gap analysis and recruiter risk signals for this Indian job seeker. Be specific and actionable.";
  try {
    var parsed = await runAIStable(prompt, SYS, signal);
    if (!parsed || parsed.error || parsed.fallback) return null;
    return parsed;
  } catch(e) {
    if (e && e.name === "AbortError") throw e;
    return null;
  }
}

// -- EXTRACTION QUALITY CHECK (deterministic) -------------------------
function atsCheckExtractionQuality(text) {
  if (!text || !text.trim()) return null;
  var alpha = (text.match(/[a-zA-Z]/g) || []).length;
  var nonSpace = text.replace(/\s/g, "").length;
  var ratio = nonSpace > 0 ? alpha / nonSpace : 0;
  if (ratio < 0.52 && text.length > 200)
    return "Extraction quality looks poor — possible two-column or image-based PDF. For best accuracy, use a single-column PDF or DOCX, or paste resume text manually below.";
  if (text.trim().length < 400)
    return "Very little text extracted. Try a plain PDF or DOCX, or paste your resume text manually.";
  return null;
}

// -- COMPOSITE ATS SCORE (weighted blend) -----------------------------
// keyword coverage 50% + semantic match 30% + section structure 10% + format 10%
function atsCompositeScore(p1, p2) {
  if (!p1) return null;
  var kw = p1.score || 0;
  var semantic;
  if (p2 && typeof p2.semantic_score === "number" && p2.semantic_score > 0) {
    semantic = p2.semantic_score;
  } else if (p2 && p2.overall_verdict) {
    semantic = p2.overall_verdict === "Strong" ? 88 : p2.overall_verdict === "Weak" ? 32 : 62;
  } else {
    semantic = Math.min(100, Math.round(kw * 0.85));
  }
  var flagCount = (p1.sectionFlags || []).length;
  var section = flagCount === 0 ? 100 : flagCount <= 2 ? 70 : 40;
  var fmt = p1.fmtScore === "Good" ? 100 : p1.fmtScore === "Fair" ? 70 : 40;
  return Math.round(0.50 * kw + 0.30 * semantic + 0.10 * section + 0.10 * fmt);
}

// -- PHASE 3: RESUME REWRITE ENGINE -----------------------------------
async function atsRunPhase3(jdText, resumeText, matchResult, p2Result, signal) {
  var SYS = "You are a precision resume writer for India's job market.\n" +
    "CRITICAL ANTI-HALLUCINATION RULES — violation is unacceptable:\n" +
    "1. ONLY rewrite and strengthen content that EXISTS in the original resume.\n" +
    "2. NEVER fabricate skills, tools, companies, roles, projects, or achievements.\n" +
    "3. NEVER add technologies or certifications the candidate did not mention.\n" +
    "4. Use JD keywords ONLY where they truthfully describe existing experience.\n" +
    "5. Improve phrasing, add power verbs, quantify using context already present.\n" +
    "6. Preserve all factual details: dates, company names, role titles exactly.\n" +
    "7. Output plain text — no HTML, no markdown tables, hyphens for bullets only.\n" +
    "Return ONLY valid JSON — no markdown, no text outside the JSON object:\n" +
    "{\"rewritten_resume\":\"\",\"changes_made\":[{\"section\":\"\",\"change\":\"\"}],\"keywords_added\":[],\"sections_improved\":[],\"hallucination_check\":\"CLEAN\"}";
  var missingKW = ((matchResult && matchResult.missing) || []).slice(0, 15).join(", ");
  var gaps = p2Result ? (p2Result.top_gaps || []).slice(0, 3).map(function(g){ return g.skill || ""; }).join(", ") : "";
  var prompt = "TARGET JOB DESCRIPTION:\n" + (jdText || "").slice(0, 1200) +
    "\n\nORIGINAL RESUME — THIS IS THE ONLY SOURCE OF TRUTH:\n" + (resumeText || "").slice(0, 3500) +
    "\n\nMISSING KEYWORDS TO INCORPORATE (only where 100% truthfully applicable):\n" + missingKW +
    (gaps ? "\n\nIDENTIFIED GAPS (do NOT fabricate — only surface if candidate has genuine partial exposure):\n" + gaps : "") +
    "\n\nRewrite the resume. Be conservative — accuracy and honesty over impressiveness. Never invent anything.";
  try {
    var parsed = await runAIStable(prompt, SYS, signal);
    if (!parsed || parsed.error || parsed.fallback) return null;
    return parsed;
  } catch(e) {
    if (e && e.name === "AbortError") throw e;
    return null;
  }
}

function ATSOptimizerPanel(props) {
  var jdText   = props.jdText   || "";
  var jdResult = props.jdResult || {};
  var JC = DS.cyan;

  var sa = useState(false);  var open       = sa[0]; var setOpen       = sa[1];
  var sb = useState(null);   var resumeFile = sb[0]; var setResumeFile = sb[1];
  var sc = useState("");     var resumeTxt  = sc[0]; var setResumeTxt  = sc[1];
  var sd = useState(false);  var extracting = sd[0]; var setExtracting = sd[1];
  var se = useState(null);   var p1         = se[0]; var setP1         = se[1];
  var sf = useState(false);  var p2Loading  = sf[0]; var setP2Loading  = sf[1];
  var sg = useState(null);   var p2         = sg[0]; var setP2         = sg[1];
  var sh = useState(null);   var atsErr     = sh[0]; var setAtsErr     = sh[1];
  var si = useState(false);  var p3Loading  = si[0]; var setP3Loading  = si[1];
  var sj = useState(null);   var p3         = sj[0]; var setP3         = sj[1];
  var sk = useState(false);  var copied     = sk[0]; var setCopied     = sk[1];
  var sl = useState(null);   var atsWarn    = sl[0]; var setAtsWarn    = sl[1];
  var sm = useState(false);  var showManual = sm[0]; var setShowManual = sm[1];
  var sn = useState("");     var manualTxt  = sn[0]; var setManualTxt  = sn[1];
  var abRef = useRef(null); var ab3Ref = useRef(null);

  useEffect(function() {
    return function() {
      if (abRef.current) try { abRef.current.abort(); } catch(e) {}
      if (ab3Ref.current) try { ab3Ref.current.abort(); } catch(e) {}
    };
  }, []);

  function applyManualText() {
    var text = manualTxt.trim();
    if (!text || text.length < 50) { setAtsErr("Pasted text too short — please paste your full resume."); return; }
    setResumeTxt(text);
    setResumeFile("Pasted text");
    setAtsWarn(null); setAtsErr(null); setP1(null); setP2(null); setP3(null);
    var keywords = atsExtractJDKeywords(jdResult);
    if (keywords.length === 0) { setAtsErr("JD has no extractable skills. Re-analyze the job description first."); return; }
    var match = atsMatchKeywords(keywords, text);
    var sectionFlags = atsCheckSections(text);
    var fmtScore = sectionFlags.length === 0 ? "Good" : sectionFlags.length <= 2 ? "Fair" : "Needs Work";
    var p1result = { keywords: keywords, matched: match.matched, missing: match.missing, score: match.score, sectionFlags: sectionFlags, fmtScore: fmtScore };
    setP1(p1result);
    if (keywords.length < 8) setAtsWarn("Low-specificity JD — only " + keywords.length + " skills extracted. Match score may be unreliable. Re-analyze the JD above for richer results.");
    try { s3Update("jd", { match_score: match.score, ats_keywords: match.matched.slice(0, 8), gaps_detected: match.missing.slice(0, 20), resume_optimized: false }); } catch(e2) {}
    setShowManual(false);
  }

  async function handleFile(file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setAtsErr("File too large — max 5MB."); return; }
    setResumeFile(file.name);
    setExtracting(true); setAtsErr(null); setAtsWarn(null); setP1(null); setP2(null); setP3(null); setResumeTxt("");
    try {
      var text = await extractResume(file);
      if (!text || text.trim().length < 50)
        throw new Error("Could not extract readable text. Try a text-based PDF or DOCX, or paste text manually.");
      // Extraction quality guard
      var qualityWarn = atsCheckExtractionQuality(text);
      if (qualityWarn) setAtsWarn(qualityWarn);
      setResumeTxt(text);
      var keywords = atsExtractJDKeywords(jdResult);
      if (keywords.length === 0)
        throw new Error("JD has no extractable skills. Re-analyze the job description first.");
      // Low-specificity JD warning
      if (keywords.length < 8 && !qualityWarn)
        setAtsWarn("Low-specificity JD — only " + keywords.length + " skills extracted. Match score may be unreliable. Re-analyze the JD above for richer results.");
      var match = atsMatchKeywords(keywords, text);
      var sectionFlags = atsCheckSections(text);
      var fmtScore = sectionFlags.length === 0 ? "Good" : sectionFlags.length <= 2 ? "Fair" : "Needs Work";
      setP1({ keywords: keywords, matched: match.matched, missing: match.missing, score: match.score, sectionFlags: sectionFlags, fmtScore: fmtScore });
      // Extended s3 payload
      try { s3Update("jd", { match_score: match.score, ats_keywords: match.matched.slice(0, 8), gaps_detected: match.missing.slice(0, 20), resume_optimized: false }); } catch(e2) {}
    } catch(e) {
      if (e && e.name === "AbortError") return;
      setAtsErr((e && e.message) || "Resume extraction failed. Please retry.");
    }
    setExtracting(false);
  }

  async function runPhase2() {
    if (!resumeTxt || !jdText) return;
    if (abRef.current) try { abRef.current.abort(); } catch(e) {}
    abRef.current = new AbortController();
    setP2Loading(true); setP2(null); setP3(null); setAtsErr(null);
    try {
      var r2 = await atsRunPhase2(jdText, resumeTxt, p1 || { matched: [], missing: [] }, abRef.current.signal);
      if (!r2) setAtsErr("AI gap analysis unavailable. Check API keys or retry.");
      else {
        setP2(r2);
        // Update s3 with composite score now that p2 is available
        var comp = atsCompositeScore(p1, r2);
        try { s3Update("jd", { match_score: comp !== null ? comp : (p1 ? p1.score : 0), ats_keywords: p1 ? p1.matched.slice(0, 8) : [], gaps_detected: p1 ? p1.missing.slice(0, 20) : [], resume_optimized: false }); } catch(e2) {}
      }
    } catch(e) {
      if (e && e.name === "AbortError") return;
      setAtsErr("Gap analysis failed. Please retry.");
    }
    setP2Loading(false);
  }

  async function runPhase3() {
    if (!resumeTxt || !jdText) return;
    if (ab3Ref.current) try { ab3Ref.current.abort(); } catch(e) {}
    ab3Ref.current = new AbortController();
    setP3Loading(true); setP3(null); setAtsErr(null); setCopied(false);
    try {
      var r3 = await atsRunPhase3(jdText, resumeTxt, p1 || { matched: [], missing: [] }, p2 || null, ab3Ref.current.signal);
      if (!r3 || !r3.rewritten_resume) setAtsErr("Resume rewrite unavailable. Check API keys or retry.");
      else {
        setP3(r3);
        try { s3Update("jd", { match_score: atsCompositeScore(p1, p2) || (p1 ? p1.score : 0), ats_keywords: p1 ? p1.matched.slice(0, 8) : [], gaps_detected: p1 ? p1.missing.slice(0, 20) : [], resume_optimized: true }); } catch(e2) {}
      }
    } catch(e) {
      if (e && e.name === "AbortError") return;
      setAtsErr("Resume rewrite failed. Please retry.");
    }
    setP3Loading(false);
  }

  function handleCopy() {
    if (!p3 || !p3.rewritten_resume) return;
    try {
      navigator.clipboard.writeText(p3.rewritten_resume);
      setCopied(true);
      setTimeout(function() { setCopied(false); }, 2500);
    } catch(e) {}
  }

  var composite  = atsCompositeScore(p1, p2);
  var dispScore  = composite !== null ? composite : (p1 ? p1.score : null);
  var scoreColor = dispScore === null ? JC : dispScore >= 75 ? DS.green : dispScore >= 50 ? DS.amber : DS.red;
  function sevColor(s) { return (s === "HIGH" || s === "High") ? DS.red : (s === "MEDIUM" || s === "Medium") ? DS.amber : DS.green; }

  return (
    <div style={{ marginTop: 4 }}>
      <button onClick={function() { setOpen(!open); }}
        style={{ width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 18px",background:open?JC+"12":JC+"07",border:"1px solid "+JC+(open?"40":"20"),borderRadius:open?"14px 14px 0 0":"14px",color:JC,cursor:"pointer",transition:"all 0.2s",textAlign:"left" }}>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <span style={{ fontSize:15 }}>🎯</span>
          <div>
            <div style={{ fontSize:12,fontWeight:700,color:JC,letterSpacing:0.3 }}>Optimize Resume for This Job</div>
            <div style={{ fontSize:11,color:DS.t3,marginTop:2 }}>
              {p3 ? "✓ Optimized resume ready" : p2 ? "Phase 2 complete — rewrite available" : p1 ? "Phase 1 complete — run AI gap analysis" : "Upload resume → ATS match + gap analysis + rewrite"}
            </div>
          </div>
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:10,flexShrink:0 }}>
          {dispScore !== null && (
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:14,fontWeight:900,color:scoreColor,fontFamily:"'JetBrains Mono',monospace",lineHeight:1 }}>{dispScore}%</div>
              <div style={{ fontSize:8,color:DS.t4,letterSpacing:1,fontFamily:"'JetBrains Mono',monospace" }}>{composite !== null ? "COMPOSITE" : "KEYWORD"}</div>
            </div>
          )}
          <span style={{ color:JC+"77",fontSize:16,transform:open?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.2s",display:"inline-block" }}>›</span>
        </div>
      </button>

      {open && (
        <div style={{ background:DS.bg1,border:"1px solid "+JC+"20",borderTop:"none",borderRadius:"0 0 14px 14px",padding:"16px 18px",display:"flex",flexDirection:"column",gap:14 }}>

          {/* ── UPLOAD ZONE ──────────────────────────────── */}
          {!p1 && !extracting && !showManual && (
            <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
              <label style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:10,padding:"24px 16px",background:JC+"05",border:"2px dashed "+JC+"28",borderRadius:12,cursor:"pointer",transition:"all 0.2s" }}
                onMouseEnter={function(e){e.currentTarget.style.borderColor=JC+"55";e.currentTarget.style.background=JC+"09";}}
                onMouseLeave={function(e){e.currentTarget.style.borderColor=JC+"28";e.currentTarget.style.background=JC+"05";}}>
                <span style={{ fontSize:28 }}>📄</span>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:13,fontWeight:600,color:DS.t1,marginBottom:4 }}>Upload your resume</div>
                  <div style={{ fontSize:11,color:DS.t3 }}>PDF · DOCX · TXT · JPG · PNG — max 5MB</div>
                </div>
                <input type="file" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" style={{ display:"none" }}
                  onChange={function(e){ var f=e.target.files&&e.target.files[0]; if(f)handleFile(f); e.target.value=""; }}/>
              </label>
              <button onClick={function(){ setShowManual(true); }}
                style={{ fontSize:11,color:DS.t3,background:"transparent",border:"1px solid "+DS.border0,borderRadius:8,padding:"8px 14px",cursor:"pointer" }}>
                📋 Paste resume text instead (recommended for image-based PDFs)
              </button>
            </div>
          )}

          {/* ── MANUAL PASTE ZONE ────────────────────────── */}
          {showManual && !p1 && (
            <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
              <div style={{ fontSize:11,color:DS.t2,fontWeight:600 }}>Paste your full resume text:</div>
              <textarea value={manualTxt} onChange={function(e){ setManualTxt(e.target.value); }}
                placeholder="Paste your resume text here..."
                style={{ width:"100%",minHeight:160,background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:10,padding:"12px 14px",color:DS.t1,fontSize:12,lineHeight:1.65,resize:"vertical",boxSizing:"border-box",fontFamily:"inherit",outline:"none" }}/>
              <div style={{ display:"flex",gap:8 }}>
                <button onClick={applyManualText}
                  style={{ flex:1,padding:"10px",background:JC+"15",border:"1px solid "+JC+"35",borderRadius:8,color:JC,fontSize:12,fontWeight:700,cursor:"pointer" }}>
                  ✓ Analyze this text
                </button>
                <button onClick={function(){ setShowManual(false); setManualTxt(""); }}
                  style={{ padding:"10px 14px",background:"transparent",border:"1px solid "+DS.border0,borderRadius:8,color:DS.t3,fontSize:11,cursor:"pointer" }}>
                  Back
                </button>
              </div>
            </div>
          )}

          {/* ── EXTRACTING SPINNER ───────────────────────── */}
          {extracting && (
            <div style={{ display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:JC+"06",border:"1px solid "+JC+"20",borderRadius:10 }}>
              <div style={{ width:15,height:15,border:"2px solid "+JC+"30",borderTopColor:JC,borderRadius:"50%",animation:"teSpin 0.7s linear infinite",flexShrink:0 }}/>
              <span style={{ fontSize:12,color:DS.t2 }}>Extracting resume text from {resumeFile || "file"}...</span>
            </div>
          )}

          {/* ── ERROR ────────────────────────────────────── */}
          {atsErr && (
            <div style={{ background:"rgba(244,63,94,0.07)",border:"1px solid rgba(244,63,94,0.22)",borderRadius:10,padding:"10px 14px",fontSize:12,color:DS.red,display:"flex",gap:8,alignItems:"flex-start" }}>
              <span style={{ flexShrink:0 }}>⚠</span><span>{atsErr}</span>
            </div>
          )}

          {/* ── NON-BLOCKING WARNING ─────────────────────── */}
          {atsWarn && (
            <div style={{ background:"rgba(245,158,11,0.06)",border:"1px solid rgba(245,158,11,0.22)",borderRadius:10,padding:"10px 14px",fontSize:11,color:DS.amber,display:"flex",gap:8,alignItems:"flex-start" }}>
              <span style={{ flexShrink:0 }}>⚠</span>
              <div>
                <span>{atsWarn}</span>
                {atsWarn.indexOf("two-column") !== -1 && !showManual && (
                  <button onClick={function(){ setShowManual(true); }}
                    style={{ display:"block",marginTop:6,fontSize:10,color:JC,background:"transparent",border:"none",cursor:"pointer",padding:0,textDecoration:"underline" }}>
                    Paste text manually instead →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── PHASE 1 RESULTS ──────────────────────────── */}
          {p1 && !extracting && (
            <div style={{ display:"flex",flexDirection:"column",gap:12,animation:"teFadeIn 0.3s ease" }}>

              {/* file name + change button */}
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8 }}>
                <span style={{ fontSize:11,color:DS.t3 }}>📄 {resumeFile || "Resume"}</span>
                <label style={{ fontSize:11,color:JC,cursor:"pointer",padding:"4px 10px",border:"1px solid "+JC+"30",borderRadius:6,background:JC+"06",display:"inline-flex",alignItems:"center",gap:5 }}>
                  ↺ Change
                  <input type="file" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" style={{ display:"none" }}
                    onChange={function(e){ var f=e.target.files&&e.target.files[0]; if(f)handleFile(f); e.target.value=""; }}/>
                </label>
              </div>

              {/* ── COMPOSITE / KEYWORD SCORE RING ─────────── */}
              <div style={{ background:"linear-gradient(135deg,"+scoreColor+"06,"+DS.bg0+")",border:"1px solid "+scoreColor+"22",borderRadius:14,padding:"20px",display:"flex",alignItems:"center",gap:20,flexWrap:"wrap",boxShadow:"0 4px 24px "+scoreColor+"10" }}>
                <div style={{ flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:6 }}>
                  <ScoreRing value={dispScore||0} color={scoreColor} size={80}/>
                  <div style={{ fontSize:8,color:DS.t4,letterSpacing:2.5,fontFamily:"'JetBrains Mono',monospace",fontWeight:600,textTransform:"uppercase",textAlign:"center" }}>
                    {composite !== null ? "COMPOSITE" : "KEYWORD"}
                  </div>
                </div>
                <div style={{ flex:1,minWidth:150 }}>
                  <div style={{ fontSize:9,color:DS.t3,letterSpacing:2.5,fontFamily:"'JetBrains Mono',monospace",marginBottom:6,fontWeight:600 }}>ATS MATCH SCORE</div>
                  {composite !== null && (
                    <div style={{ fontSize:9,color:DS.t4,fontFamily:"'JetBrains Mono',monospace",marginBottom:6,lineHeight:1.6 }}>
                      KW {p1.score}% · SEM {p2&&p2.semantic_score ? p2.semantic_score : "—"}% · STRUCT · FMT
                    </div>
                  )}
                  <div style={{ fontSize:18,fontWeight:900,color:scoreColor,marginBottom:10,letterSpacing:"-0.5px",lineHeight:1 }}>
                    {dispScore>=75?"Strong Match":dispScore>=50?"Moderate Match":"Weak Match"}
                  </div>
                  <div style={{ height:4,background:"rgba(255,255,255,0.04)",borderRadius:4,overflow:"hidden",marginBottom:12 }}>
                    <div style={{ height:"100%",width:(dispScore||0)+"%",background:"linear-gradient(90deg,"+scoreColor+"77,"+scoreColor+")",borderRadius:4,boxShadow:"0 0 8px "+scoreColor+"44",transition:"width 1.2s cubic-bezier(0.4,0,0.2,1)" }}/>
                  </div>
                  <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                    <span style={{ fontSize:11,color:DS.green,background:DS.green+"10",border:"1px solid "+DS.green+"22",borderRadius:100,padding:"3px 10px",fontWeight:600 }}>✓ {p1.matched.length} matched</span>
                    <span style={{ fontSize:11,color:DS.red,background:DS.red+"10",border:"1px solid "+DS.red+"22",borderRadius:100,padding:"3px 10px",fontWeight:700 }}>✕ {p1.missing.length} missing</span>
                    <span style={{ fontSize:11,color:p1.fmtScore==="Good"?DS.green:p1.fmtScore==="Fair"?DS.amber:DS.red,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:100,padding:"3px 10px" }}>Format: {p1.fmtScore}</span>
                  </div>
                </div>
              </div>

              {/* ── KEYWORD COVERAGE MATRIX ─────────────────── */}
              <div style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12,overflow:"hidden" }}>
                <div style={{ padding:"12px 16px",borderBottom:"1px solid "+DS.border0,display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                  <div style={{ fontSize:9,color:DS.t3,letterSpacing:2.5,fontFamily:"'JetBrains Mono',monospace",fontWeight:700 }}>KEYWORD COVERAGE MATRIX</div>
                  <div style={{ fontSize:9,color:DS.t4,fontFamily:"'JetBrains Mono',monospace" }}>{p1.matched.length}/{p1.matched.length+p1.missing.length} FOUND</div>
                </div>
                <div style={{ padding:"14px 16px",display:"flex",flexDirection:"column",gap:14 }}>
                  {p1.matched.length > 0 && (
                    <div>
                      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}>
                        <div style={{ width:4,height:4,borderRadius:"50%",background:DS.green }}/>
                        <span style={{ fontSize:10,color:DS.green,fontWeight:700,letterSpacing:1.5,fontFamily:"'JetBrains Mono',monospace" }}>FOUND IN RESUME</span>
                      </div>
                      <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
                        {p1.matched.map(function(kw,i){ return <span className="te-kw-found" key={i} style={{ fontSize:11,background:"rgba(16,185,129,0.09)",border:"1px solid rgba(16,185,129,0.22)",borderRadius:100,padding:"4px 11px",color:DS.green,fontWeight:500,cursor:"default",display:"inline-block" }}>{kw}</span>; })}
                      </div>
                    </div>
                  )}
                  {p1.missing.length > 0 && (
                    <div style={{ background:"rgba(244,63,94,0.04)",border:"1px solid rgba(244,63,94,0.14)",borderRadius:10,padding:"12px 14px" }}>
                      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}>
                        <div style={{ width:4,height:4,borderRadius:"50%",background:DS.red,animation:"tePulse 1.5s ease infinite",boxShadow:"0 0 6px "+DS.red }}/>
                        <span style={{ fontSize:10,color:DS.red,fontWeight:700,letterSpacing:1.5,fontFamily:"'JetBrains Mono',monospace" }}>MISSING — ADD TO RESUME</span>
                        <span style={{ fontSize:9,color:DS.red+"77",fontFamily:"'JetBrains Mono',monospace",marginLeft:"auto" }}>PRIORITY</span>
                      </div>
                      <div style={{ display:"flex",flexWrap:"wrap",gap:7 }}>
                        {p1.missing.map(function(kw,i){ return <span className="te-kw-missing" key={i} style={{ fontSize:11,background:"rgba(244,63,94,0.12)",border:"1px solid rgba(244,63,94,0.35)",borderRadius:100,padding:"4px 12px",color:"#FF6B7A",fontWeight:700,cursor:"default",display:"inline-block",boxShadow:"0 0 8px rgba(244,63,94,0.12)" }}>{kw}</span>; })}
                      </div>
                    </div>
                  )}
                  {p1.matched.length===0&&p1.missing.length===0&&(
                    <div style={{ fontSize:12,color:DS.t3,padding:"8px 0" }}>No keywords extracted. Re-run the JD analysis above for richer results.</div>
                  )}
                </div>
              </div>

              {/* ── FORMAT WARNINGS ─────────────────────────── */}
              {p1.sectionFlags.length > 0 && (
                <div style={{ background:DS.amber+"05",border:"1px solid "+DS.amber+"22",borderRadius:12,padding:"12px 16px" }}>
                  <div style={{ fontSize:9,color:DS.amber,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace",marginBottom:10 }}>FORMAT WARNINGS</div>
                  {p1.sectionFlags.map(function(flag,i){
                    return (
                      <div key={i} style={{ display:"flex",gap:8,alignItems:"flex-start",marginBottom:i<p1.sectionFlags.length-1?8:0 }}>
                        <span style={{ color:DS.amber,flexShrink:0,fontSize:11,paddingTop:1 }}>⚠</span>
                        <span style={{ fontSize:12,color:DS.t2,lineHeight:1.6 }}>{flag}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── PHASE 2 TRIGGER ─────────────────────────── */}
              {!p2 && !p2Loading && (
                <button onClick={runPhase2} className="te-btn-primary"
                  style={{ width:"100%",padding:"14px 20px",background:"linear-gradient(135deg,"+JC+"1A,"+JC+"10)",border:"1px solid "+JC+"40",borderRadius:12,color:JC,fontSize:13,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,letterSpacing:0.5,boxShadow:"0 4px 20px "+JC+"18,inset 0 1px 0 "+JC+"22",position:"relative",overflow:"hidden" }}
                  onMouseEnter={function(e){e.currentTarget.style.background="linear-gradient(135deg,"+JC+"28,"+JC+"18)";e.currentTarget.style.boxShadow="0 8px 28px "+JC+"28,inset 0 1px 0 "+JC+"33";e.currentTarget.style.borderColor=JC+"66";}}
                  onMouseLeave={function(e){e.currentTarget.style.background="linear-gradient(135deg,"+JC+"1A,"+JC+"10)";e.currentTarget.style.boxShadow="0 4px 20px "+JC+"18,inset 0 1px 0 "+JC+"22";e.currentTarget.style.borderColor=JC+"40";}}>
                  <span style={{ fontSize:16 }}>🧠</span>
                  <span>Run AI Gap Analysis</span>
                  <span style={{ fontSize:10,color:JC+"77",fontWeight:400,marginLeft:4,fontFamily:"'JetBrains Mono',monospace",letterSpacing:1 }}>PHASE 2</span>
                </button>
              )}

              {p2Loading && (
                <div style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:JC+"06",border:"1px solid "+JC+"20",borderRadius:10 }}>
                  <div style={{ width:14,height:14,border:"2px solid "+JC+"30",borderTopColor:JC,borderRadius:"50%",animation:"teSpin 0.7s linear infinite",flexShrink:0 }}/>
                  <span style={{ fontSize:12,color:DS.t2 }}>Running semantic gap analysis...</span>
                </div>
              )}

              {/* ── PHASE 2 RESULTS ─────────────────────────── */}
              {p2 && !p2Loading && (
                <div style={{ display:"flex",flexDirection:"column",gap:10,animation:"teFadeIn 0.3s ease" }}>

                  <div style={{ background:DS.bg0,border:"1px solid "+(p2.overall_verdict==="Strong"?DS.green:p2.overall_verdict==="Weak"?DS.red:DS.amber)+"28",borderRadius:12,padding:"14px 16px" }}>
                    <div style={{ fontSize:9,color:DS.t3,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace",marginBottom:8 }}>AI VERDICT · PHASE 2</div>
                    <div style={{ display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:p2.verdict_reason?8:0 }}>
                      <span style={{ fontSize:14,fontWeight:800,color:p2.overall_verdict==="Strong"?DS.green:p2.overall_verdict==="Weak"?DS.red:DS.amber }}>
                        {p2.overall_verdict==="Strong"?"✓":p2.overall_verdict==="Weak"?"✕":"⚠"} {p2.overall_verdict||"—"} Match
                      </span>
                      {p2.seniority_match&&<Pill text={p2.seniority_match} color={p2.seniority_match==="Match"?DS.green:DS.amber}/>}
                      {p2.gap_severity&&<Pill text={"Gap: "+p2.gap_severity} color={p2.gap_severity==="Low"?DS.green:p2.gap_severity==="Medium"?DS.amber:DS.red}/>}
                    </div>
                    {p2.verdict_reason&&<div style={{ fontSize:12,color:DS.t2,lineHeight:1.65 }}>{p2.verdict_reason}</div>}
                    {p2.seniority_reason&&<div style={{ fontSize:11,color:DS.t3,lineHeight:1.6,marginTop:4 }}>{p2.seniority_reason}</div>}
                  </div>

                  {p2.top_gaps&&p2.top_gaps.length>0&&(
                    <div style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12,padding:"14px 16px" }}>
                      <div style={{ fontSize:9,color:DS.t3,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace",marginBottom:12 }}>CRITICAL GAPS TO CLOSE</div>
                      {p2.top_gaps.map(function(gap,i){
                        var gc=sevColor(gap.severity);
                        return (
                          <div key={i} style={{ background:gc+"05",border:"1px solid "+gc+"1A",borderLeft:"3px solid "+gc,borderRadius:8,padding:"10px 12px",marginBottom:i<p2.top_gaps.length-1?8:0 }}>
                            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap" }}>
                              <span style={{ fontSize:12,fontWeight:700,color:DS.t1 }}>{gap.skill||"—"}</span>
                              {gap.severity&&<Pill text={gap.severity} color={gc}/>}
                            </div>
                            {gap.why_needed&&<div style={{ fontSize:11,color:DS.t3,marginBottom:gap.how_to_add?4:0,lineHeight:1.55 }}>Why: {gap.why_needed}</div>}
                            {gap.how_to_add&&<div style={{ fontSize:11,color:JC,lineHeight:1.55 }}>Fix: {gap.how_to_add}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {p2.recruiter_risk_signals&&p2.recruiter_risk_signals.length>0&&(
                    <div style={{ background:DS.bg0,border:"1px solid "+DS.red+"15",borderRadius:12,padding:"14px 16px" }}>
                      <div style={{ fontSize:9,color:DS.red,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace",marginBottom:12 }}>RECRUITER RISK SIGNALS</div>
                      {p2.recruiter_risk_signals.map(function(r,i){
                        var rc=sevColor(r.impact);
                        return (
                          <div key={i} style={{ display:"flex",gap:10,alignItems:"flex-start",padding:"8px 10px",background:rc+"05",border:"1px solid "+rc+"18",borderRadius:8,marginBottom:i<p2.recruiter_risk_signals.length-1?8:0 }}>
                            <span style={{ color:rc,flexShrink:0,fontSize:11,paddingTop:2 }}>●</span>
                            <div>
                              <div style={{ fontSize:12,color:DS.t1,fontWeight:600,marginBottom:r.fix?2:0 }}>{r.signal||"—"}</div>
                              {r.fix&&<div style={{ fontSize:11,color:DS.t3,lineHeight:1.55 }}>Fix: {r.fix}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {p2.semantic_matches&&p2.semantic_matches.length>0&&(
                    <div style={{ background:DS.bg0,border:"1px solid "+DS.green+"15",borderRadius:12,padding:"14px 16px" }}>
                      <div style={{ fontSize:9,color:DS.green,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace",marginBottom:10 }}>SEMANTIC MATCHES (hidden strengths)</div>
                      <div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>
                        {p2.semantic_matches.map(function(sm,i){
                          return (
                            <div key={i} style={{ background:DS.green+"07",border:"1px solid "+DS.green+"22",borderRadius:8,padding:"5px 10px",display:"flex",alignItems:"center",gap:5 }}>
                              <span style={{ fontSize:11,color:DS.green }}>{sm.resume_term||"—"}</span>
                              <span style={{ fontSize:11,color:DS.t4 }}>→</span>
                              <span style={{ fontSize:11,color:DS.t2 }}>{sm.jd_term||"—"}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {p2.quick_wins&&p2.quick_wins.length>0&&(
                    <div style={{ background:DS.green+"05",border:"1px solid "+DS.green+"18",borderRadius:12,padding:"12px 16px" }}>
                      <div style={{ fontSize:9,color:DS.green,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace",marginBottom:10 }}>QUICK WINS — do these first</div>
                      <div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>
                        {p2.quick_wins.map(function(w,i){ return <Pill key={i} text={w} color={DS.green}/>; })}
                      </div>
                    </div>
                  )}

                  <button onClick={function(){ setP2(null); setP3(null); }}
                    style={{ alignSelf:"flex-start",fontSize:10,padding:"4px 12px",background:"transparent",border:"1px solid "+DS.border0,borderRadius:6,color:DS.t4,cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",letterSpacing:1 }}>
                    ↺ RE-RUN ANALYSIS
                  </button>
                </div>
              )}

              {/* ── PHASE 3 TRIGGER ─────────────────────────── */}
              {p1 && !p3 && !p3Loading && (
                <button onClick={runPhase3}
                  style={{ padding:"14px 18px",background:"linear-gradient(135deg,"+DS.green+"18,"+JC+"12)",border:"1px solid "+DS.green+"40",borderRadius:12,color:DS.green,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",letterSpacing:0.5,display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"all 0.2s",boxShadow:"0 2px 12px "+DS.green+"12" }}
                  onMouseEnter={function(e){e.currentTarget.style.background="linear-gradient(135deg,"+DS.green+"28,"+JC+"1C)";e.currentTarget.style.boxShadow="0 4px 20px "+DS.green+"22";}}
                  onMouseLeave={function(e){e.currentTarget.style.background="linear-gradient(135deg,"+DS.green+"18,"+JC+"12)";e.currentTarget.style.boxShadow="0 2px 12px "+DS.green+"12";}}>
                  ✨ Generate Optimized Resume
                  <span style={{ fontSize:10,color:DS.green+"99",fontWeight:400,letterSpacing:0 }}>{p2 ? "uses gap analysis" : "run gap analysis first for best results"}</span>
                </button>
              )}

              {p3Loading && (
                <div style={{ display:"flex",flexDirection:"column",gap:8,padding:"16px",background:DS.green+"06",border:"1px solid "+DS.green+"20",borderRadius:12 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                    <div style={{ width:14,height:14,border:"2px solid "+DS.green+"30",borderTopColor:DS.green,borderRadius:"50%",animation:"teSpin 0.7s linear infinite",flexShrink:0 }}/>
                    <span style={{ fontSize:12,color:DS.t2,fontWeight:600 }}>Rewriting resume against JD requirements...</span>
                  </div>
                  <div style={{ fontSize:10,color:DS.t4,lineHeight:1.6,paddingLeft:26 }}>
                    AI is strengthening phrasing and surfacing matching experience. Only your existing content will be used.
                  </div>
                </div>
              )}

              {/* ── PHASE 3 OUTPUT ──────────────────────────── */}
              {p3 && !p3Loading && (
                <div style={{ display:"flex",flexDirection:"column",gap:10,animation:"teFadeIn 0.3s ease" }}>

                  {/* header bar */}
                  <div style={{ background:"linear-gradient(135deg,"+DS.green+"10,"+JC+"08)",border:"1px solid "+DS.green+"30",borderRadius:12,padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10 }}>
                    <div>
                      <div style={{ fontSize:9,color:DS.green,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace",marginBottom:4 }}>PHASE 3 · OPTIMIZED RESUME</div>
                      <div style={{ fontSize:12,color:DS.t1,fontWeight:600 }}>
                        {p3.sections_improved&&p3.sections_improved.length>0 ? p3.sections_improved.length+" sections improved" : "Resume rewritten"}
                        {p3.keywords_added&&p3.keywords_added.length>0 ? " · "+p3.keywords_added.length+" keywords added" : ""}
                      </div>
                    </div>
                    <button onClick={handleCopy}
                      style={{ padding:"8px 16px",background:copied?DS.green+"20":DS.bg3,border:"1px solid "+(copied?DS.green+"50":DS.border0),borderRadius:8,color:copied?DS.green:DS.t2,fontSize:11,fontWeight:700,cursor:"pointer",transition:"all 0.2s",display:"flex",alignItems:"center",gap:6 }}>
                      {copied ? "✓ Copied!" : "⎘ Copy Resume"}
                    </button>
                  </div>

                  {/* changes summary */}
                  {p3.changes_made&&p3.changes_made.length>0&&(
                    <div style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:10,padding:"12px 14px" }}>
                      <div style={{ fontSize:9,color:DS.t3,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace",marginBottom:8 }}>CHANGES MADE</div>
                      {p3.changes_made.slice(0,6).map(function(c,i){
                        return (
                          <div key={i} style={{ display:"flex",gap:8,marginBottom:i<Math.min(p3.changes_made.length,6)-1?6:0,alignItems:"flex-start" }}>
                            <span style={{ color:DS.green,flexShrink:0,fontSize:11,paddingTop:1 }}>↗</span>
                            <div>
                              {c.section&&<span style={{ fontSize:10,color:DS.t4,fontFamily:"'JetBrains Mono',monospace",marginRight:6 }}>[{c.section}]</span>}
                              <span style={{ fontSize:11,color:DS.t2,lineHeight:1.55 }}>{c.change||""}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* keywords added pills */}
                  {p3.keywords_added&&p3.keywords_added.length>0&&(
                    <div style={{ background:JC+"05",border:"1px solid "+JC+"18",borderRadius:10,padding:"10px 14px" }}>
                      <div style={{ fontSize:9,color:JC,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace",marginBottom:8 }}>KEYWORDS INCORPORATED</div>
                      <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
                        {p3.keywords_added.map(function(kw,i){ return <span key={i} style={{ fontSize:11,background:JC+"10",border:"1px solid "+JC+"28",borderRadius:6,padding:"2px 8px",color:JC }}>{kw}</span>; })}
                      </div>
                    </div>
                  )}

                  {/* rewritten resume text */}
                  <div style={{ background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:12,padding:"16px" }}>
                    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12 }}>
                      <div style={{ fontSize:9,color:DS.t3,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace" }}>REWRITTEN RESUME</div>
                      <button onClick={handleCopy}
                        style={{ fontSize:10,color:copied?DS.green:DS.t4,background:"transparent",border:"none",cursor:"pointer",padding:0,display:"flex",alignItems:"center",gap:4 }}>
                        {copied?"✓ Copied":"⎘ Copy"}
                      </button>
                    </div>
                    <pre style={{ fontSize:11,color:DS.t2,lineHeight:1.75,whiteSpace:"pre-wrap",wordBreak:"break-word",margin:0,fontFamily:"inherit",maxHeight:340,overflowY:"auto" }}>
                      {p3.rewritten_resume || ""}
                    </pre>
                  </div>

                  {/* disclaimer */}
                  <div style={{ background:"rgba(245,158,11,0.05)",border:"1px solid rgba(245,158,11,0.18)",borderRadius:10,padding:"10px 14px",fontSize:10,color:DS.amber,lineHeight:1.65,display:"flex",gap:8,alignItems:"flex-start" }}>
                    <span style={{ flexShrink:0 }}>⚠</span>
                    <span>AI-assisted rewrite — verify every claim matches your actual experience before submitting. Never submit a resume with fabricated skills, roles, or achievements.</span>
                  </div>

                  <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                    <button onClick={function(){ setP3(null); setCopied(false); }}
                      style={{ fontSize:10,padding:"5px 12px",background:"transparent",border:"1px solid "+DS.border0,borderRadius:6,color:DS.t4,cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",letterSpacing:1 }}>
                      ↺ RE-GENERATE
                    </button>
                    <button onClick={handleCopy}
                      style={{ fontSize:10,padding:"5px 14px",background:DS.green+"10",border:"1px solid "+DS.green+"30",borderRadius:6,color:DS.green,cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",letterSpacing:1 }}>
                      {copied?"✓ COPIED":"⎘ COPY FULL RESUME"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── FOOTER NOTE ──────────────────────────────── */}
          <div style={{ fontSize:10,color:DS.t4,lineHeight:1.6,borderTop:"1px solid "+DS.border0,paddingTop:10 }}>
            ⓘ Composite score = keyword coverage (50%) + semantic match (30%) + section structure (10%) + format (10%). Neither composite nor keyword score simulates any specific ATS platform (Workday, Greenhouse, Taleo, etc.). Phase 3 rewrite never fabricates experience — verify all claims before submitting.
          </div>
        </div>
      )}
    </div>
  );
}

// ===================================================
// JD ANALYZER
// ===================================================
function JDAnalyzerSection() {
  var JC=DS.cyan;
  var s0=useState("");var jdText=s0[0];var setJdText=s0[1];
  var s1=useState(false);var loading=s1[0];var setLoading=s1[1];
  var s2=useState(null);var result=s2[0];var setResult=s2[1];
  var s3=useState(null);var error=s3[0];var setError=s3[1];
  var s4=useState(0);var progress=s4[0];var setProgress=s4[1];
  var s5=useState(0);var stepIdx=s5[0];var setStepIdx=s5[1];
  var abRef=useRef(null);var intRef=useRef(null);
  var JD_SYS="You are an expert job market analyst for India. Decode this job description with surgical precision.\nRespond ONLY with valid JSON:\n{\"fit_verdict\":\"Strong Fit\",\"fit_reason\":\"One sentence why.\",\"hidden_requirements\":[],\"red_flags\":[],\"skills_needed\":[],\"keyword_suggestions\":[],\"role_reality\":\"2 sentence assessment.\",\"real_vs_posted\":[{\"posted\":\"Fast-paced environment\",\"reality\":\"High pressure, poor processes\"}],\"culture_signals\":[],\"ghost_job_probability\":0,\"ghost_signals\":[],\"salary_realistic\":true,\"salary_estimate\":\"₹12-18 LPA\",\"experience_required\":\"\",\"growth_potential\":\"High\",\"toxicity_flags\":[],\"negotiation_tips\":[],\"apply_verdict\":\"Apply\",\"apply_reason\":\"One sentence.\"}";
  var JD_STEPS=["Parsing job requirements...","Detecting hidden expectations...","Scanning for red flags...","Identifying required skills...","Extracting ATS keywords...","Completing analysis..."];
  useEffect(function(){return function(){clearInterval(intRef.current);if(abRef.current)abRef.current.abort();};},[]);
  async function analyze(){
    if(!jdText.trim()||jdText.trim().length<30){setError("Please paste a job description (at least 30 characters).");return;}
    if(abRef.current)abRef.current.abort();abRef.current=new AbortController();
    setError(null);setResult(null);setLoading(true);setProgress(0);setStepIdx(0);
    var s=0;intRef.current=setInterval(function(){s++;if(s<JD_STEPS.length){setStepIdx(s);setProgress(Math.round((s/JD_STEPS.length)*88));}},900);
    try{
      var parsed=await runAIStable(jdText,JD_SYS,abRef.current.signal);
      if(!parsed||parsed.error||parsed.fallback){var r2=RULE_ENGINE.analyze(jdText||"");parsed={hidden_requirements:["Verify responsibilities directly with employer"],red_flags:(r2&&r2.red_flags&&r2.red_flags.length>0)?r2.red_flags:["Unable to fully analyze — review manually."],skills_needed:["Review JD manually for required skills"],keyword_suggestions:["Extract keywords from job title and responsibilities"],ai_fallback:true};setError("AI analysis limited — showing rule-based result. Retry for full intelligence.");}
      clearInterval(intRef.current);setProgress(100);try{updatePassport("jd",parsed);}catch(ppe){}
      setTimeout(function(){setResult(parsed);setLoading(false);},300);
    }catch(e){
      clearInterval(intRef.current);setLoading(false);
      if(e&&e.name==="AbortError")return;
      try{var fb=RULE_ENGINE.analyze(jdText||"");setResult({hidden_requirements:["Verify responsibilities directly with employer"],red_flags:(fb&&fb.red_flags&&fb.red_flags.length>0)?fb.red_flags:["AI unavailable — manual review required"],skills_needed:["Review JD manually"],keyword_suggestions:["Extract keywords from job title"],ai_fallback:true});setError("Analysis failed — showing basic result. Please retry.");}catch(fe){setError("Analysis failed. Please retry.");}
    }
  }
  var SECS=result?[
    {key:"hidden_requirements",label:"Hidden Requirements",icon:"🔍",color:DS.amber,desc:"Unstated expectations you'll actually be evaluated on",items:result.hidden_requirements||[],isTag:false},
    {key:"red_flags",label:"Red Flags",icon:"🚩",color:DS.red,desc:"Warning signs you must know before applying",items:result.red_flags||[],isTag:false},
    {key:"skills_needed",label:"Skills Required",icon:"⚡",color:DS.green,desc:"Real skills needed — both listed and implied",items:result.skills_needed||[],isTag:true},
    {key:"keyword_suggestions",label:"Keywords for Your Resume",icon:"🏷",color:JC,desc:"ATS-optimized keywords to include in your application",items:result.keyword_suggestions||[],isTag:true},
  ]:[];
  return (
    <div style={{ animation:"teFadeIn 0.3s ease" }}>
      <div style={{ background:DS.bg1,border:"1px solid "+JC+"30",borderRadius:16,overflow:"hidden",marginBottom:14 }}>
        <div style={{ padding:"12px 16px",borderBottom:"1px solid "+JC+"20",display:"flex",alignItems:"center",justifyContent:"space-between",background:"rgba(6,182,212,0.04)" }}>
          <span style={{ fontSize:12,fontWeight:600,color:JC }}>Paste job description</span>
          <span style={{ fontSize:11,color:DS.t3,fontFamily:"'JetBrains Mono',monospace" }}>{jdText.length} chars</span>
        </div>
        <textarea value={jdText} onChange={function(e){setJdText(e.target.value);setError(null);setResult(null);}} placeholder={"Paste the full job description here...\n\n• Job title + responsibilities\n• Required qualifications\n• Skills and experience\n\nThe more complete the JD, the sharper the analysis."} rows={9} style={{ width:"100%",background:"rgba(0,8,20,0.6)",color:DS.t1,border:"none",outline:"none",padding:"16px",fontSize:13,lineHeight:1.8,resize:"vertical",fontFamily:"'Sora',sans-serif",caretColor:JC,boxSizing:"border-box" }}/>
      </div>
      {error&&<div style={{ background:"rgba(244,63,94,0.08)",border:"1px solid rgba(244,63,94,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:13,color:DS.red }}>⚠ {error}</div>}
      <div style={{ display:"flex",gap:10,marginBottom:14,flexWrap:"wrap" }}>
        <button onClick={analyze} disabled={loading} style={{ flex:1,minWidth:180,padding:"13px",background:loading?"transparent":JC+"15",border:"1px solid "+(loading?JC+"22":JC+"44"),borderRadius:12,color:loading?JC+"66":JC,fontSize:13,fontWeight:700,cursor:loading?"not-allowed":"pointer",transition:"all 0.2s",display:"flex",alignItems:"center",justifyContent:"center",gap:10 }}>
          {loading?<><div style={{ width:13,height:13,border:"2px solid "+JC+"33",borderTopColor:JC,borderRadius:"50%",animation:"teSpin 0.7s linear infinite" }}/>Analysing...</>:"🔍 Analyse Job Description"}
        </button>
        <label style={{ display:"inline-flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:JC,padding:"12px 16px",background:JC+"08",border:"1px solid "+JC+"22",borderRadius:12,whiteSpace:"nowrap" }}>
          📎 Upload JD
          <input type="file" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" style={{ display:"none" }} onChange={async function(e){try{var file=e.target.files&&e.target.files[0];if(!file)return;var text=await extractTextFromFile(file);if(!text){try{text=await extractResumeText(file,null);}catch(fe){text="";}}if(text&&text.length>10){setJdText(text.slice(0,8000));setError(null);setResult(null);}}catch(err){}}}/>
        </label>
      </div>
      {loading&&<div style={{ marginBottom:14,height:3,background:DS.bg3,borderRadius:2,overflow:"hidden" }}><div style={{ height:"100%",width:progress+"%",background:"linear-gradient(90deg,"+JC+","+JC+"88)",transition:"width 0.5s ease",boxShadow:"0 0 8px "+JC+"66" }}/></div>}
      {result&&!loading&&(
        <div style={{ marginTop:20,display:"flex",flexDirection:"column",gap:14,animation:"teFadeIn 0.3s ease" }}>
          <DecisionBlock type="jd" data={result}/>
          <GlobalIntelligencePanel result={result} type="jd"/>
          <ResultSummary type="jd" data={result}/>
          <ExpandableSection title="Full JD Analysis" accentColor={DS.cyan}>
          <JDFitVerdict result={result}/>
          <JDRealVsPosted result={result}/>
          <JDGhostJobDetector result={result}/>
          {result.role_reality&&(<div style={{ background:JC+"08",border:"1px solid "+JC+"25",borderRadius:14,padding:"14px 16px" }}><div style={{ fontSize:12,fontWeight:700,color:JC,marginBottom:8 }}>Role Assessment</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.75,marginBottom:result.growth_potential?10:0 }}>{result.role_reality}</div>{result.growth_potential&&<div style={{ display:"flex",gap:8,flexWrap:"wrap",alignItems:"center" }}><Pill text={"Growth: "+result.growth_potential} color={result.growth_potential==="High"?DS.green:result.growth_potential==="Medium"?DS.amber:DS.red}/>{result.experience_required&&<Pill text={"Exp: "+result.experience_required} color={DS.blue}/>}</div>}</div>)}
          <ExpandableSection title="Show Detailed Breakdown" defaultOpen={true} accentColor={DS.cyan}>
          <div style={{padding:"8px 0 0"}}>
          {SECS.map(function(sec){if(!sec.items||sec.items.length===0)return null;return(<div key={sec.key} style={{ background:DS.bg1,border:"1px solid "+sec.color+"20",borderRadius:14,overflow:"hidden",marginBottom:8 }}><div style={{ padding:"12px 16px",borderBottom:"1px solid "+sec.color+"15",background:sec.color+"05",display:"flex",alignItems:"center",gap:10 }}><span style={{ fontSize:16 }}>{sec.icon}</span><div style={{ flex:1 }}><div style={{ fontSize:12,fontWeight:700,color:sec.color }}>{sec.label}</div><div style={{ fontSize:11,color:DS.t3,marginTop:2 }}>{sec.desc}</div></div><span style={{ fontSize:11,background:sec.color+"18",border:"1px solid "+sec.color+"30",borderRadius:100,padding:"2px 10px",color:sec.color,fontWeight:700 }}>{sec.items.length}</span></div><div style={{ padding:"14px 16px" }}>{sec.isTag?<div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>{sec.items.map(function(item,i){return <Pill key={i} text={item} color={sec.color}/>;})}</div>:sec.items.map(function(item,i){return(<div key={i} style={{ display:"flex",gap:10,alignItems:"flex-start",padding:"10px 12px",background:sec.color+"06",border:"1px solid "+sec.color+"15",borderRadius:10,marginBottom:i<sec.items.length-1?8:0 }}><span style={{ color:sec.color,flexShrink:0,fontSize:12,paddingTop:1,fontWeight:700 }}>{i+1}.</span><span style={{ fontSize:13,color:DS.t2,lineHeight:1.7 }}>{item}</span></div>);})}</div></div>);})}
          <JDCultureToxicityScanner result={result}/>
          </div></ExpandableSection>
          </ExpandableSection>
          <AIChatAssistant context={result} contextType="jd"/>
          <ATSOptimizerPanel jdText={jdText} jdResult={result}/>
          <button onClick={function(){setResult(null);setJdText("");setError(null);}} style={{ alignSelf:"flex-start",fontSize:13,padding:"8px 16px",background:"transparent",border:"1px solid "+DS.border0,borderRadius:10,color:DS.t3,cursor:"pointer" }}>← Analyse another JD</button>
        </div>
      )}
      {!result&&!loading&&<div style={{ marginTop:24,textAlign:"center",padding:"32px 20px",color:DS.t3 }}><div style={{ fontSize:36,marginBottom:12,opacity:0.4 }}>📋</div><div style={{ fontSize:13,lineHeight:1.75 }}>Paste a job description and we'll reveal what they're really looking for.</div></div>}
    </div>
  );
}

// ===================================================
// RESUME MODE (abbreviated — delegates to sub-components)
// ===================================================
function ResumeMode({ callFn }) {
  var r0=useState(null);var rr=r0[0];var setRr=r0[1];
  var r1=useState(false);var busy=r1[0];var setBusy=r1[1];
  var r2=useState(null);var err=r2[0];var setErr=r2[1];
  var r3=useState(0);var prog=r3[0];var setProg=r3[1];
  var r5=useState("roles");var rt=r5[0];var setRt=r5[1];
  var r6=useState(null);var fname=r6[0];var setFname=r6[1];
  var r7=useState(0);var selR=r7[0];var setSelR=r7[1];
  var r8=useState("");var resumeExtracted=r8[0];var setResumeExtracted=r8[1];
  var fref=useRef(null);var abRef=useRef(null);

  var upload=async function(e){
    var file=e.target.files[0];if(!file)return;
    var allowed=["pdf","docx","txt","jpg","jpeg","png"];
    var fileExt=(file.name||"").toLowerCase().split(".").pop();
    if(!allowed.includes(fileExt)){setErr("Supported: PDF, DOCX, TXT, JPG, PNG");return;}
    if(file.size>5*1024*1024){setErr("Max 5MB.");return;}
    setFname(file.name);setBusy(true);setErr(null);setRr(null);setProg(10);
    if(abRef.current)abRef.current.abort();abRef.current=new AbortController();
    try{
      setProg(25);var text=await extractPDF(file);
      if(!text||text.trim().length<30)throw new Error("Could not extract readable text. Try a text-based PDF or DOCX.");
      var fakeCheck=detectFakeResume(text);CROSS_INTEL.store("resume","last_fake_check",fakeCheck);
      setProg(50);setResumeExtracted(text.slice(0,4000));
      var cleanText=text.replace(/\s+/g," ").slice(0,6000);
      var resumePrompt="Analyze this Indian job seeker resume in extreme detail:\n\n\"\"\""+cleanText+"\"\"\"";
      var parsed=await withTimeout(runAIStable(resumePrompt,RESUME_SYS,abRef.current.signal),90000);
      if(!parsed||parsed.error||parsed.fallback){
        // Retry with shorter, simpler prompt before giving up
        try{
          var shortPrompt="Analyze this resume for an Indian job seeker. Resume text:\n\n\"\"\""+text.replace(/\s+/g," ").slice(0,3000)+"\"\"\"";
          parsed=await withTimeout(runAIStable(shortPrompt,RESUME_SYS,abRef.current.signal),60000);
        }catch(re){if(re&&re.name==="AbortError")throw re;}
      }
      if(!parsed||parsed.error||parsed.fallback){try{var data=await callFn(resumePrompt,abRef.current.signal);var tr2=gt(data);if(tr2){var p2=ultraParseJSON(tr2);if(p2&&!p2.error&&!p2.fallback)parsed=p2;}}catch(fe){if(fe&&fe.name==="AbortError")throw fe;}}
      if(!parsed||parsed.error||parsed.fallback){parsed={_fallback:true,ats_score:0,interview_readiness:0,overall_grade:"—",candidate_level:"Analysis Unavailable",years_experience:0,summary:"AI analysis unavailable. Please retry.",top_roles:[],interview_readiness_breakdown:{technical_depth:0,communication_clarity:0,portfolio_strength:0,industry_knowledge:0},salary_benchmarks:{current_market_value:"—",target_after_upskilling:"—",negotiation_tip:"—",in_demand_skills_premium:[]},skills_found:[],skills_missing:[],rewrites:[],action_plan:{day_30:[],day_60:[],day_90:[]},ats_issues:[],strengths:[],quick_wins:[],india_insights:[]};setErr("AI analysis limited — showing basic result. Please retry.");}
      CROSS_INTEL.store("resume","last",parsed);try{s3Update("resume",parsed);}catch(e){}try{updatePassport("resume",parsed);}catch(ppe){}
      setProg(100);setTimeout(function(){setRr(parsed);setBusy(false);try{if(navigator.vibrate)navigator.vibrate([10,50,10]);}catch(e){}},300);
    }catch(e){setBusy(false);if(e&&e.name==="AbortError")return;setErr((e&&e.message)||"Analysis failed. Please retry.");}
    if(fref.current)fref.current.value="";
  };
  var sc=function(v){if(v>=80)return DS.green;if(v>=60)return DS.amber;if(v>=40)return DS.amber;return DS.red;};
  var gc=function(g){if(!g)return DS.amber;var x=g[0];if(x==="A")return DS.green;if(x==="B")return DS.amber;if(x==="C")return DS.amber;return DS.red;};
  var TABS=[{id:"roles",l:"Top Roles"},{id:"scores",l:"Scores"},{id:"skills",l:"Skill Gaps"},{id:"rewrites",l:"Rewrites"},{id:"plan",l:"90-Day Plan"},{id:"extras",l:"More Tools"}];

  return (
    <div style={{ maxWidth:860,margin:"0 auto" }}>
      {!rr&&!busy&&(
        <div style={{ background:DS.bg1,border:"2px dashed "+DS.border1,borderRadius:20,padding:"48px 32px",textAlign:"center",marginBottom:24,cursor:"pointer" }}
          onClick={function(){if(fref.current)fref.current.click();}}
          onDragOver={function(e){e.preventDefault();e.currentTarget.style.borderColor=DS.blue+"60";e.currentTarget.style.background=DS.blue+"05";}}
          onDragLeave={function(e){e.currentTarget.style.borderColor=DS.border1;e.currentTarget.style.background=DS.bg1;}}
          onDrop={async function(e){e.preventDefault();e.currentTarget.style.borderColor=DS.border1;e.currentTarget.style.background=DS.bg1;var files=e.dataTransfer.files;if(files&&files[0]){await upload({target:{files:files}});}}}>
          <div style={{ fontSize:44,marginBottom:14 }}>📄</div>
          <div style={{ fontSize:18,fontWeight:700,color:DS.t1,marginBottom:8 }}>Drop your resume here</div>
          <div style={{ fontSize:13,color:DS.t2,marginBottom:24 }}>PDF, DOCX, TXT, or image — up to 5MB</div>
          <div style={{ display:"inline-flex",alignItems:"center",gap:8,padding:"12px 28px",background:DS.blue+"15",border:"1px solid "+DS.blue+"40",borderRadius:14,color:DS.blue,fontSize:14,fontWeight:700 }}>📂 Choose File</div>
          <input ref={fref} type="file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.txt" onChange={upload} style={{ display:"none" }}/>
        </div>
      )}
      {busy&&<Thinking steps={["Reading file","Extracting text","Running career forensics","Scoring ATS","Analysing market position","Building role recommendations","Generating skill gap report","Completing"]} currentStep={Math.floor(prog/13)} progress={prog} color={DS.blue} title="Analysing your resume..."/>}
      {err&&<div style={{ background:"rgba(244,63,94,0.08)",border:"1px solid rgba(244,63,94,0.2)",borderRadius:12,padding:"12px 16px",fontSize:13,color:DS.red,marginBottom:16 }}>⚠ {err}</div>}
      {rr&&!busy&&(
        <div style={{ animation:"teFadeIn 0.35s ease" }}>
          <DecisionBlock type="resume" data={rr}/>
          <GlobalIntelligencePanel result={rr} type="resume"/>
          <ResultSummary type="resume" data={rr}/>
          <ExpandableSection title="Full Resume Analysis" accentColor={DS.blue}>
          <div style={{ background:DS.bg1,border:"1px solid "+DS.border1,borderRadius:20,overflow:"hidden",marginBottom:20 }}>
            <div style={{ height:3,background:"linear-gradient(90deg,"+DS.blue+","+DS.purple+")" }}/>
            <div style={{ padding:"24px 28px" }}>
              <div style={{ display:"flex",flexWrap:"wrap",gap:24,alignItems:"center",marginBottom:18 }}>
                <div style={{ flex:1,minWidth:200 }}>
                  <div style={{ fontSize:11,color:DS.t3,marginBottom:4 }}>File: {fname}</div>
                  <div style={{ fontSize:20,fontWeight:800,color:DS.t1,marginBottom:6 }}>{rr.candidate_level||"Profile Analysed"}</div>
                  <div style={{ fontSize:14,color:DS.t2,lineHeight:1.7 }}>{rr.summary}</div>
                </div>
                <div style={{ display:"flex",gap:16,flexWrap:"wrap",alignItems:"center" }}>
                  <div style={{ textAlign:"center" }}><ScoreRing value={rr.ats_score||0} size={72} color={sc(rr.ats_score||0)}/><div style={{ fontSize:9,color:DS.t3,marginTop:5,fontFamily:"'JetBrains Mono',monospace" }}>ATS SCORE</div></div>
                  <div style={{ textAlign:"center" }}><ScoreRing value={rr.interview_readiness||0} size={72} color={sc(rr.interview_readiness||0)}/><div style={{ fontSize:9,color:DS.t3,marginTop:5,fontFamily:"'JetBrains Mono',monospace" }}>READINESS</div></div>
                  {rr.overall_grade&&<div style={{ textAlign:"center",width:72,height:72,background:gc(rr.overall_grade)+"15",border:"2px solid "+gc(rr.overall_grade)+"40",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column" }}><div style={{ fontSize:24,fontWeight:900,color:gc(rr.overall_grade) }}>{rr.overall_grade}</div><div style={{ fontSize:8,color:DS.t3,fontFamily:"'JetBrains Mono',monospace" }}>GRADE</div></div>}
                </div>
              </div>
              {rr.quick_wins&&rr.quick_wins.length>0&&(<div style={{ background:DS.green+"08",border:"1px solid "+DS.green+"20",borderRadius:12,padding:"12px 14px" }}><div style={{ fontSize:11,fontWeight:700,color:DS.green,marginBottom:10 }}>Quick wins — do these first</div><div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>{rr.quick_wins.slice(0,5).map(function(w,i){return <Pill key={i} text={w} color={DS.green}/>;})}</div></div>)}
            </div>
          </div>
          <div style={{ display:"flex",borderBottom:"1px solid "+DS.border0,overflowX:"auto",marginBottom:20,background:DS.bg1,borderRadius:"14px 14px 0 0",overflow:"hidden" }}>
            {TABS.map(function(t){var active=rt===t.id;return <button key={t.id} onClick={function(){setRt(t.id);}} style={{ padding:"13px 16px",background:active?DS.blue+"12":"transparent",border:"none",borderBottom:"2px solid "+(active?DS.blue:"transparent"),color:active?DS.blue:DS.t3,fontSize:12,fontWeight:active?700:500,cursor:"pointer",whiteSpace:"nowrap",transition:"all 0.2s" }}>{t.l}</button>;})}
          </div>
          {rt==="roles"&&rr.top_roles&&rr.top_roles.length>0&&(<div style={{ animation:"teFadeIn 0.25s ease" }}>{rr.top_roles.map(function(role,i){var active=selR===i;return(<div key={i} style={{ background:DS.bg1,border:"1px solid "+(active?DS.blue+"50":DS.border0),borderRadius:16,marginBottom:14,overflow:"hidden",transition:"all 0.25s",cursor:"pointer" }} onClick={function(){setSelR(active?-1:i);}}><div style={{ padding:"16px 20px",display:"flex",alignItems:"center",gap:16,flexWrap:"wrap" }}><div style={{ width:52,height:52,borderRadius:12,background:DS.blue+"15",border:"1px solid "+DS.blue+"30",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}><span style={{ fontSize:18,fontWeight:900,color:DS.blue,fontFamily:"'JetBrains Mono',monospace" }}>{role.match||0}</span></div><div style={{ flex:1,minWidth:140 }}><div style={{ fontSize:16,fontWeight:700,color:DS.t1,marginBottom:4 }}>{role.role}</div><div style={{ fontSize:12,color:DS.t2 }}>{role.why}</div></div><div style={{ display:"flex",gap:10,alignItems:"center",flexWrap:"wrap" }}>{role.salary_range&&<Pill text={role.salary_range} color={DS.green}/>}<span style={{ fontSize:18,color:DS.t4,transform:active?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.2s" }}>›</span></div></div>{active&&(<div style={{ padding:"0 20px 20px",borderTop:"1px solid "+DS.border0,animation:"teFadeIn 0.2s ease" }}>{role.gap&&<div style={{ background:"rgba(244,63,94,0.07)",border:"1px solid rgba(244,63,94,0.18)",borderRadius:10,padding:"10px 14px",fontSize:13,color:DS.red,margin:"14px 0" }}>Gap: {role.gap}</div>}{role.top_companies&&role.top_companies.length>0&&(<div style={{ marginBottom:12 }}><div style={{ fontSize:11,color:DS.t3,marginBottom:8 }}>Top hiring companies</div><div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>{role.top_companies.map(function(c,ci){return <Pill key={ci} text={c} color={DS.blue}/>;})}</div></div>)}{role.roadmap&&role.roadmap.length>0&&(<div><div style={{ fontSize:11,color:DS.t3,marginBottom:10 }}>Roadmap to get hired</div>{role.roadmap.map(function(step,si){return <div key={si} style={{ display:"flex",gap:10,marginBottom:8 }}><span style={{ color:DS.blue,fontSize:12,flexShrink:0 }}>{si+1}.</span><span style={{ fontSize:13,color:DS.t2,lineHeight:1.6 }}>{step}</span></div>;})}</div>)}</div>)}</div>);})}</div>)}
          {rt==="scores"&&(<div style={{ animation:"teFadeIn 0.25s ease" }}><ResumeMarketMirror result={rr}/><ResumeLieDetectorPanel resumeText={resumeExtracted}/><div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:14,marginBottom:20 }}>{rr.interview_readiness_breakdown&&Object.entries(rr.interview_readiness_breakdown).map(function(en,i){var label=en[0].replace(/_/g," ");var val=en[1];return(<div key={i} style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,padding:"16px" }}><div style={{ fontSize:11,color:DS.t3,textTransform:"capitalize",marginBottom:8 }}>{label}</div><div style={{ fontSize:26,fontWeight:900,color:sc(val),fontFamily:"'JetBrains Mono',monospace",marginBottom:8 }}>{val}</div><ABar value={val} color={sc(val)} delay={i*80}/></div>);})} </div>{rr.salary_benchmarks&&(<div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,padding:"18px" }}><div style={{ fontSize:13,fontWeight:700,color:DS.t1,marginBottom:14 }}>Your market value</div><div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14 }}><div style={{ background:DS.bg0,borderRadius:12,padding:"14px" }}><div style={{ fontSize:11,color:DS.t3,marginBottom:6 }}>Current market value</div><div style={{ fontSize:16,fontWeight:800,color:DS.red }}>{rr.salary_benchmarks.current_market_value}</div></div><div style={{ background:DS.bg0,borderRadius:12,padding:"14px" }}><div style={{ fontSize:11,color:DS.t3,marginBottom:6 }}>After upskilling</div><div style={{ fontSize:16,fontWeight:800,color:DS.green }}>{rr.salary_benchmarks.target_after_upskilling}</div></div></div>{rr.salary_benchmarks.negotiation_tip&&<div style={{ fontSize:13,color:DS.t2,background:DS.bg0,borderRadius:10,padding:"12px 14px" }}>💡 {rr.salary_benchmarks.negotiation_tip}</div>}{rr.salary_benchmarks.in_demand_skills_premium&&rr.salary_benchmarks.in_demand_skills_premium.length>0&&(<div style={{ marginTop:12 }}><div style={{ fontSize:11,color:DS.t3,marginBottom:8 }}>Premium skills</div><div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>{rr.salary_benchmarks.in_demand_skills_premium.map(function(s,i){return <Pill key={i} text={s} color={DS.green}/>;})}</div></div>)}</div>)}</div>)}
          {rt==="skills"&&(<div style={{ animation:"teFadeIn 0.25s ease" }}>{rr.skills_found&&rr.skills_found.length>0&&(<div style={{ marginBottom:20 }}><div style={{ fontSize:12,fontWeight:700,color:DS.green,marginBottom:12 }}>Skills you have</div><div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>{rr.skills_found.map(function(s,i){return <Pill key={i} text={s} color={DS.green}/>;})}</div></div>)}{rr.skills_missing&&rr.skills_missing.length>0&&(<div style={{ marginBottom:20 }}><div style={{ fontSize:12,fontWeight:700,color:DS.red,marginBottom:12 }}>Skills you need</div>{rr.skills_missing.map(function(sk,i){var pc=sk.priority==="HIGH"?DS.red:sk.priority==="MEDIUM"?DS.amber:DS.green;return(<div key={i} style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,padding:"16px",marginBottom:10 }}><div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:8,flexWrap:"wrap" }}><div style={{ fontSize:15,fontWeight:700,color:DS.t1 }}>{sk.skill}</div><Pill text={sk.priority+" priority"} color={pc}/>{sk.learn_in&&<span style={{ fontSize:12,color:DS.t3 }}>Learn in: {sk.learn_in}</span>}</div><div style={{ fontSize:13,color:DS.t2 }}>{sk.why}</div>{sk.impact_on_salary&&<div style={{ fontSize:12,color:DS.green,marginTop:8 }}>💰 {sk.impact_on_salary}</div>}</div>);})}</div>)}{rr.ats_issues&&rr.ats_issues.length>0&&(<div><div style={{ fontSize:12,fontWeight:700,color:DS.amber,marginBottom:12 }}>ATS issues to fix</div>{rr.ats_issues.map(function(issue,i){return <Flag key={i} text={issue} type="yellow"/>;})}</div>)}</div>)}
          {rt==="rewrites"&&(<div style={{ animation:"teFadeIn 0.25s ease" }}>{rr.rewrites&&rr.rewrites.length>0?rr.rewrites.map(function(rw,i){return(<div key={i} style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:16,padding:"18px",marginBottom:14 }}><div style={{ fontSize:11,fontWeight:700,color:DS.t2,marginBottom:14 }}>{rw.section}</div><div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10 }}><div style={{ background:"rgba(244,63,94,0.07)",border:"1px solid rgba(244,63,94,0.18)",borderRadius:10,padding:"12px 14px" }}><div style={{ fontSize:10,color:DS.red,marginBottom:6 }}>BEFORE</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.65 }}>{rw.original}</div></div><div style={{ background:"rgba(16,185,129,0.07)",border:"1px solid rgba(16,185,129,0.18)",borderRadius:10,padding:"12px 14px" }}><div style={{ fontSize:10,color:DS.green,marginBottom:6 }}>AFTER</div><div style={{ fontSize:13,color:DS.t1,lineHeight:1.65,fontWeight:500 }}>{rw.rewritten}</div></div></div>{rw.why&&<div style={{ fontSize:12,color:DS.t3 }}>Why: {rw.why}</div>}</div>);}): <div style={{ textAlign:"center",padding:"32px",color:DS.t3 }}>No rewrites generated.</div>}</div>)}
          {rt==="plan"&&(<div style={{ animation:"teFadeIn 0.25s ease" }}>{rr.action_plan&&[["First 30 days",rr.action_plan.day_30,DS.blue],["Days 31–60",rr.action_plan.day_60,DS.purple],["Days 61–90",rr.action_plan.day_90,DS.teal]].map(function(phase,pi){return phase[1]&&phase[1].length>0?(<div key={pi} style={{ marginBottom:20 }}><div style={{ fontSize:13,fontWeight:700,color:phase[2],marginBottom:12,display:"flex",alignItems:"center",gap:10 }}><div style={{ width:28,height:28,borderRadius:"50%",background:phase[2]+"15",border:"1px solid "+phase[2]+"30",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:900,color:phase[2] }}>{pi+1}</div>{phase[0]}</div>{phase[1].map(function(task,ti){return <div key={ti} style={{ display:"flex",gap:12,marginBottom:10 }}><div style={{ width:20,height:20,borderRadius:"50%",background:phase[2]+"15",border:"1px solid "+phase[2]+"25",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:phase[2],flexShrink:0,marginTop:2 }}>{ti+1}</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.65 }}>{task}</div></div>;})}</div>):null;})}</div>)}
          {rt==="extras"&&(<div style={{ animation:"teFadeIn 0.25s ease" }}><ATSHistoryChart atsScore={rr.ats_score||0}/><InterviewGenerator callFn={callFn} resumeText={resumeExtracted} detectedRole={rr.top_roles&&rr.top_roles[0]?rr.top_roles[0].role:""}/><CoverLetterGenerator callFn={callFn} resumeText={resumeExtracted}/><OfferNegotiationAssistant/><OfferComparisonEngine/><InterviewDebriefAssistant/></div>)}
          <SalaryNegotiationAnchor result={rr}/>
          <NegotiationScriptPanel
            role={rr.top_roles&&rr.top_roles[0]&&rr.top_roles[0].role}
            salaryRange={rr.salary_benchmarks&&rr.salary_benchmarks.target_after_upskilling}
            experienceLevel={rr.candidate_level}
          />
          </ExpandableSection>
          <AIChatAssistant context={rr} contextType="resume"/>
          <P6ShareResultBlock result={rr} type="resume"/>
          <NextActionCard moduleType="resume"/>
          <button onClick={function(){setRr(null);setFname(null);setErr(null);setProg(0);setResumeExtracted("");}} style={{ marginTop:20,fontSize:13,color:DS.t3,background:"transparent",border:"1px solid "+DS.border0,padding:"10px 20px",borderRadius:10,cursor:"pointer",transition:"all 0.2s" }} onMouseEnter={function(e){e.currentTarget.style.color=DS.t1;}} onMouseLeave={function(e){e.currentTarget.style.color=DS.t3;}}>← Analyse another resume</button>
        </div>
      )}
    </div>
  );
}


// ===================================================
// HR INTEL SECTION
// ===================================================
function HRIntelSection() {
  var s0=useState("");var jobTitle=s0[0];var setJobTitle=s0[1];
  var s1=useState("");var jobDesc=s1[0];var setJobDesc=s1[1];
  var s2=useState([]);var resumes=s2[0];var setResumes=s2[1];
  var s3=useState(null);var ranking=s3[0];var setRanking=s3[1];
  var s4=useState(false);var loading=s4[0];var setLoading=s4[1];
  var s5=useState(0);var hrStep=s5[0];var setHrStep=s5[1];
  var s6=useState(0);var hrProgress=s6[0];var setHrProgress=s6[1];
  var s7=useState(null);var error=s7[0];var setError=s7[1];
  var s8=useState(null);var expanded=s8[0];var setExpanded=s8[1];
  var s9=useState(false);var processing=s9[0];var setProcessing=s9[1];
  var fileRef=useRef(null);var intRef=useRef(null);var abortRef=useRef(null);
  useEffect(function(){return function(){clearInterval(intRef.current);if(abortRef.current)abortRef.current.abort();};},[]);
  var handleFiles=useCallback(async function(e){
    var files=Array.from(e.target.files||[]);if(!files.length)return;
    var remaining=20-resumes.length;if(remaining<=0){setError("Maximum 20 resumes reached.");return;}
    files=files.slice(0,remaining);setProcessing(true);setError(null);
    var baseIdx=resumes.length;setResumes(function(prev){return prev.concat(files.map(function(f){return{name:f.name,text:"",status:"reading"};}));});
    for(var i=0;i<files.length;i++){
      var ci=baseIdx+i;var cn=files[i].name;
      try{var text=await extractResumeText(files[i],null);if(!text||text.length<30)throw new Error("Too short");var trimmed=text.slice(0,4000);(function(idx,name,t){setResumes(function(prev){var next=prev.slice();if(next[idx])next[idx]={name:name,text:t,status:"ok"};return next;});})(ci,cn,trimmed);}
      catch(err){(function(idx,name){setResumes(function(prev){var next=prev.slice();if(next[idx])next[idx]={name:name,text:"",status:"failed"};return next;});})(ci,cn);}
    }
    setProcessing(false);if(fileRef.current)fileRef.current.value="";
  },[resumes]);
  function removeResume(idx){setResumes(function(prev){return prev.filter(function(_,i){return i!==idx;});});}
  async function runRanking(){
    var valid=resumes.filter(function(r){return r.status==="ok"&&r.text.length>30;});
    if(!jobTitle.trim()){setError("Enter a job title.");return;}if(!jobDesc.trim()||jobDesc.trim().length<20){setError("Enter a job description.");return;}if(valid.length<2){setError("Upload at least 2 valid resumes.");return;}
    if(abortRef.current)abortRef.current.abort();abortRef.current=new AbortController();
    setError(null);setRanking(null);setLoading(true);setHrStep(0);setHrProgress(0);setExpanded(null);
    var s=0;intRef.current=setInterval(function(){s++;if(s<HR_STEPS.length){setHrStep(s);setHrProgress(Math.round((s/HR_STEPS.length)*88));}},700);
    try{
      var candidateBlock=valid.map(function(r,i){return "CANDIDATE "+(i+1)+" (File: "+r.name+"):\n"+r.text;}).join("\n\n---\n\n");
      var prompt=HR_RANKING_PROMPT+"\n\nJOB TITLE: "+jobTitle.trim()+"\n\nJOB DESCRIPTION:\n"+jobDesc.trim().slice(0,3000)+"\n\nCANDIDATES ("+valid.length+" total):\n\n"+candidateBlock;
      var parsed=await runAIStable(prompt,null,abortRef.current.signal);
      if(!parsed||parsed.error||!parsed.candidates){try{var raw=await callGeminiRaw(prompt,abortRef.current.signal);if(raw){var rp2=ultraParseJSON(raw);if(rp2&&rp2.candidates)parsed=rp2;}}catch(re){if(re&&re.name==="AbortError")throw re;}}
      if(!parsed||!parsed.candidates||!Array.isArray(parsed.candidates)||parsed.candidates.length===0){parsed={_fallback:true,job_title:jobTitle.trim(),total_candidates:valid.length,candidates:valid.map(function(r,i){return{rank:i+1,name:r.name.replace(/\.[^.]+$/,""),fit_score:50,verdict:"Needs Review",why_selected:"AI ranking unavailable — manual review required.",strengths:["Could not be determined"],weaknesses:["Could not be determined"],risk_flags:[]};})};setError("AI ranking incomplete — showing placeholder. Retry for full analysis.");}
      CROSS_INTEL.store("hr","last",parsed);try{s3Update("hr",parsed);}catch(e){}try{updatePassport("offer",parsed);}catch(ppe){}
      clearInterval(intRef.current);setHrProgress(100);setHrStep(HR_STEPS.length-1);
      setTimeout(function(){setRanking(parsed);setLoading(false);},400);
    }catch(e){clearInterval(intRef.current);setLoading(false);if(e&&e.name==="AbortError")return;setRanking({_fallback:true,job_title:jobTitle.trim(),total_candidates:valid.length,candidates:valid.map(function(r,i){return{rank:i+1,name:r.name.replace(/\.[^.]+$/,""),fit_score:0,verdict:"Error",why_selected:"Analysis failed.",strengths:[],weaknesses:[],risk_flags:[]};})});setError((e&&e.message)||"Ranking failed. Please retry.");}
  }
  function exportCSV(){if(!ranking||!ranking.candidates)return;var rows=[["Rank","Name","Fit Score","Verdict","Why Selected","Strengths","Weaknesses","Risk Flags"]];ranking.candidates.forEach(function(c){rows.push([c.rank,c.name,c.fit_score,c.verdict,(c.why_selected||"").replace(/,/g,";"),(c.strengths||[]).join("; "),(c.weaknesses||[]).join("; "),(c.risk_flags||[]).join("; ")]);});var csv=rows.map(function(r){return r.map(function(v){var cell=String(v).replace(/"/g,"\"\"");return "\""+cell+"\"";}).join(",");}).join("\n");try{var blob=new Blob([csv],{type:"text/csv"});var url=URL.createObjectURL(blob);var a=document.createElement("a");a.href=url;a.download="hr-intel-ranking.csv";a.click();setTimeout(function(){try{URL.revokeObjectURL(url);}catch(e){}},1000);}catch(e){}}
  var validCount=resumes.filter(function(r){return r.status==="ok";}).length;
  var shortlisted=ranking&&ranking.shortlist&&ranking.shortlist.selected_candidates?ranking.shortlist.selected_candidates:[];
  return (
    <div style={{ maxWidth:860,margin:"0 auto" }}>
      <div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:16,padding:"20px",marginBottom:14 }}>
        <div style={{ fontSize:14,fontWeight:700,color:DS.t1,marginBottom:14 }}>Job Details</div>
        <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
          <div><div style={{ fontSize:11,color:DS.t3,marginBottom:6 }}>Job Title</div><input value={jobTitle} onChange={function(e){setJobTitle(e.target.value);setError(null);}} placeholder="e.g. Senior React Developer, Data Analyst..." style={{ width:"100%",background:DS.bg0,color:DS.t1,border:"1px solid "+DS.border1,borderRadius:10,padding:"11px 14px",fontSize:13,outline:"none",boxSizing:"border-box" }}/></div>
          <div><div style={{ fontSize:11,color:DS.t3,marginBottom:6 }}>Job Description</div><textarea value={jobDesc} onChange={function(e){setJobDesc(e.target.value);setError(null);}} placeholder="Paste the full job description..." style={{ width:"100%",minHeight:110,background:DS.bg0,color:DS.t1,border:"1px solid "+DS.border1,borderRadius:10,padding:"12px 14px",fontSize:13,outline:"none",resize:"vertical",boxSizing:"border-box",lineHeight:1.7 }}/></div>
        </div>
      </div>
      <div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:16,overflow:"hidden",marginBottom:14 }}>
        <div style={{ padding:"14px 20px",borderBottom:"1px solid "+DS.border0,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8 }}>
          <div style={{ fontSize:13,fontWeight:700,color:DS.t1 }}>Candidate Resumes <span style={{ fontSize:11,color:DS.t3,fontWeight:400 }}>({validCount}/20)</span></div>
          <div style={{ display:"flex",alignItems:"center",gap:10 }}>
            <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.txt" onChange={handleFiles} style={{ display:"none" }} id="hr-upload" disabled={processing||resumes.length>=20}/>
            <label htmlFor="hr-upload" style={{ cursor:(processing||resumes.length>=20)?"not-allowed":"pointer",fontSize:12,color:processing?DS.amber:DS.purple,background:DS.purple+"10",padding:"8px 16px",border:"1px solid "+DS.purple+"30",borderRadius:10,fontWeight:600,whiteSpace:"nowrap",opacity:resumes.length>=20?0.4:1 }}>
              {processing?"Reading...":"+ Add Resumes"}
            </label>
          </div>
        </div>
        {resumes.length===0?(<div style={{ padding:"36px 20px",textAlign:"center" }}><div style={{ fontSize:32,marginBottom:10 }}>📄</div><div style={{ fontSize:13,color:DS.t3,marginBottom:6 }}>No resumes uploaded yet</div><div style={{ fontSize:12,color:DS.t4 }}>Supports: PDF · DOCX · PNG · JPG · TXT · up to 20 files</div></div>):(
          <div style={{ padding:"14px 20px",display:"flex",flexDirection:"column",gap:8 }}>
            {resumes.map(function(r,i){var st=r.status;var stc=st==="ok"?DS.green:st==="failed"?DS.red:DS.amber;return(<div key={i} style={{ display:"flex",alignItems:"center",gap:10,background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:10,padding:"10px 14px" }}><span style={{ fontSize:16,flexShrink:0 }}>📄</span><div style={{ flex:1,minWidth:0 }}><div style={{ fontSize:12,color:DS.t1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{r.name}</div>{r.text&&<div style={{ fontSize:11,color:DS.t3,marginTop:2 }}>{r.text.length.toLocaleString()} chars</div>}</div><Pill text={st==="ok"?"Ready":st==="failed"?"Failed":"Reading..."} color={stc}/><button onClick={function(){removeResume(i);}} style={{ background:"transparent",border:"none",color:DS.t4,cursor:"pointer",fontSize:18,padding:"0 4px",lineHeight:1 }}>×</button></div>);})}
            {resumes.length<20&&(<label htmlFor="hr-upload" style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"10px",border:"1px dashed "+DS.border1,borderRadius:10,cursor:processing?"wait":"pointer",color:DS.t3,fontSize:12 }}>+ Add more ({20-resumes.length} remaining)</label>)}
          </div>
        )}
      </div>
      {error&&<div style={{ background:"rgba(244,63,94,0.08)",border:"1px solid rgba(244,63,94,0.2)",borderRadius:12,padding:"12px 16px",marginBottom:14,fontSize:13,color:DS.red }}>⚠ {error}</div>}
      <button onClick={loading?function(){if(abortRef.current)abortRef.current.abort();setLoading(false);clearInterval(intRef.current);}:runRanking} disabled={processing} style={{ width:"100%",padding:"14px",border:"1px solid "+(loading?DS.purple+"50":DS.purple+"60"),background:loading?"transparent":DS.purple+"12",borderRadius:14,color:DS.purple,fontSize:14,fontWeight:700,cursor:processing?"not-allowed":"pointer",transition:"all 0.2s",boxShadow:loading?"none":"0 0 24px "+DS.purple+"15",opacity:processing?0.5:1,display:"flex",alignItems:"center",justifyContent:"center",gap:12 }}>
        {loading?<><div style={{ width:16,height:16,border:"2px solid "+DS.purple+"30",borderTopColor:DS.purple,borderRadius:"50%",animation:"teSpin 0.7s linear infinite" }}/>Ranking candidates — click to cancel</>:"🎯 Rank All Candidates"}
      </button>
      {loading&&<div style={{ marginTop:14 }}><Thinking steps={HR_STEPS} currentStep={hrStep} progress={hrProgress} color={DS.purple} title="Ranking candidates..."/></div>}
      {ranking&&!loading&&(
        <div style={{ marginTop:20,animation:"teFadeIn 0.35s ease" }}>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10 }}>
            <div style={{ fontSize:16,fontWeight:800,color:DS.t1 }}>Results — {ranking.job_title||jobTitle}</div>
            <button onClick={exportCSV} style={{ fontSize:12,color:DS.green,background:DS.green+"10",border:"1px solid "+DS.green+"30",padding:"8px 16px",borderRadius:10,cursor:"pointer",fontWeight:600 }}>Export CSV</button>
          </div>
          {ranking.candidates&&ranking.candidates.map(function(c,i){
            var isExp=expanded===i;var sc=riskColor(100-c.fit_score);var isShort=shortlisted.includes(c.name)||shortlisted.includes(c.rank);
            return(
              <div key={i} style={{ background:DS.bg1,border:"1px solid "+(i===0?sc+"50":DS.border0),borderRadius:16,marginBottom:12,overflow:"hidden",transition:"all 0.25s" }}>
                <div style={{ padding:"16px 20px",display:"flex",alignItems:"center",gap:14,cursor:"pointer",flexWrap:"wrap" }} onClick={function(){setExpanded(isExp?null:i);}}>
                  <div style={{ width:44,height:44,borderRadius:12,background:sc+"15",border:"1px solid "+sc+"30",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}><span style={{ fontSize:13,fontWeight:900,color:sc,fontFamily:"'JetBrains Mono',monospace" }}>{c.rank}</span></div>
                  <div style={{ flex:1,minWidth:140 }}>
                    <div style={{ display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:4 }}>
                      <div style={{ fontSize:15,fontWeight:700,color:DS.t1 }}>{c.name}</div>
                      {isShort&&<Pill text="⭐ Shortlisted" color={DS.green}/>}
                      {i===0&&<Pill text="Top Pick" color={sc}/>}
                    </div>
                    <div style={{ fontSize:12,color:DS.t2 }}>{c.verdict}</div>
                  </div>
                  <div style={{ display:"flex",alignItems:"center",gap:14,flexShrink:0 }}>
                    <div style={{ textAlign:"center" }}><div style={{ fontSize:22,fontWeight:900,color:sc,fontFamily:"'JetBrains Mono',monospace" }}>{c.fit_score}</div><div style={{ fontSize:9,color:DS.t3 }}>FIT SCORE</div></div>
                    <span style={{ fontSize:18,color:DS.t4,transform:isExp?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.2s" }}>›</span>
                  </div>
                </div>
                {isExp&&(<div style={{ padding:"0 20px 20px",borderTop:"1px solid "+DS.border0,animation:"teFadeIn 0.2s ease" }}>
                  <div style={{ marginTop:14 }}>
                    {c.why_selected&&<div style={{ fontSize:13,color:DS.t2,lineHeight:1.7,marginBottom:14,padding:"12px 14px",background:DS.bg0,borderRadius:10 }}>{c.why_selected}</div>}
                    <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12 }}>
                      {c.strengths&&c.strengths.length>0&&(<div style={{ background:"rgba(16,185,129,0.06)",border:"1px solid rgba(16,185,129,0.18)",borderRadius:10,padding:"12px 14px" }}><div style={{ fontSize:11,fontWeight:700,color:DS.green,marginBottom:8 }}>Strengths</div>{c.strengths.map(function(s,si){return <div key={si} style={{ fontSize:12,color:DS.t2,marginBottom:5,display:"flex",gap:8 }}><span style={{ color:DS.green }}>✓</span>{s}</div>;})}</div>)}
                      {c.weaknesses&&c.weaknesses.length>0&&(<div style={{ background:"rgba(244,63,94,0.06)",border:"1px solid rgba(244,63,94,0.18)",borderRadius:10,padding:"12px 14px" }}><div style={{ fontSize:11,fontWeight:700,color:DS.red,marginBottom:8 }}>Weaknesses</div>{c.weaknesses.map(function(w,wi){return <div key={wi} style={{ fontSize:12,color:DS.t2,marginBottom:5,display:"flex",gap:8 }}><span style={{ color:DS.red }}>✕</span>{w}</div>;})}</div>)}
                    </div>
                    {c.risk_flags&&c.risk_flags.length>0&&(<div style={{ background:"rgba(245,158,11,0.06)",border:"1px solid rgba(245,158,11,0.18)",borderRadius:10,padding:"12px 14px" }}><div style={{ fontSize:11,fontWeight:700,color:DS.amber,marginBottom:8 }}>Risk Flags</div>{c.risk_flags.map(function(f,fi){return <div key={fi} style={{ fontSize:12,color:DS.t2,marginBottom:5,display:"flex",gap:8 }}><span style={{ color:DS.amber }}>⚠</span>{f}</div>;})}</div>)}
                  </div>
                </div>)}
              </div>
            );
          })}
          {ranking.hiring_recommendation&&<div style={{ background:DS.green+"08",border:"1px solid "+DS.green+"25",borderRadius:14,padding:"14px 18px",marginTop:4 }}><div style={{ fontSize:12,fontWeight:700,color:DS.green,marginBottom:8 }}>Hiring Recommendation</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.7 }}>{ranking.hiring_recommendation}</div></div>}
          <HRPersonaSimulator ranking={ranking}/>
        </div>
      )}
    </div>
  );
}

// -- 90-DAY ROADMAP PROMPT -----------------------------------------
var ROADMAP_90D_PROMPT = "You are a senior Decision Intelligence System powering a production application.\n\nCRITICAL RULES (DO NOT BREAK):\n- Return ONLY valid JSON\n- No markdown\n- No explanation\n- No text before or after JSON\n- Start with { and end with }\n- Use double quotes only\n- No trailing commas\n- Ensure JSON is fully complete and not truncated\n\nOBJECTIVE:\nGenerate a highly detailed, execution-focused 90-day roadmap for a job seeker based on their resume profile.\n\nUSER CONTEXT:\nIndia job market. User wants REAL execution steps, not generic advice. Based on the resume provided, identify the most realistic career trajectory.\n\nOUTPUT FORMAT (STRICT JSON):\n{\"roadmap_90_days\":{\"phase_1\":{\"title\":\"Foundation Phase (Days 1-30)\",\"weeks\":[{\"week\":1,\"focus\":\"\",\"tasks\":[{\"task\":\"\",\"details\":\"\",\"tools\":[],\"deliverable\":\"\",\"time_required\":\"\",\"difficulty\":\"low\"}]}]},\"phase_2\":{\"title\":\"Build & Proof Phase (Days 31-60)\",\"weeks\":[]},\"phase_3\":{\"title\":\"Execution & Monetization Phase (Days 61-90)\",\"weeks\":[]}}}\n\nSTRICT EXECUTION RULES:\n- Exactly 12 weeks (weeks 1-12 across 3 phases)\n- Each week must have 3-5 REAL tasks\n- Tasks must be SPECIFIC to the resume (no generic advice)\n- Each task must produce a measurable deliverable\n- Include real tools (GitHub, LinkedIn, Naukri, specific platforms)\n- Include portfolio-building steps\n- Include application and networking steps\n- Difficulty: low | medium | high\n- time_required: e.g. '2 hours', '1 day'\n\nQUALITY: This roadmap should feel like it came from a top-tier mentor, not a basic AI.";

// ===================================================
// CAREER ENGINE SECTION
// ===================================================
function CareerEngineSection() {
  var C=DS.purple;
  var s1=useState("");var resumeText=s1[0];var setResumeText=s1[1];
  var s2=useState(false);var scanning=s2[0];var setScanning=s2[1];
  var s3=useState(null);var result=s3[0];var setResult=s3[1];
  var s4=useState(null);var error=s4[0];var setError=s4[1];
  var s5=useState(0);var progress=s5[0];var setProgress=s5[1];
  var s6=useState(0);var stepIdx=s6[0];var setStepIdx=s6[1];
  var s7=useState("ladder");var tab=s7[0];var setTab=s7[1];
  var s8=useState(1);var openLevel=s8[0];var setOpenLevel=s8[1];
  var s9=useState(null);var roadmap=s9[0];var setRoadmap=s9[1];
  var s10=useState(false);var roadmapLoading=s10[0];var setRoadmapLoading=s10[1];
  var abortRef=useRef(null);var intRef=useRef(null);var fileRef=useRef(null);
  useEffect(function(){return function(){clearInterval(intRef.current);if(abortRef.current)abortRef.current.abort();};},[]);
  var CSTEPS=["Parsing resume...","Running competency forensics...","Checking project authenticity...","Mapping domain alignment...","Computing ATS score...","Building career ladder...","Identifying growth path...","Running rejection analysis...","Finalising report..."];
  var LVC=["#64748b","#60a5fa","#22c55e","#f59e0b","#a78bfa"];
  var EL={Yes:{c:DS.green,bg:"rgba(16,185,129,0.06)",bd:"rgba(16,185,129,0.2)"},Partial:{c:DS.amber,bg:"rgba(245,158,11,0.06)",bd:"rgba(245,158,11,0.2)"},No:{c:DS.red,bg:"rgba(244,63,94,0.06)",bd:"rgba(244,63,94,0.2)"}};
  async function handleUpload(e){var file=e.target.files&&e.target.files[0];if(!file)return;setError(null);try{var text=await extractPDF(file);if(text&&text.length>20)setResumeText(text);else setError("Could not extract text. Paste manually.");}catch(err){setError("Upload failed. Paste resume text manually.");}if(fileRef.current)fileRef.current.value="";}
  async function runCareerAnalysis(){
    if(!resumeText.trim()||resumeText.trim().length<30){setError("Paste your resume text (min 30 chars).");return;}
    if(abortRef.current)abortRef.current.abort();abortRef.current=new AbortController();
    setError(null);setResult(null);setScanning(true);setStepIdx(0);setProgress(0);setTab("ladder");
    var s=0;intRef.current=setInterval(function(){s++;if(s<CSTEPS.length){setStepIdx(s);setProgress(Math.round((s/CSTEPS.length)*90));}else{clearInterval(intRef.current);}},520);
    try{
      var parsed=await withTimeout(runAIStable("Analyze this resume for India job market:\n\n"+resumeText.replace(/\s+/g," ").slice(0,6000),CAREER_ENGINE_PROMPT,abortRef.current.signal),55000);
      if(!parsed||parsed.error||parsed.fallback){parsed={_fallback:true,candidate_profile:{level:"Analysis Unavailable",domain:"—",summary:"AI could not analyze at this time. Please retry.",employability_risk:"Retry for full analysis."},scores:{ats_score:0,interview_readiness:0,technical_depth:0,portfolio:0,communication:0,market_demand:0},strengths:[],weaknesses:[],hidden_advantages:[],career_ladder:[],salary_intelligence:{current_value_range:"—",potential_value_range:"—",overvaluation_risk:"—"},growth_path:{target_role:"—",current_gap_level:"—",time_to_reach:"—",difficulty:"—",steps:[]},rejection_analysis:{top_reasons:[],missing_signals:[],recruiter_red_flags:[]},skills_gap:[]};setError("AI analysis limited. Please retry.");}
      CROSS_INTEL.store("career","last",parsed);try{s3Update("career",parsed);}catch(e){}
      clearInterval(intRef.current);setProgress(100);setStepIdx(CSTEPS.length-1);
      setTimeout(function(){setResult(parsed);setScanning(false);try{if(navigator.vibrate)navigator.vibrate([10,50,10]);}catch(e){}},300);
      // Generate 90-day roadmap in background
      (async function() {
        var rmSignal = new AbortController().signal;
        setRoadmapLoading(true);
        try {
          var rmPrompt = ROADMAP_90D_PROMPT + "\n\nRESUME SUMMARY:\n" + resumeText.replace(/\s+/g," ").slice(0,2000);
          var rmResult = await withTimeout(runAIStable(rmPrompt, null, rmSignal), 30000);
          if (rmResult && !rmResult.error && rmResult.roadmap_90_days) setRoadmap(rmResult.roadmap_90_days);
        } catch(re) {} finally { setRoadmapLoading(false); }
      })();
    }catch(e){clearInterval(intRef.current);setScanning(false);if(e&&e.name==="AbortError")return;setError((e&&e.message)||"Analysis failed. Please retry.");}
  }
  var TABS=[{id:"ladder",l:"Career Ladder"},{id:"path",l:"Growth Path"},{id:"rejection",l:"Rejection Analysis"},{id:"skills",l:"Skill Gaps"},{id:"salary",l:"Salary Intel"},{id:"roadmap90",l:"90-Day Plan"}];
  return (
    <div style={{ maxWidth:900,margin:"0 auto" }}>
      {!result&&!scanning&&(
        <div style={{ background:DS.bg1,border:"1px solid "+C+"25",borderRadius:16,padding:"20px",marginBottom:16 }}>
          <div style={{ fontSize:13,fontWeight:700,color:DS.t1,marginBottom:14 }}>Paste your resume</div>
          <textarea value={resumeText} onChange={function(e){setResumeText(e.target.value);setError(null);}} placeholder={"Paste your full resume text here...\n\nInclude: experience, education, skills, projects.\nThe more detail, the more accurate the analysis."} rows={9} style={{ width:"100%",background:DS.bg0,border:"1px solid "+DS.border1,borderRadius:12,padding:"14px 16px",color:DS.t1,fontSize:13,lineHeight:1.8,resize:"vertical",outline:"none",caretColor:C,boxSizing:"border-box" }}/>
          <div style={{ display:"flex",gap:10,marginTop:12,flexWrap:"wrap" }}>
            <button onClick={runCareerAnalysis} style={{ flex:1,minWidth:160,padding:"12px 20px",background:C+"15",border:"1px solid "+C+"40",borderRadius:12,color:C,fontSize:14,fontWeight:700,cursor:"pointer",transition:"all 0.2s" }}>🚀 Analyse Career</button>
            <label style={{ display:"inline-flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:C,padding:"12px 16px",background:C+"08",border:"1px solid "+C+"25",borderRadius:12 }}>📎 Upload<input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.txt" onChange={handleUpload} style={{ display:"none" }}/></label>
          </div>
        </div>
      )}
      {error&&<div style={{ background:"rgba(244,63,94,0.08)",border:"1px solid rgba(244,63,94,0.2)",borderRadius:12,padding:"12px 16px",marginBottom:16,fontSize:13,color:DS.red }}>⚠ {error}</div>}
      {scanning&&<Thinking steps={CSTEPS} currentStep={stepIdx} progress={progress} color={C} title="Building your career intelligence report..."/>}
      {result&&!scanning&&(
        <div style={{ animation:"teFadeIn 0.35s ease" }}>
          <DecisionBlock type="career" data={result}/>
          <GlobalIntelligencePanel result={result} type="career"/>
          <ResultSummary type="career" data={result}/>
          <ExpandableSection title="Full Career Analysis" accentColor={DS.purple}>
          <div style={{ background:DS.bg1,border:"1px solid "+C+"30",borderRadius:16,overflow:"hidden",marginBottom:18 }}>
            <div style={{ height:3,background:"linear-gradient(90deg,"+C+","+DS.blue+")" }}/>
            <div style={{ padding:"20px 24px" }}>
              <div style={{ display:"flex",flexWrap:"wrap",gap:20,alignItems:"flex-start",marginBottom:14 }}>
                <div style={{ flex:1,minWidth:200 }}>
                  <div style={{ fontSize:11,color:DS.t3,marginBottom:4 }}>Career Level</div>
                  <div style={{ fontSize:20,fontWeight:800,color:DS.t1,marginBottom:6 }}>{result.candidate_profile&&result.candidate_profile.level||"Analysed"}</div>
                  <div style={{ fontSize:13,color:DS.t2,lineHeight:1.7 }}>{result.candidate_profile&&result.candidate_profile.summary}</div>
                </div>
                <div style={{ display:"flex",gap:12,flexWrap:"wrap" }}>
                  {result.scores&&[["ATS",result.scores.ats_score,DS.blue],["Readiness",result.scores.interview_readiness,DS.green],["Technical",result.scores.technical_depth,C]].map(function(sc,i){return(<div key={i} style={{ textAlign:"center" }}><ScoreRing value={sc[1]||0} size={60} color={sc[2]}/><div style={{ fontSize:9,color:DS.t3,marginTop:5,fontFamily:"'JetBrains Mono',monospace" }}>{sc[0].toUpperCase()}</div></div>);})}
                </div>
              </div>
              {result.candidate_profile&&result.candidate_profile.employability_risk&&(<div style={{ background:"rgba(244,63,94,0.07)",border:"1px solid rgba(244,63,94,0.18)",borderRadius:10,padding:"10px 14px",fontSize:13,color:DS.t2 }}><span style={{ color:DS.red,fontWeight:600 }}>Risk: </span>{result.candidate_profile.employability_risk}</div>)}
            </div>
          </div>
          <CareerRiskRadar result={result}/>
          <CareerObituary result={result}/>
          <div style={{ display:"flex",borderBottom:"1px solid "+DS.border0,overflowX:"auto",marginBottom:18,background:DS.bg1,borderRadius:"14px 14px 0 0",overflow:"hidden" }}>
            {TABS.map(function(t){var active=tab===t.id;return <button key={t.id} onClick={function(){setTab(t.id);}} style={{ padding:"12px 16px",background:active?C+"12":"transparent",border:"none",borderBottom:"2px solid "+(active?C:"transparent"),color:active?C:DS.t3,fontSize:12,fontWeight:active?700:500,cursor:"pointer",whiteSpace:"nowrap",transition:"all 0.2s" }}>{t.l}</button>;})}
          </div>
          {tab==="ladder"&&result.career_ladder&&(
            <div style={{ animation:"teFadeIn 0.25s ease" }}>
              {result.career_ladder.map(function(level,li){var lc=LVC[Math.min(li,LVC.length-1)];var isOpen=openLevel===li+1;return(<div key={li} style={{ background:DS.bg1,border:"1px solid "+(isOpen?lc+"40":DS.border0),borderRadius:14,marginBottom:12,overflow:"hidden",transition:"border-color 0.25s" }}><div style={{ padding:"14px 18px",display:"flex",alignItems:"center",gap:14,cursor:"pointer",flexWrap:"wrap" }} onClick={function(){setOpenLevel(isOpen?0:li+1);}}><div style={{ width:40,height:40,borderRadius:10,background:lc+"15",border:"1px solid "+lc+"30",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}><span style={{ fontSize:11,fontWeight:900,color:lc,fontFamily:"'JetBrains Mono',monospace" }}>L{li+1}</span></div><div style={{ flex:1 }}><div style={{ fontSize:14,fontWeight:700,color:DS.t1 }}>{level.level_name}</div><div style={{ fontSize:12,color:DS.t3 }}>{level.salary_range}</div></div><span style={{ fontSize:18,color:DS.t4,transform:isOpen?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.2s" }}>›</span></div>{isOpen&&level.roles&&(<div style={{ padding:"0 18px 18px",borderTop:"1px solid "+DS.border0,animation:"teFadeIn 0.2s ease" }}>{level.roles.map(function(role,ri){var elf=EL[role.eligibility]||EL["Partial"];return(<div key={ri} style={{ background:elf.bg,border:"1px solid "+elf.bd,borderRadius:12,padding:"14px 16px",marginTop:10 }}><div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:8,flexWrap:"wrap" }}><div style={{ fontSize:14,fontWeight:700,color:DS.t1,flex:1 }}>{role.job_title}</div><Pill text={role.eligibility} color={elf.c}/><span style={{ fontSize:18,fontWeight:900,color:elf.c,fontFamily:"'JetBrains Mono',monospace" }}>{role.confidence_score}</span></div><div style={{ fontSize:12,color:DS.t2,marginBottom:role.gap?6:0 }}>{role.reason}</div>{role.gap&&<div style={{ fontSize:12,color:DS.amber }}>Gap: {role.gap}</div>}{role.salary_range&&<div style={{ fontSize:12,color:DS.green,marginTop:4 }}>💰 {role.salary_range}</div>}</div>);})}</div>)}</div>);})}
            </div>
          )}
          {tab==="path"&&result.growth_path&&(
            <div style={{ animation:"teFadeIn 0.25s ease" }}>
              <div style={{ background:C+"08",border:"1px solid "+C+"25",borderRadius:14,padding:"16px 18px",marginBottom:16 }}>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:12 }}>
                  <div><div style={{ fontSize:11,color:DS.t3,marginBottom:4 }}>Target Role</div><div style={{ fontSize:15,fontWeight:700,color:C }}>{result.growth_path.target_role}</div></div>
                  <div><div style={{ fontSize:11,color:DS.t3,marginBottom:4 }}>Time to Reach</div><div style={{ fontSize:14,fontWeight:700,color:DS.t1 }}>{result.growth_path.time_to_reach}</div></div>
                  <div><div style={{ fontSize:11,color:DS.t3,marginBottom:4 }}>Difficulty</div><Pill text={result.growth_path.difficulty||"—"} color={result.growth_path.difficulty==="Hard"?DS.red:result.growth_path.difficulty==="Easy"?DS.green:DS.amber}/></div>
                  <div><div style={{ fontSize:11,color:DS.t3,marginBottom:4 }}>Gap Level</div><Pill text={result.growth_path.current_gap_level||"—"} color={result.growth_path.current_gap_level==="High"?DS.red:result.growth_path.current_gap_level==="Low"?DS.green:DS.amber}/></div>
                </div>
              </div>
              {result.growth_path.steps&&result.growth_path.steps.map(function(step,si){return(<div key={si} style={{ display:"flex",gap:14,marginBottom:14,alignItems:"flex-start" }}><div style={{ width:28,height:28,borderRadius:"50%",background:C+"15",border:"1px solid "+C+"30",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:C,flexShrink:0,fontFamily:"'JetBrains Mono',monospace" }}>{si+1}</div><div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:12,padding:"12px 14px",flex:1 }}><div style={{ fontSize:13,fontWeight:600,color:DS.t1,marginBottom:step.tools?6:0 }}>{step.action||step}</div>{step.tools&&<div style={{ fontSize:11,color:DS.t3 }}>Tools: {Array.isArray(step.tools)?step.tools.join(", "):step.tools}</div>}{step.outcome&&<div style={{ fontSize:12,color:DS.green,marginTop:4 }}>Outcome: {step.outcome}</div>}</div></div>);})}
            </div>
          )}
          {tab==="rejection"&&result.rejection_analysis&&(
            <div style={{ animation:"teFadeIn 0.25s ease" }}>
              {result.rejection_analysis.top_reasons&&result.rejection_analysis.top_reasons.length>0&&(<div style={{ marginBottom:18 }}><div style={{ fontSize:13,fontWeight:700,color:DS.red,marginBottom:12 }}>Why recruiters reject you</div>{result.rejection_analysis.top_reasons.map(function(r,i){return <Flag key={i} text={r} type="red"/>;})}</div>)}
              {result.rejection_analysis.missing_signals&&result.rejection_analysis.missing_signals.length>0&&(<div style={{ marginBottom:18 }}><div style={{ fontSize:13,fontWeight:700,color:DS.amber,marginBottom:12 }}>What's missing from your profile</div>{result.rejection_analysis.missing_signals.map(function(s,i){return <Flag key={i} text={s} type="yellow"/>;})}</div>)}
              {result.rejection_analysis.recruiter_red_flags&&result.rejection_analysis.recruiter_red_flags.length>0&&(<div><div style={{ fontSize:13,fontWeight:700,color:DS.purple,marginBottom:12 }}>Recruiter red flags</div>{result.rejection_analysis.recruiter_red_flags.map(function(f,i){return <Flag key={i} text={f} type="yellow"/>;})}</div>)}
            </div>
          )}
          {tab==="skills"&&(<div style={{ animation:"teFadeIn 0.25s ease" }}>{result.skills_gap&&result.skills_gap.length>0&&result.skills_gap.map(function(sk,i){var pc=sk.priority==="HIGH"?DS.red:sk.priority==="MEDIUM"?DS.amber:DS.green;return(<div key={i} style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,padding:"16px",marginBottom:12 }}><div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:8,flexWrap:"wrap" }}><div style={{ fontSize:15,fontWeight:700,color:DS.t1,flex:1 }}>{sk.skill_name}</div><Pill text={sk.priority+" priority"} color={pc}/>{sk.time_to_learn&&<Pill text={"Learn in "+sk.time_to_learn} color={DS.blue}/>}</div><div style={{ fontSize:13,color:DS.t2,marginBottom:sk.impact_on_salary?6:0 }}>{sk.why_needed}</div>{sk.impact_on_salary&&<div style={{ fontSize:12,color:DS.green }}>💰 {sk.impact_on_salary}</div>}</div>);})} {result.strengths&&result.strengths.length>0&&(<div style={{ marginTop:16,background:DS.green+"08",border:"1px solid "+DS.green+"20",borderRadius:14,padding:"16px" }}><div style={{ fontSize:12,fontWeight:700,color:DS.green,marginBottom:12 }}>Your strengths</div>{result.strengths.map(function(s,i){return <div key={i} style={{ fontSize:13,color:DS.t2,marginBottom:8,display:"flex",gap:10 }}><span style={{ color:DS.green }}>✓</span>{s}</div>;})}</div>)}</div>)}
          {tab==="salary"&&result.salary_intelligence&&(<div style={{ animation:"teFadeIn 0.25s ease" }}><div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:18 }}><div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,padding:"16px" }}><div style={{ fontSize:11,color:DS.t3,marginBottom:8 }}>Current Market Value</div><div style={{ fontSize:20,fontWeight:800,color:DS.red }}>{result.salary_intelligence.current_value_range||"—"}</div></div><div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,padding:"16px" }}><div style={{ fontSize:11,color:DS.t3,marginBottom:8 }}>Potential After Upskilling</div><div style={{ fontSize:20,fontWeight:800,color:DS.green }}>{result.salary_intelligence.potential_value_range||"—"}</div></div></div>{result.salary_intelligence.overvaluation_risk&&<div style={{ background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.22)",borderRadius:12,padding:"12px 16px",fontSize:13,color:DS.t2 }}><span style={{ color:DS.amber,fontWeight:600 }}>Overvaluation risk: </span>{result.salary_intelligence.overvaluation_risk}</div>}</div>)}
          {tab==="roadmap90"&&(
            <div style={{ animation:"teFadeIn 0.25s ease" }}>
              {roadmapLoading&&<div style={{ padding:"24px",textAlign:"center" }}><div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:12,color:DS.purple }}><div style={{ width:14,height:14,border:"2px solid "+DS.border0,borderTopColor:DS.purple,borderRadius:"50%",animation:"teSpin 0.7s linear infinite" }}/><span style={{ fontSize:10,fontFamily:"'JetBrains Mono',monospace",letterSpacing:2 }}>GENERATING 90-DAY EXECUTION PLAN...</span></div></div>}
              {!roadmapLoading&&!roadmap&&<div style={{ padding:"20px",textAlign:"center",color:DS.t3,fontSize:12 }}>90-day roadmap will appear here after analysis completes.</div>}
              {roadmap&&[{key:"phase_1",color:DS.green},{key:"phase_2",color:DS.purple},{key:"phase_3",color:DS.blue}].map(function(ph){
                var phase=roadmap[ph.key];if(!phase)return null;
                return <div key={ph.key} style={{ marginBottom:16 }}>
                  <div style={{ fontSize:10,fontWeight:900,color:ph.color,letterSpacing:2,fontFamily:"'JetBrains Mono',monospace",marginBottom:10,padding:"8px 12px",background:ph.color+"11",border:"1px solid "+ph.color+"33",borderRadius:6 }}>{phase.title}</div>
                  {(phase.weeks||[]).map(function(week){
                    return <div key={week.week} style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:10,padding:"12px 14px",marginBottom:8 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:8 }}>
                        <div style={{ width:26,height:26,borderRadius:4,background:ph.color+"22",border:"1px solid "+ph.color+"44",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:ph.color,fontWeight:900,fontFamily:"'JetBrains Mono',monospace",flexShrink:0 }}>W{week.week}</div>
                        <div style={{ fontSize:11,fontWeight:700,color:DS.t1 }}>{week.focus}</div>
                      </div>
                      {(week.tasks||[]).map(function(task,ti){
                        return <div key={ti} style={{ display:"flex",gap:10,marginBottom:7,padding:"9px 11px",background:DS.bg0,border:"1px solid "+DS.border0,borderRadius:6 }}>
                          <div style={{ width:18,height:18,borderRadius:"50%",background:ph.color+"18",border:"1px solid "+ph.color+"33",color:ph.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:900,flexShrink:0 }}>{ti+1}</div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:12,color:DS.t1,fontWeight:600,marginBottom:2 }}>{task.task}</div>
                            {task.details&&<div style={{ fontSize:10,color:DS.t3,lineHeight:1.5,marginBottom:3 }}>{task.details}</div>}
                            <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginTop:3 }}>
                              {task.deliverable&&<span style={{ fontSize:9,color:DS.green,background:DS.green+"10",padding:"1px 7px",borderRadius:3,border:"1px solid "+DS.green+"25" }}>→ {task.deliverable}</span>}
                              {task.time_required&&<span style={{ fontSize:9,color:DS.t4,fontFamily:"'JetBrains Mono',monospace" }}>⏱ {task.time_required}</span>}
                              {task.difficulty&&<span style={{ fontSize:9,color:{low:DS.green,medium:DS.amber,high:DS.red}[task.difficulty]||DS.t3 }}>{task.difficulty.toUpperCase()}</span>}
                            </div>
                            {task.tools&&task.tools.length>0&&<div style={{ fontSize:9,color:DS.t4,marginTop:3 }}>Tools: {task.tools.join(", ")}</div>}
                          </div>
                        </div>;
                      })}
                    </div>;
                  })}
                </div>;
              })}
            </div>
          )}
          <CareerObituarySection result={result}/>
          <NegotiationScriptPanel
            role={result.growth_path&&result.growth_path.target_role}
            salaryRange={result.salary_intelligence&&result.salary_intelligence.potential_value_range}
            experienceLevel={result.candidate_profile&&result.candidate_profile.level}
          />
          </ExpandableSection>
          <AIChatAssistant context={result} contextType="resume"/>
          <P6ShareResultBlock result={result} type="career"/>
          <NextActionCard moduleType="career"/>
          <button onClick={function(){setResult(null);setResumeText("");setError(null);setProgress(0);setRoadmap(null);}} style={{ marginTop:16,fontSize:13,color:DS.t3,background:"transparent",border:"1px solid "+DS.border0,padding:"10px 20px",borderRadius:10,cursor:"pointer",transition:"all 0.2s" }} onMouseEnter={function(e){e.currentTarget.style.color=DS.t1;}} onMouseLeave={function(e){e.currentTarget.style.color=DS.t3;}}>← Analyse another resume</button>
        </div>
      )}
    </div>
  );
}


// ===================================================
// PATHFINDER SECTION
// ===================================================
function PathfinderSection() {
  var PC=DS.teal;
  var s0=useState("");var resumeText=s0[0];var setResumeText=s0[1];
  var s1=useState("");var goal=s1[0];var setGoal=s1[1];
  var s2=useState("");var situation=s2[0];var setSituation=s2[1];
  var s3=useState("");var constraints=s3[0];var setConstraints=s3[1];
  var s4=useState("mentor");var tone=s4[0];var setTone=s4[1];
  var s5=useState(false);var running=s5[0];var setRunning=s5[1];
  var s6=useState(null);var result=s6[0];var setResult=s6[1];
  var s7=useState(null);var error=s7[0];var setError=s7[1];
  var s8=useState(0);var stepIdx=s8[0];var setStepIdx=s8[1];
  var s9=useState(0);var progress=s9[0];var setProgress=s9[1];
  var s10=useState("overview");var activeTab=s10[0];var setActiveTab=s10[1];
  var s11=useState(0);var activePath=s11[0];var setActivePath=s11[1];
  var s12=useState(null);var uploadedFile=s12[0];var setUploadedFile=s12[1];
  var s13=useState(null);var uploadedParts=s13[0];var setUploadedParts=s13[1];
  var abortRef=useRef(null);var intRef=useRef(null);var fileRef=useRef(null);
  useEffect(function(){return function(){clearInterval(intRef.current);if(abortRef.current)abortRef.current.abort();};},[]);
  var PSTEPS=["Parsing resume...","Profiling skill depth...","Mapping market demand...","Computing unfair advantage...","Building Path 1: Primary...","Building Path 2: Growth...","Building Path 3: Alternative...","Running truth engine...","Generating decision matrix...","Finalising paths..."];
  async function handleFileUpload(e){
    var file=e.target.files&&e.target.files[0];if(!file)return;setError(null);
    try{var ext=(file.name||"").toLowerCase().split(".").pop();if(!["pdf","docx","txt","jpg","jpeg","png"].includes(ext)){setError("Supported: PDF, DOCX, TXT, JPG, PNG");return;}var text=await extractPDF(file);if(text&&text.length>20){setResumeText(text);setUploadedFile(file.name);}else setError("Could not extract text. Paste manually.");if(["jpg","jpeg","png"].includes(ext)){var ab=await file.arrayBuffer();var b64=btoa(String.fromCharCode.apply(null,new Uint8Array(ab)));setUploadedParts([{inline_data:{mime_type:file.type,data:b64}}]);}}catch(err){setError("File upload failed. Please paste resume text manually.");}if(fileRef.current)fileRef.current.value="";
  }
  async function run(){
    if(!resumeText.trim()||resumeText.trim().length<30){setError("Paste your resume text (min 30 chars).");return;}
    if(abortRef.current)abortRef.current.abort();abortRef.current=new AbortController();
    setError(null);setResult(null);setRunning(true);setStepIdx(0);setProgress(0);setActiveTab("overview");
    var s=0;intRef.current=setInterval(function(){s++;if(s<PSTEPS.length){setStepIdx(s);setProgress(Math.round((s/PSTEPS.length)*90));}else{clearInterval(intRef.current);}},700);
    try{
      var parsed=await withTimeout(runPathfinder(resumeText,goal,situation,constraints,tone,abortRef.current.signal,uploadedParts||[]),60000);
      if(!parsed||parsed.error||(!parsed.paths&&!parsed.user_analysis)){setError("Pathfinder analysis failed. Please retry.");clearInterval(intRef.current);setRunning(false);return;}
      CROSS_INTEL.store("pathfinder","last",parsed);try{s3Update("pathfinder",parsed);}catch(e){}try{updatePassport("pathfinder",parsed);}catch(ppe){}
      clearInterval(intRef.current);setProgress(100);
      setTimeout(function(){setResult(parsed);setRunning(false);try{if(navigator.vibrate)navigator.vibrate([10,50,10]);}catch(e){}},300);
    }catch(e){clearInterval(intRef.current);setRunning(false);if(e&&e.name==="AbortError")return;setError((e&&e.message)||"Analysis failed. Please retry.");}
  }
  var PATH_CFG={primary:{color:PC,bg:PC+"08",border:PC+"25",icon:"🎯",label:"Primary"},growth:{color:DS.blue,bg:DS.blue+"08",border:DS.blue+"25",icon:"📈",label:"Growth"},alternative:{color:DS.amber,bg:DS.amber+"08",border:DS.amber+"25",icon:"🔀",label:"Alternative"}};
  var paths=result&&result.paths&&result.paths.length>0?result.paths:null;
  var ua=result&&result.user_analysis;
  var fm=result&&result.final_recommendation;
  var dm=result&&result.decision_matrix;
  var TABS=[{id:"overview",l:"Profile"},{id:"paths",l:"3 Paths"},{id:"matrix",l:"Decision Matrix"}];
  return (
    <div style={{ maxWidth:900,margin:"0 auto" }}>
      {!result&&!running&&(
        <div>
          <div style={{ background:DS.bg1,border:"1px solid "+PC+"25",borderRadius:16,padding:"20px",marginBottom:14 }}>
            <div style={{ fontSize:13,fontWeight:700,color:DS.t1,marginBottom:14 }}>Your resume <span style={{ fontSize:11,color:DS.t3,fontWeight:400 }}>(required)</span></div>
            <textarea value={resumeText} onChange={function(e){setResumeText(e.target.value);setError(null);}} placeholder={"Paste your full resume here...\n\nThe more detail, the more precise your 3 career paths."} rows={7} style={{ width:"100%",background:DS.bg0,border:"1px solid "+DS.border1,borderRadius:12,padding:"14px",color:DS.t1,fontSize:13,lineHeight:1.8,resize:"vertical",outline:"none",caretColor:PC,boxSizing:"border-box" }}/>
            <div style={{ marginTop:10,display:"flex",gap:8,flexWrap:"wrap" }}>
              <label style={{ display:"inline-flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:PC,padding:"8px 14px",background:PC+"08",border:"1px solid "+PC+"22",borderRadius:10 }}>📎 Upload Resume<input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.txt" onChange={handleFileUpload} style={{ display:"none" }}/></label>
              {uploadedFile&&<span style={{ fontSize:12,color:DS.green,padding:"8px 12px",background:DS.green+"08",border:"1px solid "+DS.green+"20",borderRadius:10 }}>✓ {uploadedFile}</span>}
            </div>
          </div>
          <div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:16,padding:"20px",marginBottom:14 }}>
            <div style={{ fontSize:12,fontWeight:600,color:DS.t2,marginBottom:14 }}>Optional context (improves accuracy)</div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
              <div><div style={{ fontSize:11,color:DS.t3,marginBottom:6 }}>Your goal</div><input value={goal} onChange={function(e){setGoal(e.target.value);}} placeholder="e.g. Get into product management..." style={{ width:"100%",background:DS.bg0,border:"1px solid "+DS.border1,borderRadius:10,padding:"10px 12px",color:DS.t1,fontSize:12,outline:"none",boxSizing:"border-box" }}/></div>
              <div><div style={{ fontSize:11,color:DS.t3,marginBottom:6 }}>Current situation</div><input value={situation} onChange={function(e){setSituation(e.target.value);}} placeholder="e.g. Employed, 3 YOE, Bangalore..." style={{ width:"100%",background:DS.bg0,border:"1px solid "+DS.border1,borderRadius:10,padding:"10px 12px",color:DS.t1,fontSize:12,outline:"none",boxSizing:"border-box" }}/></div>
              <div><div style={{ fontSize:11,color:DS.t3,marginBottom:6 }}>Constraints</div><input value={constraints} onChange={function(e){setConstraints(e.target.value);}} placeholder="e.g. Can't relocate, 10h/week..." style={{ width:"100%",background:DS.bg0,border:"1px solid "+DS.border1,borderRadius:10,padding:"10px 12px",color:DS.t1,fontSize:12,outline:"none",boxSizing:"border-box" }}/></div>
              <div><div style={{ fontSize:11,color:DS.t3,marginBottom:6 }}>Tone</div><select value={tone} onChange={function(e){setTone(e.target.value);}} style={{ width:"100%",background:DS.bg0,border:"1px solid "+DS.border1,borderRadius:10,padding:"10px 12px",color:DS.t1,fontSize:12,outline:"none" }}><option value="mentor">Mentor (supportive)</option><option value="brutal">Brutal honesty</option><option value="strategic">Strategic advisor</option></select></div>
            </div>
          </div>
          {error&&<div style={{ background:"rgba(244,63,94,0.08)",border:"1px solid rgba(244,63,94,0.2)",borderRadius:12,padding:"12px 16px",marginBottom:14,fontSize:13,color:DS.red }}>⚠ {error}</div>}
          <button onClick={run} style={{ width:"100%",padding:"14px",background:PC+"15",border:"1px solid "+PC+"40",borderRadius:14,color:PC,fontSize:15,fontWeight:800,cursor:"pointer",transition:"all 0.2s",letterSpacing:0.3 }}>🧭 Find My 3 Career Paths</button>
        </div>
      )}
      {running&&<Thinking steps={PSTEPS} currentStep={stepIdx} progress={progress} color={PC} title="Computing your career paths..."/>}
      {result&&!running&&(
        <div style={{ animation:"teFadeIn 0.35s ease" }}>
          <DecisionBlock type="pathfinder" data={result}/>
          <GlobalIntelligencePanel result={result} type="pathfinder"/>
          <ResultSummary type="pathfinder" data={result}/>
          <div style={{ display:"flex",borderBottom:"1px solid "+DS.border0,overflowX:"auto",marginBottom:20,background:DS.bg1,borderRadius:"14px 14px 0 0",overflow:"hidden" }}>
            {TABS.map(function(t){var active=activeTab===t.id;return <button key={t.id} onClick={function(){setActiveTab(t.id);}} style={{ padding:"13px 20px",background:active?PC+"12":"transparent",border:"none",borderBottom:"2px solid "+(active?PC:"transparent"),color:active?PC:DS.t3,fontSize:13,fontWeight:active?700:500,cursor:"pointer",whiteSpace:"nowrap",transition:"all 0.2s" }}>{t.l}</button>;})}
          </div>
          {activeTab==="overview"&&ua&&(
            <div style={{ animation:"teFadeIn 0.25s ease",display:"flex",flexDirection:"column",gap:14 }}>
              <div style={{ background:PC+"08",border:"1px solid "+PC+"25",borderRadius:16,padding:"18px" }}>
                <div style={{ display:"flex",gap:16,alignItems:"flex-start",flexWrap:"wrap" }}>
                  <ScoreRing value={ua.readiness_score||0} color={PC} size={68}/>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex",gap:10,flexWrap:"wrap",marginBottom:8 }}><Pill text={(ua.experience_level||"").toUpperCase()} color={PC}/><Pill text={"Market: "+(ua.market_position||"").toUpperCase()} color={DS.blue}/></div>
                    <div style={{ fontSize:14,color:DS.t2,lineHeight:1.7 }}>{ua.profile_summary}</div>
                  </div>
                </div>
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
                <div style={{ background:DS.bg1,border:"1px solid rgba(244,63,94,0.2)",borderRadius:14,padding:"14px 16px" }}><div style={{ fontSize:11,fontWeight:700,color:DS.red,marginBottom:10 }}>Truth Engine</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.7 }}>{ua.truth_analysis}</div></div>
                <div style={{ background:DS.bg1,border:"1px solid "+PC+"25",borderRadius:14,padding:"14px 16px" }}><div style={{ fontSize:11,fontWeight:700,color:PC,marginBottom:10 }}>Your Unfair Advantage</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.7 }}>{ua.unfair_advantage}</div></div>
              </div>
              {ua.stop_doing&&ua.stop_doing.length>0&&(<div style={{ background:"rgba(244,63,94,0.05)",border:"1px solid rgba(244,63,94,0.15)",borderRadius:14,padding:"14px 16px" }}><div style={{ fontSize:12,fontWeight:700,color:DS.red,marginBottom:12 }}>⚑ Stop doing immediately</div>{ua.stop_doing.map(function(s,i){return (<div key={i} style={{ display:"flex",gap:10,marginBottom:8,padding:"8px 10px",background:"rgba(244,63,94,0.05)",borderRadius:8,border:"1px solid rgba(244,63,94,0.1)" }}><span style={{ color:DS.red,flexShrink:0,fontSize:11,fontWeight:700 }}>{i+1}.</span><span style={{ fontSize:13,color:DS.t2,lineHeight:1.6 }}>{s}</span></div>);})}  </div>)}
              {fm&&<div style={{ background:PC+"08",border:"1px solid "+PC+"25",borderRadius:14,padding:"14px 16px" }}><div style={{ fontSize:11,fontWeight:700,color:PC,marginBottom:6 }}>Recommended Path</div><div style={{ fontSize:15,fontWeight:800,color:PC,marginBottom:6 }}>{(fm.recommended_path||"").toUpperCase()}</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.7 }}>{fm.reason}</div></div>}
              {result.immediate_next_steps&&result.immediate_next_steps.length>0&&(<div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,padding:"14px 16px" }}><div style={{ fontSize:12,fontWeight:700,color:PC,marginBottom:12 }}>Do this today</div>{result.immediate_next_steps.map(function(s,i){return (<div key={i} style={{ display:"flex",gap:12,marginBottom:10,alignItems:"flex-start" }}><div style={{ width:24,height:24,borderRadius:"50%",background:PC+"15",border:"1px solid "+PC+"25",color:PC,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0 }}>{i+1}</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.65,paddingTop:2 }}>{s}</div></div>);})}  </div>)}
              <PathfinderWeek1Plan result={result}/>
            </div>
          )}
          {activeTab==="paths"&&paths&&(
            <div style={{ animation:"teFadeIn 0.25s ease" }}>
              <PathCollisionDetector paths={paths}/>
              <div style={{ display:"flex",gap:10,marginBottom:16,flexWrap:"wrap" }}>
                {paths.map(function(p,i){var cfg=PATH_CFG[p.path_type]||PATH_CFG.primary;var isRec=fm&&p.path_type===fm.recommended_path;var isActive=activePath===i;return(<button key={i} onClick={function(){try{if(navigator.vibrate)navigator.vibrate(10);}catch(e){}setActivePath(i);}} style={{ flex:1,padding:"14px",borderRadius:14,position:"relative",border:"1px solid "+(isActive?cfg.color+"60":isRec?"rgba(56,189,248,0.5)":DS.border0),background:isActive?cfg.bg:isRec?"rgba(56,189,248,0.06)":"transparent",color:isActive?cfg.color:isRec?"#38BDF8":DS.t3,fontSize:12,fontWeight:isActive?700:500,cursor:"pointer",transition:"all 0.25s",minWidth:100,boxShadow:isActive?"0 0 20px "+cfg.color+"20":"none" }}>{isRec&&<div style={{ position:"absolute",top:-9,right:6,background:"linear-gradient(90deg,#38BDF8,#8B5CF6)",padding:"3px 8px",fontSize:9,fontWeight:800,borderRadius:6,color:"#fff",letterSpacing:1,whiteSpace:"nowrap" }}>★ RECOMMENDED</div>}<div style={{ fontSize:18,marginBottom:5 }}>{cfg.icon}</div><div style={{ fontSize:13,marginBottom:3,fontWeight:700 }}>{cfg.label}</div><div style={{ fontSize:11 }}>{p.readiness_score||0}/100</div></button>);})}
              </div>
              {paths[activePath]&&(function(){
                var p=paths[activePath];var cfg=PATH_CFG[p.path_type]||PATH_CFG.primary;
                return (<div style={{ display:"flex",flexDirection:"column",gap:12 }}>
                  <div style={{ background:cfg.bg,border:"1px solid "+cfg.border,borderRadius:16,padding:"18px",display:"flex",gap:14,alignItems:"flex-start",flexWrap:"wrap" }}><ScoreRing value={p.readiness_score||0} color={cfg.color} size={60}/><div style={{ flex:1,minWidth:180 }}><div style={{ fontSize:17,fontWeight:800,color:DS.t1,marginBottom:4 }}>{p.title}</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.65,marginBottom:12 }}>{p.fit_reason}</div><div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>{[["Demand",p.market_demand],["Difficulty",p.difficulty],["Risk",p.risk]].map(function(x){return x[1]?<Pill key={x[0]} text={x[0]+": "+x[1]} color={cfg.color}/>:null;})}</div></div></div>
                  <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
                    <div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:12,padding:"14px" }}><div style={{ fontSize:10,color:DS.green,marginBottom:6 }}>💰 SALARY RANGE</div><div style={{ fontSize:14,fontWeight:700,color:DS.green }}>{p.salary&&p.salary.entry}</div><div style={{ fontSize:11,color:DS.t3,marginTop:4 }}>Growth: {p.salary&&p.salary.growth}</div></div>
                    <div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:12,padding:"14px" }}><div style={{ fontSize:10,color:DS.blue,marginBottom:6 }}>⏱ TIMELINE</div><div style={{ fontSize:14,fontWeight:700,color:DS.blue }}>{p.timeline&&p.timeline.entry_level}</div><div style={{ fontSize:11,color:DS.t3,marginTop:4 }}>Senior: {p.timeline&&p.timeline.growth_level}</div></div>
                  </div>
                  {p.skill_gap&&p.skill_gap.length>0&&(<div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:12,padding:"14px" }}><div style={{ fontSize:11,fontWeight:700,color:DS.amber,marginBottom:10 }}>Skill Gaps</div><div style={{ display:"flex",flexWrap:"wrap",gap:8 }}>{p.skill_gap.map(function(sk,i){return <Pill key={i} text={sk} color={DS.amber}/>;})}</div></div>)}
                  {p.failure_points&&p.failure_points.length>0&&(<div style={{ background:"rgba(244,63,94,0.05)",border:"1px solid rgba(244,63,94,0.15)",borderRadius:12,padding:"14px" }}><div style={{ fontSize:11,fontWeight:700,color:DS.red,marginBottom:10 }}>⚑ Failure Points</div>{p.failure_points.map(function(f,i){return <div key={i} style={{ display:"flex",gap:8,marginBottom:6,fontSize:13,color:DS.t2 }}><span style={{ color:DS.red }}>→</span>{f}</div>;})}</div>)}
                  <div style={{ background:cfg.bg,border:"1px solid "+cfg.border,borderRadius:12,padding:"14px" }}><div style={{ fontSize:11,fontWeight:700,color:cfg.color,marginBottom:8 }}>Outcome</div><div style={{ fontSize:13,color:DS.t1,marginBottom:12,lineHeight:1.6 }}>{p.outcome}</div><div style={{ fontSize:11,color:DS.t3,marginBottom:5 }}>Final Advice</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.65 }}>{p.final_advice}</div></div>
                  <button onClick={function(){downloadPathfinderPDF(result);}} style={{ alignSelf:"flex-start",fontSize:12,color:cfg.color,background:cfg.bg,border:"1px solid "+cfg.border,padding:"10px 20px",borderRadius:10,cursor:"pointer",fontWeight:600 }}>⬇ Download Full Report (PDF)</button>
                </div>);
              })()}
            </div>
          )}
          {activeTab==="matrix"&&dm&&(<div style={{ display:"flex",flexDirection:"column",gap:12,animation:"teFadeIn 0.25s ease" }}>{[["Primary vs Growth",dm.primary_vs_growth],["Primary vs Alternative",dm.primary_vs_alternative],["Growth vs Alternative",dm.growth_vs_alternative]].map(function(item){return item[1]?(<div key={item[0]} style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,padding:"14px 16px" }}><div style={{ fontSize:11,fontWeight:700,color:PC,marginBottom:8 }}>{item[0]}</div><div style={{ fontSize:13,color:DS.t2,lineHeight:1.75 }}>{item[1]}</div></div>):null;})}</div>)}
          <NextActionCard moduleType="pathfinder"/>
          <P6ShareResultBlock result={result} type="pathfinder"/>
          <P6NextActionBlock type="pathfinder"/>
          <button onClick={function(){setResult(null);setResumeText("");setGoal("");setSituation("");setConstraints("");setError(null);setProgress(0);setUploadedFile(null);setUploadedParts(null);}} style={{ marginTop:20,fontSize:13,color:DS.t3,background:"transparent",border:"1px solid "+DS.border0,padding:"10px 20px",borderRadius:10,cursor:"pointer" }} onMouseEnter={function(e){e.currentTarget.style.color=DS.t1;}} onMouseLeave={function(e){e.currentTarget.style.color=DS.t3;}}>← Run again with different resume</button>
        </div>
      )}
    </div>
  );
}

// ===================================================
// ONBOARDING OVERLAY
// ===================================================
function OnboardingOverlay({ onComplete }) {
  var sp=useState({experience_level:"mid",domain:"general",goal:"job_search"});var prof=sp[0];var setProf=sp[1];
  function save(){try{saveUserProfile(Object.assign({},prof,{setup:true}));}catch(e){}try{if(typeof localStorage!=="undefined")localStorage.setItem("te_onboarded","1");}catch(e){}onComplete();}
  return (
    <div style={{ position:"fixed",inset:0,zIndex:10000,background:"rgba(1,8,18,0.97)",backdropFilter:"blur(16px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
      <div style={{ background:DS.bg1,border:"1px solid "+DS.border1,borderRadius:24,padding:"36px 32px",maxWidth:460,width:"100%" }}>
        <div style={{ width:48,height:48,background:DS.goldDim,border:"1px solid "+DS.goldBorder,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:16 }}>
          <Icon name="shield" size={24} color={DS.gold}/>
        </div>
        <div style={{ fontSize:22,fontWeight:900,color:DS.t1,marginBottom:8 }}>Welcome to Trust Engine</div>
        <div style={{ fontSize:14,color:DS.t2,lineHeight:1.7,marginBottom:28 }}>India's most advanced AI job intelligence platform. Tell us about yourself for personalised analysis.</div>
        <div style={{ display:"flex",flexDirection:"column",gap:16,marginBottom:28 }}>
          <div>
            <div style={{ fontSize:12,color:DS.t3,marginBottom:8 }}>Experience level</div>
            <div style={{ display:"flex",gap:8 }}>{[{v:"fresher",l:"Fresher"},{v:"mid",l:"Mid-level"},{v:"senior",l:"Senior"}].map(function(o){return <button key={o.v} onClick={function(){setProf(function(p){return Object.assign({},p,{experience_level:o.v});});}} style={{ flex:1,padding:"10px",borderRadius:10,border:"1px solid "+(prof.experience_level===o.v?DS.blue+"60":DS.border1),background:prof.experience_level===o.v?DS.blue+"12":"transparent",color:prof.experience_level===o.v?DS.blue:DS.t2,fontSize:13,fontWeight:prof.experience_level===o.v?700:500,cursor:"pointer",transition:"all 0.18s" }}>{o.l}</button>;})}</div>
          </div>
          <div>
            <div style={{ fontSize:12,color:DS.t3,marginBottom:8 }}>Your goal</div>
            <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>{[{v:"job_search",l:"Find a job"},{v:"switch",l:"Switch careers"},{v:"growth",l:"Get promoted"}].map(function(o){return <button key={o.v} onClick={function(){setProf(function(p){return Object.assign({},p,{goal:o.v});});}} style={{ flex:1,minWidth:100,padding:"10px",borderRadius:10,border:"1px solid "+(prof.goal===o.v?DS.teal+"60":DS.border1),background:prof.goal===o.v?DS.teal+"12":"transparent",color:prof.goal===o.v?DS.teal:DS.t2,fontSize:13,fontWeight:prof.goal===o.v?700:500,cursor:"pointer",transition:"all 0.18s" }}>{o.l}</button>;})}</div>
          </div>
        </div>
        <button onClick={save} style={{ width:"100%",padding:"14px",background:"linear-gradient(135deg,"+DS.blue+","+DS.teal+")",border:"none",borderRadius:14,color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer" }}>Get Started →</button>
        <button onClick={onComplete} style={{ width:"100%",padding:"10px",marginTop:10,background:"transparent",border:"none",color:DS.t3,fontSize:13,cursor:"pointer" }}>Skip for now</button>
      </div>
    </div>
  );
}

// ===================================================
// MOBILE BOTTOM NAV
// ===================================================
function MobileBottomNav({ mode, setMode, setShowH, setShowR, setResult }) {
  var TABS=[{id:"scan",icon:"🛡",label:"Scan"},{id:"resume",icon:"📄",label:"Resume"},{id:"career",icon:"🚀",label:"Career"},{id:"pathfinder",icon:"🧭",label:"Paths"},{id:"hr",icon:"🎯",label:"HR"}];
  function haptic(){try{if(navigator.vibrate)navigator.vibrate(10);}catch(e){}}
  return (
    <nav className="te-bottom-nav" style={{ position:"fixed",bottom:0,left:0,right:0,zIndex:300,background:"rgba(5,14,30,0.97)",backdropFilter:"blur(24px)",WebkitBackdropFilter:"blur(24px)",borderTop:"1px solid rgba(255,255,255,0.06)",padding:"6px 0 env(safe-area-inset-bottom,6px)",display:"none" }}>
      <div style={{ display:"flex",justifyContent:"space-around",alignItems:"center",maxWidth:480,margin:"0 auto" }}>
        {TABS.map(function(t){var active=mode===t.id;return(<button key={t.id} onClick={function(){haptic();setMode(t.id);setShowH(false);setShowR(false);setResult(null);}} style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"5px 2px",background:"none",border:"none",cursor:"pointer",opacity:active?1:0.45,flex:1,minWidth:0,transition:"all 0.2s" }}><div style={{ display:"flex",alignItems:"center",justifyContent:"center",transform:active?"scale(1.12)":"scale(1)",transition:"all 0.2s",width:20,height:20 }}><Icon name={t.id==="home"?"shield":t.id==="scan"?"scan":t.id==="resume"?"resume":t.id==="pathfinder"?"path":"jd"} size={18} color={active?DS.blue:DS.t4}/></div><div style={{ fontSize:9,fontFamily:"'JetBrains Mono',monospace",letterSpacing:0.3,color:active?DS.blue:DS.t3,fontWeight:active?700:400,transition:"all 0.2s",whiteSpace:"nowrap" }}>{t.label}</div>{active&&<div style={{ width:3,height:3,borderRadius:"50%",background:DS.blue,boxShadow:"0 0 6px "+DS.blue }}/>}</button>);})}
      </div>
    </nav>
  );
}

// ===================================================
// ERROR BOUNDARY
// ===================================================
class ErrorBoundary extends React.Component {
  constructor(props){super(props);this.state={hasError:false,errorMsg:""};this.reset=this.reset.bind(this);}
  static getDerivedStateFromError(error){return{hasError:true,errorMsg:error&&error.message?error.message:"Unknown error"};}
  componentDidCatch(error,info){try{console.error("[ErrorBoundary]",error&&error.message);}catch(e){}}
  reset(){this.setState({hasError:false,errorMsg:""});}
  render(){
    if(this.state.hasError){return(<div style={{ minHeight:"100vh",background:DS.bg0,display:"flex",alignItems:"center",justifyContent:"center",padding:24 }}><div style={{ maxWidth:480,textAlign:"center" }}><div style={{ fontSize:48,marginBottom:16 }}>⚠️</div><div style={{ fontSize:18,fontWeight:700,color:DS.red,marginBottom:10 }}>Something went wrong</div><div style={{ fontSize:13,color:DS.t2,marginBottom:16,lineHeight:1.7 }}>An unexpected error occurred. This is usually a temporary AI parsing issue.</div>{this.state.errorMsg&&<div style={{ fontSize:11,color:DS.t3,marginBottom:20,fontFamily:"'JetBrains Mono',monospace",background:DS.bg1,padding:"8px 12px",borderRadius:8,border:"1px solid "+DS.border0 }}>{this.state.errorMsg.slice(0,120)}</div>}<div style={{ display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap" }}><button onClick={this.reset} style={{ padding:"10px 24px",background:"rgba(56,189,248,0.1)",border:"1px solid rgba(56,189,248,0.3)",borderRadius:10,color:"#38BDF8",fontSize:13,fontWeight:700,cursor:"pointer" }}>↺ Try Again</button><button onClick={function(){window.location.reload();}} style={{ padding:"10px 24px",background:"rgba(244,63,94,0.08)",border:"1px solid rgba(244,63,94,0.25)",borderRadius:10,color:DS.red,fontSize:13,fontWeight:700,cursor:"pointer" }}>🔄 Refresh</button></div></div></div>);}
    return this.props.children;
  }
}





// ===================================================
// PHASE 6 — DECISION UX + PRODUCT EXPERIENCE LAYER
// ===================================================

// ── DecisionBlock ─────────────────────────────────
function DecisionBlock({ type, data, onActionClick }) {
  var action = useMemo(function() { return getPrimaryAction(type, data); }, [type, data]);
  var sv = useState(false); var show = sv[0]; var setShow = sv[1];
  useEffect(function() { var t = setTimeout(function() { setShow(true); }, 40); return function() { clearTimeout(t); }; }, []);
  if (!action) return null;

  var verdictText = "";
  var verdictSub = "";
  if (type === "scan") {
    var score = (data && data.risk_score) || 0;
    verdictText = score >= 70 ? "This job is likely a scam" : score >= 40 ? "This job looks suspicious" : "This job looks legitimate";
    verdictSub = score >= 70 ? "Do not apply. Do not pay any fees." : score >= 40 ? "Verify the company before proceeding." : "Still confirm the company before applying.";
  } else if (type === "resume") {
    var ats2 = (data && data.ats_score) || 0;
    verdictText = ats2 >= 75 ? "Your resume is strong" : ats2 >= 55 ? "Your resume needs improvement" : "Your resume has critical gaps";
    verdictSub = "ATS Score: " + ats2 + "/100" + (data && data.candidate_level ? " · " + data.candidate_level : "");
  } else if (type === "career") {
    verdictText = "Career analysis complete";
    verdictSub = (data && data.candidate_profile && data.candidate_profile.level) || "Review your intelligence report below";
  } else if (type === "pathfinder") {
    var rec = data && data.final_recommendation && data.final_recommendation.recommended_path;
    verdictText = "Your career paths are ready";
    verdictSub = rec ? "Recommended: " + rec.toUpperCase() + " path" : "3 paths identified — review below";
  } else if (type === "jd") {
    verdictText = (data && data.fit_verdict) ? data.fit_verdict + " for your profile" : "Job description decoded";
    verdictSub = (data && data.role_reality) ? data.role_reality.slice(0, 80) + "..." : "Review full analysis below";
  }

  // Extract 3 key signals
  var signals = [];
  try {
    if (type === "scan" && data) {
      if (data.red_flags && data.red_flags.length > 0) signals.push(data.red_flags[0]);
      if (data.manipulation_signals && data.manipulation_signals.length > 0) signals.push(data.manipulation_signals[0]);
      if (data.final_reasoning) signals.push(data.final_reasoning.slice(0, 80) + (data.final_reasoning.length > 80 ? "…" : ""));
    } else if (type === "resume" && data) {
      if (data.ats_issues && data.ats_issues.length > 0) signals.push(data.ats_issues[0]);
      if (data.skills_missing && data.skills_missing.length > 0) signals.push("Missing: " + data.skills_missing[0].skill);
      if (data.quick_wins && data.quick_wins.length > 0) signals.push("Quick win: " + data.quick_wins[0]);
    } else if (type === "jd" && data) {
      if (data.red_flags && data.red_flags.length > 0) signals.push(data.red_flags[0]);
      if (data.hidden_requirements && data.hidden_requirements.length > 0) signals.push("Hidden: " + data.hidden_requirements[0]);
      if (data.negotiation_tips && data.negotiation_tips.length > 0) signals.push(data.negotiation_tips[0]);
    } else if (type === "career" && data) {
      if (data.rejection_analysis && data.rejection_analysis.top_reasons && data.rejection_analysis.top_reasons.length > 0) signals.push(data.rejection_analysis.top_reasons[0]);
      if (data.skills_gap && data.skills_gap.length > 0) signals.push("Gap: " + data.skills_gap[0].skill_name);
      if (data.candidate_profile && data.candidate_profile.employability_risk) signals.push(data.candidate_profile.employability_risk.slice(0, 70));
    } else if (type === "pathfinder" && data) {
      if (data.user_analysis && data.user_analysis.truth_analysis) signals.push(data.user_analysis.truth_analysis.slice(0, 80));
      if (data.user_analysis && data.user_analysis.unfair_advantage) signals.push("Advantage: " + data.user_analysis.unfair_advantage.slice(0, 60));
      if (data.immediate_next_steps && data.immediate_next_steps.length > 0) signals.push("Do now: " + data.immediate_next_steps[0]);
    }
  } catch(e) {}
  signals = signals.filter(Boolean).slice(0, 3);

  return (
    <div style={{ opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(-8px)", transition: "all 0.5s cubic-bezier(0.22,1,0.36,1)", marginBottom: 20 }}>
      {/* LEVEL 1 — VERDICT: dominant, large, no decoration noise */}
      <div style={{ padding: "28px 24px 20px", background: action.color + "06", border: "1px solid " + action.color + "18", borderRadius: "18px 18px 0 0", borderBottom: "none" }}>
        <div style={{ fontSize: "clamp(26px,5vw,36px)", fontWeight: 900, color: action.color, lineHeight: 1.1, letterSpacing: "-0.5px", marginBottom: 6 }}>{verdictText}</div>
        <div style={{ fontSize: 14, color: DS.t3, lineHeight: 1.5, fontWeight: 400 }}>{verdictSub}</div>
      </div>
      {/* LEVEL 3 — SIGNALS: muted, no color dominance */}
      {signals.length > 0 && (
        <div style={{ background: "rgba(5,14,30,0.95)", borderLeft: "1px solid " + action.color + "15", borderRight: "1px solid " + action.color + "15", padding: "12px 24px", display: "flex", flexDirection: "column", gap: 7 }}>
          {signals.map(function(s, i) {
            return (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: DS.t4, flexShrink: 0, marginTop: 7 }} />
                <span style={{ fontSize: 12, color: DS.t3, lineHeight: 1.6 }}>{s}</span>
              </div>
            );
          })}
        </div>
      )}
      {/* LEVEL 2 — ACTION: solid fill, commands the eye */}
      <div style={{ padding: "16px 24px 22px", background: "rgba(5,14,30,0.95)", border: "1px solid " + action.color + "18", borderTop: "1px solid " + action.color + "10", borderRadius: "0 0 18px 18px" }}>
        <button
          onClick={function() {
            try { if (action.fn) action.fn(); } catch(e) {}
            try { if (onActionClick) onActionClick(action.scrollTo); } catch(e) {}
          }}
          style={{ width: "100%", minHeight: 52, padding: "14px 20px", background: action.color, border: "none", borderRadius: 12, color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", transition: "all 0.15s", letterSpacing: 0.3, boxShadow: "0 8px 32px " + action.color + "40", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          onMouseEnter={function(e) { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={function(e) { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}>
          {action.label}
        </button>
      </div>
    </div>
  );
}

// ── ExpandableSection ─────────────────────────────
function ExpandableSection({ title, children, defaultOpen, accentColor }) {
  var sv = useState(!!defaultOpen); var open = sv[0]; var setOpen = sv[1];
  var c = accentColor || DS.t4;
  return (
    <div style={{ marginBottom: 16 }}>
      <button onClick={function() { setOpen(function(v) { return !v; }); }}
        style={{ width: "100%", padding: "14px 20px", background: open ? c + "08" : "transparent", border: "1px solid " + (open ? c + "28" : DS.border0), borderRadius: open ? "12px 12px 0 0" : 12, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 10, transition: "all 0.25s var(--ease-out)" }}
        onMouseEnter={function(e) { e.currentTarget.style.borderColor = c + "35"; e.currentTarget.style.background = c + "08"; }}
        onMouseLeave={function(e) { e.currentTarget.style.borderColor = open ? c + "28" : DS.border0; e.currentTarget.style.background = open ? c + "08" : "transparent"; }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 14, height: 2, background: open ? c : DS.t4, borderRadius: 1, transition: "background 0.22s ease" }}/>
          <span style={{ fontSize: 11, fontWeight: 600, color: open ? c : DS.t3, letterSpacing: "0.12em", fontFamily: "'JetBrains Mono',monospace", transition: "color 0.22s ease", textTransform: "uppercase" }}>{title}</span>
        </div>
        <div style={{ width: 22, height: 22, borderRadius: "50%", background: DS.bg3, display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.32s var(--ease-out)", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}>
          <Icon name="chevron" size={14} color={DS.t4}/>
        </div>
      </button>
      <div className="te-expandable-content" style={{ maxHeight: open ? "9999px" : "0px", opacity: open ? 1 : 0, overflow: "hidden", transition: "max-height 0.42s var(--ease-inout), opacity 0.28s ease", border: open ? "1px solid " + c + "18" : "none", borderTop: "none", borderRadius: "0 0 12px 12px" }}>
        {children}
      </div>
    </div>
  );
}

// ── MobileResultBar ───────────────────────────────
function MobileResultBar({ result, type, onNewScan, onShare }) {
  if (!result) return null;
  function doShare() {
    try {
      var text = type === "scan"
        ? "I checked a job post using Trust Engine — Risk: " + (result.risk_score || 0) + "/100. Verdict: " + (result.verdict || "Unknown") + "\ntrust-engine-ashen.vercel.app"
        : "I analyzed my resume — ATS: " + (result.ats_score || 0) + "/100\ntrust-engine-ashen.vercel.app";
      if (navigator.share) { navigator.share({ title: "Trust Engine", text: text }); }
      else { navigator.clipboard && navigator.clipboard.writeText(text).catch(function() {}); }
    } catch(e) {}
  }
  return (
    <div style={{ position: "fixed", bottom: 60, left: 0, right: 0, zIndex: 190, padding: "8px 16px", background: "rgba(1,8,18,0.96)", backdropFilter: "blur(16px)", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", gap: 8 }}>
      {onNewScan && (
        <button onClick={onNewScan} style={{ flex: 1, minHeight: 44, padding: "10px", background: DS.bg2, border: "1px solid " + DS.border1, borderRadius: 10, color: DS.t2, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          🔍 New Scan
        </button>
      )}
      <button onClick={doShare} style={{ flex: 1, minHeight: 44, padding: "10px", background: DS.blue + "12", border: "1px solid " + DS.blue + "30", borderRadius: 10, color: DS.blue, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
        📤 Share
      </button>
    </div>
  );
}
// ===================================================
// END PHASE 6 COMPONENTS
// ===================================================


// ===================================================
// PHASE 6 — DECISION UX + PRODUCT EXPERIENCE LAYER
// ===================================================

// ── computeIntelligenceScore ──────────────────────────
// Combines available signals into a single 0-100 score.
// Higher = user is in a stronger, safer career position.
function computeIntelligenceScore(result, type) {
  try {
    var m = s3Get();
    var scores = [];
    // Live result (freshest signal, double weight)
    if (type === "scan" && result && result.risk_score != null) {
      scores.push({ v: Math.max(0, 100 - Number(result.risk_score)), w: 2 });
    }
    if (type === "resume" && result && result.ats_score != null) {
      scores.push({ v: Number(result.ats_score), w: 2 });
    }
    if (type === "career" && result && result.scores && result.scores.ats_score != null) {
      scores.push({ v: Number(result.scores.ats_score), w: 2 });
    }
    if (type === "pathfinder" && result && result.user_analysis && result.user_analysis.readiness_score != null) {
      scores.push({ v: Number(result.user_analysis.readiness_score), w: 2 });
    }
    if (type === "jd" && result && result.match_score != null) {
      scores.push({ v: Number(result.match_score), w: 2 });
    }
    // Cross-module history (lower weight)
    if (m.risk_score != null && type !== "scan") {
      scores.push({ v: Math.max(0, 100 - Number(m.risk_score)), w: 1 });
    }
    if (m.ats_score != null && type !== "resume" && type !== "career") {
      scores.push({ v: Number(m.ats_score), w: 1 });
    }
    if (m.pathfinder && m.pathfinder.readiness != null && type !== "pathfinder") {
      scores.push({ v: Number(m.pathfinder.readiness), w: 1 });
    }
    if (scores.length === 0) return null;
    var totalW = scores.reduce(function(a, s) { return a + s.w; }, 0);
    var totalV = scores.reduce(function(a, s) { return a + s.v * s.w; }, 0);
    return Math.round(Math.min(100, Math.max(0, totalV / totalW)));
  } catch(e) { return null; }
}

// ── GlobalIntelligencePanel ───────────────────────────
// Surfaces the hidden s3/s5 intelligence layer in one compact block.
// Level 2 importance: sits between DecisionBlock and ExpandableSection.
function GlobalIntelligencePanel({ result, type }) {
  var sa = useState(false); var show = sa[0]; var setShow = sa[1];
  useEffect(function() {
    var t = setTimeout(function() { setShow(true); }, 120);
    return function() { clearTimeout(t); };
  }, []);

  var intel = useMemo(function() {
    try {
      var m     = s3Get();
      var pred  = s5PredictOutcome(m);
      var traj  = s3GetTrajectory();
      var learn = s5GetLearningAdjustment();
      var score = computeIntelligenceScore(result, type);
      return { pred: pred, traj: traj, learn: learn, score: score };
    } catch(e) {
      return { pred: null, traj: null, learn: null, score: null };
    }
  }, [result, type]);

  var pred  = intel.pred;
  var traj  = intel.traj;
  var learn = intel.learn;
  var score = intel.score;

  var hasPred  = pred && pred.signals_used > 0;
  var hasTraj  = traj && traj.sessions >= 2;
  var hasLearn = learn && learn.learning_active && learn.total_feedback >= 3;

  // Need at least one signal to render anything
  if (score === null && !hasPred) return null;

  var scoreColor = score == null ? DS.t3
    : score >= 70 ? DS.green
    : score >= 45 ? DS.amber
    : DS.red;
  var scoreLabel = score == null ? "Gathering data"
    : score >= 70 ? "Strong position"
    : score >= 45 ? "Developing"
    : "Needs attention";

  var legitPct  = hasPred ? Math.max(0, 100 - pred.scam_probability) : null;
  var succPct   = hasPred ? pred.success_probability : null;
  var confLabel = hasPred
    ? (pred.confidence_band === "high" ? "High confidence" : pred.confidence_band === "medium" ? "Medium confidence" : "Early signal")
    : null;

  var trajDir   = hasTraj ? (traj.ats_trend || traj.readiness_trend || "stable") : null;
  var trajIcon  = trajDir === "improving" ? "↑" : trajDir === "declining" ? "↓" : "→";
  var trajColor = trajDir === "improving" ? DS.green : trajDir === "declining" ? DS.red : DS.t4;
  var trajText  = trajDir === "improving" ? "Improving across sessions"
    : trajDir === "declining" ? "Declining — review inputs"
    : "Stable trajectory";

  var learnText = hasLearn
    ? (learn.recent_accuracy >= 75
        ? "System confidence high · " + learn.recent_accuracy + "% accuracy"
        : learn.recent_accuracy >= 55
        ? "System adapting · " + learn.total_feedback + " ratings processed"
        : "System calibrating · keep using for sharper results")
    : null;

  return (
    <div style={{ opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(6px)", transition: "all 0.4s cubic-bezier(0.22,1,0.36,1)", marginBottom: 12 }}>
      <div style={{ background: DS.bg1, border: "1px solid rgba(255,255,255,0.05)", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ height: 1, background: "linear-gradient(90deg,transparent," + scoreColor + "28,transparent)" }} />
        <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>

          {/* SCORE RING */}
          {score !== null && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <div style={{ position: "relative", width: 44, height: 44 }}>
                <svg width={44} height={44} style={{ transform: "rotate(-90deg)" }}>
                  <circle cx={22} cy={22} r={18} fill="none" stroke={DS.bg3} strokeWidth={4}/>
                  <circle cx={22} cy={22} r={18} fill="none" stroke={scoreColor} strokeWidth={4}
                    strokeDasharray={113} strokeDashoffset={show ? Math.round(113 - (score / 100) * 113) : 113}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.22,1,0.36,1) 0.15s" }}/>
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: scoreColor, fontFamily: "'JetBrains Mono',monospace" }}>{score}</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: DS.t4, letterSpacing: 2.5, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, marginBottom: 2 }}>INTELLIGENCE</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: scoreColor }}>{scoreLabel}</div>
              </div>
            </div>
          )}

          {/* DIVIDER */}
          {score !== null && hasPred && (
            <div style={{ width: 1, height: 36, background: DS.border0, flexShrink: 0 }} />
          )}

          {/* PREDICTION BARS */}
          {hasPred && (
            <div style={{ flex: 1, minWidth: 140, display: "flex", flexDirection: "column", gap: 7 }}>
              {legitPct !== null && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 10, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", width: 56, flexShrink: 0 }}>LEGIT</div>
                  <div style={{ flex: 1, height: 3, background: DS.bg3, borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (show ? legitPct : 0) + "%", background: legitPct >= 60 ? DS.green : legitPct >= 40 ? DS.amber : DS.red, borderRadius: 2, transition: "width 1s cubic-bezier(0.22,1,0.36,1) 0.2s" }} />
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: DS.t3, fontFamily: "'JetBrains Mono',monospace", width: 28, textAlign: "right" }}>{legitPct}%</div>
                </div>
              )}
              {succPct !== null && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 10, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", width: 56, flexShrink: 0 }}>SUCCESS</div>
                  <div style={{ flex: 1, height: 3, background: DS.bg3, borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (show ? succPct : 0) + "%", background: succPct >= 60 ? DS.green : succPct >= 40 ? DS.amber : DS.red, borderRadius: 2, transition: "width 1s cubic-bezier(0.22,1,0.36,1) 0.35s" }} />
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: DS.t3, fontFamily: "'JetBrains Mono',monospace", width: 28, textAlign: "right" }}>{succPct}%</div>
                </div>
              )}
              {confLabel && (
                <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 0.5 }}>{confLabel}</div>
              )}
            </div>
          )}

          {/* TRAJECTORY */}
          {hasTraj && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, padding: "4px 10px", background: trajColor + "08", border: "1px solid " + trajColor + "18", borderRadius: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 900, color: trajColor, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1 }}>{trajIcon}</span>
              <span style={{ fontSize: 10, color: DS.t3 }}>{trajText}</span>
            </div>
          )}
        </div>

        {/* LEARNING STATUS STRIP */}
        {learnText && (
          <div style={{ padding: "6px 18px 9px", borderTop: "1px solid " + DS.border0, display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: DS.green, animation: "tePulse 2s ease infinite", flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 0.3 }}>{learnText}</span>
          </div>
        )}
        {/* CONFIDENCE FOOTER */}
        <div style={{ padding: "6px 18px 10px", borderTop: learnText ? "none" : "1px solid " + DS.border0, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <span className="te-intel-feedback">
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: DS.green, display: "inline-block", animation: "tePulse 2s ease infinite" }}/>
            {hasPred && pred.confidence_band === "high" ? "HIGH CONFIDENCE" : hasPred && pred.confidence_band === "medium" ? "MEDIUM CONFIDENCE" : "EARLY SIGNAL"}
          </span>
          {hasPred && pred.signals_used > 0 && (
            <span style={{ fontSize: 10, color: DS.t4, fontFamily: "'JetBrains Mono',monospace" }}>
              {pred.signals_used} signal{pred.signals_used !== 1 ? "s" : ""} · {hasLearn && learn.total_feedback ? learn.total_feedback + " ratings" : "adaptive"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── getPrimaryAction ─────────────────────────────────
function getPrimaryAction(type, data) {
  try {
    if (type === "scan") {
      var score = (data && data.risk_score) || 0;
      if (score >= 70) return { label: "🚨 Report to CyberCrime", color: DS.red, fn: function() { window.open("https://cybercrime.gov.in", "_blank"); } };
      if (score >= 40) return { label: "⚠ Verify Company Now", color: DS.amber, fn: function() { window.open("https://mcaservices.mca.gov.in", "_blank"); } };
      return { label: "✓ Apply Safely", color: DS.green, fn: function() { try { navigator.clipboard.writeText("This job was verified as safe by Trust Engine."); } catch(e) {} } };
    }
    if (type === "resume") {
      var ats = (data && data.ats_score) || 0;
      if (ats < 50) return { label: "🔧 Fix Resume Now", color: DS.red, fn: function() { try { p4Navigate("resume"); } catch(e) {} } };
      if (ats < 70) return { label: "📈 Improve ATS Score", color: DS.amber, fn: function() { try { p4Navigate("pathfinder"); } catch(e) {} } };
      return { label: "🧭 Find Your Career Path", color: DS.green, fn: function() { try { p4Navigate("pathfinder"); } catch(e) {} } };
    }
    if (type === "jd") {
      var apply = data && data.apply_verdict;
      if (apply === "Apply") return { label: "✓ Customize Resume for This JD", color: DS.green, fn: function() { try { p4Navigate("resume"); } catch(e) {} } };
      if (apply === "Customize") return { label: "📄 Fix Resume First", color: DS.amber, fn: function() { try { p4Navigate("resume"); } catch(e) {} } };
      return { label: "🔍 Check Company Safety", color: DS.red, fn: function() { try { p4Navigate("scan"); } catch(e) {} } };
    }
    if (type === "career") {
      return { label: "🧭 Explore Career Paths", color: DS.purple, fn: function() { try { p4Navigate("pathfinder"); } catch(e) {} } };
    }
    if (type === "pathfinder") {
      return { label: "🛡 Verify a Job Opportunity", color: DS.teal, fn: function() { try { p4Navigate("scan"); } catch(e) {} } };
    }
    return { label: "→ Continue", color: DS.blue, fn: function() {} };
  } catch(e) { return { label: "→ Continue", color: DS.blue, fn: function() {} }; }
}


// ── ResultSummary ─────────────────────────────────────
function ResultSummary({ type, data }) {
  if (!data) return null;
  var bullets = [];
  try {
    if (type === "scan") {
      var rf = (data.red_flags || []).slice(0, 2);
      var ms = (data.manipulation_signals || []).slice(0, 1);
      bullets = rf.concat(ms).slice(0, 3);
      if (bullets.length === 0 && data.final_reasoning) bullets = [data.final_reasoning.slice(0, 80)];
    } else if (type === "resume") {
      var issues = (data.ats_issues || []).slice(0, 1);
      var missing = (data.skills_missing || []).slice(0, 1).map(function(s) { return "Missing: " + (s.skill || s); });
      var wins = (data.quick_wins || []).slice(0, 1);
      bullets = issues.concat(missing).concat(wins).slice(0, 3);
    } else if (type === "jd") {
      bullets = (data.red_flags || []).slice(0, 2).concat((data.hidden_requirements || []).slice(0, 1)).slice(0, 3);
    } else if (type === "career") {
      var top_reasons = data.rejection_analysis && (data.rejection_analysis.top_reasons || []);
      bullets = (top_reasons || []).slice(0, 2).concat((data.strengths || []).slice(0, 1)).slice(0, 3);
    } else if (type === "pathfinder") {
      var ua2 = data.user_analysis;
      if (ua2) {
        if (ua2.unfair_advantage) bullets.push("Advantage: " + ua2.unfair_advantage.slice(0, 60));
        if (ua2.truth_analysis) bullets.push(ua2.truth_analysis.slice(0, 60));
      }
      if (data.immediate_next_steps && data.immediate_next_steps.length > 0) bullets.push(data.immediate_next_steps[0].slice(0, 60));
      bullets = bullets.slice(0, 3);
    }
  } catch(e) {}
  if (!bullets.length) return null;
  var dotColor = type === "scan" ? ((data.risk_score || 0) >= 70 ? DS.red : DS.amber) : type === "resume" ? DS.blue : DS.t3;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 16 }}>
      {bullets.map(function(b, i) {
        return (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "9px 12px", background: DS.bg1, border: "1px solid " + DS.border0, borderRadius: 10 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor, flexShrink: 0, marginTop: 5 }} />
            <span style={{ fontSize: 13, color: DS.t2, lineHeight: 1.5 }}>{typeof b === "string" ? b : JSON.stringify(b)}</span>
          </div>
        );
      })}
    </div>
  );
}


// ── ResultActionBar (mobile bottom bar for result screens) ──
function ResultActionBar({ onShare, onReset, shareText, resetLabel }) {
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 20, marginBottom: 8 }}>
      {onReset && (
        <button onClick={onReset}
          style={{ flex: 1, minHeight: 44, padding: "11px 14px", background: DS.bg1, border: "1px solid " + DS.border0, borderRadius: 12, color: DS.t3, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
          ← {resetLabel || "New scan"}
        </button>
      )}
      {onShare && (
        <button onClick={onShare}
          style={{ flex: 2, minHeight: 44, padding: "11px 14px", background: "rgba(37,211,102,0.1)", border: "1px solid rgba(37,211,102,0.3)", borderRadius: 12, color: "#25D366", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
          💬 Share Result
        </button>
      )}
    </div>
  );
}

// ===================================================
// END PHASE 6 COMPONENTS
// ===================================================

// ===================================================
// PHASE 5 — ADVANCED INTELLIGENCE LAYER
// ===================================================

// ── PASSPORT KEY ──────────────────────────────────────
var PASSPORT_KEY = "te_v6_passport";
var IMMUNITY_KEY = "te_v6_immunity";

// ── PASSPORT LOGIC ────────────────────────────────────
function updatePassport(event, data) {
  try {
    var p = memGet(PASSPORT_KEY) || {
      scans_total: 0, scams_avoided: 0, ats_scores: [], paths_explored: 0,
      jd_decoded: 0, offers_analysed: 0, interviews_debriefed: 0,
      first_use: Date.now(), last_use: Date.now(), sessions: 0,
    };
    p.last_use = Date.now();
    if (event === "scan") {
      p.scans_total = (p.scans_total || 0) + 1;
      if (data && data.risk_score >= 70) p.scams_avoided = (p.scams_avoided || 0) + 1;
    }
    if (event === "resume" && data && data.ats_score) {
      if (!p.ats_scores) p.ats_scores = [];
      p.ats_scores.unshift(data.ats_score);
      if (p.ats_scores.length > 10) p.ats_scores = p.ats_scores.slice(0, 10);
    }
    if (event === "pathfinder") p.paths_explored = (p.paths_explored || 0) + 1;
    if (event === "jd") p.jd_decoded = (p.jd_decoded || 0) + 1;
    if (event === "offer") p.offers_analysed = (p.offers_analysed || 0) + 1;
    if (event === "debrief") p.interviews_debriefed = (p.interviews_debriefed || 0) + 1;
    memSet(PASSPORT_KEY, p);
    return p;
  } catch(e) { return null; }
}

function getPassport() {
  try { return memGet(PASSPORT_KEY) || null; } catch(e) { return null; }
}

function getATSImprovement(scores) {
  if (!scores || scores.length < 2) return 0;
  return scores[0] - scores[scores.length - 1];
}

// ── IMMUNITY LOGIC ────────────────────────────────────
function getImmunityScore() {
  try {
    var p = getPassport();
    var pts = 0;
    if (!p) return { level: 0, label: "Newcomer", points: 0, next_level: 10, badge: "🆕" };
    pts += (p.scans_total || 0) * 2;
    pts += (p.scams_avoided || 0) * 10;
    pts += (p.paths_explored || 0) * 5;
    pts += (p.jd_decoded || 0) * 3;
    pts += (p.offers_analysed || 0) * 4;
    pts += (p.interviews_debriefed || 0) * 3;
    // ATS improvement bonus
    var atsImprove = getATSImprovement(p.ats_scores || []);
    if (atsImprove > 0) pts += Math.min(atsImprove, 30);
    var levels = [
      { min: 0,   level: 0, label: "Newcomer",        badge: "🆕", next: 20  },
      { min: 20,  level: 1, label: "Alert",            badge: "👁",  next: 60  },
      { min: 60,  level: 2, label: "Aware",            badge: "🛡",  next: 120 },
      { min: 120, level: 3, label: "Defender",         badge: "⚡",  next: 250 },
      { min: 250, level: 4, label: "Scam Hunter",      badge: "🔥",  next: 500 },
      { min: 500, level: 5, label: "Elite Protector",  badge: "💎",  next: 999 },
    ];
    var current = levels[0];
    for (var i = levels.length - 1; i >= 0; i--) {
      if (pts >= levels[i].min) { current = levels[i]; break; }
    }
    return { level: current.level, label: current.label, badge: current.badge, points: pts, next_level: current.next, scams_avoided: p.scams_avoided || 0 };
  } catch(e) {
    return { level: 0, label: "Newcomer", points: 0, next_level: 20, badge: "🆕", scams_avoided: 0 };
  }
}

// ── 1. PREDICTION ENGINE ──────────────────────────────
function PredictionEngine({ result }) {
  if (!result) return null;
  var decision = useMemo(function() {
    try { return s3ComputeGlobalDecision(); } catch(e) { return null; }
  }, []);
  var scamPct = decision && decision.scam_probability != null ? Math.min(100, Math.max(0, Math.round(decision.scam_probability))) : null;
  var succPct = decision && decision.success_probability != null ? Math.min(100, Math.max(0, Math.round(decision.success_probability))) : null;
  if (scamPct === null && succPct === null) return null;
  var sv = useState(false); var anim = sv[0]; var setAnim = sv[1];
  useEffect(function() { var t = setTimeout(function() { setAnim(true); }, 300); return function() { clearTimeout(t); }; }, []);
  var legitPct = scamPct != null ? Math.max(0, 100 - scamPct) : null;
  var combined = (legitPct != null && succPct != null) ? Math.round((legitPct + succPct) / 2) : (legitPct || succPct || 0);
  var combColor = combined >= 65 ? DS.green : combined >= 40 ? DS.amber : DS.red;
  var combLabel = combined >= 65 ? "Worth pursuing" : combined >= 40 ? "Proceed carefully" : "High risk";
  return (
    <div style={{ background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.18)", borderRadius: 14, padding: "14px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 14 }}>🔮</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: DS.blue, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>PREDICTION ENGINE</div>
          <div style={{ fontSize: 11, color: DS.t3, marginTop: 1 }}>Cross-module intelligence synthesis</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: combColor, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1 }}>{combined}%</div>
          <div style={{ fontSize: 9, color: combColor, fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{combLabel}</div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {legitPct !== null && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <span style={{ fontSize: 11, color: DS.t2 }}>Chance job is legitimate</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: legitPct >= 60 ? DS.green : legitPct >= 40 ? DS.amber : DS.red, fontFamily: "'JetBrains Mono',monospace" }}>{legitPct}%</span>
            </div>
            <div style={{ height: 5, background: DS.bg3, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: (anim ? legitPct : 0) + "%", background: legitPct >= 60 ? DS.green : legitPct >= 40 ? DS.amber : DS.red, borderRadius: 3, transition: "width 1s cubic-bezier(0.22,1,0.36,1)" }} />
            </div>
          </div>
        )}
        {succPct !== null && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <span style={{ fontSize: 11, color: DS.t2 }}>Chance of getting this job</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: succPct >= 60 ? DS.green : succPct >= 40 ? DS.amber : DS.red, fontFamily: "'JetBrains Mono',monospace" }}>{succPct}%</span>
            </div>
            <div style={{ height: 5, background: DS.bg3, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: (anim ? succPct : 0) + "%", background: succPct >= 60 ? DS.green : succPct >= 40 ? DS.amber : DS.red, borderRadius: 3, transition: "width 1s cubic-bezier(0.22,1,0.36,1) 0.1s" }} />
            </div>
          </div>
        )}
      </div>
      {decision && decision.reasoning && (
        <div style={{ marginTop: 10, fontSize: 11, color: DS.t3, lineHeight: 1.6, borderTop: "1px solid " + DS.border0, paddingTop: 10 }}>
          {decision.reasoning.slice(0, 160)}{decision.reasoning.length > 160 ? "…" : ""}
        </div>
      )}
    </div>
  );
}

// ── 2. REAL-TIME SCAM INTEL PANEL ─────────────────────
async function getScamIntel(domain, city) {
  try {
    var url = SB + "/rest/v1/scans?select=verdict,quick_verdict,scam_type,platform,risk_score,created_at&order=created_at.desc&limit=200";
    var r = await fetch(url, { headers: { "apikey": SK, "Authorization": "Bearer " + SK } });
    if (!r.ok) return null;
    var data = await r.json();
    var cutoff = Date.now() - 7 * 24 * 3600000;
    var week = data.filter(function(x) { return x.created_at && new Date(x.created_at).getTime() > cutoff; });
    var scams = week.filter(function(x) { return x.quick_verdict === "SCAM" || x.risk_score >= 70; });
    var typeMap = {};
    scams.forEach(function(x) { if (x.scam_type) typeMap[x.scam_type] = (typeMap[x.scam_type] || 0) + 1; });
    var topType = Object.keys(typeMap).sort(function(a, b) { return typeMap[b] - typeMap[a]; })[0] || null;
    var platMap = {};
    scams.forEach(function(x) { if (x.platform) platMap[x.platform] = (platMap[x.platform] || 0) + 1; });
    var topPlat = Object.keys(platMap).sort(function(a, b) { return platMap[b] - platMap[a]; })[0] || null;
    return { total_this_week: week.length, scams_this_week: scams.length, top_scam_type: topType, top_platform: topPlat, scam_rate: week.length > 0 ? Math.round((scams.length / week.length) * 100) : 0 };
  } catch(e) { return null; }
}

function ScamIntelPanel() {
  var sv = useState(null); var intel = sv[0]; var setIntel = sv[1];
  var sl = useState(false); var loading = sl[0]; var setLoading = sl[1];
  var so = useState(false); var open = so[0]; var setOpen = so[1];
  useEffect(function() {
    if (!open || intel) return;
    setLoading(true);
    getScamIntel().then(function(d) { setIntel(d); setLoading(false); }).catch(function() { setLoading(false); });
  }, [open]);
  var scamRate = intel ? intel.scam_rate : 0;
  var rateColor = scamRate >= 50 ? DS.red : scamRate >= 25 ? DS.amber : DS.green;
  return (
    <div style={{ background: DS.bg1, border: "1px solid rgba(244,63,94,0.18)", borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
      <button onClick={function() { setOpen(function(v) { return !v; }); }}
        style={{ width: "100%", padding: "13px 16px", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: DS.red, animation: "tePulse 2s infinite", boxShadow: "0 0 8px " + DS.red + "66", flexShrink: 0 }} />
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 9, color: DS.red, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>LIVE SCAM INTELLIGENCE · LAST 7 DAYS</div>
            <div style={{ fontSize: 11, color: DS.t3, marginTop: 1 }}>India job market threat patterns</div>
          </div>
        </div>
        <span style={{ color: DS.t4, fontSize: 16, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>›</span>
      </button>
      {open && (
        <div style={{ padding: "0 16px 16px", borderTop: "1px solid rgba(244,63,94,0.1)", animation: "teFadeIn 0.2s ease" }}>
          {loading && (
            <div style={{ padding: "14px 0", display: "flex", alignItems: "center", gap: 10, color: DS.red }}>
              <div style={{ width: 12, height: 12, border: "2px solid " + DS.red + "30", borderTopColor: DS.red, borderRadius: "50%", animation: "teSpin 0.7s linear infinite" }} />
              <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>Fetching live intelligence...</span>
            </div>
          )}
          {!loading && !intel && (
            <div style={{ padding: "14px 0", fontSize: 12, color: DS.t3 }}>Could not load live data. Check connection.</div>
          )}
          {intel && !loading && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div style={{ background: DS.bg0, border: "1px solid " + DS.border0, borderRadius: 10, padding: "12px" }}>
                  <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>SCANS THIS WEEK</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: DS.t1, fontFamily: "'JetBrains Mono',monospace" }}>{intel.total_this_week}</div>
                </div>
                <div style={{ background: DS.bg0, border: "1px solid " + rateColor + "22", borderRadius: 10, padding: "12px" }}>
                  <div style={{ fontSize: 9, color: DS.t4, fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>SCAM RATE</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: rateColor, fontFamily: "'JetBrains Mono',monospace" }}>{scamRate}%</div>
                </div>
              </div>
              {intel.scams_this_week > 0 && (
                <div style={{ background: DS.red + "06", border: "1px solid " + DS.red + "20", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: DS.red, marginBottom: 6 }}>🚨 {intel.scams_this_week} confirmed scams detected this week</div>
                  {intel.top_scam_type && <div style={{ fontSize: 12, color: DS.t2 }}>Top pattern: <span style={{ color: DS.amber, fontWeight: 600 }}>{intel.top_scam_type}</span></div>}
                  {intel.top_platform && <div style={{ fontSize: 12, color: DS.t2, marginTop: 4 }}>Most used platform: <span style={{ color: DS.cyan, fontWeight: 600 }}>{intel.top_platform}</span></div>}
                </div>
              )}
              {intel.scams_this_week === 0 && (
                <div style={{ background: DS.green + "06", border: "1px solid " + DS.green + "20", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: DS.green }}>
                  ✓ No scam spikes detected this week. Stay alert regardless.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 3. REPUTATION PASSPORT ────────────────────────────
function ReputationPassport() {
  var passport = useMemo(function() { return getPassport(); }, []);
  var immunity = useMemo(function() { return getImmunityScore(); }, []);
  var sv = useState(false); var open = sv[0]; var setOpen = sv[1];
  if (!passport) return null;
  var total = (passport.scans_total || 0) + (passport.paths_explored || 0) + (passport.jd_decoded || 0) + (passport.offers_analysed || 0) + (passport.interviews_debriefed || 0);
  var atsImprove = getATSImprovement(passport.ats_scores || []);
  var currentATS = passport.ats_scores && passport.ats_scores.length > 0 ? passport.ats_scores[0] : null;
  if (total < 3) return null;
  var daysSinceFirst = passport.first_use ? Math.floor((Date.now() - passport.first_use) / 86400000) : 0;
  var narrative = "";
  if (passport.scams_avoided > 0) narrative += "Protected yourself from " + passport.scams_avoided + " scam" + (passport.scams_avoided > 1 ? "s" : "") + ". ";
  if (atsImprove > 0) narrative += "Improved resume score by " + atsImprove + " points. ";
  if (passport.paths_explored > 0) narrative += "Explored " + passport.paths_explored + " career path" + (passport.paths_explored > 1 ? "s" : "") + ". ";
  if (passport.jd_decoded > 0) narrative += "Decoded " + passport.jd_decoded + " job description" + (passport.jd_decoded > 1 ? "s" : "") + ".";
  return (
    <div style={{ background: DS.bg1, border: "1px solid " + G + "25", borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
      <button onClick={function() { setOpen(function(v) { return !v; }); }}
        style={{ width: "100%", padding: "14px 18px", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 38, height: 38, background: G + "15", border: "1px solid " + G + "30", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{immunity.badge}</div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 9, color: G, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>REPUTATION PASSPORT · LEVEL {immunity.level}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: DS.t1, marginTop: 1 }}>{immunity.label} · {total} interactions</div>
          </div>
        </div>
        <span style={{ color: G + "80", fontSize: 16, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>›</span>
      </button>
      {open && (
        <div style={{ padding: "0 18px 18px", borderTop: "1px solid " + G + "15", animation: "teFadeIn 0.2s ease" }}>
          {narrative && (
            <div style={{ marginTop: 12, padding: "12px 14px", background: G + "06", border: "1px solid " + G + "18", borderRadius: 10, fontSize: 13, color: DS.t2, lineHeight: 1.7, marginBottom: 14 }}>
              {narrative}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))", gap: 8, marginBottom: 14 }}>
            {[
              { label: "SCANS", value: passport.scans_total || 0, color: DS.red, icon: "🛡" },
              { label: "SCAMS AVOIDED", value: passport.scams_avoided || 0, color: DS.green, icon: "🚫" },
              { label: "PATHS EXPLORED", value: passport.paths_explored || 0, color: DS.teal, icon: "🧭" },
              { label: "JDS DECODED", value: passport.jd_decoded || 0, color: DS.cyan, icon: "📋" },
              { label: "OFFERS ANALYSED", value: passport.offers_analysed || 0, color: DS.amber, icon: "💼" },
              { label: "DAYS ACTIVE", value: daysSinceFirst, color: DS.purple, icon: "📅" },
            ].map(function(item) {
              return (
                <div key={item.label} style={{ background: DS.bg0, border: "1px solid " + item.color + "18", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 16, marginBottom: 4 }}>{item.icon}</div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: item.color, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1 }}>{item.value}</div>
                  <div style={{ fontSize: 8, color: DS.t4, letterSpacing: 1, fontFamily: "'JetBrains Mono',monospace", marginTop: 4 }}>{item.label}</div>
                </div>
              );
            })}
          </div>
          {currentATS != null && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: DS.blue + "06", border: "1px solid " + DS.blue + "18", borderRadius: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 14 }}>📄</span>
              <div style={{ fontSize: 12, color: DS.t2 }}>
                Latest ATS: <span style={{ color: DS.blue, fontWeight: 700 }}>{currentATS}/100</span>
                {atsImprove > 0 && <span style={{ color: DS.green, marginLeft: 8 }}>↑ +{atsImprove} improved</span>}
                {atsImprove < 0 && <span style={{ color: DS.red, marginLeft: 8 }}>↓ {atsImprove} dropped</span>}
              </div>
            </div>
          )}
          {/* Immunity progress */}
          <div style={{ padding: "10px 14px", background: G + "06", border: "1px solid " + G + "18", borderRadius: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: G, fontWeight: 700 }}>{immunity.badge} {immunity.label}</span>
              <span style={{ fontSize: 10, color: DS.t3, fontFamily: "'JetBrains Mono',monospace" }}>{immunity.points} / {immunity.next_level} pts</span>
            </div>
            <div style={{ height: 4, background: DS.bg3, borderRadius: 2, overflow: "hidden" }}>
              <div style={{ height: "100%", width: Math.min(100, Math.round((immunity.points / Math.max(immunity.next_level, 1)) * 100)) + "%", background: G, borderRadius: 2, transition: "width 1s cubic-bezier(0.22,1,0.36,1)" }} />
            </div>
            <div style={{ fontSize: 10, color: DS.t4, marginTop: 6 }}>
              {immunity.next_level - immunity.points > 0 ? (immunity.next_level - immunity.points) + " pts to Level " + (immunity.level + 1) : "Max level reached!"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 4. IMMUNITY BADGE (header) ────────────────────────
function ImmunityBadge() {
  var immunity = useMemo(function() { return getImmunityScore(); }, []);
  if (!immunity || immunity.level === 0) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, background: G + "10", border: "1px solid " + G + "25", borderRadius: 20, padding: "4px 10px", flexShrink: 0 }}>
      <span style={{ fontSize: 13 }}>{immunity.badge}</span>
      <span style={{ fontSize: 9, color: G, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, letterSpacing: 0.5 }}>LVL {immunity.level}</span>
    </div>
  );
}

// ── 5. AI CHAT ASSISTANT ──────────────────────────────
var CHAT_SYS_TEMPLATE = "You are Trust Engine's AI advisor. You have access to an analysis result and you answer user questions about it concisely. Be specific, actionable, and direct. Use plain language. Max 3 sentences per response. Never repeat the question back.";

function AIChatAssistant({ context, contextType }) {
  var sm = useState([]); var messages = sm[0]; var setMessages = sm[1];
  var si = useState(""); var input = si[0]; var setInput = si[1];
  var sl = useState(false); var loading = sl[0]; var setLoading = sl[1];
  var so = useState(false); var open = so[0]; var setOpen = so[1];
  var abRef = useRef(null);
  var msgEndRef = useRef(null);
  var MAX_MSGS = 10;
  useEffect(function() { return function() { if (abRef.current) abRef.current.abort(); }; }, []);
  useEffect(function() {
    if (msgEndRef.current) { try { msgEndRef.current.scrollIntoView({ behavior: "smooth" }); } catch(e) {} }
  }, [messages]);

  var ctxSummary = useMemo(function() {
    if (!context) return "";
    try {
      var c = context;
      if (contextType === "scan") {
        return "SCAN RESULT:\nVerdict: " + (c.verdict || "Unknown") + "\nRisk Score: " + (c.risk_score || 0) + "/100\nRed Flags: " + (c.red_flags || []).slice(0, 3).join("; ") + "\nReasoning: " + (c.final_reasoning || "").slice(0, 200);
      }
      if (contextType === "resume") {
        return "RESUME RESULT:\nATS Score: " + (c.ats_score || 0) + "/100\nLevel: " + (c.candidate_level || "") + "\nTop Role: " + (c.top_roles && c.top_roles[0] ? c.top_roles[0].role : "") + "\nKey Gaps: " + (c.skills_missing || []).slice(0, 3).map(function(s) { return s.skill; }).join(", ");
      }
      if (contextType === "jd") {
        return "JD ANALYSIS:\nRole Reality: " + (c.role_reality || "") + "\nRed Flags: " + (c.red_flags || []).slice(0, 3).join("; ") + "\nHidden Requirements: " + (c.hidden_requirements || []).slice(0, 2).join("; ");
      }
      return "ANALYSIS RESULT:\n" + JSON.stringify(c).slice(0, 400);
    } catch(e) { return ""; }
  }, [context, contextType]);

  var QUICK_PROMPTS = {
    scan: ["Why is the salary suspicious?", "What should I say to this recruiter?", "Write a polite email declining this offer"],
    resume: ["What's my most critical skill gap?", "How do I negotiate a higher salary?", "Rewrite my summary in 2 sentences"],
    jd: ["What's the real workload here?", "Is this role worth applying for?", "What ATS keywords should I add?"],
  };
  var quickPrompts = QUICK_PROMPTS[contextType] || [];

  async function send(text) {
    var msg = (text || input).trim();
    if (!msg || loading || messages.length >= MAX_MSGS) return;
    if (abRef.current) abRef.current.abort();
    abRef.current = new AbortController();
    var userMsg = { role: "user", content: msg };
    setMessages(function(prev) { return prev.concat([userMsg]); });
    setInput("");
    setLoading(true);
    try {
      var history = messages.slice(-6).map(function(m) { return m.role.toUpperCase() + ": " + m.content; }).join("\n");
      var prompt = ctxSummary + "\n\nCONVERSATION HISTORY:\n" + history + "\n\nUSER: " + msg + "\n\nRespond concisely as Trust Engine AI advisor:";
      var result = await runAI(prompt, CHAT_SYS_TEMPLATE, abRef.current.signal);
      var reply = (result && result.text) ? result.text.trim() : "I couldn't process that. Please try rephrasing.";
      // Strip JSON if it leaked
      if (reply.startsWith("{")) { try { var p = JSON.parse(reply); reply = p.response || p.message || p.text || reply; } catch(e) {} }
      setMessages(function(prev) { return prev.concat([{ role: "assistant", content: reply }]); });
    } catch(e) {
      if (e && e.name !== "AbortError") {
        setMessages(function(prev) { return prev.concat([{ role: "assistant", content: "AI unavailable. Please retry in a moment." }]); });
      }
    } finally { setLoading(false); }
  }

  var iconMap = { scan: "🛡", resume: "📄", jd: "📋" };
  var colorMap = { scan: DS.red, resume: DS.blue, jd: DS.cyan };
  var CC = colorMap[contextType] || DS.purple;
  var remaining = MAX_MSGS - messages.length;

  return (
    <div style={{ background: DS.bg1, border: "1px solid " + CC + "25", borderRadius: 16, overflow: "hidden", marginTop: 20, marginBottom: 20 }}>
      <button onClick={function() { setOpen(function(v) { return !v; }); }}
        style={{ width: "100%", padding: "14px 18px", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, background: CC + "15", border: "1px solid " + CC + "30", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>💬</div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 9, color: CC, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>ASK AI ADVISOR</div>
            <div style={{ fontSize: 12, color: DS.t3, marginTop: 1 }}>Ask anything about this result</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {messages.length > 0 && <span style={{ fontSize: 10, color: CC, fontFamily: "'JetBrains Mono',monospace" }}>{messages.filter(function(m){return m.role==="user";}).length} Q</span>}
          <span style={{ color: CC + "80", fontSize: 16, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>›</span>
        </div>
      </button>
      {open && (
        <div style={{ borderTop: "1px solid " + CC + "15" }}>
          {/* Quick prompts */}
          {messages.length === 0 && quickPrompts.length > 0 && (
            <div style={{ padding: "12px 16px", display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid " + DS.border0 }}>
              {quickPrompts.map(function(q, i) {
                return (
                  <button key={i} onClick={function() { send(q); }}
                    style={{ fontSize: 11, padding: "5px 11px", background: CC + "08", border: "1px solid " + CC + "22", borderRadius: 20, color: CC, cursor: "pointer", transition: "all 0.15s" }}
                    onMouseEnter={function(e){e.currentTarget.style.background=CC+"18";}}
                    onMouseLeave={function(e){e.currentTarget.style.background=CC+"08";}}>
                    {q}
                  </button>
                );
              })}
            </div>
          )}
          {/* Messages */}
          {messages.length > 0 && (
            <div style={{ maxHeight: 320, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
              {messages.map(function(m, i) {
                var isUser = m.role === "user";
                return (
                  <div key={i} style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}>
                    <div style={{ maxWidth: "85%", padding: "9px 13px", borderRadius: isUser ? "12px 12px 2px 12px" : "12px 12px 12px 2px", background: isUser ? CC + "18" : DS.bg0, border: "1px solid " + (isUser ? CC + "30" : DS.border0), fontSize: 13, color: isUser ? DS.t1 : DS.t1, lineHeight: 1.65 }}>
                      {m.content}
                    </div>
                  </div>
                );
              })}
              {loading && (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div style={{ padding: "9px 13px", background: DS.bg0, border: "1px solid " + DS.border0, borderRadius: "12px 12px 12px 2px" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      {[0,1,2].map(function(i){return <div key={i} style={{ width:5,height:5,borderRadius:"50%",background:CC,opacity:0.6,animation:"tePulse 1s ease infinite",animationDelay:i*0.2+"s" }}/>;})}
                    </div>
                  </div>
                </div>
              )}
              <div ref={msgEndRef} />
            </div>
          )}
          {/* Input */}
          {remaining > 0 ? (
            <div style={{ padding: "10px 14px", borderTop: "1px solid " + DS.border0, display: "flex", gap: 8 }}>
              <input value={input} onChange={function(e) { setInput(e.target.value); }}
                onKeyDown={function(e) { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={"Ask about this " + (contextType || "result") + "... (" + remaining + " left)"}
                disabled={loading}
                style={{ flex: 1, background: DS.bg0, border: "1px solid " + DS.border1, borderRadius: 10, padding: "9px 12px", color: DS.t1, fontSize: 13, outline: "none", caretColor: CC }} />
              <button onClick={function(){send();}} disabled={loading || !input.trim()}
                style={{ padding: "9px 16px", background: input.trim() ? CC + "18" : DS.bg0, border: "1px solid " + (input.trim() ? CC + "40" : DS.border0), borderRadius: 10, color: input.trim() ? CC : DS.t4, fontSize: 13, fontWeight: 700, cursor: input.trim() ? "pointer" : "default", transition: "all 0.15s" }}>
                ↑
              </button>
            </div>
          ) : (
            <div style={{ padding: "10px 14px", borderTop: "1px solid " + DS.border0, fontSize: 12, color: DS.t4, textAlign: "center" }}>
              Session limit reached. Reload page for a new session.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
// ===================================================
// END PHASE 5 COMPONENTS
// ===================================================

// ===================================================
// PHASE 4 — UX COMPONENTS
// ===================================================

// ── Helper: relative time ──────────────────────────
function relTime(ts) {
  if (!ts) return null;
  var diff = Date.now() - (typeof ts === "string" ? new Date(ts).getTime() : ts);
  var d = Math.floor(diff / 86400000);
  var h = Math.floor(diff / 3600000);
  var m = Math.floor(diff / 60000);
  if (d > 60) return null;
  if (d > 0) return d + "d ago";
  if (h > 0) return h + "h ago";
  if (m > 0) return m + "m ago";
  return "just now";
}

// ── Helper: module last result meta ───────────────
function getModuleLastMeta(moduleId) {
  try {
    var cross = memGet("te_v6_cross") || {};
    var m = cross[moduleId];
    if (!m) return null;
    var lastEntry = Object.values(m).sort(function(a, b) { return (b.ts || 0) - (a.ts || 0); })[0];
    if (!lastEntry) return null;
    var d = lastEntry.data;
    var t = relTime(lastEntry.ts);
    if (!t) return null;
    if (moduleId === "resume" && d) {
      var sc = d.ats_score || d.scores && d.scores.ats_score;
      if (sc) return "ATS " + sc + "/100 · " + t;
    }
    if (moduleId === "scan" && d) {
      var rs = d.risk_score;
      if (rs != null) return "Risk " + rs + "/100 · " + t;
    }
    if (moduleId === "career" && d) {
      var ats2 = d.scores && d.scores.ats_score;
      if (ats2) return "Score " + ats2 + " · " + t;
    }
    if (moduleId === "pathfinder" && d) {
      var ua = d.user_analysis;
      if (ua && ua.readiness_score) return "Readiness " + ua.readiness_score + " · " + t;
    }
    if (moduleId === "hr" && d) {
      var total = d.total_candidates || (d.candidates && d.candidates.length);
      if (total) return total + " candidates · " + t;
    }
    if (moduleId === "jd" && d) {
      return "Decoded · " + t;
    }
    return t;
  } catch(e) { return null; }
}

// ── ZONE 1 — HOME ACTIVE ALERT ─────────────────────
function HomeActiveAlert({ hist, liveStats, alertDismissed, setAlertDismissed, setMode, setShowH, setShowR }) {
  if (alertDismissed) return null;

  // Check for recent high-risk scans
  var recentHighRisk = (hist || []).filter(function(h) {
    return h.score >= 70 && Date.now() - new Date(h.ts).getTime() < 7 * 24 * 3600000;
  });

  // Check for scam spike in live stats
  var scamSpike = (liveStats && liveStats.scams_today >= 5);

  // Check pattern memory for recent spikes
  var patterns = null;
  var patternSpike = false;
  try {
    patterns = memGet(PATTERN_KEY) || {};
    var history = patterns.verdict_history || [];
    var recent24h = history.filter(function(h) { return Date.now() - h.ts < 86400000; });
    patternSpike = recent24h.filter(function(h) { return h.verdict === "Likely Scam"; }).length >= 3;
  } catch(e) {}

  if (!recentHighRisk.length && !scamSpike && !patternSpike) return null;

  var msg = recentHighRisk.length > 0
    ? "⚠ You scanned a high-risk job recently. Check it again or verify before applying."
    : scamSpike
    ? "⚠ " + (liveStats.scams_today || "") + " scam patterns detected in India's job market today. Verify before applying."
    : "⚠ Multiple scam patterns detected in your recent scan history.";

  var color = recentHighRisk.length > 0 ? DS.red : DS.amber;

  return (
    <div style={{ background: color + "08", border: "1px solid " + color + "30", borderRadius: 14, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 12, animation: "teFadeIn 0.4s ease" }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: color, fontWeight: 600, lineHeight: 1.6, marginBottom: 8 }}>{msg}</div>
        {recentHighRisk.length > 0 && (
          <button onClick={function() { setMode("scan"); setShowH(false); setShowR(false); }}
            style={{ fontSize: 11, padding: "5px 12px", background: color + "12", border: "1px solid " + color + "30", borderRadius: 7, color: color, cursor: "pointer", fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>
            VIEW SCAN →
          </button>
        )}
      </div>
      <button onClick={function() { setAlertDismissed(true); }}
        style={{ background: "transparent", border: "none", color: DS.t4, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px", flexShrink: 0 }}>×</button>
    </div>
  );
}

// ── ZONE 2 — HOME PRIMARY ACTION ───────────────────
function HomePrimaryAction({ hist, setMode, setShowH, setShowR }) {
  var profile = useMemo(function() { try { return getUserProfile(); } catch(e) { return {}; } }, []);
  var cross = useMemo(function() { try { return memGet("te_v6_cross") || {}; } catch(e) { return {}; } }, []);
  var atsHist = useMemo(function() { try { return JSON.parse(typeof localStorage !== "undefined" ? (localStorage.getItem("te_v5_ats_hist") || "[]") : "[]"); } catch(e) { return []; } }, []);

  var hasResume = !!(cross.resume && Object.keys(cross.resume).length > 0);
  var hasPathfinder = !!(cross.pathfinder && Object.keys(cross.pathfinder).length > 0);
  var hasScanHist = hist && hist.length > 0;
  var atsDropped = atsHist.length >= 2 && atsHist[0].score < atsHist[1].score;

  var cta;
  if (hasResume && atsDropped) {
    var drop = atsHist[1].score - atsHist[0].score;
    cta = { label: "Your resume score dropped " + drop + " points — see why →", color: DS.amber, mode: "resume" };
  } else if (hasPathfinder) {
    var pData = Object.values(cross.pathfinder).sort(function(a,b){return (b.ts||0)-(a.ts||0);})[0];
    var daysAgo = pData && pData.ts ? Math.floor((Date.now() - pData.ts) / 86400000) : null;
    if (daysAgo != null && daysAgo >= 14) {
      cta = { label: "It's been " + daysAgo + " days — are you on track with your career path? →", color: DS.teal, mode: "pathfinder" };
    }
  }
  if (!cta && hasResume) {
    cta = { label: "View your resume intelligence report →", color: DS.blue, mode: "resume" };
  }
  if (!cta && hasScanHist) {
    cta = { label: "Check another job post for fraud →", color: DS.red, mode: "scan" };
  }
  if (!cta) {
    cta = { label: "Check if your job offer is real →", color: DS.red, mode: "scan" };
  }

  return (
    <div style={{ marginBottom: 24, animation: "teFadeIn 0.5s ease 0.1s both" }}>
      <button
        onClick={function() { setMode(cta.mode); setShowH(false); setShowR(false); }}
        className="te-btn-primary"
        style={{ width: "100%", minHeight: 54, padding: "15px 22px", background: "linear-gradient(135deg,"+cta.color+"14,"+cta.color+"0A)", border: "1px solid " + cta.color + "30", borderRadius: 14, color: cta.color, fontSize: "clamp(13px,2.5vw,15px)", fontWeight: 700, cursor: "pointer", transition: "all 0.25s", textAlign: "left", letterSpacing: 0.2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, boxShadow: "0 4px 24px " + cta.color + "10,inset 0 1px 0 "+cta.color+"14" }}
        onMouseEnter={function(e) { e.currentTarget.style.background = "linear-gradient(135deg,"+cta.color+"22,"+cta.color+"14)"; e.currentTarget.style.boxShadow = "0 8px 36px " + cta.color + "22,inset 0 1px 0 "+cta.color+"22"; e.currentTarget.style.borderColor = cta.color+"50"; }}
        onMouseLeave={function(e) { e.currentTarget.style.background = "linear-gradient(135deg,"+cta.color+"14,"+cta.color+"0A)"; e.currentTarget.style.boxShadow = "0 4px 24px " + cta.color + "10,inset 0 1px 0 "+cta.color+"14"; e.currentTarget.style.borderColor = cta.color+"30"; }}>
        <span>{cta.label}</span>
        <span style={{ fontSize: 18, flexShrink: 0, opacity:0.7 }}>›</span>
      </button>
    </div>
  );
}

// ===================================================
// END PHASE 4 UX COMPONENTS
// ===================================================

// ── ScanPrimaryAction — single dominant CTA below VerdictTheater ──
function ScanPrimaryAction({ result }) {
  if (!result) return null;
  var action = getPrimaryAction("scan", result);
  var col = action.color;
  var sv = useState(false); var vis = sv[0]; var setVis = sv[1];
  useEffect(function() { var t = setTimeout(function() { setVis(true); }, 1700); return function() { clearTimeout(t); }; }, [result]);
  return (
    <div style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(8px)", transition: "all 0.4s var(--ease-out)", marginBottom: 16 }}>
      <button onClick={action.fn}
        className="te-btn-primary"
        style={{
          width: "100%", padding: "15px 24px",
          background: col + "12",
          border: "1px solid " + col + "38",
          borderRadius: 14,
          color: col,
          fontSize: 14, fontWeight: 700,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          boxShadow: "0 4px 28px " + col + "12, inset 0 1px 0 " + col + "12",
          letterSpacing: 0.2,
          transition: "all 0.25s var(--ease-out)",
        }}
        onMouseEnter={function(e) { e.currentTarget.style.background = col + "1E"; e.currentTarget.style.boxShadow = "0 8px 36px " + col + "24, inset 0 1px 0 " + col + "18"; e.currentTarget.style.borderColor = col + "55"; }}
        onMouseLeave={function(e) { e.currentTarget.style.background = col + "12"; e.currentTarget.style.boxShadow = "0 4px 28px " + col + "12, inset 0 1px 0 " + col + "12"; e.currentTarget.style.borderColor = col + "38"; }}>
        <span>{action.label}</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, opacity: 0.7 }}><path d="M3 8h10M9 4l4 4-4 4" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
    </div>
  );
}

// ===================================================
// AppCore — MAIN APPLICATION
// ===================================================
function AppCore() {
  var s0=useState("");var input=s0[0];var setInput=s0[1];
  var s1=useState(false);var scanning=s1[0];var setScanning=s1[1];
  var s2=useState(0);var stepIdx=s2[0];var setStepIdx=s2[1];
  var s3=useState(0);var progress=s3[0];var setProgress=s3[1];
  var s4=useState(null);var result=s4[0];var setResult=s4[1];
  var s5=useState(null);var error=s5[0];var setError=s5[1];
  var s6=useState("flags");var tab=s6[0];var setTab=s6[1];
  var s7=useState(0);var chars=s7[0];var setChars=s7[1];
  var s8=useState(false);var ocrOn=s8[0];var setOcrOn=s8[1];
  var s9=useState(0);var ocrProg=s9[0];var setOcrProg=s9[1];
  var s10=useState(false);var copied=s10[0];var setCopied=s10[1];
  var s11=useState([]);var hist=s11[0];var setHist=s11[1];
  var s12=useState(false);var showH=s12[0];var setShowH=s12[1];
  var s13=useState(false);var showR=s13[0];var setShowR=s13[1];
  var s14=useState([]);var recent=s14[0];var setRecent=s14[1];
  var s15=useState(null);var stats=s15[0];var setStats=s15[1];
  var s16=useState(false);var loadR=s16[0];var setLoadR=s16[1];
  var s17=useState("home");var mode=s17[0];var setMode=s17[1];
  var s18=useState(null);var coData=s18[0];var setCoData=s18[1];
  var s19=useState(false);var coLoading=s19[0];var setCoLoading=s19[1];
  var s20=useState(null);var coError=s20[0];var setCoError=s20[1];
  var s21=useState(null);var coName=s21[0];var setCoName=s21[1];
  var sOB=useState(false);var showOnboarding=sOB[0];var setShowOnboarding=sOB[1];
  var sAD=useState(false);var alertDismissed=sAD[0];var setAlertDismissed=sAD[1];
  var intRef=useRef(null);var abortRef=useRef(null);var ocrRef=useRef(null);

  useEffect(function(){
    setHist(lh());
    try{if(typeof localStorage!=="undefined"&&!localStorage.getItem("te_onboarded"))setShowOnboarding(true);}catch(e){}
    var p4NavHandler=function(ev){try{var m=ev&&ev.detail&&ev.detail.mode;if(m){setMode(m);setShowH(false);setShowR(false);setResult(null);}}catch(e){}};
    try{window.addEventListener("te_p4_navigate",p4NavHandler);}catch(e){}
    return function(){clearInterval(intRef.current);if(abortRef.current)abortRef.current.abort();try{window.removeEventListener("te_p4_navigate",p4NavHandler);}catch(e){}};
  },[]);

  var resumeCall=useCallback(async function(msg,signal){return gemini(msg,RESUME_SYS,signal);},[]);
  var handleInput=useCallback(function(e){var v=e.target.value.slice(0,MAX_CHARS);setInput(v);setChars(v.length);setError(null);},[]);
  var handleOCR=useCallback(async function(e){
    var file=e.target.files[0];if(!file)return;
    setOcrOn(true);setOcrProg(0);setError(null);setInput("Loading OCR engine...");
    try{
      if(!window.Tesseract){await new Promise(function(resolve,reject){var s=document.createElement("script");s.src="https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js";s.onload=resolve;s.onerror=function(){reject(new Error("Failed to load OCR."));};document.head.appendChild(s);});}
      setInput("Extracting text (Hindi + English)...");
      var res=await window.Tesseract.recognize(file,"eng+hin",{logger:function(m){if(m.status==="recognizing text")setOcrProg(Math.round(m.progress*100));}});
      var ex=res.data.text.trim();if(!ex||ex.length<5)throw new Error("No text found.");
      var cap=ex.slice(0,MAX_CHARS);setInput(cap);setChars(cap.length);setOcrProg(0);
    }catch(err){setError(err.message||"OCR failed.");setInput("");setChars(0);}
    finally{setOcrOn(false);if(ocrRef.current)ocrRef.current.value="";}
  },[]);
  var loadReg=useCallback(async function(){setLoadR(true);var sc=await sbRecent();var st=await sbStats();setRecent(sc);setStats(st);setLoadR(false);},[]);
  var runScan=useCallback(async function(){
    var trimmed=input.trim();if(trimmed.length<10){setError("Paste a job post, message, or URL to analyse.");return;}
    var cached=checkCache(trimmed);if(cached&&cached.result){setResult(cached.result);setTab("flags");setError(null);return;}
    if(abortRef.current)abortRef.current.abort();abortRef.current=new AbortController();
    setError(null);setResult(null);setScanning(true);setStepIdx(0);setProgress(0);setTab("flags");setShowH(false);setShowR(false);
    var s=0;intRef.current=setInterval(function(){s++;if(s<SCAN_STEPS.length){setStepIdx(s);setProgress((s/SCAN_STEPS.length)*88);}},950);
    try{
      var content=trimmed,srcLabel="Direct",pn="";
      if(isURL(trimmed)){var r2=rp(trimmed);if(r2){pn="["+r2+" blocks scraping]";}else{var sc3=await scrape(trimmed,abortRef.current.signal);if(sc3){content=sc3;srcLabel="URL Scraped";}else pn="[Scraping blocked]";}}
      var sp3=spd(content),aj2=ajd(content),sal=sc2(content),plat=dp(trimmed);
      var patternCtx=getPatternContext(trimmed);var profile=getUserProfile();var personalCtx=getPersonalizedContext(profile);var regionCtx=getRegionContext();
      var ctx="LOCAL_ANALYSIS:\n- Source: "+srcLabel+" "+pn+"\n- Platform: "+plat+"\n- Scam Phrases: "+(sp3.length>0?sp3.join(", "):"None")+"\n- AI JD Score: "+aj2+"\n- Salary Flags: "+(sal.length>0?sal.join(" | "):"None")+"\n- "+regionCtx+"\n- "+personalCtx+(patternCtx?"\n- "+patternCtx:"");
      var fullScanPrompt=ctx+"\n\nCONTENT:\n\"\"\""+content+"\"\"\"";
      var parsed=await runAIStable(fullScanPrompt,SCAN_SYS,abortRef.current.signal);
      if(!parsed||parsed.error||parsed.fallback){try{var data=await gemini(fullScanPrompt,SCAN_SYS,abortRef.current.signal);var tr=gt(data);if(tr){var dp2=ultraParseJSON(tr);if(dp2&&!dp2.error)parsed=dp2;}}catch(ge){if(ge&&ge.name==="AbortError")throw ge;}}
      if(!parsed||parsed.error||parsed.fallback){parsed=RULE_ENGINE.analyze(input);setError("AI analysis unavailable — showing rule-based result.");}
      learnFromScan(trimmed,parsed,"scan");CROSS_INTEL.store("scan","last",parsed);try{s3Update("scan",parsed);}catch(e){}
      clearInterval(intRef.current);setProgress(100);setStepIdx(SCAN_STEPS.length-1);
      sbSave(parsed);sh(parsed,trimmed);setHist(lh());try{p6TrackActivity("scan");}catch(pe){}try{updatePassport("scan",parsed);}catch(ppe){}
      setTimeout(function(){
      setResult(parsed);setScanning(false);
      try{
        var sc4=parsed&&parsed.risk_score||0;
        if(sc4>=70){navigator.vibrate&&navigator.vibrate([200]);}
        else if(sc4>=40){navigator.vibrate&&navigator.vibrate([100,50,100]);}
        else{navigator.vibrate&&navigator.vibrate([50]);}
      }catch(e){}
    },400);
    }catch(e){
      clearInterval(intRef.current);setScanning(false);if(e&&e.name==="AbortError")return;
      var fallback=RULE_ENGINE.analyze(input);
      if(fallback){setResult(fallback);setError("AI unavailable — showing rule-based analysis.");}
      else{var _em=e.message||"Scan failed. Please retry.";if(_em.includes("EXHAUSTED")||_em.includes("limit")||_em.includes("busy")){_em="AI quota reached. Please retry in a moment.";}setError(_em);}
    }
  },[input]);

  var nl=chars>MAX_CHARS*0.88;
  var MODES=[
    {id:"scan",icon:"🛡",label:"Job Fraud Scanner",tagline:"Detect scams before they harm you",desc:"Paste any job post, WhatsApp message, or URL. Scans 30+ India fraud patterns instantly.",color:DS.red,bg:"rgba(244,63,94,0.07)",border:"rgba(244,63,94,0.2)",glow:"rgba(244,63,94,0.12)"},
    {id:"resume",icon:"📄",label:"Resume Intelligence",tagline:"Know your exact market value",desc:"Upload your resume. Get ATS score, salary benchmarks, role matches, and skill gaps.",color:DS.blue,bg:"rgba(59,130,246,0.07)",border:"rgba(59,130,246,0.2)",glow:"rgba(59,130,246,0.1)"},
    {id:"career",icon:"🚀",label:"Career Intelligence",tagline:"Your career ladder, mapped precisely",desc:"5-level India career ladder with real salaries and what's blocking you right now.",color:DS.purple,bg:"rgba(139,92,246,0.07)",border:"rgba(139,92,246,0.2)",glow:"rgba(139,92,246,0.1)"},
    {id:"pathfinder",icon:"🧭",label:"Pathfinder",tagline:"3 executable career paths",desc:"PATHFINDER computes 3 real career paths from your resume — with execution plans.",color:DS.teal,bg:"rgba(0,212,170,0.07)",border:"rgba(0,212,170,0.2)",glow:"rgba(0,212,170,0.1)"},
    {id:"hr",icon:"🎯",label:"HR Intelligence",tagline:"Rank 20 candidates in 60 seconds",desc:"Upload resumes + paste JD. AI ranks every candidate and explains exactly why.",color:DS.amber,bg:"rgba(245,158,11,0.07)",border:"rgba(245,158,11,0.2)",glow:"rgba(245,158,11,0.1)"},
    {id:"jd",icon:"📋",label:"JD Analyzer",tagline:"Decode any job description",desc:"Paste a JD. AI reveals hidden requirements, red flags, and ATS keywords.",color:DS.cyan,bg:"rgba(6,182,212,0.07)",border:"rgba(6,182,212,0.2)",glow:"rgba(6,182,212,0.1)"},
  ];
  var activeMode=MODES.find(function(m){return m.id===mode;});
  var isHome=!activeMode&&!showH&&!showR;
  var liveStats=getLiveStats();

  // Ambient verdict background tint (very subtle, scan module only)
  var ambientBg = DS.bg0;
  if (result && mode === "scan") {
    var rs = result.risk_score || 0;
    ambientBg = rs >= 70
      ? "radial-gradient(ellipse 80% 40% at 50% 0%,rgba(244,63,94,0.03) 0%,#010812 65%)"
      : rs >= 40
      ? "radial-gradient(ellipse 80% 40% at 50% 0%,rgba(245,158,11,0.025) 0%,#010812 65%)"
      : "radial-gradient(ellipse 80% 40% at 50% 0%,rgba(16,185,129,0.025) 0%,#010812 65%)";
  }

  return (
    <div style={{ minHeight:"100vh",background:ambientBg,color:DS.t1,fontFamily:"'Sora','Plus Jakarta Sans',-apple-system,system-ui,sans-serif",overflowX:"hidden",width:"100%",maxWidth:"100vw",boxSizing:"border-box",transition:"background 1.2s ease" }}>
      {showOnboarding&&<OnboardingOverlay onComplete={function(){setShowOnboarding(false);}}/>}
      <AIStatusBar/>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;-webkit-tap-highlight-color:transparent;margin:0;padding:0}
        html,body{overflow-x:hidden!important;width:100%!important;max-width:100%!important;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility;background:#010812}
        
        :root{
          --sp1:8px;--sp2:16px;--sp3:24px;--sp4:32px;--sp5:40px;--sp6:48px;
          --ease-out:cubic-bezier(0.22,1,0.36,1);
          --ease-inout:cubic-bezier(0.4,0,0.2,1);
          --ease-spring:cubic-bezier(0.34,1.56,0.64,1);
          --gold:#c9a84c;
          --t-instant:80ms;--t-fast:150ms;--t-med:300ms;--t-slow:500ms;--t-cinematic:800ms;
        }

        @media(max-width:640px){
          .te-scan-grid{grid-template-columns:1fr!important}
          .te-scan-right{display:none!important}
          .te-bottom-nav{display:flex!important}
          .te-main{padding-bottom:80px!important;padding-left:16px!important;padding-right:16px!important}
          .te-ticker-wrap{display:none!important}
          .te-ai-badge{display:none!important}
          .te-db-btn{display:none!important}
          .te-header-inner{padding:0 16px!important}
          .te-hero-stat-row{gap:12px!important}
          .te-module-grid{grid-template-columns:1fr 1fr!important;gap:8px!important}
        }
        @media(max-width:400px){.te-module-grid{grid-template-columns:1fr!important}}

        @keyframes teSlideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes teFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes teCardIn{from{opacity:0;transform:translateY(24px) scale(0.96)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes teSpin{to{transform:rotate(360deg)}}
        @keyframes tePulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.4;transform:scale(0.75)}}
        @keyframes teShimmer{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}
        @keyframes teFadeHero{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
        @keyframes teUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes teScaleIn{from{opacity:0;transform:scale(0.93)}to{opacity:1;transform:scale(1)}}
        @keyframes teBorderPulse{0%,100%{border-color:rgba(244,63,94,0.25)}50%{border-color:rgba(244,63,94,0.65)}}
        @keyframes teGreenBorderPulse{0%,100%{border-color:rgba(16,185,129,0.2)}50%{border-color:rgba(16,185,129,0.55)}}
        @keyframes teFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
        @keyframes teVerdictReveal{from{opacity:0;transform:scale(0.95) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes teRipple{from{transform:scale(0);opacity:0.35}to{transform:scale(2.8);opacity:0}}
        @keyframes teSlideRight{from{opacity:0;transform:translateX(-16px)}to{opacity:1;transform:translateX(0)}}
        @keyframes teVerdictFlood{from{opacity:0;transform:scale(0.92)}to{opacity:1;transform:scale(1)}}
        @keyframes teCountUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes teGoldPulse{0%,100%{box-shadow:0 0 0 rgba(201,168,76,0)}50%{box-shadow:0 0 24px rgba(201,168,76,0.35)}}
        @keyframes teStepIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
        @keyframes teCheckDraw{from{stroke-dashoffset:20}to{stroke-dashoffset:0}}
        @keyframes teLeftBorderPulse{0%,100%{opacity:0.5}50%{opacity:1}}

        ::-webkit-scrollbar{width:2px;height:2px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:#1E3050;border-radius:2px}
        ::-webkit-scrollbar-thumb:hover{background:#2A4066}

        textarea,input{font-family:'Sora',system-ui,sans-serif!important;transition:border-color var(--t-fast) ease,box-shadow var(--t-fast) ease}
        textarea::placeholder{color:#1E2F42!important;font-size:13px}
        input::placeholder{color:#1E2F42!important}
        textarea:focus,input:focus{outline:none}
        input,select{color-scheme:dark}
        button{font-family:'Sora',system-ui,sans-serif;outline:none}
        button:focus-visible{outline:2px solid rgba(201,168,76,0.5);outline-offset:2px}
        select option{background:#0D1F38}
        ::selection{background:rgba(201,168,76,0.2);color:#F0F6FF}

        .te-mode-card{
          cursor:pointer;
          transition:transform 0.32s var(--ease-out),border-color 0.22s ease,box-shadow 0.32s ease,background 0.22s ease;
          border-left:3px solid transparent!important;
        }
        .te-mode-card:hover{
          transform:translateY(-4px);
          box-shadow:0 20px 60px rgba(0,0,0,0.5)!important;
          border-left-color:var(--card-accent,rgba(201,168,76,0.5))!important;
        }
        .te-mode-card:active{transform:translateY(-1px) scale(0.995);transition-duration:0.1s}

        .te-btn-primary{position:relative;overflow:hidden;transition:transform var(--t-fast) ease,box-shadow var(--t-fast) ease}
        .te-btn-primary:hover{transform:translateY(-1px)}
        .te-btn-primary:active{transform:translateY(0) scale(0.98);transition-duration:0.08s}
        .te-btn-ghost{transition:background var(--t-fast) ease,border-color var(--t-fast) ease,color var(--t-fast) ease}

        .te-kw-missing{animation:teBorderPulse 2.5s ease infinite;font-weight:700!important}
        .te-kw-found{transition:transform var(--t-fast) ease,box-shadow var(--t-fast) ease}
        .te-kw-found:hover{transform:scale(1.05)}

        .te-logo-icon{animation:teFloat 4s ease-in-out infinite}

        .te-textarea-premium:focus{background:rgba(0,8,20,0.9)!important;box-shadow:0 0 0 2px rgba(201,168,76,0.12)!important}
        .te-input-focus:focus{box-shadow:0 0 0 2px rgba(201,168,76,0.15)!important}

        .te-verdict-enter{animation:teVerdictReveal 0.5s var(--ease-out) both}

        .te-bottom-nav{
          display:none;position:fixed;bottom:0;left:0;right:0;z-index:300;
          background:rgba(5,14,30,0.97);backdrop-filter:blur(28px);-webkit-backdrop-filter:blur(28px);
          border-top:1px solid rgba(255,255,255,0.06);padding:8px 4px;
          padding-bottom:calc(8px + env(safe-area-inset-bottom,0px));box-shadow:0 -8px 32px rgba(0,0,0,0.4);
        }
        .te-expandable-content{overflow:hidden;transition:max-height 0.35s var(--ease-inout),opacity 0.3s ease}

        .te-verdict-theater{animation:teVerdictFlood 0.6s var(--ease-out) both}
        .te-verdict-score{animation:teCountUp 0.4s var(--ease-out) 0.3s both}
        .te-verdict-label{animation:teSlideUp 0.4s var(--ease-out) 0.55s both}
        .te-verdict-body{animation:teFadeIn 0.5s ease 0.85s both}
        .te-step-in{animation:teStepIn 0.3s var(--ease-out) both}
        .te-gold-pulse{animation:teGoldPulse 2s ease infinite}

        /* ── Micro-interactions ── */
        button:not(:disabled){transition:transform 80ms ease,box-shadow 150ms ease,background 150ms ease,border-color 150ms ease,color 150ms ease}
        button:not(:disabled):active{transform:scale(0.97)!important;transition-duration:60ms!important}

        .te-card-lift{transition:transform 0.3s var(--ease-out),box-shadow 0.3s ease,border-color 0.22s ease}
        .te-card-lift:hover{transform:translateY(-2px)!important;box-shadow:0 16px 48px rgba(0,0,0,0.45)!important}
        .te-card-lift:active{transform:translateY(0)!important;transition-duration:0.1s!important}

        /* ── Intelligence confidence badge ── */
        .te-intel-feedback{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;background:rgba(16,185,129,0.06);border:1px solid rgba(16,185,129,0.15);border-radius:100px;font-size:10px;color:#10B981;font-family:'JetBrains Mono',monospace;letter-spacing:0.05em;font-weight:600}

        /* ── Color priority: mute secondary accents in result view ── */
        .te-result-zone .te-secondary-accent{opacity:0.65}
        .te-result-zone .te-secondary-accent:hover{opacity:1}

        /* ── Spacing normalization ── */
        .te-panel{padding:16px 24px}
        .te-panel-sm{padding:12px 16px}
        .te-panel-lg{padding:24px 32px}
      `}</style>

      {/* HEADER */}
      <header style={{ position:"sticky",top:0,zIndex:200,background:"rgba(1,8,18,0.98)",backdropFilter:"blur(32px)",WebkitBackdropFilter:"blur(32px)",borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
        <div className="te-header-inner" style={{ maxWidth:1200,margin:"0 auto",padding:"0 24px",height:56,display:"flex",alignItems:"center",gap:16 }}>
          <button onClick={function(){setMode("home");setShowH(false);setShowR(false);setResult(null);}} style={{ display:"flex",alignItems:"center",gap:10,background:"none",border:"none",cursor:"pointer",padding:0,flexShrink:0 }}>
            <div className="te-logo-icon" style={{ width:34,height:34,background:"linear-gradient(135deg,#0A1628,#0F2040)",border:"1px solid "+DS.goldBorder,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 0 20px rgba(201,168,76,0.12),inset 0 1px 0 rgba(255,255,255,0.06)" }}>
              <Icon name="shield" size={18} color={DS.gold}/>
            </div>
            <div>
              <div style={{ fontSize:14,fontWeight:800,color:DS.t1,letterSpacing:"-0.4px",lineHeight:1.1 }}>Trust Engine</div>
              <div style={{ fontSize:10,color:DS.gold,letterSpacing:"0.15em",fontFamily:"'JetBrains Mono',monospace",opacity:0.7,marginTop:1,fontWeight:500 }}>OS · INDIA</div>
            </div>
          </button>
          {activeMode&&(
            <div style={{ display:"flex",alignItems:"center",gap:8,animation:"teSlideRight 0.3s var(--ease-out)",flex:1 }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke={DS.t4} strokeWidth="1.5" strokeLinecap="round"/></svg>
              <div style={{ display:"flex",alignItems:"center",gap:8,background:activeMode.bg,border:"1px solid "+activeMode.border,borderRadius:8,padding:"5px 12px" }}>
                <Icon name={activeMode.id==="scan"?"scan":activeMode.id==="resume"?"resume":activeMode.id==="career"?"career":activeMode.id==="pathfinder"?"path":activeMode.id==="hr"?"hr":"jd"} size={13} color={activeMode.color}/>
                <span style={{ fontSize:11,color:activeMode.color,fontWeight:700,letterSpacing:"0.05em",fontFamily:"'JetBrains Mono',monospace" }}>{activeMode.label.toUpperCase()}</span>
              </div>
            </div>
          )}
          {showH&&<div style={{ display:"flex",alignItems:"center",gap:8,flex:1,animation:"teSlideRight 0.3s var(--ease-out)" }}><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke={DS.t4} strokeWidth="1.5" strokeLinecap="round"/></svg><span style={{ fontSize:11,color:DS.t2,fontWeight:500 }}>Scan History</span></div>}
          {showR&&<div style={{ display:"flex",alignItems:"center",gap:8,flex:1,animation:"teSlideRight 0.3s var(--ease-out)" }}><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke={DS.t4} strokeWidth="1.5" strokeLinecap="round"/></svg><span style={{ fontSize:11,color:DS.t2,fontWeight:500 }}>Live Registry</span></div>}
          {isHome&&<div style={{ flex:1 }}/>}
          <div style={{ display:"flex",alignItems:"center",gap:8,flexShrink:0 }}>
            <div className="te-ticker-wrap"><LiveIntelTicker/></div>
            <ImmunityBadge/>
            <div className="te-ai-badge" style={{ display:"flex",alignItems:"center",gap:6,background:DS.bg1,border:"1px solid "+DS.border1,borderRadius:20,padding:"5px 12px" }}>
              <div style={{ width:6,height:6,borderRadius:"50%",background:DS.green,animation:"tePulse 2s ease infinite",flexShrink:0 }}/>
              <span style={{ fontSize:9,color:DS.green,fontFamily:"'JetBrains Mono',monospace",letterSpacing:"0.1em",fontWeight:600,whiteSpace:"nowrap" }}>AI · LIVE</span>
            </div>
            <button onClick={function(){setShowH(!showH);setShowR(false);setResult(null);setMode("home");}}
              style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:showH?DS.bg2:"transparent",border:"1px solid "+(showH?DS.border1:"transparent"),borderRadius:8,padding:"6px 10px",cursor:"pointer",color:showH?DS.t1:DS.t3,fontSize:12,fontWeight:500,transition:"all 0.15s" }}>
              <Icon name="clock" size={14} color={showH?DS.t1:DS.t3}/>
              {hist.length>0&&<span style={{ fontSize:10,fontFamily:"'JetBrains Mono',monospace",color:showH?DS.t1:DS.t4 }}>{hist.length}</span>}
            </button>
            <button onClick={function(){setShowR(!showR);if(!showR)loadReg();setShowH(false);setMode("home");}} className="te-db-btn"
              style={{ display:"flex",alignItems:"center",justifyContent:"center",background:showR?DS.bg2:"transparent",border:"1px solid "+(showR?DS.border1:"transparent"),borderRadius:8,padding:"7px",cursor:"pointer",color:showR?DS.t1:DS.t3,transition:"all 0.15s" }}>
              <Icon name="live" size={14} color={showR?DS.t1:DS.t3}/>
            </button>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <div className="te-main" style={{ maxWidth:1200,margin:"0 auto",padding:"0 24px",width:"100%" }}>

        {/* HOME */}
        {isHome&&(
          <div style={{ paddingTop:40,paddingBottom:100 }}>
            {/* ZONE 1 — ACTIVE ALERT */}
            <HomeActiveAlert
              hist={hist}
              liveStats={liveStats}
              alertDismissed={alertDismissed}
              setAlertDismissed={setAlertDismissed}
              setMode={setMode}
              setShowH={setShowH}
              setShowR={setShowR}
            />
            {/* ZONE 2 — PRIMARY CTA */}
            <HomePrimaryAction
              hist={hist}
              setMode={setMode}
              setShowH={setShowH}
              setShowR={setShowR}
            />
            <MonthlyCareerAudit/>
            <GlobalDecisionPanel/>
            {/* Hero */}
            <div style={{ textAlign:"center",marginBottom:56,position:"relative",overflow:"hidden" }}>
              <div style={{ position:"absolute",top:-60,left:"50%",transform:"translateX(-50%)",width:"min(700px,100vw)",height:500,background:"radial-gradient(ellipse at 50% 30%,rgba(59,130,246,0.07) 0%,rgba(139,92,246,0.03) 45%,transparent 72%)",pointerEvents:"none",zIndex:0 }}/>
              <div style={{ position:"relative",zIndex:1 }}>
                <div style={{ display:"inline-flex",alignItems:"center",gap:8,background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.065)",borderRadius:100,padding:"5px 16px 5px 10px",marginBottom:28,animation:"teFadeHero 0.5s ease both",backdropFilter:"blur(8px)" }}>
                  <div style={{ width:5,height:5,borderRadius:"50%",background:DS.green,animation:"tePulse 2s ease infinite",boxShadow:"0 0 6px "+DS.green }}/>
                  <span style={{ fontSize:9,color:DS.t3,letterSpacing:2.5,fontFamily:"'JetBrains Mono',monospace",fontWeight:600 }}>LIVE · INDIA INTELLIGENCE</span>
                </div>
                <h1 style={{ fontSize:"clamp(30px,5.5vw,56px)",fontWeight:900,color:DS.t1,letterSpacing:"-2px",lineHeight:1.08,marginBottom:16,animation:"teFadeHero 0.6s ease 0.06s both",margin:"0 0 16px" }}>
                  Is this job real<br/>
                  <span style={{ background:"linear-gradient(135deg,"+DS.blue+" 20%,"+DS.teal+")",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",letterSpacing:"-2px" }}>or a scam?</span>
                </h1>
                <p style={{ fontSize:"clamp(14px,1.8vw,16px)",color:DS.t2,maxWidth:380,margin:"0 auto 24px",lineHeight:1.85,animation:"teFadeHero 0.6s ease 0.14s both",fontWeight:400 }}>Paste any job post, WhatsApp message, or URL.<br/>Get the verdict in 15 seconds.</p>
                <div className="te-hero-stat-row" style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:24,flexWrap:"wrap",marginBottom:44,animation:"teFadeHero 0.6s ease 0.22s both" }}>
                  <div style={{ display:"flex",alignItems:"center",gap:7 }}>
                    <span style={{ fontSize:16,fontWeight:900,color:DS.t1,fontFamily:"'JetBrains Mono',monospace",letterSpacing:"-0.5px" }}>{liveStats.total||0}</span>
                    <span style={{ fontSize:11,color:DS.t3,letterSpacing:0.3 }}>total scans</span>
                  </div>
                  <div style={{ width:1,height:14,background:"rgba(255,255,255,0.06)" }}/>
                  <div style={{ display:"flex",alignItems:"center",gap:7 }}>
                    <span style={{ fontSize:16,fontWeight:900,color:DS.red,fontFamily:"'JetBrains Mono',monospace",letterSpacing:"-0.5px" }}>{Math.max(liveStats.scams_today||0,0)}</span>
                    <span style={{ fontSize:11,color:DS.t3,letterSpacing:0.3 }}>scams caught today</span>
                  </div>
                  <div style={{ width:1,height:14,background:"rgba(255,255,255,0.06)" }}/>
                  <span style={{ fontSize:11,color:DS.t3,letterSpacing:"0.03em" }}>Built for India · IN</span>
                </div>
                {/* Primary scan input */}
                <div style={{ maxWidth:600,margin:"0 auto",animation:"teFadeHero 0.7s ease 0.32s both" }}>
                  <div style={{ background:DS.bg1,border:"1.5px solid rgba(244,63,94,0.22)",borderRadius:16,overflow:"hidden",boxShadow:"0 0 0 1px rgba(244,63,94,0.06),0 8px 40px rgba(244,63,94,0.07),0 24px 80px rgba(0,0,0,0.4)" }}>
                    <div style={{ padding:"10px 16px",borderBottom:"1px solid rgba(244,63,94,0.1)",display:"flex",alignItems:"center",justifyContent:"space-between",background:"rgba(244,63,94,0.035)" }}>
                      <span style={{ fontSize:9,color:DS.red,fontFamily:"'JetBrains Mono',monospace",letterSpacing:2.5,fontWeight:700,opacity:0.9 }}>PASTE JOB MESSAGE</span>
                      <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                        <input type="file" accept="image/*" onChange={handleOCR} ref={ocrRef} style={{ display:"none" }} id="home-ocr"/>
                        <label htmlFor="home-ocr" style={{ cursor:ocrOn?"wait":"pointer",fontSize:11,color:ocrOn?DS.amber:DS.t3,background:DS.bg0,padding:"4px 10px",border:"1px solid "+DS.border0,borderRadius:6,fontFamily:"'JetBrains Mono',monospace" }}>{ocrOn?("⏳ "+ocrProg+"%"):"📸 Scan Image"}</label>
                        <span style={{ fontSize:11,color:nl?DS.red:DS.t4,fontFamily:"'JetBrains Mono',monospace" }}>{chars}/{MAX_CHARS}</span>
                      </div>
                    </div>
                    <textarea value={input} onChange={handleInput} maxLength={MAX_CHARS} disabled={ocrOn} placeholder={"Paste suspicious job content here...\n\n• WhatsApp messages · LinkedIn posts · Telegram\n• Job portals · Emails · Hindi / Telugu / Tamil"} className="te-textarea-premium" style={{ width:"100%",minHeight:150,background:"transparent",color:DS.t1,border:"none",outline:"none",padding:"16px",fontSize:14,lineHeight:1.8,resize:"none",fontFamily:"'Sora',sans-serif",caretColor:DS.red,display:"block",boxSizing:"border-box" }}/>
                    {ocrOn&&<div style={{ height:2,background:DS.bg3 }}><div style={{ height:"100%",width:ocrProg+"%",background:DS.amber,transition:"width 0.2s" }}/></div>}
                    {error&&<div style={{ padding:"10px 16px",background:"rgba(244,63,94,0.07)",borderTop:"1px solid rgba(244,63,94,0.15)",fontSize:13,color:DS.red,display:"flex",gap:8 }}><span>⚠</span>{error}</div>}
                    <div style={{ padding:"12px 14px" }}>
                      <button onClick={scanning?function(){if(abortRef.current)abortRef.current.abort();setScanning(false);clearInterval(intRef.current);}:runScan} disabled={ocrOn} className="te-btn-primary" style={{ width:"100%",padding:"14px",background:scanning?"rgba(244,63,94,0.08)":"linear-gradient(135deg,#E8384A,#F43F5E)",border:"1px solid "+(scanning?"rgba(244,63,94,0.25)":"rgba(255,255,255,0.08)"),borderRadius:12,color:scanning?DS.red:"#fff",fontSize:13,fontWeight:800,cursor:(scanning||ocrOn)?"not-allowed":"pointer",transition:"all 0.22s",boxShadow:scanning?"none":"0 4px 24px rgba(244,63,94,0.3),inset 0 1px 0 rgba(255,255,255,0.1)",letterSpacing:0.5,display:"flex",alignItems:"center",justifyContent:"center",gap:10,opacity:ocrOn?0.4:1 }}>
                        {scanning?<><div style={{ width:13,height:13,border:"2px solid rgba(244,63,94,0.25)",borderTopColor:DS.red,borderRadius:"50%",animation:"teSpin 0.6s linear infinite" }}/><span style={{ fontSize:11,letterSpacing:1.5,fontFamily:"'JetBrains Mono',monospace" }}>SCANNING — TAP TO CANCEL</span></>:<><span style={{ fontSize:14 }}>▶</span><span style={{ letterSpacing:0.8 }}>Check This Job Post</span></>}
                      </button>
                      {scanning&&<div style={{ marginTop:10,height:2,background:DS.bg3,borderRadius:2,overflow:"hidden" }}><div style={{ height:"100%",width:progress+"%",background:"linear-gradient(90deg,"+DS.red+",#F97316)",transition:"width 0.5s ease" }}/></div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Result on home — CLEAN 4-LAYER HIERARCHY */}
            {result&&!scanning&&(
              <div style={{ maxWidth:900,margin:"0 auto 48px",animation:"teSlideUp 0.4s ease" }}>
                {/* L1+L2+L3 — DecisionBlock handles verdict, signals, one action */}
                <DecisionBlock type="scan" data={result}/>
                {/* L2 — Intelligence layer: score + prediction + trajectory */}
                <GlobalIntelligencePanel result={result} type="scan"/>
                {/* L4 — Everything else collapsed, user pulls it open */}
                <ExpandableSection title="Deep Analysis" accentColor={DS.red} defaultOpen={false}>
                  <div style={{padding:"16px 20px",display:"flex",flexDirection:"column",gap:16}}>
                    <ScanDetail result={result} tab={tab} setTab={setTab} copied={copied} setCopied={setCopied} coData={coData} coLoading={coLoading} coError={coError} setCoData={setCoData} setCoLoading={setCoLoading} setCoError={setCoError} coName={coName} setCoName={setCoName}/>
                    <PredictionEngine result={result}/>
                    <TrustCertificate result={result}/>
                    <ScamNetworkMap result={result}/>
                    <AIChatAssistant context={result} contextType="scan"/>
                    <P6ShareResultBlock result={result} type="scan"/>
                    <NextActionCard moduleType="scan"/>
                    <P6NextActionBlock type="scan"/>
                  </div>
                </ExpandableSection>
                <MobileResultBar result={result} type="scan" onNewScan={function(){setResult(null);setInput("");setChars(0);}}/>
              </div>
            )}

            {/* Module Grid */}
            <div style={{ marginBottom:40 }}>
              <ReputationPassport/>
              <ScamIntelPanel/>
              <div style={{ textAlign:"center",marginBottom:28,paddingTop:8 }}>
                <div style={{ width:40,height:1,background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent)",margin:"0 auto 20px" }}/>
                <div style={{ fontSize:9,color:DS.t4,letterSpacing:3.5,fontFamily:"'JetBrains Mono',monospace",marginBottom:10,fontWeight:600 }}>MORE INTELLIGENCE TOOLS</div>
                <div style={{ fontSize:20,fontWeight:800,color:DS.t1,letterSpacing:"-0.5px" }}>Six AI engines. One platform.</div>
              </div>
              {/* Featured fraud scanner */}
              <div className="te-mode-card" onClick={function(){setMode("scan");setShowH(false);setShowR(false);}} style={{ background:"linear-gradient(135deg,rgba(244,63,94,0.06) 0%,"+DS.bg1+" 60%)",border:"1px solid rgba(244,63,94,0.2)",borderRadius:18,padding:"22px 26px",marginBottom:16,display:"flex",alignItems:"center",gap:22,flexWrap:"wrap",position:"relative",overflow:"hidden",animation:"teCardIn 0.45s cubic-bezier(0.22,1,0.36,1) both",boxShadow:"0 8px 32px rgba(244,63,94,0.08)" }}>
                <div style={{ position:"absolute",top:0,left:0,right:0,height:2,background:"linear-gradient(90deg,transparent 5%,"+DS.red+" 40%,"+DS.red+"88 70%,transparent 95%)" }}/>
                <div style={{ position:"absolute",top:0,bottom:0,right:-40,width:200,background:"radial-gradient(ellipse at right,rgba(244,63,94,0.06),transparent 70%)",pointerEvents:"none" }}/>
                <div style={{ width:52,height:52,background:"rgba(244,63,94,0.1)",border:"1px solid rgba(244,63,94,0.22)",borderRadius:13,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 0 20px rgba(244,63,94,0.15)" }}><Icon name="scan" size={24} color={DS.red}/></div>
                <div style={{ flex:1,minWidth:200,position:"relative",zIndex:1 }}>
                  <div style={{ fontSize:9,color:DS.red,letterSpacing:3,fontFamily:"'JetBrains Mono',monospace",fontWeight:700,marginBottom:6,opacity:0.85 }}>MOST CRITICAL TOOL</div>
                  <div style={{ fontSize:17,fontWeight:800,color:DS.t1,marginBottom:5,letterSpacing:"-0.4px" }}>Job Fraud Scanner</div>
                  <div style={{ fontSize:12,color:DS.t2,lineHeight:1.75 }}>Paste any job post, WhatsApp message, or URL. AI scans 30+ India fraud patterns in 15 seconds.</div>
                </div>
                <div style={{ display:"flex",alignItems:"center",gap:8,flexShrink:0,padding:"9px 16px",background:"rgba(244,63,94,0.1)",border:"1px solid rgba(244,63,94,0.28)",borderRadius:10,position:"relative",zIndex:1 }}>
                  <span style={{ fontSize:12,color:DS.red,fontWeight:700,letterSpacing:0.3 }}>Scan Now</span>
                  <span style={{ color:DS.red,fontSize:14,fontWeight:900 }}>›</span>
                </div>
              </div>
              {/* ZONE 3 — MODULE GRID */}
              <div className="te-module-grid" style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12 }}>
                {MODES.slice(1).map(function(m,i){
                  var cardMeta = getModuleLastMeta(m.id);
                  var iconName = m.id==="scan"?"scan":m.id==="resume"?"resume":m.id==="career"?"career":m.id==="pathfinder"?"path":m.id==="hr"?"hr":"jd";
                  return (
                    <div key={m.id} className="te-mode-card" onClick={function(){setMode(m.id);setShowH(false);setShowR(false);}}
                      style={{ "--card-accent":m.color,background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:16,padding:"24px",display:"flex",flexDirection:"column",position:"relative",overflow:"hidden",animation:"teCardIn 0.45s var(--ease-out) "+(i*0.07)+"s both" }}
                      onMouseEnter={function(e){
                        e.currentTarget.style.borderColor=m.border;
                        e.currentTarget.style.boxShadow="0 20px 60px "+m.glow+",0 4px 16px rgba(0,0,0,0.4)";
                        e.currentTarget.style.background="linear-gradient(145deg,"+m.color+"06,"+DS.bg1+" 60%)";
                      }}
                      onMouseLeave={function(e){
                        e.currentTarget.style.borderColor=DS.border0;
                        e.currentTarget.style.boxShadow="none";
                        e.currentTarget.style.background=DS.bg1;
                      }}>
                      <div style={{ position:"absolute",top:0,left:0,right:0,height:1,background:"linear-gradient(90deg,transparent 5%,"+m.color+"44 50%,transparent 95%)" }}/>
                      <div style={{ position:"absolute",top:-32,right:-32,width:96,height:96,background:"radial-gradient(circle,"+m.color+"09,transparent 70%)",pointerEvents:"none" }}/>
                      <div style={{ width:44,height:44,background:m.bg,border:"1px solid "+m.border,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:16,flexShrink:0,boxShadow:"0 4px 12px "+m.color+"18" }}>
                        <Icon name={iconName} size={20} color={m.color}/>
                      </div>
                      <div style={{ fontSize:15,fontWeight:800,color:DS.t1,marginBottom:6,letterSpacing:"-0.3px",lineHeight:1.2 }}>{m.label}</div>
                      <div style={{ fontSize:12,color:m.color,fontWeight:600,marginBottom:10,lineHeight:1.5,opacity:0.9 }}>{m.tagline}</div>
                      <div style={{ fontSize:12,color:DS.t3,lineHeight:1.75,flex:1 }}>{m.desc}</div>
                      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:16,paddingTop:14,borderTop:"1px solid "+DS.border0 }}>
                        <span style={{ fontSize:11,color:m.color,fontWeight:700,letterSpacing:"0.03em",display:"flex",alignItems:"center",gap:4 }}>
                          Open
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 6h6M6 3l3 3-3 3" stroke={m.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </span>
                        {cardMeta&&<span style={{ fontSize:9,color:DS.t4,fontFamily:"'JetBrains Mono',monospace",textAlign:"right",lineHeight:1.4 }}>{cardMeta}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* System status — subtle footer */}
            <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:16,marginTop:16,paddingTop:16,borderTop:"1px solid "+DS.border0,flexWrap:"wrap" }}>
              <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                <div style={{ width:5,height:5,borderRadius:"50%",background:DS.green,animation:"tePulse 2s infinite" }}/>
                <span style={{ fontSize:10,color:DS.t4,fontFamily:"'JetBrains Mono',monospace",letterSpacing:"0.12em" }}>SYSTEMS ONLINE</span>
              </div>
              <span style={{ fontSize:10,color:DS.t4,fontFamily:"'JetBrains Mono',monospace" }}>·</span>
              <span style={{ fontSize:10,color:DS.t4,fontFamily:"'JetBrains Mono',monospace",letterSpacing:"0.08em" }}>GEMINI 2.5 + OPENROUTER</span>
              <span style={{ fontSize:10,color:DS.t4,fontFamily:"'JetBrains Mono',monospace" }}>·</span>
              <span style={{ fontSize:10,color:DS.t4,fontFamily:"'JetBrains Mono',monospace",letterSpacing:"0.08em" }}>6 MODULES ACTIVE</span>
            </div>
          </div>
        )}

        {/* HISTORY */}
        {showH&&!showR&&(
          <div style={{ paddingTop:40,paddingBottom:80,animation:"teFadeIn 0.3s ease" }}>
            <div style={{ marginBottom:28 }}>
              <div style={{ fontSize:10,color:DS.t3,letterSpacing:3,marginBottom:8,fontFamily:"'JetBrains Mono',monospace" }}>INTELLIGENCE VAULT</div>
              <div style={{ fontSize:26,fontWeight:800,color:DS.t1 }}>Scan History</div>
              <div style={{ fontSize:14,color:DS.t2,marginTop:6 }}>Your previous threat analyses. Click any to reload the full result.</div>
            </div>
            {hist.length===0?(<div style={{ textAlign:"center",padding:"64px 24px",color:DS.t3 }}>
                <div style={{ width:56,height:56,background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px" }}>
                  <Icon name="clock" size={24} color={DS.t4}/>
                </div>
                <div style={{ fontSize:14,fontWeight:600,color:DS.t2,marginBottom:8 }}>No scan history yet</div>
                <div style={{ fontSize:13,color:DS.t3,lineHeight:1.7,maxWidth:300,margin:"0 auto" }}>Each analysis is stored locally. No data leaves your device.</div>
              </div>):(
              <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                {hist.map(function(item,idx){
                  var q=item.qv?({SCAM:{color:DS.red,label:"✕ SCAM"},GHOST:{color:DS.amber,label:"👻 GHOST"},REAL:{color:DS.green,label:"✓ REAL"},SUSPICIOUS:{color:DS.purple,label:"? SUSPICIOUS"}}[item.qv]||{color:DS.purple,label:"? SUSPICIOUS"}):null;
                  var sc=item.score>=70?DS.red:item.score>=40?DS.amber:DS.green;
                  return (
                    <div key={item.id} onClick={function(){setResult(item.result);setShowH(false);setTab("flags");setMode("scan");}}
                      style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:12,padding:"14px 18px",cursor:"pointer",display:"flex",alignItems:"center",gap:14,transition:"all 0.2s" }}
                      onMouseEnter={function(e){e.currentTarget.style.borderColor=DS.border1;e.currentTarget.style.background=DS.bg2;}}
                      onMouseLeave={function(e){e.currentTarget.style.borderColor=DS.border0;e.currentTarget.style.background=DS.bg1;}}>
                      <div style={{ width:42,height:42,background:sc+"15",border:"1px solid "+sc+"33",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}><span style={{ fontSize:13,fontWeight:900,color:sc,fontFamily:"'JetBrains Mono',monospace" }}>{item.score}</span></div>
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:4 }}>
                          {q&&<span style={{ fontSize:10,color:q.color,background:q.color+"14",border:"1px solid "+q.color+"30",padding:"2px 8px",borderRadius:6,fontFamily:"'JetBrains Mono',monospace",fontWeight:700 }}>{q.label}</span>}
                          <span style={{ fontSize:11,color:DS.t3 }}>Scan #{idx+1} · {new Date(item.ts).toLocaleDateString("en-IN")}</span>
                        </div>
                        <div style={{ fontSize:13,color:DS.t2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{item.preview}...</div>
                      </div>
                      <span style={{ color:DS.t4,fontSize:18,flexShrink:0 }}>›</span>
                    </div>
                  );
                })}
                <button onClick={function(){try{if(typeof localStorage!=="undefined")localStorage.removeItem(HK);}catch(e){}setHist([]);}} style={{ marginTop:8,fontSize:12,color:DS.red,background:"transparent",border:"1px solid rgba(244,63,94,0.2)",padding:"7px 14px",borderRadius:8,cursor:"pointer",alignSelf:"flex-start" }}>Clear vault</button>
              </div>
            )}
          </div>
        )}

        {/* LIVE REGISTRY */}
        {showR&&!showH&&(
          <div style={{ paddingTop:40,paddingBottom:80,animation:"teFadeIn 0.3s ease" }}>
            <div style={{ marginBottom:28 }}>
              <div style={{ fontSize:10,color:DS.t3,letterSpacing:3,marginBottom:8,fontFamily:"'JetBrains Mono',monospace" }}>INDIA SCAM REGISTRY · LIVE</div>
              <div style={{ fontSize:26,fontWeight:800,color:DS.t1 }}>Live Intelligence Feed</div>
              <div style={{ fontSize:14,color:DS.t2,marginTop:6 }}>Real-time scam patterns detected across India's job market.</div>
            </div>
            {stats&&<div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:24 }}>
              {[{l:"TOTAL SCANS",v:stats.total,c:DS.t1},{l:"CONFIRMED SCAM",v:stats.scams,c:DS.red},{l:"GHOST HIRING",v:stats.ghost,c:DS.amber},{l:"TOP PATTERN",v:stats.top?stats.top.slice(0,12):"—",c:DS.purple}].map(function(s){return <div key={s.l} style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:12,padding:"16px" }}><div style={{ fontSize:9,color:DS.t3,letterSpacing:2,marginBottom:6,fontFamily:"'JetBrains Mono',monospace" }}>{s.l}</div><div style={{ fontSize:22,fontWeight:900,color:s.c,fontFamily:"'JetBrains Mono',monospace" }}>{s.v}</div></div>;})}
            </div>}
            {loadR?<div style={{ textAlign:"center",padding:"40px 0",color:DS.t3 }}>Loading...</div>:recent.length===0?<div style={{ textAlign:"center",padding:"60px 0",color:DS.t3 }}><div style={{ fontSize:40,marginBottom:16 }}>📡</div><div>No data yet.</div></div>:recent.map(function(item,i){var sc2=item.risk_score>=70?DS.red:item.risk_score>=40?DS.amber:DS.green;return <div key={i} style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:10,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:8 }}><div><div style={{ fontSize:12,fontWeight:600,color:DS.t1,marginBottom:3 }}>{item.scam_type||"General Fraud"}</div><div style={{ fontSize:11,color:DS.t3 }}>{item.platform||"Unknown"}</div></div><div style={{ fontSize:15,fontWeight:900,color:sc2,fontFamily:"'JetBrains Mono',monospace",flexShrink:0 }}>{item.risk_score}/100</div></div>;})}
          </div>
        )}

        {/* MODULE VIEW */}
        {activeMode&&!showH&&!showR&&(
          <div style={{ paddingTop:28,paddingBottom:80,animation:"teFadeIn 0.3s ease" }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,gap:16,flexWrap:"wrap" }}>
              <div style={{ display:"flex",alignItems:"center",gap:14 }}>
                <div style={{ width:44,height:44,background:activeMode.bg,border:"1px solid "+activeMode.border,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center" }}>
                  <Icon name={activeMode.id==="scan"?"scan":activeMode.id==="resume"?"resume":activeMode.id==="career"?"career":activeMode.id==="pathfinder"?"path":activeMode.id==="hr"?"hr":"jd"} size={22} color={activeMode.color}/>
                </div>
                <div>
                  <div style={{ fontSize:20,fontWeight:800,color:DS.t1,letterSpacing:"-0.3px" }}>{activeMode.label}</div>
                  <div style={{ fontSize:13,color:DS.t2,marginTop:2 }}>{activeMode.tagline}</div>
                </div>
              </div>
              <button onClick={function(){setMode("home");setResult(null);}} style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:10,padding:"8px 16px",cursor:"pointer",color:DS.t2,fontSize:12,fontWeight:500,transition:"all 0.2s",flexShrink:0 }} onMouseEnter={function(e){e.currentTarget.style.color=DS.t1;e.currentTarget.style.borderColor=DS.border1;}} onMouseLeave={function(e){e.currentTarget.style.color=DS.t2;e.currentTarget.style.borderColor=DS.border0;}}>← All tools</button>
            </div>
            <div style={{ height:1,background:"linear-gradient(90deg,"+activeMode.color+"55,transparent)",marginBottom:24 }}/>

            {mode==="resume"&&<ResumeMode callFn={resumeCall}/>}
            {mode==="hr"&&<HRIntelSection/>}
            {mode==="career"&&<CareerEngineSection/>}
            {mode==="pathfinder"&&<PathfinderSection/>}
            {mode==="jd"&&<JDAnalyzerSection/>}

            {mode==="scan"&&(
              <div>
                <div className="te-scan-grid" style={{ display:"grid",gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)",gap:20,marginBottom:20 }}>
                  {/* Left: input */}
                  <div style={{ display:"flex",flexDirection:"column",gap:12,minWidth:0 }}>
                    <div style={{ background:DS.bg1,border:"1px solid "+DS.border1,borderRadius:14,overflow:"hidden" }}>
                      <div style={{ padding:"10px 14px",borderBottom:"1px solid "+DS.border0,display:"flex",alignItems:"center",justifyContent:"space-between",background:"rgba(244,63,94,0.04)",flexWrap:"wrap",gap:8 }}>
                        <span style={{ fontSize:11,color:DS.red,fontFamily:"'JetBrains Mono',monospace",letterSpacing:1 }}>JOB MESSAGE</span>
                        <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                          <input type="file" accept="image/*" onChange={handleOCR} ref={ocrRef} style={{ display:"none" }} id="ocr-scan"/>
                          <label htmlFor="ocr-scan" style={{ cursor:ocrOn?"wait":"pointer",fontSize:10,color:ocrOn?DS.amber:DS.t3,background:DS.bg0,padding:"3px 9px",border:"1px solid "+DS.border0,borderRadius:5,fontFamily:"'JetBrains Mono',monospace" }}>{ocrOn?("⏳ "+ocrProg+"%"):"📸 OCR"}</label>
                          <span style={{ fontSize:10,color:nl?DS.red:DS.t4,fontFamily:"'JetBrains Mono',monospace" }}>{chars}/{MAX_CHARS}</span>
                        </div>
                      </div>
                      <textarea value={input} onChange={handleInput} maxLength={MAX_CHARS} disabled={ocrOn} placeholder={"Paste suspicious job content...\n\n// WhatsApp  LinkedIn  Naukri\n// Telegram  Indeed  Hindi  Telugu"} style={{ width:"100%",minHeight:240,background:"rgba(0,0,0,0.3)",color:DS.t1,border:"none",outline:"none",padding:"14px",fontSize:13,lineHeight:1.8,resize:"vertical",fontFamily:"'Sora',sans-serif",caretColor:DS.red,display:"block",boxSizing:"border-box" }}/>
                      {ocrOn&&<div style={{ height:2,background:DS.bg3 }}><div style={{ height:"100%",width:ocrProg+"%",background:DS.amber,transition:"width 0.2s" }}/></div>}
                    </div>
                    {error&&<div style={{ background:"rgba(244,63,94,0.07)",border:"1px solid rgba(244,63,94,0.18)",borderRadius:10,padding:"10px 14px",fontSize:13,color:DS.red,display:"flex",gap:8 }}><span>⚠</span><span>{error}</span></div>}
                    <button onClick={scanning?function(){if(abortRef.current)abortRef.current.abort();setScanning(false);clearInterval(intRef.current);}:runScan} disabled={ocrOn} style={{ width:"100%",padding:"13px",background:scanning?"transparent":DS.red,border:"1px solid "+(scanning?"rgba(244,63,94,0.3)":"transparent"),borderRadius:12,color:scanning?DS.red:"#fff",fontSize:13,fontWeight:800,cursor:(scanning||ocrOn)?"not-allowed":"pointer",transition:"all 0.2s",boxShadow:scanning?"none":"0 4px 20px rgba(244,63,94,0.25)",letterSpacing:0.3,display:"flex",alignItems:"center",justifyContent:"center",gap:10,opacity:ocrOn?0.5:1 }}>
                      {scanning?<><div style={{ width:12,height:12,border:"2px solid rgba(244,63,94,0.3)",borderTopColor:DS.red,borderRadius:"50%",animation:"teSpin 0.6s linear infinite" }}/><span style={{ fontSize:11 }}>ANALYSING — CLICK TO CANCEL</span></>:"▶  Check This Job Post"}
                    </button>
                    {scanning&&<div style={{ height:2,background:DS.bg3,borderRadius:1,overflow:"hidden" }}><div style={{ height:"100%",width:progress+"%",background:"linear-gradient(90deg,"+DS.red+",#F97316)",transition:"width 0.5s ease" }}/></div>}
                  </div>
                  {/* Right: result preview */}
                  <div className="te-scan-right" style={{ display:"flex",flexDirection:"column",gap:12,minWidth:0 }}>
                    {!result&&!scanning&&(
                      <div style={{ background:DS.bg1,border:"1px solid "+DS.border0,borderRadius:14,flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 20px",textAlign:"center",minHeight:280 }}>
                        <div style={{ width:56,height:56,background:"rgba(244,63,94,0.06)",border:"1px solid rgba(244,63,94,0.15)",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:16 }}>
                          <Icon name="shield" size={26} color={DS.red}/>
                        </div>
                        <div style={{ fontSize:13,color:DS.t3,marginBottom:8 }}>Waiting for analysis</div>
                        <div style={{ fontSize:13,color:DS.t4,lineHeight:1.7 }}>Paste job content on the left and click Check to see the verdict here.</div>
                      </div>
                    )}
                    {scanning&&(
                      <div style={{ background:DS.bg1,border:"1px solid rgba(244,63,94,0.18)",borderRadius:14,padding:"20px",minHeight:280 }}>
                        <Thinking steps={SCAN_STEPS.map(function(s){return s.label;})} currentStep={stepIdx} progress={progress} color={DS.red} title="Scanning for fraud patterns..." fullscreen={true}/>
                      </div>
                    )}
                    {result&&!scanning&&(
                      <div>
                        <VerdictTheater result={result}/>
                        <ScanPrimaryAction result={result}/>
                      </div>
                    )}
                  </div>
                </div>
                {result&&!scanning&&(
                  <div className="te-result-zone">
                  <ExpandableSection title="Deep Analysis" accentColor={DS.red} defaultOpen={false}>
                    <div style={{padding:"16px 20px",display:"flex",flexDirection:"column",gap:16}}>
                      <GlobalIntelligencePanel result={result} type="scan"/>
                      <ScanDetail result={result} tab={tab} setTab={setTab} copied={copied} setCopied={setCopied} coData={coData} coLoading={coLoading} coError={coError} setCoData={setCoData} setCoLoading={setCoLoading} setCoError={setCoError} coName={coName} setCoName={setCoName}/>
                      <PredictionEngine result={result}/>
                      <TrustCertificate result={result}/>
                      <ScamNetworkMap result={result}/>
                      <AIChatAssistant context={result} contextType="scan"/>
                      <P6ShareResultBlock result={result} type="scan"/>
                      <NextActionCard moduleType="scan"/>
                      <P6NextActionBlock type="scan"/>
                    </div>
                  </ExpandableSection>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <MobileBottomNav mode={mode} setMode={setMode} setShowH={setShowH} setShowR={setShowR} setResult={setResult}/>
    </div>
  );
}

// ===================================================
// ROOT EXPORT
// ===================================================
export default function App() {
  return (
    <ErrorBoundary>
      <AppCore/>
    </ErrorBoundary>
  );
}
